/* === js/config-frontend.js === */
/**
 * Frontend Configuration
 * Client-side settings only - NO API KEYS!
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/config-frontend.js">
 * USED BY: map.js (CONFIG.MAP_DEFAULTS, CONFIG.MAP_LAYERS), map-overlays.js (CONFIG.BED_TYPE_CLASSES), main-server.js (getScoreColor, getScoreLabel),
 *          ui-builders.js (getScoreColor for detail card), brag-board.js (getScoreColor for photo overlay)
 * EXPORTS: CONFIG object, getScoreColor(score), getScoreLabel(score) as globals
 * NOTE: getScoreColor() must stay in sync with server/routes/ssr.js getScoreColor()
 */

const CONFIG = {
    MAP_DEFAULTS: {
        center: [29.3013, -94.7977],
        zoom: 10,
        // Keyless CyclOSM — used for the initial setView() (before any layer is resolved),
        // spots.js's standalone "add a My Spot" mini-map (which has no key-substitution logic
        // of its own), and as js/map.js ensureBaseLayer()'s fallback for MAP_LAYERS.voyager
        // when no MapTiler key is available. Intentionally NOT kept in sync with voyager below
        // anymore — they diverged on purpose when voyager moved to MapTiler Basic.
        tileLayer: 'https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
        attribution: '<a href="https://github.com/cyclosm/cyclosm-cartocss-style/releases" title="CyclOSM - Open Bicycle render">CyclOSM</a> | Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
    },
    MAP_LAYERS: {
        voyager: {
            name: 'Streets',
            // {key} is resolved by Leaflet from the tileLayer options object, not hardcoded
            // here — see js/map.js ensureBaseLayer() (same mechanism satellite below already
            // uses). If no key has loaded yet, or one was withheld near the monthly quota (see
            // server/services/maptilerUsage.js), ensureBaseLayer() falls back to the keyless
            // MAP_DEFAULTS (CyclOSM) above instead of requesting tiles with an empty key.
            url: 'https://api.maptiler.com/maps/basic-v2/{z}/{x}/{y}.png?key={key}',
            attribution: '<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> | Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            tileSize: 512,
            zoomOffset: -1,
            icon: '🗺️'
        },
        satellite: {
            name: 'Satellite',
            // {key} is resolved by Leaflet from the tileLayer options object, not hardcoded
            // here — see js/map.js ensureBaseLayer(). Fetched at runtime from
            // AUTH_CLIENT.config.maptilerKey (GET /api/auth/config), NOT stored in this file
            // per its own "NO API KEYS" rule above. The key should be domain-restricted
            // (Referrer) in the MapTiler account dashboard — see setup steps in
            // tasks/082726-COST-AMPLIFICATION-HARDENING-PLAN.md.
            url: 'https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key={key}',
            attribution: '<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> | Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            icon: '🛰️'
        }
    },
    DISPLAY: {
        TOP_SPOTS_COUNT: 5
    },
    // Bed Type overlay classes, in scripts/bedtype/classes.py CLASS_KEYS order (the
    // tile property `c` is the key). One colour table for the map fill and the species
    // bottom-type share bar.
    BED_TYPE_CLASSES: {
        sand:    { name: 'Sand',       color: '#e8c35a' },
        sandMud: { name: 'Sand & mud', color: '#a88c55' },
        mud:     { name: 'Mud',        color: '#5b4a3a' },
        gravel:  { name: 'Gravel',     color: '#9a9a9a' },
        shell:   { name: 'Shell',      color: '#e86fb0' },
        rock:    { name: 'Rock',       color: '#d23c3c' }
    }
};

// getScoreColor() and getScoreLabel() are now defined in server/utils/shared-utils.js
// They're loaded as globals before this file via the app bundle

/**
 * Get fishing recommendation based on score
 */
function getRecommendation(score, loc) {
    if (score >= 75) return `Excellent conditions at ${loc.name}! ${loc.description}. This is prime time to fish here!`;
    if (score >= 60) return `Good conditions at ${loc.name}. ${loc.description}. You should have decent success today.`;
    if (score >= 45) return `Fair conditions at ${loc.name}. ${loc.description}. Fish may be less active, but it's still worth trying.`;
    return `Challenging conditions at ${loc.name} today. ${loc.description}. Consider trying a more sheltered location.`;
}

/* ==========================================================================
   FISH BITE INDEX (FBI) CALCULATION
   All FBI calculations are now performed server-side to protect business logic.
   See: server/services/scoring.js
   ========================================================================== */

// Protect CONFIG from modification (security measure)
if (typeof Object.freeze === 'function') {
    Object.freeze(CONFIG.MAP_DEFAULTS);
    Object.freeze(CONFIG.MAP_LAYERS.voyager);
    Object.freeze(CONFIG.MAP_LAYERS.satellite);
    Object.freeze(CONFIG.MAP_LAYERS);
    Object.freeze(CONFIG.DISPLAY);
    Object.values(CONFIG.BED_TYPE_CLASSES).forEach(Object.freeze);
    Object.freeze(CONFIG.BED_TYPE_CLASSES);
    Object.freeze(CONFIG);
}

// Export for Node.js (testing)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { CONFIG };
}

;
/* === server/utils/shared-utils.js === */
/**
 * Shared Utility Functions
 * Used by both client and server to avoid code duplication
 *
 * LOADED BY: Frontend bundle (scripts/bundle.js), server require('../utils/shared-utils')
 * USED BY: main-server.js, ui-builders.js, gear.js, config-frontend.js, ssr.js, scoring.js
 * EXPORTS: getMoonPhase, getScoreColor, getScoreLabel, getSunTimes, TX_SUNRISE_HOURS, TX_SUNSET_HOURS,
 *          LOCATION_TYPES, WATER_TYPES, validateSpotInput, formatLimitDisplay, etc.
 */

// TX Gulf Coast monthly averages (decimal hours, ~29.5°N latitude)
// Fallback when OpenWeather API sunrise/sunset data missing
// TODO: MULTI-STATE — need solar calculation for non-TX states
var TX_SUNRISE_HOURS = [7.25, 7.1, 6.75, 6.3, 6.0, 6.2, 6.4, 6.5, 6.75, 7.0, 7.15, 7.25];
var TX_SUNSET_HOURS = [17.75, 18.25, 18.75, 19.5, 20.0, 20.5, 20.5, 20.0, 19.25, 18.5, 17.75, 17.5];

/**
 * Get sunrise/sunset times for a given month (0-indexed)
 * @param {number} month - Month index (0 = January, 11 = December)
 * @returns {{ sunrise: number, sunset: number }} Times in decimal hours
 */
function getSunTimes(month) {
    return { sunrise: TX_SUNRISE_HOURS[month], sunset: TX_SUNSET_HOURS[month] };
}

/**
 * Get color for fishing score (White -> Green gradient)
 * Low scores (0-69): White
 * Good/Excellent (70-100): Green (transitions from white to deep green)
 */
function getScoreColor(score) {
    score = Math.max(0, Math.min(100, score));
    if (score < 70) {
        return 'rgb(255, 255, 255)';
    } else {
        const t = (score - 70) / 30;
        const r = Math.round(200 - (200 - 34) * t);
        const g = Math.round(230 - (230 - 160) * t);
        const b = Math.round(200 - (200 - 34) * t);
        return `rgb(${r}, ${g}, ${b})`;
    }
}

/**
 * Get label for fishing score
 */
function getScoreLabel(score) {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Poor';
}

/**
 * Calculate moon phase with fishing-specific scoring
 * @param {Date} date - Date to calculate moon phase for (defaults to now)
 * @returns {{ name: string, icon: string, score: number, maxScore: number, description: string, type: string }}
 */
function getMoonPhase(date) {
    if (!date) date = new Date();
    const known = new Date(2000, 0, 6, 18, 14);
    const phase = ((date - known) / (1000 * 60 * 60 * 24) % 29.53058867) / 29.53058867;

    if (phase < 0.033 || phase > 0.967) return { name: 'New Moon', icon: '\u{1F311}', score: 6, maxScore: 6, description: 'New Moon - Peak feeding', type: 'positive' };
    if (phase < 0.217) return { name: 'Waxing Crescent', icon: '\u{1F312}', score: 2, maxScore: 6, description: 'Waxing Crescent - Fair feeding', type: 'neutral' };
    if (phase < 0.283) return { name: 'First Quarter', icon: '\u{1F313}', score: 4, maxScore: 6, description: 'First Quarter - Good feeding', type: 'positive' };
    if (phase < 0.467) return { name: 'Waxing Gibbous', icon: '\u{1F314}', score: 2, maxScore: 6, description: 'Waxing Gibbous - Reduced feeding', type: 'neutral' };
    if (phase < 0.533) return { name: 'Full Moon', icon: '\u{1F315}', score: 6, maxScore: 6, description: 'Full Moon - Peak feeding', type: 'positive' };
    if (phase < 0.717) return { name: 'Waning Gibbous', icon: '\u{1F316}', score: 2, maxScore: 6, description: 'Waning Gibbous - Reduced feeding', type: 'neutral' };
    if (phase < 0.783) return { name: 'Last Quarter', icon: '\u{1F317}', score: 4, maxScore: 6, description: 'Last Quarter - Good feeding', type: 'positive' };
    return { name: 'Waning Crescent', icon: '\u{1F318}', score: 2, maxScore: 6, description: 'Waning Crescent - Fair feeding', type: 'neutral' };
}

/**
 * Convert wind degrees to 8-point compass direction name.
 * Pure math — no metadata (images, emojis, tips).
 * @param {number} deg - Wind direction in degrees (0-360)
 * @returns {string} Direction name: 'N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'
 */
function degreesToCompass8(deg) {
    const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    deg = ((deg % 360) + 360) % 360;
    return dirs[Math.round(deg / 45) % 8];
}

/**
 * Convert inches of mercury to millibar
 * @param {number} inHg - Pressure in inches of mercury
 * @returns {number} Pressure in millibar
 */
function inchesToMillibar(inHg) {
    return Math.round(inHg * 33.8638);
}

/**
 * Convert millibar to inches of mercury
 * @param {number} mb - Pressure in millibar
 * @returns {number} Pressure in inches of mercury
 */
function millibarToInches(mb) {
    return (mb / 33.8638).toFixed(2);
}

// getWindDirection has been moved to weather.js - use that for all wind direction needs

/**
 * Get weather emoji for condition code
 * @param {string} code - Weather condition code (e.g., '01d', '10d')
 * @returns {string} Weather emoji
 */
function getWeatherEmoji(code) {
    const weatherEmojis = {
        '01d': '☀️', '01n': '🌙',
        '02d': '⛅', '02n': '☁️',
        '03d': '☁️', '03n': '☁️',
        '04d': '☁️', '04n': '☁️',
        '09d': '🌧️', '09n': '🌧️',
        '10d': '🌦️', '10n': '🌧️',
        '11d': '⛈️', '11n': '⛈️',
        '13d': '❄️', '13n': '❄️',
        '50d': '🌫️', '50n': '🌫️'
    };
    return weatherEmojis[code] || '🌤️';
}

/**
 * Format temperature with unit
 * @param {number} temp - Temperature value
 * @param {string} unit - Unit ('F' or 'C')
 * @returns {string} Formatted temperature string
 */
function formatTemperature(temp, unit = 'F') {
    return `${Math.round(temp)}°${unit}`;
}

/**
 * Format wind speed with direction
 * @param {number} speed - Wind speed in mph
 * @param {number} deg - Wind direction in degrees
 * @returns {string} Formatted wind string (e.g., "10 mph NE")
 */
function formatWind(speed, deg) {
    // Uses getWindDirection from weather.js which returns object with .name
    const windDir = typeof getWindDirection === 'function' ? getWindDirection(deg) : { name: 'N' };
    return `${Math.round(speed)} mph ${windDir.name}`;
}

/**
 * Convert meters per second to miles per hour
 * @param {number} mps - Speed in meters per second
 * @returns {number} Speed in miles per hour
 */
function mpsToMph(mps) {
    return mps * 2.237;
}

/**
 * Convert Kelvin to Fahrenheit
 * @param {number} kelvin - Temperature in Kelvin
 * @returns {number} Temperature in Fahrenheit
 */
function kelvinToFahrenheit(kelvin) {
    return (kelvin - 273.15) * 9/5 + 32;
}

/**
 * Clamp a value between min and max
 * @param {number} value - Value to clamp
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} Clamped value
 */
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

/**
 * Deep freeze an object to prevent modifications (for config protection)
 * @param {Object} obj - Object to freeze
 * @returns {Object} Frozen object
 */
function deepFreeze(obj) {
    Object.freeze(obj);
    Object.getOwnPropertyNames(obj).forEach(prop => {
        if (obj[prop] !== null
            && (typeof obj[prop] === "object" || typeof obj[prop] === "function")
            && !Object.isFrozen(obj[prop])) {
            deepFreeze(obj[prop]);
        }
    });
    return obj;
}

/**
 * Convert NWS hourly periods to forecast table format
 * Takes raw NWS period data and converts to format expected by buildForecastTable
 * @param {Array} periods - NWS hourly periods array
 * @param {Array} tideData24h - 24-hour tide data for the location
 * @returns {Array} Forecast data in table format
 */
function convertNWSPeriodsToForecast(periods, tideData24h) {
    if (!periods || !Array.isArray(periods) || periods.length === 0) {
        return [];
    }

    console.log(`[CONVERT] Converting periods: ${periods.length} total (all-hourly (One Call + Open-Meteo))...`);

    // Limit to 168 periods max (7 days × 24h all-hourly)
    const limitedPeriods = periods.slice(0, 168);

    console.log(`[CONVERT] After slice: ${limitedPeriods.length} periods`);

    // Parse wind speed string (e.g., "8 mph" -> 8)
    const parseWindSpeed = (str) => {
        if (!str) return 0;
        const match = str.match(/\d+/);
        return match ? parseInt(match[0]) : 0;
    };

    // Convert periods to forecast table format
    return limitedPeriods.map((period, index) => {
        const time = new Date(period.startTime);
        const isHourly = period.isHourly !== false; // Default to true if not specified

        // All periods are hourly — simple index-based hour count
        const hoursFromStart = index;
        const day = Math.floor(hoursFromStart / 24) + 1;

        // Use server-provided tideHeight if available (already matched server-side)
        // Fall back to client-side matching only if server didn't provide it
        let tideHeight = 1.5; // Default
        if (period.tideHeight !== undefined && period.tideHeight !== null) {
            const h = parseFloat(period.tideHeight);
            tideHeight = isNaN(h) ? 1.5 : h;
        } else if (tideData24h && Array.isArray(tideData24h)) {
            const tidePoint = tideData24h.find(t => {
                const tideTime = new Date(t.time);
                return Math.abs(tideTime.getTime() - time.getTime()) < 3600000; // Within 1 hour
            });
            if (tidePoint) {
                const h = parseFloat(tidePoint.height);
                tideHeight = isNaN(h) ? 1.5 : h;
            }
        }

        return {
            timestamp: time,
            day: day,
            tideHeight: tideHeight,
            airTemp: Math.round(period.temperature || 68),
            waterTemp: period.waterTemp != null
                ? Math.round(parseFloat(period.waterTemp))
                : Math.round(period.temperature || 68) - 5, // Fallback estimate
            waterTempEstimated: period.waterTemp == null,
            wind: {
                speed: parseWindSpeed(period.windSpeed || '0 mph'),
                gust: parseWindSpeed(period.windGust || '0 mph'),
                direction: period.windDirection || 'N'
            },
            cloudCover: period.skyCover?.[0]?.coverage || 50,
            barometer: period.pressure || 1013, // API pressure already in mb
            fbiScore: period.fbiScore, // Server-calculated FBI score
            fbiExplanation: period.fbiExplanation, // Explanation of score factors
            isHourly: isHourly, // Pass through interval type for UI
            weather: period.shortForecast // Weather condition for icon
        };
    });
}

/**
 * Sort/filter options for the Bite Likelihood card's species list.
 * Shared between ui-builders.js (rendering) and scoring.js (rank-aware explanation text)
 * so the two can never drift apart on what counts as "top 3" vs. the rest.
 */
var SPECIES_CARD_SORT_OPTIONS = { minScore: 50, minCount: 3, includeClosedSeason: false };

/**
 * Sort and filter species scores with configurable thresholds.
 * Shared by ui-builders.js (bite likelihood card) and gear.js (map popup).
 * @param {Object} speciesScores - { speciesKey: { score, closedSeason, displayName, ... } }
 * @param {Object} options
 * @param {number} options.minScore - Minimum score threshold (default: 0)
 * @param {number} options.minCount - Floor guarantee — show at least this many (default: 0)
 * @param {number} options.maxCount - Maximum species to return (default: Infinity)
 * @param {boolean} options.includeClosedSeason - Include closed-season species (default: true)
 * @returns {Array} Array of [key, data] entries, sorted by score descending
 */
function getSortedSpecies(speciesScores, options = {}) {
    const { minScore = 0, minCount = 0, maxCount = Infinity, includeClosedSeason = true } = options;

    let sorted = Object.entries(speciesScores || {})
        .filter(([, data]) => includeClosedSeason || !data.closedSeason)
        .sort((a, b) => b[1].score - a[1].score);

    if (minScore > 0) {
        const filtered = sorted.filter(([, data]) => data.score >= minScore);
        sorted = filtered.length >= minCount ? filtered : sorted.slice(0, minCount);
    }

    if (maxCount < Infinity) {
        sorted = sorted.slice(0, maxCount);
    }

    return sorted;
}

// --- Spot enums and validation (shared by frontend + backend) ---

// Freshwater removed — FBI scoring (tides, water temp, shore angle) is calibrated for coastal fishing only
// Freshwater scoring not yet calibrated — will be added with regional expansion
var LOCATION_TYPES = ['pier', 'jetty', 'shore', 'kayak', 'boat', 'wade', 'bridge', 'dock', 'other'];
var WATER_TYPES = ['saltwater', 'brackish'];

/**
 * Validate spot input data.
 * Full rule set: name, lat, lon, locationType, waterType, notes.
 * @param {Object} data - Spot form data
 * @returns {{ valid: boolean, errors: string[] }}
 */
function validateSpotInput(data) {
    var errors = [];

    if (!data.name || !data.name.trim()) {
        errors.push('Name is required');
    } else if (data.name.trim().length > 100) {
        errors.push('Name must be 100 characters or fewer');
    }

    var lat = parseFloat(data.lat);
    if (isNaN(lat) || lat < -90 || lat > 90) {
        errors.push('Latitude must be between -90 and 90');
    }

    var lon = parseFloat(data.lon);
    if (isNaN(lon) || lon < -180 || lon > 180) {
        errors.push('Longitude must be between -180 and 180');
    }

    if (data.locationType && LOCATION_TYPES.indexOf(data.locationType) === -1) {
        errors.push('Invalid location type. Must be one of: ' + LOCATION_TYPES.join(', '));
    }

    if (data.waterType && WATER_TYPES.indexOf(data.waterType) === -1) {
        errors.push('Invalid water type. Must be one of: ' + WATER_TYPES.join(', '));
    }

    if (data.notes && data.notes.length > 1000) {
        errors.push('Notes must be 1000 characters or fewer');
    }

    return { valid: errors.length === 0, errors: errors };
}

/**
 * Format spot limit display text.
 * @param {number} count - Current spot count
 * @param {number} max - Maximum allowed (-1 = unlimited)
 * @param {string} tier - User tier
 * @returns {string} Display text
 */
function formatLimitDisplay(count, max, tier) {
    if (max === -1) return count + ' spots saved (unlimited)';
    return count + ' / ' + max + ' spots used';
}

// ─── My Spot Key Helpers ─────────────────────────────────────────────────────

var MYSPOT_PREFIX = 'myspot_';

/**
 * Extract spot ID from a myspot_ prefixed key.
 * @param {string} key - Location key (e.g., 'myspot_abc-123')
 * @returns {string|null} Spot ID or null if not a spot key
 */
function getSpotIdFromKey(key) {
    if (!key || typeof key !== 'string') return null;
    return key.indexOf(MYSPOT_PREFIX) === 0 ? key.slice(MYSPOT_PREFIX.length) : null;
}

/**
 * Build a myspot_ prefixed key from a spot ID.
 * @param {string} id - Spot UUID
 * @returns {string} Prefixed key (e.g., 'myspot_abc-123')
 */
function getSpotKeyFromId(id) {
    return MYSPOT_PREFIX + id;
}

// Export for Node.js (server-side)
// Note: getWindDirection is now in weather.js - import it separately if needed server-side
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        getMoonPhase,
        getScoreColor,
        getScoreLabel,
        TX_SUNRISE_HOURS,
        TX_SUNSET_HOURS,
        getSunTimes,
        getWeatherEmoji,
        formatTemperature,
        formatWind,
        mpsToMph,
        kelvinToFahrenheit,
        clamp,
        deepFreeze,
        convertNWSPeriodsToForecast,
        inchesToMillibar,
        millibarToInches,
        getSortedSpecies,
        SPECIES_CARD_SORT_OPTIONS,
        degreesToCompass8,
        LOCATION_TYPES,
        WATER_TYPES,
        validateSpotInput,
        formatLimitDisplay,
        getSpotIdFromKey,
        getSpotKeyFromId
    };
}

// For browser (client-side), functions are already in global scope

;
/* === js/ui-helpers.js === */
/**
 * UI Helper Functions
 * Reusable HTML building blocks to reduce duplication
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/ui-helpers.js">
 * USED BY: ui-builders.js → buildDetailHTML() uses buildDetailBox(), buildMeasureRow()
 * EXPORTS: buildDetailBox(), buildMeasureRow(), formatTimestamp() as globals
 */

/**
 * Build a detail box component
 * @param {string} label - Box label text
 * @param {string} value - Main value to display
 * @param {string} status - Status text below value
 * @param {Object} options - Optional styling and classes
 * @returns {string} HTML string for detail box
 */
function buildDetailBox(label, value, status = '', options = {}) {
    const {
        labelColor = '#1A3D64',
        valueColor = '#1D546C',
        statusColor = '#7f8c8d',
        valueSize = '20px',
        className = 'detail-box'
    } = options;

    return `
        <div class="${className}">
            <div style="font-size: 10px; color: ${labelColor}; font-weight: bold; margin-bottom: 4px;">
                ${label}
            </div>
            <div style="font-size: ${valueSize}; font-weight: bold; color: ${valueColor};">
                ${value}
            </div>
            ${status ? `<div style="font-size: 11px; color: ${statusColor}; margin-top: 2px;">${status}</div>` : ''}
        </div>
    `;
}

/**
 * Build an info row with icon
 * @param {string} icon - Icon/emoji to display
 * @param {string} label - Label text
 * @param {string} value - Value text
 * @returns {string} HTML string for info row
 */
function buildInfoRow(icon, label, value) {
    return `
        <div style="display: flex; align-items: center; gap: 10px; margin: 8px 0;">
            <span style="font-size: 20px;">${icon}</span>
            <div style="flex: 1;">
                <div style="font-size: 11px; color: #7f8c8d; font-weight: 500;">${label}</div>
                <div style="font-size: 14px; color: #0C2B4E; font-weight: 600;">${value}</div>
            </div>
        </div>
    `;
}

/**
 * Build a section header
 * @param {string} title - Section title
 * @param {string} icon - Optional icon/emoji
 * @returns {string} HTML string for section header
 */
function buildSectionHeader(title, icon = '') {
    return `
        <div style="font-size: 16px; font-weight: bold; color: #0C2B4E; margin: 20px 0 12px 0;
                    padding-bottom: 8px; border-bottom: 2px solid #E8EEF2;">
            ${icon ? `<span style="margin-right: 8px;">${icon}</span>` : ''}${title}
        </div>
    `;
}

/**
 * Build a badge component
 * @param {string} text - Badge text
 * @param {string} type - Badge type (success, warning, danger, info)
 * @returns {string} HTML string for badge
 */
function buildBadge(text, type = 'info') {
    const colors = {
        success: { bg: '#d4edda', text: '#155724', border: '#c3e6cb' },
        warning: { bg: '#fff3cd', text: '#856404', border: '#ffeaa7' },
        danger: { bg: '#f8d7da', text: '#721c24', border: '#f5c6cb' },
        info: { bg: '#d1ecf1', text: '#0c5460', border: '#bee5eb' }
    };

    const color = colors[type] || colors.info;

    return `
        <span style="display: inline-block; padding: 4px 10px; border-radius: 12px;
                     font-size: 11px; font-weight: 600;
                     background: ${color.bg}; color: ${color.text};
                     border: 1px solid ${color.border};">
            ${text}
        </span>
    `;
}

/**
 * Build a card container
 * @param {string} content - Card content HTML
 * @param {Object} options - Styling options
 * @returns {string} HTML string for card
 */
function buildCard(content, options = {}) {
    const {
        padding = '15px',
        background = '#f8f9fa',
        borderRadius = '6px',
        margin = '10px 0',
        className = ''
    } = options;

    return `
        <div class="${className}" style="background: ${background}; padding: ${padding};
                                         border-radius: ${borderRadius}; margin: ${margin};">
            ${content}
        </div>
    `;
}

/**
 * Build a grid container
 * @param {Array<string>} items - Array of HTML strings to place in grid
 * @param {number} columns - Number of columns
 * @returns {string} HTML string for grid
 */
function buildGrid(items, columns = 2) {
    return `
        <div style="display: grid; grid-template-columns: repeat(${columns}, 1fr); gap: 12px;">
            ${items.join('')}
        </div>
    `;
}

/**
 * Build a progress bar
 * @param {number} percentage - Progress percentage (0-100)
 * @param {string} color - Bar color
 * @param {string} label - Optional label
 * @returns {string} HTML string for progress bar
 */
function buildProgressBar(percentage, color = '#1D546C', label = '') {
    const clampedPercent = Math.min(Math.max(percentage, 0), 100);

    return `
        <div>
            ${label ? `<div style="font-size: 11px; color: #7f8c8d; margin-bottom: 4px;">${label}</div>` : ''}
            <div style="width: 100%; height: 8px; background: #e0e0e0; border-radius: 4px; overflow: hidden;">
                <div style="width: ${clampedPercent}%; height: 100%; background: ${color};
                            transition: width 0.3s ease;"></div>
            </div>
        </div>
    `;
}

/**
 * Build a list item with bullet
 * @param {string} text - List item text
 * @param {string} icon - Optional bullet icon/emoji
 * @returns {string} HTML string for list item
 */
function buildListItem(text, icon = '●') {
    return `
        <div style="display: flex; gap: 8px; margin: 6px 0; font-size: 13px; color: #333;">
            <span style="color: #1D546C; font-weight: bold;">${icon}</span>
            <span>${text}</span>
        </div>
    `;
}

/**
 * Escape HTML to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Sanitize user input to prevent injection attacks
 * @param {string} input - User input to sanitize
 * @returns {string} Sanitized input
 */
function sanitizeInput(input) {
    if (typeof input !== 'string') return '';
    return input
        .replace(/[<>]/g, '')  // Remove angle brackets
        .replace(/javascript:/gi, '')  // Remove javascript: protocol
        .replace(/on\w+=/gi, '')  // Remove event handlers
        .trim()
        .substring(0, 1000);  // Limit length
}

/**
 * Scroll behavior that honors the OS "reduce motion" setting.
 * CSS scroll-behavior does not apply to JS scroll calls that pass behavior explicitly.
 * @returns {'auto'|'smooth'}
 */
function scrollBehavior() {
    const reduce = typeof window !== 'undefined' && window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return reduce ? 'auto' : 'smooth';
}

/**
 * Mark the nav item for targetTab as current: `active` class plus aria-current="page".
 * Every other item (including ones without data-tab) is cleared.
 * @param {NodeList|Array<Element>} items - Nav buttons carrying data-tab
 * @param {string} targetTab
 */
function setNavCurrent(items, targetTab) {
    items.forEach(function(item) {
        const isCurrent = item.getAttribute('data-tab') === targetTab;
        item.classList.toggle('active', isCurrent);
        if (isCurrent) {
            item.setAttribute('aria-current', 'page');
        } else {
            item.removeAttribute('aria-current');
        }
    });
}

/**
 * Relative time for "Updated 5 min ago" style labels. Smallest unit is minutes.
 * @param {number} secondsAgo - Elapsed seconds (negative values count as "just now")
 * @returns {string} "just now", "N min ago", "1 hour ago", "N hours ago", "1 day ago", "N days ago"
 */
function timeAgo(secondsAgo) {
    if (secondsAgo < 60) return 'just now';
    if (secondsAgo < 3600) return `${Math.floor(secondsAgo / 60)} min ago`;
    if (secondsAgo < 86400) {
        const hours = Math.floor(secondsAgo / 3600);
        return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    }
    const days = Math.floor(secondsAgo / 86400);
    return days === 1 ? '1 day ago' : `${days} days ago`;
}

// URL hash for each tab that has one. The map (publicSpots) is the default and has no hash;
// admin deliberately has none (its nav wiring is left alone).
const TAB_HASHES = {
    mySpots: 'angler-list',
    logCatch: 'logbook',
    bragBoard: 'brag-board',
    analytics: 'stats',
    settings: 'settings'
};
const AUTH_ONLY_TABS = ['mySpots', 'logCatch', 'analytics', 'settings'];

/**
 * @param {string} tab
 * @returns {string} "#logbook" style hash, or '' for tabs without one
 */
function hashForTab(tab) {
    return Object.prototype.hasOwnProperty.call(TAB_HASHES, tab) ? '#' + TAB_HASHES[tab] : '';
}

/**
 * Tab to show for a URL hash. Unknown hashes, and signed-in-only tabs for guests, fall back to
 * the map.
 * @param {string} hash - location.hash, with or without the leading '#'
 * @param {boolean} isAuthenticated
 * @returns {string} tab id
 */
function resolveTabFromHash(hash, isAuthenticated) {
    const name = String(hash || '').replace(/^#/, '');
    const tab = Object.keys(TAB_HASHES).find(function(key) { return TAB_HASHES[key] === name; });
    if (!tab) return 'publicSpots';
    if (!isAuthenticated && AUTH_ONLY_TABS.indexOf(tab) !== -1) return 'publicSpots';
    return tab;
}

/**
 * Area the top-spot card covers, from the user's saved default filters. Mirrors what the map
 * shows after load: with nothing saved (all/all/all) the map shows Texas.
 * @param {{region?: string, state?: string, showCount?: string}|null} filters
 * @returns {{region: string, state: string, label: string}} label is '' when all states
 */
function topSpotScope(filters) {
    const saved = filters || {};
    const region = saved.region || 'all';
    const state = saved.state || 'all';
    const count = saved.showCount || 'all';
    if (region === 'all' && state === 'all' && count === 'all') {
        return { region: 'all', state: 'Texas', label: 'Texas' };
    }

    let label = '';
    if (region !== 'all') {
        label = region;
    } else if (state !== 'all') {
        label = state;
    }
    return { region, state, label };
}

/**
 * Highest-scoring public spot in the given area. My Spots are excluded (they load later and are
 * the user's own). Order of the input does not matter.
 * @param {Array} allConditions
 * @param {{region: string, state: string}} scope
 * @returns {Object|null}
 */
function pickTopSpot(allConditions, scope) {
    let best = null;
    (allConditions || []).forEach(function(item) {
        if (!item || item._isMySpot || !item.location || !item.conditions) return;
        if (scope.state !== 'all' && item.location.state !== scope.state) return;
        if (scope.region !== 'all' && item.location.region !== scope.region) return;
        const score = item.conditions.score;
        if (!Number.isFinite(score)) return;
        if (!best || score > best.conditions.score) best = item;
    });
    return best;
}

/**
 * Calendar day in America/Chicago as YYYY-MM-DD ("today" for once-a-day UI).
 * @param {Date} date
 * @returns {string}
 */
function chicagoDateKey(date) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

const _dateFormatters = new Map();

/**
 * Format a date or time for display. Every user-facing date in the app goes through here so
 * formats stay consistent (en-US, matching the Texas audience) instead of following each
 * browser's default locale. Formatters are cached per options set.
 * @param {Date|string|number} value - Anything `new Date()` accepts
 * @param {Intl.DateTimeFormatOptions} [options] - Defaults to numeric month/day/year (9/14/2026)
 * @returns {string} Formatted text, or '' for an invalid date
 */
function formatDate(value, options) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const opts = options || { month: 'numeric', day: 'numeric', year: 'numeric' };
    const key = JSON.stringify(opts);
    let formatter = _dateFormatters.get(key);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-US', opts);
        _dateFormatters.set(key, formatter);
    }
    return formatter.format(date);
}

/**
 * Red tide warning pill. Shared by the conditions row and the expanded FBI card so
 * both read as the same warning.
 *
 * Every field comes from the HABSOS feed, so text is escaped and the tooltip value
 * is quote-escaped before it goes into the data-tip attribute.
 *
 * @param {{category: string, cellCount: number|null, sampledAt: string, siteName: string, distanceKm: number}} redTide
 * @param {string} [extraStyle] - Inline style for the card, which has its own margins
 * @returns {string} pill markup, or '' when there is nothing to warn about
 */
function buildRedTidePillHTML(redTide, extraStyle) {
    if (!redTide) return '';

    const concentration = redTide.category
        ? redTide.category.charAt(0).toUpperCase() + redTide.category.slice(1)
        : 'Elevated';
    const sampled = formatDate(redTide.sampledAt, { month: 'short', day: 'numeric' });
    const miles = Number.isFinite(redTide.distanceKm)
        ? Math.round(redTide.distanceKm * 0.621371)
        : null;

    const tip = [
        `${concentration} concentration`,
        sampled ? `sampled ${sampled}` : '',
        miles !== null ? `${miles} mi away` : ''
    ].filter(Boolean).join(', ');

    const styleAttr = extraStyle ? ` style="${extraStyle}"` : '';
    return `<span class="location-tag red-tide"${styleAttr} tabindex="0" data-tip="${escapeHtml(tip).replace(/"/g, '&quot;')}"><span aria-hidden="true">\u26A0</span> Red Tide</span>`;
}

/**
 * Every warning pill for a location, in priority order, as one HTML string.
 *
 * The order is the message: red tide first because a bloom means "do not come", then cold
 * stun because the fish are thin, then the NOAA alert. Any combination can be live at once.
 *
 * The one source of pill order. The conditions row (js/main-server.js) calls it directly and
 * takes the default margins; every other surface goes through buildWarningPillRowHTML(), which
 * passes extraStyle to hand the spacing to its flex row.
 *
 * @param {Object|null} conditions - A location's conditions object
 * @param {Object} [options]
 * @param {string} [options.extraStyle] - Inline style applied to every pill
 * @returns {string} concatenated pill markup, or '' when nothing is wrong
 */
function buildWarningPillsHTML(conditions, options) {
    if (!conditions) return '';

    const extraStyle = (options && options.extraStyle) || '';
    const styleAttr = extraStyle ? ` style="${extraStyle}"` : '';

    return [
        buildRedTidePillHTML(conditions.redTide, extraStyle),
        buildColdStunPillHTML(conditions.coldStun, extraStyle),
        conditions.weatherAlert
            ? `<span class="location-tag noaa-alert"${styleAttr}>${escapeHtml(conditions.weatherAlert.event)}</span>`
            : ''
    ].join('');
}

/**
 * Warning pills wrapped in their flex row, or '' when nothing is wrong.
 *
 * The only builder of .warning-pills: the expanded FBI card, the map sidebar and the ROD
 * sidebar repaint all call it, so the wrapper and the margin reset live here once. Each pill's margin-left is zeroed inline
 * because the pill palettes set 6px at the same (0,2,0) weight a CSS reset would have, and a
 * tie decided by source order is one refactor away from breaking.
 *
 * @param {Object|null} conditions - A location's conditions object
 * @param {Object} [options]
 * @param {string} [options.modifier] - BEM modifier, e.g. 'card' → .warning-pills--card
 * @returns {string}
 */
function buildWarningPillRowHTML(conditions, options) {
    const pills = buildWarningPillsHTML(conditions, { extraStyle: 'margin-left: 0;' });
    if (!pills) return '';
    const modifier = options && options.modifier ? ` warning-pills--${options.modifier}` : '';
    return `<div class="warning-pills${modifier}">${pills}</div>`;
}

/**
 * Replace the warning pills in an open map sidebar after a Premium Live Data refresh.
 *
 * The ROD button lives in the expanded card on the Angler List, but a sidebar left open on the
 * map keeps its content across tab switches. Without this, a bloom the refresh cleared would
 * still show there. Only the pills are repainted; the chart and report refresh on the next open.
 *
 * No-op unless the sidebar shows this location. The key is compared through dataset rather
 * than spliced into a selector, so a key with quotes or brackets cannot break the lookup.
 *
 * @param {Element|null} sidebarContentEl - #mapSidebarContent
 * @param {string} locationKey
 * @param {Object|null} conditions - The refreshed conditions
 */
function repaintSidebarWarningPills(sidebarContentEl, locationKey, conditions) {
    const wrapper = sidebarContentEl && sidebarContentEl.querySelector('.sidebar-popup-wrapper');
    if (!wrapper || wrapper.dataset.locationKey !== locationKey) return;
    const heading = wrapper.querySelector('.sidebar-popup-title > h3');
    if (!heading) return;

    const existing = wrapper.querySelector('.sidebar-popup-title > .warning-pills');
    if (existing) existing.remove();
    const rowHTML = buildWarningPillRowHTML(conditions);
    if (rowHTML) heading.insertAdjacentHTML('afterend', rowHTML);
}

/**
 * Cold stun warning pill.
 *
 * The server decides when this pill is warranted: cacheWarmer only attaches conditions.coldStun
 * while the event is open and for COLD_STUN_PILL_DAYS after it ends, deliberately shorter than
 * the multiplier's 60-day recovery tail. The pill answers "is something wrong
 * right now"; the multiplier answers "has this water recovered". A severe event still costs
 * a few percent at day 45 with no pill showing, which is intended.
 *
 * Blue rather than red, unlike the red tide and NOAA alert pills. A bloom means "do not come";
 * a stun event means the fish are thin. Spending the red on both would cost the red tide pill
 * the urgency it exists for.
 *
 * @param {{severity: string, endedAt: string|Date|null, minTempF: number|null}} coldStun
 * @param {string} [extraStyle] - Inline style for the card, which has its own margins
 * @returns {string} pill markup, or '' when there is nothing to warn about
 */
function buildColdStunPillHTML(coldStun, extraStyle) {
    if (!coldStun || !coldStun.severity) return '';

    const low = Number.isFinite(coldStun.minTempF) ? `${Math.round(coldStun.minTempF)}F` : null;
    const ended = coldStun.endedAt
        ? formatDate(coldStun.endedAt, { month: 'short', day: 'numeric' })
        : null;

    const tip = [
        low ? `Bay water hit ${low}` : 'Bay water dropped below the stun threshold',
        ended ? `ended ${ended}` : 'still ongoing',
        coldStun.severity === 'severe' ? 'fish kill likely' : 'fish stunned'
    ].filter(Boolean).join(', ');

    const styleAttr = extraStyle ? ` style="${extraStyle}"` : '';
    return `<span class="location-tag cold-stun"${styleAttr} tabindex="0" data-tip="${escapeHtml(tip).replace(/"/g, '&quot;')}"><span aria-hidden="true">❄</span> Cold Stun</span>`;
}

/**
 * Greeting-screen text after a device-limit sign-out.
 * @param {number} [deviceLimit] - The plan's device limit, when the server could look it up
 * @returns {string}
 */
function signOutNoticeText(deviceLimit) {
    if (Number.isInteger(deviceLimit) && deviceLimit > 0) {
        return `You were signed out because your plan allows ${deviceLimit} ${deviceLimit === 1 ? 'device' : 'devices'} at a time.`;
    }
    return 'You were signed out because your account signed in on another device.';
}

const _keyboardActivationSelectors = new Set();

/**
 * Let keyboard users activate non-button click targets (table rows, card items).
 * One delegated keydown on document: Enter or Space on an element that itself matches
 * `selector` clicks it. Keys pressed inside nested controls are left alone.
 * Safe to call repeatedly with the same selector.
 * @param {string} selector
 */
function registerKeyboardActivation(selector) {
    if (_keyboardActivationSelectors.has(selector)) return;
    _keyboardActivationSelectors.add(selector);
    document.addEventListener('keydown', function(e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const target = e.target;
        if (!target || !target.matches || !target.matches(selector)) return;
        e.preventDefault(); // Space would otherwise scroll the page
        target.click();
    });
}

/**
 * Ask before a destructive action, in a native <dialog>: showModal() gives focus trapping, Escape
 * and an inert page. Cancel, Escape and a click on the backdrop all dismiss it.
 * @param {string} spotName - Name shown in the default "delete <name>" sentence
 * @param {Function} onConfirm - Called once, after the dialog closes, only when confirmed
 * @param {string} [message] - Plain-text question that replaces the default sentence
 * @param {string} [confirmLabel] - Text for the destructive button, default "Delete"
 */
function showDeleteConfirmModal(spotName, onConfirm, message, confirmLabel) {
    const existing = document.getElementById('deleteConfirmModal');
    if (existing) existing.remove();

    const returnFocus = document.activeElement;
    const dialog = document.createElement('dialog');
    dialog.id = 'deleteConfirmModal';
    dialog.className = 'confirm-dialog';
    dialog.setAttribute('aria-labelledby', 'deleteConfirmLabel');
    dialog.innerHTML =
        '<div class="confirm-dialog__body">' +
            '<p class="confirm-dialog__text" id="deleteConfirmLabel">' +
                (message
                    ? escapeHtml(message)
                    : 'Are you sure you want to delete <strong>' + escapeHtml(spotName) + '</strong>? This action cannot be undone.') +
            '</p>' +
            '<div class="confirm-dialog__actions">' +
                '<button type="button" class="btn-secondary" data-confirm-action="cancel">Cancel</button>' +
                '<button type="button" class="confirm-dialog__confirm" data-confirm-action="confirm">' +
                    escapeHtml(confirmLabel || 'Delete') +
                '</button>' +
            '</div>' +
        '</div>';
    document.body.appendChild(dialog);

    let confirmed = false;
    // The body fills the dialog, so a press landing on the dialog itself is on the backdrop.
    // Both ends must be there: a text selection dragged out of the body also clicks the dialog.
    let pressedBackdrop = false;
    dialog.addEventListener('pointerdown', function(e) { pressedBackdrop = e.target === dialog; });
    dialog.addEventListener('click', function(e) {
        if (e.target === dialog) {
            if (pressedBackdrop) dialog.close();
            return;
        }
        const button = e.target.closest('[data-confirm-action]');
        if (!button) return;
        confirmed = button.getAttribute('data-confirm-action') === 'confirm';
        dialog.close();
    });
    // Fires for every way out: Cancel, Delete, Escape, backdrop
    dialog.addEventListener('close', function() {
        dialog.remove();
        if (returnFocus && typeof returnFocus.focus === 'function' && document.contains(returnFocus)) {
            returnFocus.focus();
        }
        if (confirmed) onConfirm();
    });
    dialog.showModal();
}

// Export for Node.js (if needed)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        buildDetailBox,
        buildInfoRow,
        buildSectionHeader,
        buildBadge,
        buildCard,
        buildGrid,
        buildProgressBar,
        buildListItem,
        escapeHtml,
        sanitizeInput,
        buildRedTidePillHTML,
        buildColdStunPillHTML,
        buildWarningPillsHTML,
        buildWarningPillRowHTML,
        repaintSidebarWarningPills,
        scrollBehavior,
        setNavCurrent,
        registerKeyboardActivation,
        showDeleteConfirmModal,
        timeAgo,
        signOutNoticeText,
        formatDate,
        hashForTab,
        resolveTabFromHash,
        topSpotScope,
        pickTopSpot,
        chicagoDateKey
    };
}

;
/* === js/tooltip.js === */
/**
 * Tooltips (replaced Bootstrap Tooltip)
 *
 * LOADED BY: dist/js/app.min.js (scripts/bundle.js, right after ui-helpers.js)
 * USED BY: markup carrying data-tip (FBI score cells in ui-builders.js, section hint in app.ejs);
 *          main-server.js calls TOOLTIP.init() once and TOOLTIP.hideWithin() when a row collapses
 * EXPORTS: TOOLTIP global; sanitizeTipHtml, computeTipPosition via module.exports for tests
 *
 * Markup:
 *   data-tip="text"                 tooltip content
 *   data-tip-html                   content is HTML (sanitized to span[class] and br)
 *   data-tip-placement="top|bottom|left|right"   preferred side, default top
 *   data-tip-trigger="focus"        show on focus/tap only, not on hover
 *
 * One shared tooltip element in <body>, position: fixed. It shows while its target is hovered or
 * focused, stays open while the pointer is over the tooltip itself, and Escape hides it.
 */

const TIP_GAP = 8;
const TIP_VIEWPORT_PADDING = 8;
const TIP_HIDE_DELAY_MS = 100;
const TIP_PLACEMENTS = ['top', 'bottom', 'left', 'right'];

/**
 * Keep only text, <br> and <span class="…">. Scripts and styles are dropped with their content;
 * any other element is replaced by its (sanitized) children.
 * @param {string} html
 * @returns {DocumentFragment}
 */
function sanitizeTipHtml(html) {
    const template = document.createElement('template');
    template.innerHTML = html;
    const out = document.createDocumentFragment();

    function copyChildren(from, to) {
        from.childNodes.forEach(function(node) {
            if (node.nodeType === Node.TEXT_NODE) {
                to.appendChild(document.createTextNode(node.textContent));
                return;
            }
            if (node.nodeType !== Node.ELEMENT_NODE) return; // comments
            const tag = node.tagName.toLowerCase();
            if (tag === 'script' || tag === 'style') return;
            if (tag === 'br') {
                to.appendChild(document.createElement('br'));
            } else if (tag === 'span') {
                const span = document.createElement('span');
                const cls = node.getAttribute('class');
                if (cls) span.className = cls;
                copyChildren(node, span);
                to.appendChild(span);
            } else {
                copyChildren(node, to);
            }
        });
    }

    copyChildren(template.content, out);
    return out;
}

/**
 * Where to put the tooltip: the preferred side if it fits, else the first side that does, clamped
 * inside the viewport. Pure: takes rects and sizes, returns coordinates.
 * @param {{top:number, left:number, right:number, bottom:number, width:number, height:number}} target
 * @param {{width:number, height:number}} tip
 * @param {{width:number, height:number}} viewport
 * @param {string} [placement] - top | bottom | left | right
 * @returns {{top:number, left:number, placement:string, arrow:number}} arrow = offset along the
 *          tooltip edge (x for top/bottom, y for left/right) that points at the target's center
 */
function computeTipPosition(target, tip, viewport, placement) {
    const preferred = TIP_PLACEMENTS.includes(placement) ? placement : 'top';
    const pad = TIP_VIEWPORT_PADDING;

    function place(side) {
        switch (side) {
            case 'bottom': return { top: target.bottom + TIP_GAP, left: target.left + target.width / 2 - tip.width / 2 };
            case 'left': return { top: target.top + target.height / 2 - tip.height / 2, left: target.left - tip.width - TIP_GAP };
            case 'right': return { top: target.top + target.height / 2 - tip.height / 2, left: target.right + TIP_GAP };
            default: return { top: target.top - tip.height - TIP_GAP, left: target.left + target.width / 2 - tip.width / 2 };
        }
    }
    function fits(side, pos) {
        if (side === 'top') return pos.top >= pad;
        if (side === 'bottom') return pos.top + tip.height <= viewport.height - pad;
        if (side === 'left') return pos.left >= pad;
        return pos.left + tip.width <= viewport.width - pad;
    }
    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

    const order = [preferred].concat(TIP_PLACEMENTS.filter(side => side !== preferred));
    const side = order.find(s => fits(s, place(s))) || preferred;
    const pos = place(side);
    const top = clamp(pos.top, pad, Math.max(pad, viewport.height - tip.height - pad));
    const left = clamp(pos.left, pad, Math.max(pad, viewport.width - tip.width - pad));
    const vertical = side === 'top' || side === 'bottom';
    const arrow = vertical
        ? clamp(target.left + target.width / 2 - left, 8, tip.width - 8)
        : clamp(target.top + target.height / 2 - top, 8, tip.height - 8);
    return { top, left, placement: side, arrow };
}

const TOOLTIP = (function() {
    let tipEl = null;
    let current = null;          // target element the tooltip belongs to
    let hovered = false;
    let focused = false;
    let hideTimer = null;
    let initialized = false;

    function ensureTip() {
        if (tipEl) return tipEl;
        tipEl = document.createElement('div');
        tipEl.id = 'rtTooltip';
        tipEl.className = 'rt-tooltip';
        tipEl.setAttribute('role', 'tooltip');
        tipEl.hidden = true;
        tipEl.innerHTML = '<div class="rt-tooltip__inner"></div><div class="rt-tooltip__arrow"></div>';
        // Hovering the tooltip itself keeps it open, so its text can be read and selected
        tipEl.addEventListener('mouseenter', function() { clearTimeout(hideTimer); });
        tipEl.addEventListener('mouseleave', function() { hovered = false; scheduleHide(); });
        document.body.appendChild(tipEl);
        return tipEl;
    }

    function position() {
        if (!current || !tipEl) return;
        if (!current.isConnected) { hide(); return; }
        const rect = current.getBoundingClientRect();
        const pos = computeTipPosition(
            rect,
            { width: tipEl.offsetWidth, height: tipEl.offsetHeight },
            { width: window.innerWidth, height: window.innerHeight },
            current.getAttribute('data-tip-placement')
        );
        tipEl.style.top = pos.top + 'px';
        tipEl.style.left = pos.left + 'px';
        tipEl.className = 'rt-tooltip rt-tooltip--' + pos.placement;
        const arrow = tipEl.lastChild;
        const vertical = pos.placement === 'top' || pos.placement === 'bottom';
        arrow.style.left = vertical ? pos.arrow + 'px' : '';
        arrow.style.top = vertical ? '' : pos.arrow + 'px';
    }

    /**
     * @param {Element} target
     * @param {'hover'|'focus'} [reason] - what is keeping it open; switching targets starts fresh
     */
    function show(target, reason) {
        clearTimeout(hideTimer);
        if (current !== target) {
            if (current) current.removeAttribute('aria-describedby');
            hovered = false;
            focused = false;
        }
        if (reason === 'hover') hovered = true;
        if (reason === 'focus') focused = true;
        if (current === target) return;
        const tip = ensureTip();
        const inner = tip.firstChild;
        const content = target.getAttribute('data-tip') || '';
        inner.textContent = '';
        if (target.hasAttribute('data-tip-html')) inner.appendChild(sanitizeTipHtml(content));
        else inner.textContent = content;

        current = target;
        target.setAttribute('aria-describedby', tip.id);
        // Measure at the top-left so the width isn't squeezed by a previous position
        tip.style.top = '0px';
        tip.style.left = '0px';
        tip.hidden = false;
        position();
    }

    function hide() {
        clearTimeout(hideTimer);
        if (current) current.removeAttribute('aria-describedby');
        current = null;
        hovered = false;
        focused = false;
        if (tipEl) tipEl.hidden = true;
    }

    function scheduleHide() {
        clearTimeout(hideTimer);
        hideTimer = setTimeout(function() {
            if (!hovered && !focused) hide();
        }, TIP_HIDE_DELAY_MS);
    }

    function tipTarget(node) {
        return node && node.closest ? node.closest('[data-tip]') : null;
    }

    function init() {
        if (initialized) return;
        initialized = true;

        document.addEventListener('mouseover', function(e) {
            const target = tipTarget(e.target);
            if (!target || target.getAttribute('data-tip-trigger') === 'focus') return;
            show(target, 'hover');
        });
        document.addEventListener('mouseout', function(e) {
            if (!current || tipTarget(e.target) !== current) return;
            if (current.contains(e.relatedTarget) || (tipEl && tipEl.contains(e.relatedTarget))) return;
            hovered = false;
            scheduleHide();
        });
        document.addEventListener('focusin', function(e) {
            const target = tipTarget(e.target);
            if (!target) return;
            show(target, 'focus');
        });
        document.addEventListener('focusout', function(e) {
            if (!current || e.target !== current) return;
            focused = false;
            scheduleHide();
        });
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && current) hide();
        });
        // Capture: scrolling any container moves the target
        window.addEventListener('scroll', position, true);
        window.addEventListener('resize', position);
    }

    /** Hide the tooltip if its target is inside `container` (e.g. a row that is collapsing). */
    function hideWithin(container) {
        if (current && container && container.contains(current)) hide();
    }

    return { init, show, hide, hideWithin };
})();

if (typeof module !== 'undefined' && module.exports) {
    module.exports = { TOOLTIP, sanitizeTipHtml, computeTipPosition };
}

;
/* === js/api-client.js === */
/**
 * API Client for Backend Communication
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/api-client.js">
 * USED BY: main-server.js (API_CLIENT.get/post for all backend calls), auth-client.js
 * EXPORTS: window.API_CLIENT global object with get(), post(), baseURL
 *
 * Handles all HTTP requests to the Node.js backend server.
 * The backend manages:
 * - External API calls (OpenWeather, NOAA)
 * - Data caching (reduces API usage by 99%)
 * - Scoring algorithm (protected from client manipulation)
 * - Rate limiting and security
 */

const API_CLIENT = {
    // Dynamic URL: Use same origin for SSR, production URL for Cloudflare Pages
    baseURL: (function() {
        // If running from Railway SSR (localhost or api.reeltalkfishing.com), use same origin
        if (typeof window !== 'undefined') {
            const host = window.location.hostname;
            if (host === 'localhost' || host === '127.0.0.1') {
                return window.location.origin + '/api';
            }
            if (host === 'api.reeltalkfishing.com') {
                return window.location.origin + '/api';
            }
        }
        // Default: Use production Railway API (for Cloudflare Pages)
        return 'https://api.reeltalkfishing.com/api';
    })(),

    /**
     * Fetch all fishing conditions for all locationsv
     *
     * Returns an array of location objects with:
     * - location: { name, lat, lon, description, tideStation, type }
     * - conditions: { score, factors }
     * - weatherData: Current weather from OpenWeather API
     * - tideData: Today's high/low tides from NOAA
     * - tideData24h: 24-hour tide predictions for chart
     * - waterTemp: Current water temperature from NOAA
     *
     * @returns {Promise<Array>} Array of fishing condition objects
     * @throws {Error} If server request fails or returns error
     */
    async getFishingConditions() {
        // Total retry budget: 4 × 10s = ~40s, matches server warm-cache cycle time
        // so cold starts succeed on retry
        const MAX_RETRIES = 4;
        const BASE_DELAY = 10000; // 10 seconds

        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            try {
                const response = await fetch(`${this.baseURL}/fishing-conditions`);

                // 304 Not Modified — browser cache is fresh, no body to parse
                if (response.status === 304) {
                    console.log('[API] 304 Not Modified, using browser cache');
                    return this._lastConditionsResult;
                }

                if (!response.ok) {
                    throw new Error(`Server error: ${response.statusText}`);
                }

                const result = await response.json();

                if (!result.success) {
                    throw new Error(result.error || 'Failed to fetch fishing conditions');
                }

                // Server is warming cache — retry after delay
                if (result.warming && result.data.length === 0 && attempt < MAX_RETRIES) {
                    const delay = result.retryAfter ? result.retryAfter * 1000 : BASE_DELAY;
                    console.log(`[API] Server warming cache, retrying in ${delay / 1000}s (attempt ${attempt + 1}/${MAX_RETRIES})...`);
                    await new Promise(resolve => setTimeout(resolve, delay));
                    continue;
                }

                // Log cache status for debugging
                console.log(`[API] Data ${result.cached ? 'from cache' : 'freshly fetched'}`);
                console.log(`[API] Timestamp: ${result.timestamp}`);
                if (result.nextRefresh) {
                    console.log(`[API] Next refresh: ${result.nextRefresh}`);
                }

                // Store for 304 fallback
                this._lastConditionsResult = result;

                return result; // Return full result with data, timestamp, cached, nextRefresh

            } catch (error) {
                if (attempt === MAX_RETRIES) {
                    console.error('[API] Error:', error);
                    throw new Error(`Unable to fetch fishing data: ${error.message}`);
                }
                console.warn(`[API] Fetch failed (attempt ${attempt + 1}/${MAX_RETRIES}):`, error.message);
                await new Promise(resolve => setTimeout(resolve, BASE_DELAY));
            }
        }
    },

};

// Export to global scope
window.API_CLIENT = API_CLIENT;

;
/* === js/loading.js === */
/**
 * Loading Screen Animation
 * Displays random loading messages with fade in/out transitions
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/loading.js">
 * USED BY: main-server.js → showLoadingScreen() / hideLoadingScreen() during data fetch
 * EXPORTS: showLoadingScreen(), hideLoadingScreen(), LOADING_MESSAGES as globals
 */

// Array of loading messages
const LOADING_MESSAGES = [
    'Reeling in the data',
    'Casting the line, please wait',
    'Fish are gossiping. Almost ready',
    'Just one more cache',
    'Waiting for the ripples to fade',
    'No bites yet. Still loading',
    'Convincing the fish to cooperate',
    'Patience. Even fish take time',
    'Good things come to those who cache and wait',
    'Hooked. Hang tight',
    'Asking the fish nicely to show up',
    'Reeling in bytes',
    'Blaming the weather'
];

let loadingInterval = null;
let currentMessageIndex = -1;
let usedMessages = [];
let loadingStartTime = null;
const MIN_LOADING_DISPLAY_TIME = 4000; // Minimum 4 seconds to show loading screen

/**
 * Get a random message that hasn't been used recently
 * @returns {string} A random loading message
 */
function getRandomMessage() {
    // If all messages have been used, reset the used list
    if (usedMessages.length === LOADING_MESSAGES.length) {
        usedMessages = [];
    }

    // Get available messages (not recently used)
    const availableMessages = LOADING_MESSAGES.filter((msg, index) =>
        !usedMessages.includes(index)
    );

    // Pick a random message from available ones
    const randomIndex = Math.floor(Math.random() * availableMessages.length);
    const selectedMessage = availableMessages[randomIndex];

    // Mark this message as used
    const originalIndex = LOADING_MESSAGES.indexOf(selectedMessage);
    usedMessages.push(originalIndex);

    return selectedMessage;
}

/**
 * Update the loading message with fade in/out animation
 */
function updateLoadingMessage() {
    const loadingText = document.querySelector('.loading-popup p');
    const loadingDots = document.querySelector('.loading-dots');

    if (!loadingText || !loadingDots) return;

    // Fade out current message
    loadingText.classList.remove('fade-in');
    loadingText.classList.add('fade-out');

    // After fade out completes, change text and fade in
    setTimeout(() => {
        const newMessage = getRandomMessage();
        loadingDots.textContent = newMessage;

        loadingText.classList.remove('fade-out');
        loadingText.classList.add('fade-in');
    }, 500); // Match CSS transition duration
}

/**
 * Start the loading message rotation
 */
function startLoadingAnimation() {
    const loadingText = document.querySelector('.loading-popup p');
    if (!loadingText) return;

    // Clear any existing interval
    if (loadingInterval) {
        clearInterval(loadingInterval);
    }

    // Reset used messages and record start time
    usedMessages = [];
    loadingStartTime = Date.now();
    window.loadingAnimationStartTime = loadingStartTime;

    // Set initial message immediately with fade in
    const initialMessage = getRandomMessage();
    const loadingDots = document.querySelector('.loading-dots');
    if (loadingDots) {
        loadingDots.textContent = initialMessage;
        loadingText.classList.add('fade-in');
    }

    // Rotate messages every 2.5 seconds (2500ms display + transitions)
    loadingInterval = setInterval(updateLoadingMessage, 2500);
}

/**
 * Stop the loading message rotation
 * Ensures minimum display time so at least one message is visible
 */
function stopLoadingAnimation() {
    if (!loadingStartTime) {
        // If somehow called without starting, just clear interval
        if (loadingInterval) {
            clearInterval(loadingInterval);
            loadingInterval = null;
        }
        return;
    }

    const elapsedTime = Date.now() - loadingStartTime;
    const remainingTime = MIN_LOADING_DISPLAY_TIME - elapsedTime;

    if (remainingTime > 0) {
        // Wait for minimum display time before stopping
        setTimeout(() => {
            if (loadingInterval) {
                clearInterval(loadingInterval);
                loadingInterval = null;
            }
            loadingStartTime = null;
        }, remainingTime);
    } else {
        // Minimum time already passed, stop immediately
        if (loadingInterval) {
            clearInterval(loadingInterval);
            loadingInterval = null;
        }
        loadingStartTime = null;
    }
}

// Export functions for use in other scripts
window.startLoadingAnimation = startLoadingAnimation;
window.stopLoadingAnimation = stopLoadingAnimation;

;
/* === js/weather.js === */
/* ==================== weather.js ==================== */
// Wind direction utilities with fishing-specific tips
//
// LOADED BY: app.html / app.ejs via <script src="/js/weather.js">
// USED BY: ui-builders.js → buildDetailHTML() for wind arrow, main-server.js for wind info
// EXPORTS: getWindDirection(deg) as global — returns { name, img, tip }

/**
 * Get wind direction with image path and fishing-specific tips
 * This is the single source of truth for wind direction throughout the app
 * @param {number} deg - Wind direction in degrees (0-360)
 * @returns {Object} Wind direction object with name, img (absolute path), and tip
 */
const WIND_TIPS = {
    N:  { img: '/img/wind-north.png',      tip: 'Pushes bait south, south-facing shores get active' },
    NE: { img: '/img/wind-north-east.png',  tip: 'Water stacks southwest, fish protected structure' },
    E:  { img: '/img/wind-east.png',        tip: 'West sides stay calmer, good ambush zones' },
    SE: { img: '/img/wind-south-east.png',  tip: 'Northwest banks break the wind' },
    S:  { img: '/img/wind-south.png',       tip: 'North-facing areas stay slick and fishable' },
    SW: { img: '/img/wind-south-west.png',  tip: 'Northeast sides hold calmer water' },
    W:  { img: '/img/wind-west.png',        tip: 'East sides offer wind protection, prime holding area' },
    NW: { img: '/img/wind-north-west.png',  tip: 'Southeast shores stay calmer, fish cleaner' }
};

function getWindDirection(deg) {
    const name = degreesToCompass8(deg); // from shared-utils.js (global)
    const meta = WIND_TIPS[name] || WIND_TIPS.N;
    return { name, img: meta.img, tip: meta.tip };
}
;
/* === js/auth-client.js === */
/**
 * Authentication Client
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/auth-client.js">
 * USED BY: main-server.js (init flow), auth-ui.js (login/logout handlers)
 * DEPENDS ON: api-client.js (API_CLIENT), Google Identity Services CDN
 * EXPORTS: AUTH_CLIENT object with init(), login(), logout(), getUser() as global
 *
 * Handles Google Sign-In, token management, and auth state.
 * Uses Google Identity Services (GIS) library.
 */

const AUTH_CLIENT = {
    // In-memory token storage (NOT localStorage for security)
    accessToken: null,
    tokenExpiry: null,
    currentUser: null,
    membershipTier: 'free',
    // In-flight POST /auth/refresh shared by every caller in this tab (see _postRefresh)
    _refreshPromise: null,

    // Google client
    googleClient: null,

    // Configuration
    config: {
        googleClientId: null,
        googleEnabled: false,
        maptilerKey: null
    },

    /**
     * Fetch auth config from backend (Google client ID, MapTiler key, etc.)
     * Separated from init() so Google button can start loading before session restore.
     */
    async fetchConfig() {
        try {
            const response = await fetch(`${API_CLIENT.baseURL}/auth/config`);
            const result = await response.json();
            if (result.success) {
                this.config = result.config;
            }
        } catch (error) {
            console.error('[Auth] Config fetch error:', error);
        }
    },

    /**
     * Initialize the auth client
     * Fetches config and restores session
     */
    async init() {
        try {
            await this.fetchConfig();
            await this.tryRestoreSession();
            console.log('[Auth] Initialized', {
                googleEnabled: this.config.googleEnabled,
                authenticated: this.isAuthenticated()
            });
        } catch (error) {
            console.error('[Auth] Initialization error:', error);
        }
    },

    /**
     * Initialize Google Sign-In button
     * @param {string} buttonId - ID of the button container element
     * @param {number} retryCount - Number of retries attempted
     */
    _googleButtonRetryTimer: null,
    _googleButtonRenderedAt: 0,
    _googleInitialized: false,

    initGoogleButton(buttonId, retryCount = 0) {
        const maxRetries = 30; // 15s total (30 × 500ms) — generous for slow mobile
        const buttonContainer = document.getElementById(buttonId);

        // Cancel any pending retry from a previous call
        if (retryCount === 0 && this._googleButtonRetryTimer) {
            clearTimeout(this._googleButtonRetryTimer);
            this._googleButtonRetryTimer = null;
        }

        if (!this.config.googleEnabled || !this.config.googleClientId) {
            if (buttonContainer) buttonContainer.style.display = 'none';
            const divider = buttonContainer && buttonContainer.parentElement.querySelector('.auth-divider');
            if (divider) divider.style.display = 'none';
            return;
        }

        // Wait for Google Identity Services to load
        if (typeof google === 'undefined' || !google.accounts) {
            if (retryCount < maxRetries) {
                this._googleButtonRetryTimer = setTimeout(() => this.initGoogleButton(buttonId, retryCount + 1), 500);
            } else {
                console.error('[Auth] GIS failed to load after 15s');
                if (buttonContainer) buttonContainer.style.display = 'none';
                const divider = buttonContainer && buttonContainer.parentElement.querySelector('.auth-divider');
                if (divider) divider.style.display = 'none';
            }
            return;
        }

        // Popup mode is unreliable across browsers: Firefox/Safari can't postMessage back
        // from the popup, and Chrome's third-party-cookie deprecation leaves the popup
        // stuck blank on accounts.google.com/gsi/transform (reported Aug 2026). Redirect
        // mode is a plain top-level navigation, so it isn't affected by either issue.
        const initOptions = {
            client_id: this.config.googleClientId,
            callback: (response) => this.handleGoogleCallback(response),
            auto_select: false,
            cancel_on_tap_outside: true,
            ux_mode: 'redirect',
            login_uri: `${API_CLIENT.baseURL}/auth/google/redirect`
        };

        try {
            // Only call initialize() once — re-calling it triggers a console warning
            // and is unnecessary since config (client_id, callback) doesn't change.
            if (!this._googleInitialized) {
                google.accounts.id.initialize(initOptions);
                this._googleInitialized = true;
            }

            const container = document.getElementById(buttonId);
            if (container) {
                // Skip re-render if button was rendered less than 2s ago (prevents flicker on double-call)
                const now = Date.now();
                if (container.children.length > 0 && now - this._googleButtonRenderedAt < 2000) {
                    return;
                }
                // Clear any previous button content (for re-render after cancel)
                container.innerHTML = '';
                google.accounts.id.renderButton(container, {
                    type: 'standard',
                    theme: 'outline',
                    size: 'large',
                    text: 'continue_with',
                    shape: 'rectangular',
                    logo_alignment: 'left',
                    width: 280
                });
                this._googleButtonRenderedAt = Date.now();
                console.log('[Auth] Google button rendered');
            }
        } catch (error) {
            console.error('[Auth] Failed to initialize Google button:', error);
        }
    },

    /**
     * Handle Google Sign-In callback
     * @param {Object} response - Google credential response
     */
    async handleGoogleCallback(response) {
        if (!response.credential) {
            console.error('[Auth] No credential in Google response');
            this.onAuthError('Google sign-in failed');
            return;
        }

        try {
            // Show loading state
            this.onAuthLoading(true);

            // Start prefetching fishing data in parallel (doesn't need auth)
            if (typeof prefetchConditionsData === 'function') {
                prefetchConditionsData();
            }

            // Get device ID
            const deviceId = this.getOrCreateDeviceId();

            // Exchange Google ID token for our tokens
            const result = await fetch(`${API_CLIENT.baseURL}/auth/google/callback`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Device-ID': deviceId
                },
                credentials: 'include', // Important for cookies
                body: JSON.stringify({
                    idToken: response.credential,
                    deviceId
                })
            });

            const data = await result.json();

            if (!data.success) {
                throw new Error(data.error || 'Authentication failed');
            }

            // Store tokens and user info
            this.setAuthData(data.accessToken, data.expiresIn, data.user);
            if (data.csrfToken) this._csrfToken = data.csrfToken;
            this.membershipTier = data.user.membershipTier || 'free';

            // Store preferences if included in response (saves extra round-trip)
            if (data.preferences && typeof AUTH_UI !== 'undefined') {
                AUTH_UI.preferences = data.preferences;
            }

            // Notify success
            this.onAuthSuccess(data.user);

        } catch (error) {
            console.error('[Auth] Google callback error:', error);
            this.onAuthError(error.message);
        } finally {
            this.onAuthLoading(false);
        }
    },

    /**
     * Try to restore session using refresh token
     */
    async tryRestoreSession() {
        try {
            const { ok, data } = await this._postRefresh();

            if (!ok) {
                this._rememberSignOutReason(data);
                return false;
            }

            if (data && data.success && data.accessToken) {
                // Set the access token
                this.accessToken = data.accessToken;
                this.tokenExpiry = Date.now() + (data.expiresIn * 1000);
                if (data.csrfToken) this._csrfToken = data.csrfToken;

                // Store preferences if included in response (saves extra round-trip)
                if (data.preferences && typeof AUTH_UI !== 'undefined') {
                    AUTH_UI.preferences = data.preferences;
                }

                // Fetch user profile
                await this.fetchUserProfile();

                return true;
            }
        } catch (error) {
            // Silent fail - user just isn't logged in
            console.log('[Auth] No existing session');
        }

        return false;
    },

    /**
     * Refresh the access token
     * @returns {Promise<boolean>} True if refreshed successfully
     */
    async refreshToken() {
        try {
            const { data } = await this._postRefresh();
            if (!data) return false;

            if (data.success && data.accessToken) {
                this.accessToken = data.accessToken;
                this.tokenExpiry = Date.now() + (data.expiresIn * 1000);
                if (data.csrfToken) this._csrfToken = data.csrfToken;
                console.log('[Auth] Token refreshed');
                return true;
            }

            // Handle token reuse detection
            if (data.code === 'TOKEN_REUSE_DETECTED') {
                console.error('[Auth] Session compromised - logging out');
                this.clearAuthData();
                this.onSessionCompromised();
            }

            // Signed out by the plan's device limit: return to the greeting screen, which explains it
            if (data.code === 'SESSION_EVICTED') {
                this._rememberSignOutReason(data);
                this.clearAuthData();
                this.onLogout();
            }

            return false;
        } catch (error) {
            console.error('[Auth] Token refresh failed:', error);
            return false;
        }
    },

    /**
     * POST /auth/refresh, one at a time. Refresh tokens rotate on every use and all tabs share
     * the cookie, so a second refresh sent before the first response lands presents a spent token.
     * Callers in this tab share the in-flight request; other tabs wait on a Web Lock.
     * @returns {Promise<{ok: boolean, data: Object|null}>} data is null when the body isn't JSON
     */
    _postRefresh() {
        if (!this._refreshPromise) {
            const run = () => this._postRefreshRetryingRace();
            const request = typeof navigator !== 'undefined' && navigator.locks
                ? navigator.locks.request('reeltalk-auth-refresh', run)
                : run();
            this._refreshPromise = request.finally(() => { this._refreshPromise = null; });
        }
        return this._refreshPromise;
    },

    /**
     * A 409 REFRESH_RACE means another refresh (a tab without the lock, or an older app version)
     * just rotated the token; its new cookie lands shortly, so retry once.
     */
    async _postRefreshRetryingRace() {
        // Stop waiting after 10s so a hung request can't hold the lock for every tab, but never
        // abort: the server may already have rotated the token, and an aborted response would
        // drop the Set-Cookie carrying its replacement
        const send = async () => {
            let timer;
            const timeout = new Promise((_, reject) => {
                timer = setTimeout(() => reject(new Error('Refresh timed out')), 10000);
            });
            const request = fetch(`${API_CLIENT.baseURL}/auth/refresh`, {
                method: 'POST',
                credentials: 'include'
            }).then(async (response) => ({ ok: response.ok, data: await response.json().catch(() => null) }));
            try {
                return await Promise.race([request, timeout]);
            } finally {
                clearTimeout(timer);
            }
        };
        const isRace = (result) => !!(result.data && result.data.code === 'REFRESH_RACE');

        const first = await send();
        if (!isRace(first)) return first;
        await new Promise(resolve => setTimeout(resolve, 400));
        return send();
    },

    /**
     * Keep the reason for a forced sign-out so the greeting screen can explain it once.
     * @param {Object|null} body - refresh endpoint error body
     */
    _rememberSignOutReason(body) {
        if (body && body.code === 'SESSION_EVICTED') {
            this.lastSignOutReason = { code: body.code, deviceLimit: body.deviceLimit };
        }
    },

    /**
     * Fetch current user profile
     */
    async fetchUserProfile() {
        try {
            const result = await this.fetchWithAuth(`${API_CLIENT.baseURL}/auth/me`);
            const data = await result.json();

            if (data.success && data.user) {
                this.currentUser = data.user;
                this.membershipTier = data.user.membershipTier || 'free';
            }
        } catch (error) {
            console.error('[Auth] Failed to fetch profile:', error);
        }
    },

    /**
     * Logout
     */
    async logout() {
        try {
            const csrfToken = this.getCsrfToken();

            await fetch(`${API_CLIENT.baseURL}/auth/logout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.accessToken}`,
                    'X-CSRF-Token': csrfToken
                },
                credentials: 'include'
            });
        } catch (error) {
            console.error('[Auth] Logout error:', error);
        } finally {
            // Tell GIS not to auto-select on next sign-in attempt
            if (typeof google !== 'undefined' && google.accounts?.id) {
                google.accounts.id.disableAutoSelect();
            }
            this.clearAuthData();
            this.onLogout();
        }
    },

    /**
     * Set auth data after successful login
     */
    setAuthData(accessToken, expiresIn, user) {
        this.accessToken = accessToken;
        this.tokenExpiry = Date.now() + (expiresIn * 1000);
        this.currentUser = user;
    },

    /**
     * Clear all auth data
     */
    clearAuthData() {
        this.accessToken = null;
        this.tokenExpiry = null;
        this.currentUser = null;
        this._csrfToken = null;
        this.membershipTier = 'free';
        // Keep _googleInitialized true — GIS config (client_id, callback) doesn't
        // change between users, and re-calling initialize() triggers a console warning.
    },

    /**
     * Check if user is authenticated
     * @returns {boolean}
     */
    isAuthenticated() {
        return !!(this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry);
    },

    /**
     * Check if user has pro-level access (allowlist: pro or elite)
     * @returns {boolean}
     */
    isPro() {
        return this.membershipTier === 'pro' || this.membershipTier === 'elite';
    },

    /**
     * Check if the current user has the admin role
     * @returns {boolean}
     */
    isAdmin() {
        return !!(this.currentUser && this.currentUser.role === 'admin');
    },

    /**
     * Check if token needs refresh (within 5 minutes of expiry)
     * @returns {boolean}
     */
    needsRefresh() {
        if (!this.tokenExpiry) return false;
        const fiveMinutes = 5 * 60 * 1000;
        return Date.now() > (this.tokenExpiry - fiveMinutes);
    },

    /**
     * Fetch with authentication
     * Automatically adds auth header and handles 401
     */
    async fetchWithAuth(url, options = {}) {
        // Refresh token if needed
        if (this.needsRefresh() && this.accessToken) {
            await this.refreshToken();
        }

        // Add auth header
        const headers = {
            ...options.headers
        };

        if (this.accessToken) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
        }

        // Add CSRF token for mutations
        if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(options.method?.toUpperCase())) {
            const csrfToken = this.getCsrfToken();
            if (csrfToken) {
                headers['X-CSRF-Token'] = csrfToken;
            }
        }

        let response = await fetch(url, {
            ...options,
            headers,
            credentials: 'include'
        });

        // Handle 401 - try refresh
        if (response.status === 401 && this.accessToken) {
            const refreshed = await this.refreshToken();
            if (refreshed) {
                headers['Authorization'] = `Bearer ${this.accessToken}`;
                // refreshToken() rotates the CSRF cookie — update the header to match
                const freshCsrf = this.getCsrfToken();
                if (freshCsrf) headers['X-CSRF-Token'] = freshCsrf;
                response = await fetch(url, {
                    ...options,
                    headers,
                    credentials: 'include'
                });
            }
        }

        // Handle 403 CSRF errors — stale in-memory token, fall back to cookie
        if (response.status === 403) {
            let errBody;
            try { errBody = await response.clone().json(); } catch (_) {}
            if (errBody?.code === 'CSRF_INVALID' || errBody?.code === 'CSRF_MISSING') {
                this._csrfToken = null;
                const freshCsrf = this.getCsrfToken();
                if (freshCsrf) {
                    headers['X-CSRF-Token'] = freshCsrf;
                    return fetch(url, { ...options, headers, credentials: 'include' });
                }
            }
        }

        return response;
    },

    /**
     * Get CSRF token from cookie
     * @returns {string|null}
     */
    getCsrfToken() {
        if (this._csrfToken) return this._csrfToken;
        const match = document.cookie.match(/csrf_token=([^;]+)/);
        return match ? match[1] : null;
    },

    /**
     * Get or create device ID
     * @returns {string}
     */
    getOrCreateDeviceId() {
        let deviceId = localStorage.getItem('device_id');
        if (!deviceId) {
            deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
            localStorage.setItem('device_id', deviceId);
        }
        return deviceId;
    },

    // Event handlers (override these in your app)
    onAuthSuccess(user) {
        console.log('[Auth] Login successful:', user.email);
        // Override this to handle successful auth
        if (typeof handleAuthSuccess === 'function') {
            handleAuthSuccess(user);
        }
    },

    onAuthError(message) {
        console.error('[Auth] Auth error:', message);
        // Override this to handle auth errors
        if (typeof handleAuthError === 'function') {
            handleAuthError(message);
        }
    },

    onAuthLoading(isLoading) {
        // Override this to show/hide loading state
        if (typeof handleAuthLoading === 'function') {
            handleAuthLoading(isLoading);
        }
    },

    onLogout() {
        console.log('[Auth] Logged out');
        // Override this to handle logout
        if (typeof handleLogout === 'function') {
            handleLogout();
        }
    },

    onSessionCompromised() {
        console.error('[Auth] Session compromised!');
        // Override this to handle security incident
        if (typeof handleSessionCompromised === 'function') {
            handleSessionCompromised();
        }
    }
};

// Export to global scope
window.AUTH_CLIENT = AUTH_CLIENT;

if (typeof module !== 'undefined' && module.exports) {
    module.exports = AUTH_CLIENT;
}

;
/* === js/auth-ui.js === */
/**
 * Authentication UI Components
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/auth-ui.js">
 * USED BY: main-server.js → greeting screen flow, auth state callbacks
 * DEPENDS ON: auth-client.js (AUTH_CLIENT)
 * EXPORTS: AUTH_UI object with showGreetingScreen(), showUserMenu() as global
 *          showUpgradeModal(), startCheckout(), loadSubscriptionInfo(), manageSubscription() as globals
 *
 * Handles the greeting screen, user menu, and auth-related UI.
 */

const AUTH_UI = {
    /**
     * Show the greeting screen (for unauthenticated users)
     */
    showGreetingScreen() {
        const greetingScreen = document.getElementById('greetingScreen');
        const authStep = document.getElementById('authStep');
        const consentStep = document.getElementById('consentStep');
        const tosStep = document.getElementById('tosStep');
        const comparisonResults = document.getElementById('comparisonResults');
        const tabContainer = document.getElementById('tabContainer');
        const loading = document.getElementById('loading');

        // Hide nav during login flow
        document.body.classList.add('login-flow');

        // Show greeting screen with auth step
        if (greetingScreen) greetingScreen.style.display = 'flex';
        if (authStep) authStep.style.display = 'flex';
        if (consentStep) consentStep.style.display = 'none';
        if (tosStep) tosStep.style.display = 'none';

        // Hide other sections
        if (comparisonResults) comparisonResults.style.display = 'none';
        if (tabContainer) tabContainer.classList.add('u-hidden');
        if (loading) loading.classList.remove('show');

        // Hide account buttons
        this.hideUserMenu();

        // Only render Google button if the container is empty — re-renders are handled
        // by intermediate_iframe_close_callback in auth-client.js (popup cancel) and
        // initGoogleButton() in main-server.js (page load). Re-rendering here would
        // create a fresh GIS iframe right before the user clicks, causing a blank popup.
        if (typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.config.googleEnabled) {
            const gBtn = document.getElementById('googleButtonContainer');
            if (gBtn && !gBtn.children.length) {
                AUTH_CLIENT.initGoogleButton('googleButtonContainer');
            }
        }

        this.showSignOutNotice();
    },

    /**
     * Explain a device-limit sign-out once, on the greeting screen. Stays until the next
     * sign-in attempt replaces it (no auto-hide, unlike showAuthError).
     */
    showSignOutNotice() {
        if (typeof AUTH_CLIENT === 'undefined') return;
        const reason = AUTH_CLIENT.lastSignOutReason;
        if (!reason || reason.code !== 'SESSION_EVICTED') return;
        AUTH_CLIENT.lastSignOutReason = null;

        const notice = document.getElementById('authError');
        if (!notice) return;
        notice.textContent = signOutNoticeText(reason.deviceLimit) + ' ';
        const link = document.createElement('a');
        link.href = 'https://reeltalkfishing.com/support.html#faq-signed-out';
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = 'Learn more';
        notice.appendChild(link);
        notice.setAttribute('role', 'status');
        notice.style.display = 'block';
    },

    /**
     * Show the guest consent flow (TOS checkboxes) — step 2 within greeting card
     */
    showGuestConsentFlow() {
        const authStep = document.getElementById('authStep');
        const consentStep = document.getElementById('consentStep');
        const tosStep = document.getElementById('tosStep');
        const continueButton = document.getElementById('continueButton');

        // Toggle steps within greeting card
        if (authStep) authStep.style.display = 'none';
        if (tosStep) tosStep.style.display = 'none';
        if (consentStep) consentStep.style.display = 'flex';

        // Reset continue button
        if (continueButton) continueButton.disabled = true;

        // Reset checkboxes
        const tosCheckbox = document.getElementById('tosCheckbox');
        const pledgeCheckbox = document.getElementById('pledgeCheckbox');
        if (tosCheckbox) tosCheckbox.checked = false;
        if (pledgeCheckbox) pledgeCheckbox.checked = false;
    },

    /**
     * Show the main app (for authenticated or guest users)
     */
    async showMainApp() {
        const greetingScreen = document.getElementById('greetingScreen');
        const tabContainer = document.getElementById('tabContainer');

        // Restore nav when entering main app
        document.body.classList.remove('login-flow');

        // Hide greeting screen entirely
        if (greetingScreen) greetingScreen.style.display = 'none';

        // Show main app content
        if (tabContainer) tabContainer.classList.remove('u-hidden');

        // Ensure preferences are loaded before data render (safety net for race condition).
        // Usually a no-op — preferences already set by auth response (Fix 1).
        if (AUTH_CLIENT.isAuthenticated()) {
            await this.loadPreferences();
        }

        // Show user menu if authenticated (loadPreferences() removed from showUserMenu)
        if (AUTH_CLIENT.isAuthenticated()) {
            this.showUserMenu();
        }

        // Land on the tab named in the URL hash (deep link or reload), else the map. Before the
        // first data load switchTab doesn't exist yet; initializeTabs() applies the hash then.
        if (typeof window.switchTab === 'function') {
            window.switchTab(resolveTabFromHash(window.location.hash, AUTH_CLIENT.isAuthenticated()), { history: 'replace' });
        }

        // Load fishing data
        if (typeof compareAllLocations === 'function') {
            compareAllLocations();
        }
    },

    /**
     * Show settings button in sidebar and mobile nav, populate settings tab.
     * Also shows auth-only nav buttons (My Spots, Log Catch).
     */
    showUserMenu() {
        if (!AUTH_CLIENT.currentUser) return;

        const user = AUTH_CLIENT.currentUser;
        const safeEmail = escapeHtml(user.email || '');
        const safeAvatar = escapeHtml(user.avatarUrl || 'img/default-avatar.png');

        // Sidebar settings button (desktop)
        const sidebarBtn = document.getElementById('sidebarSettingsBtn');
        const sidebarAvatar = document.getElementById('sidebarAvatar');
        if (sidebarBtn) {
            sidebarBtn.style.display = '';
            if (sidebarAvatar) {
                sidebarAvatar.src = safeAvatar;
                sidebarAvatar.onerror = function() { this.src = 'img/default-avatar.png'; };
            }
        }

        // Mobile settings button
        const mobileBtn = document.getElementById('mobileSettingsBtn');
        const mobileAvatar = document.getElementById('mobileAvatar');
        if (mobileBtn) {
            mobileBtn.style.display = '';
            if (mobileAvatar) {
                mobileAvatar.src = safeAvatar;
                mobileAvatar.onerror = function() { this.src = 'img/default-avatar.png'; };
            }
        }

        // Show auth-only nav buttons (My Spots, Log Catch)
        ['sidebarSpotsBtn', 'sidebarCatchBtn', 'mobileSpotsBtn', 'mobileCatchBtn'].forEach(function(id) {
            var el = document.getElementById(id);
            if (el) el.style.display = '';
        });
        document.querySelectorAll('.auth-only-tab').forEach(function(el) {
            el.style.display = '';
        });

        // Show admin tab for admin users
        if (user.role === 'admin') {
            ['sidebarAdminBtn', 'topAdminBtn'].forEach(function(id) {
                var el = document.getElementById(id);
                if (el) el.style.display = '';
            });
        }

        // Populate settings tab content
        const settingsAvatar = document.getElementById('settingsAvatar');
        const settingsEmail = document.getElementById('settingsEmail');
        if (settingsAvatar) {
            settingsAvatar.src = safeAvatar;
            settingsAvatar.onerror = function() { this.src = 'img/default-avatar.png'; };
        }
        if (settingsEmail) settingsEmail.textContent = safeEmail;

        // Populate role badge — use membershipTier (covers Stripe-sourced pro) with admin override
        const roleBadge = document.getElementById('settingsRoleBadge');
        if (roleBadge) {
            const tier = (typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.membershipTier)
                ? AUTH_CLIENT.membershipTier : 'free';
            const isAdmin = user.role === 'admin';
            const tierLabels = { free: 'Free', pro: 'Pro', elite: 'Elite' };
            roleBadge.textContent = isAdmin ? 'Admin' : (tierLabels[tier] || 'Free');
            roleBadge.className = 'settings-role-badge'
                + (isAdmin ? ' settings-role-badge--admin' : '')
                + (tier === 'pro' || tier === 'elite' ? ' settings-role-badge--pro' : '');
        }

        // Load subscription info for settings tab
        if (typeof loadSubscriptionInfo === 'function') {
            loadSubscriptionInfo();
        }
    },

    /**
     * Hide settings button from sidebar and mobile nav.
     * Also hides auth-only nav buttons and clears spots/catches state.
     */
    hideUserMenu() {
        const sidebarBtn = document.getElementById('sidebarSettingsBtn');
        if (sidebarBtn) sidebarBtn.style.display = 'none';

        const mobileBtn = document.getElementById('mobileSettingsBtn');
        if (mobileBtn) mobileBtn.style.display = 'none';

        // Hide auth-only nav buttons
        ['sidebarSpotsBtn', 'sidebarCatchBtn', 'mobileSpotsBtn', 'mobileCatchBtn'].forEach(function(id) {
            var el = document.getElementById(id);
            if (el) el.style.display = 'none';
        });
        document.querySelectorAll('.auth-only-tab').forEach(function(el) {
            el.style.display = 'none';
        });

        // Hide admin tab
        ['sidebarAdminBtn', 'topAdminBtn'].forEach(function(id) {
            var el = document.getElementById(id);
            if (el) el.style.display = 'none';
        });

        // Clear spots/catches/analytics state
        if (typeof SPOTS !== 'undefined') SPOTS.clear();
        if (typeof CATCHES !== 'undefined') CATCHES.clear();
        if (typeof ANALYTICS !== 'undefined') ANALYTICS.clear();
    },

    // User preferences loaded from DB
    preferences: null,

    /**
     * Load user preferences from the server
     */
    async loadPreferences() {
        if (!AUTH_CLIENT.isAuthenticated()) return;

        // Skip fetch if preferences were already included in auth callback response
        if (this.preferences) {
            console.log('[Auth UI] Preferences already loaded, syncing dropdowns');
            this.syncSettingsDropdowns();
            this.applyMapLayerDefaults();
            return;
        }

        try {
            const response = await AUTH_CLIENT.fetchWithAuth(`${API_CLIENT.baseURL}/settings/preferences`);
            const data = await response.json();

            if (data.success) {
                this.preferences = data.preferences;
                console.log('[Auth UI] Preferences loaded:', this.preferences);

                // Pre-select settings dropdowns if we have saved defaults
                this.syncSettingsDropdowns();
                this.applyMapLayerDefaults();
            }
        } catch (error) {
            console.error('[Auth UI] Failed to load preferences:', error);
        }
    },

    /**
     * Apply saved map layer defaults to localStorage so the map loads with them.
     * Runs before map initialization — loadMapSettings() reads from localStorage.
     */
    applyMapLayerDefaults() {
        const ml = this.preferences?.mapLayers;
        if (!ml) return;

        // Without OVERLAY_CONFIG, keep the saved overlays rather than writing them all off
        const configs = getOverlayConfigs();
        const overlays = {};
        if (configs.length) {
            configs.forEach(config => {
                overlays[config.id] = !!ml.overlays?.[config.id];
            });
        } else {
            Object.entries(ml.overlays || {}).forEach(([id, on]) => {
                if (id !== 'baitShops') overlays[id] = !!on;
            });
        }
        const mapSettings = {
            layer: ml.baseLayer || 'voyager',
            showBaitShops: !!ml.overlays?.baitShops,
            overlays
        };

        try {
            localStorage.setItem('reelTalk_mapSettings', JSON.stringify(mapSettings));
            console.log('[Auth UI] Map layer defaults applied to localStorage');
        } catch (e) {
            console.error('[Auth UI] Failed to write map settings to localStorage:', e);
        }
    },

    /**
     * Build the Settings overlay default rows, one per OVERLAY_CONFIG entry (text only),
     * checked from the saved preferences. Pro layers the current tier can't use are
     * disabled with a Pro tag.
     * @param {Object} [options]
     * @param {boolean} [options.keepTicks] - a row that was enabled and stays enabled keeps
     *   its on-screen tick (unsaved edits survive a tier change); refreshLayersPanel() only
     */
    renderSettingsOverlayOptions(options) {
        const container = document.getElementById('settingsOverlayOptions');
        if (!container) return;

        const checked = { ...(this.preferences?.mapLayers?.overlays || {}) };
        const lock = typeof LOCK_ICON_SVG !== 'undefined' ? LOCK_ICON_SVG : '';
        const onScreen = {};
        if (options && options.keepTicks) {
            container.querySelectorAll('input[data-settings-overlay]:not(:disabled)').forEach(cb => {
                onScreen[cb.dataset.settingsOverlay] = cb.checked;
            });
        }

        container.innerHTML = getOverlayConfigs().map(config => {
            const locked = typeof canUseOverlay === 'function' && !canUseOverlay(config);
            if (!locked && config.id in onScreen) checked[config.id] = onScreen[config.id];
            return `<label class="settings-layer-option${locked ? ' is-locked' : ''}">
                <input type="checkbox" data-settings-overlay="${config.id}"${checked[config.id] ? ' checked' : ''}${locked ? ' disabled' : ''}>
                <span>${config.name}</span>${locked ? `<span class="map-layers-pro">${lock}Pro</span>` : ''}
            </label>`;
        }).join('');
    },

    /**
     * Sync settings dropdowns with saved preferences
     */
    syncSettingsDropdowns() {
        const filters = this.preferences?.defaultFilters;
        if (filters) {
            const showCount = document.getElementById('settingsShowCount');
            const region = document.getElementById('settingsRegion');
            const state = document.getElementById('settingsState');

            if (showCount && filters.showCount) showCount.value = filters.showCount;
            if (region && filters.region) region.value = filters.region;
            if (state && filters.state) state.value = filters.state;
        }

        // Overlay rows are rebuilt checked from saved preferences
        this.renderSettingsOverlayOptions();

        // Sync map layer checkboxes with saved preferences
        const mapLayers = this.preferences?.mapLayers;
        if (mapLayers) {
            const baseRadio = document.querySelector(`input[name="settingsBaseLayer"][value="${mapLayers.baseLayer}"]`);
            if (baseRadio) baseRadio.checked = true;

            const baitShopCb = document.getElementById('settingsBaitShops');
            if (baitShopCb) baitShopCb.checked = !!mapLayers.overlays?.baitShops;
        }
    },

    /**
     * Save default filter preferences to the server
     */
    async saveDefaultFilters() {
        const showCount = document.getElementById('settingsShowCount')?.value || 'all';
        const region = document.getElementById('settingsRegion')?.value || 'all';
        const state = document.getElementById('settingsState')?.value || 'all';
        const statusEl = document.getElementById('settingsSaveStatus');

        // Read map layer settings from checkboxes
        const baseLayer = document.querySelector('input[name="settingsBaseLayer"]:checked')?.value || 'voyager';
        // Without OVERLAY_CONFIG there are no overlay rows to read: resend the saved values
        const overlays = { ...(this.preferences?.mapLayers?.overlays || {}) };
        overlays.baitShops = document.getElementById('settingsBaitShops')?.checked || false;
        getOverlayConfigs().forEach(config => {
            const cb = document.querySelector(`input[data-settings-overlay="${config.id}"]`);
            overlays[config.id] = cb ? cb.checked : false;
        });
        const mapLayers = { baseLayer, overlays };

        try {
            const response = await AUTH_CLIENT.fetchWithAuth(`${API_CLIENT.baseURL}/settings/preferences`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    defaultFilters: { showCount, region, state },
                    mapLayers
                })
            });

            const data = await response.json();

            if (data.success) {
                this.preferences = data.preferences;
                // Sync map layer defaults to localStorage and apply to live map
                this.applyMapLayerDefaults();
                // Apply saved filters to current view immediately
                this.applyDefaultFilters();
                if (typeof syncMapLayersFromSettings === 'function') {
                    syncMapLayersFromSettings();
                }
                if (statusEl) {
                    statusEl.textContent = 'Saved!';
                    setTimeout(() => { statusEl.textContent = ''; }, 2000);
                }
                console.log('[Auth UI] Preferences saved:', this.preferences);
            } else {
                if (statusEl) statusEl.textContent = data.error || 'Save failed';
            }
        } catch (error) {
            console.error('[Auth UI] Failed to save preferences:', error);
            if (statusEl) statusEl.textContent = 'Save failed';
        }
    },

    /**
     * Populate region/state dropdowns in settings from loaded conditions data
     * (populateRegionSelect/populateStateSelect are defined in map.js, shared with the
     * popup and desktop inline filter selects)
     */
    populateSettingsDropdowns() {
        if (!window.allConditionsData || !window.allConditionsData.length) return;

        if (typeof populateRegionSelect === 'function') {
            populateRegionSelect(document.getElementById('settingsRegion'), window.allConditionsData);
        }
        if (typeof populateStateSelect === 'function') {
            populateStateSelect(document.getElementById('settingsState'), window.allConditionsData);
        }

        // Re-sync with saved preferences after populating
        this.syncSettingsDropdowns();
    },

    /**
     * Apply saved default filters to the filter popup + desktop inline selects, and trigger
     * an update
     */
    applyDefaultFilters() {
        const filters = this.preferences?.defaultFilters;
        if (!filters) return;

        // Only apply if at least one filter is non-default
        if (filters.showCount === 'all' && filters.region === 'all' && filters.state === 'all') return;

        const region = filters.region || 'all';
        const state = filters.state || 'all';
        const count = filters.showCount || 'all';

        // Set the filter popup's selects and the desktop inline selects (kept in sync —
        // the popup remains the source of truth read by other call sites)
        ['popupCountSelect', 'inlineCountSelect'].forEach(id => {
            const el = document.getElementById(id);
            if (el) el.value = count;
        });
        ['popupRegionSelect', 'inlineRegionSelect'].forEach(id => {
            const el = document.getElementById(id);
            if (el) el.value = region;
        });
        ['popupStateSelect', 'inlineStateSelect'].forEach(id => {
            const el = document.getElementById(id);
            if (el) el.value = state;
        });

        // Trigger the filter update
        if (typeof applyMapFilters === 'function') {
            applyMapFilters({ region, state, count });
            console.log('[Auth UI] Default filters applied:', filters);
        }
    },

    /**
     * Handle logout button click
     */
    async handleLogout() {
        await AUTH_CLIENT.logout();
    },

    /**
     * Show auth error message
     */
    showAuthError(message) {
        const errorDiv = document.getElementById('authError');
        if (errorDiv) {
            errorDiv.textContent = message;
            errorDiv.style.display = 'block';
            setTimeout(() => {
                errorDiv.style.display = 'none';
            }, 5000);
        } else {
            alert(message);
        }
    },

    /**
     * Show/hide loading overlay
     */
    setLoading(isLoading) {
        const loadingOverlay = document.getElementById('authLoading');
        if (loadingOverlay) {
            loadingOverlay.style.display = isLoading ? 'flex' : 'none';
        }
    }
};

// --- Upgrade Modal & Stripe Checkout ---

/**
 * Show upgrade modal with monthly/yearly pricing cards.
 * Dynamic DOM creation.
 */
function showUpgradeModal() {
    // Guard: don't show upgrade modal if user is already pro/elite
    if (typeof AUTH_CLIENT !== 'undefined' &&
        (AUTH_CLIENT.membershipTier === 'pro' || AUTH_CLIENT.membershipTier === 'elite')) {
        return;
    }

    var existingModal = document.getElementById('upgradeModal');
    if (existingModal) existingModal.remove();

    _upgradeModalOpener = document.activeElement;

    var modal = document.createElement('div');
    modal.id = 'upgradeModal';
    modal.className = 'upgrade-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'upgradeModalTitle');

    modal.innerHTML =
        '<div class="upgrade-modal-content">' +
            '<div class="upgrade-modal-header">' +
                '<h3 id="upgradeModalTitle">Upgrade to Pro</h3>' +
                '<button class="upgrade-modal-close" id="upgradeModalClose" aria-label="Close">&times;</button>' +
            '</div>' +
            '<div class="upgrade-modal-body">' +
                '<p class="upgrade-modal-subtitle">Unlock full forecasts, premium live data, all bait & rig recommendations, and more.</p>' +
                '<div class="upgrade-cards">' +
                    '<div class="upgrade-card" data-cycle="monthly">' +
                        '<div class="upgrade-card-price">$2.99<span>/mo</span></div>' +
                        '<button class="upgrade-card-btn" id="upgradeBtnMonthly">Start 7-day free trial</button>' +
                    '</div>' +
                    '<div class="upgrade-card upgrade-card--recommended" data-cycle="yearly">' +
                        '<div class="upgrade-card-badge">Save 16%</div>' +
                        '<div class="upgrade-card-price">$29.99<span>/yr</span></div>' +
                        '<button class="upgrade-card-btn" id="upgradeBtnYearly">Start 7-day free trial</button>' +
                    '</div>' +
                '</div>' +
            '</div>' +
        '</div>';

    document.body.appendChild(modal);

    // Close on backdrop click
    modal.addEventListener('click', function(e) {
        if (e.target === modal) closeUpgradeModal();
    });
    // Close on X button
    document.getElementById('upgradeModalClose').addEventListener('click', closeUpgradeModal);

    // CTA buttons
    document.getElementById('upgradeBtnMonthly').addEventListener('click', function() {
        startCheckout('monthly');
    });
    document.getElementById('upgradeBtnYearly').addEventListener('click', function() {
        startCheckout('yearly');
    });

    document.addEventListener('keydown', _upgradeModalKeydown);
    document.getElementById('upgradeBtnMonthly').focus();
}

var _upgradeModalOpener = null;

function _upgradeModalKeydown(e) {
    if (e.key === 'Escape') closeUpgradeModal();
}

function closeUpgradeModal() {
    var modal = document.getElementById('upgradeModal');
    if (modal) modal.remove();
    document.removeEventListener('keydown', _upgradeModalKeydown);
    // Return focus to whatever opened the modal, if it is still on the page
    if (_upgradeModalOpener && document.contains(_upgradeModalOpener) && _upgradeModalOpener.focus) {
        _upgradeModalOpener.focus();
    }
    _upgradeModalOpener = null;
}

/**
 * Start Stripe Checkout — POST to create-checkout-session, redirect to Stripe.
 * Debounce (R3): disable both buttons on click, re-enable on error.
 */
// Prevents double-submission: flag stays true until error (retry) or Stripe redirect (page navigates away)
var _checkoutInFlight = false;
function startCheckout(billingCycle) {
    if (_checkoutInFlight) return;
    _checkoutInFlight = true;

    // Disable both CTA buttons immediately (R3)
    var btnMonthly = document.getElementById('upgradeBtnMonthly');
    var btnYearly = document.getElementById('upgradeBtnYearly');
    if (btnMonthly) btnMonthly.disabled = true;
    if (btnYearly) btnYearly.disabled = true;

    AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/stripe/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ billingCycle: billingCycle })
    }).then(function(response) {
        return response.json();
    }).then(function(data) {
        if (data.url) {
            window.location.href = data.url;
        } else {
            throw new Error(data.error || 'Failed to create checkout session');
        }
    }).catch(function(err) {
        console.error('[Upgrade] Checkout error:', err.message);
        if (typeof showAppToast === 'function') {
            showAppToast(err.message || 'Checkout failed. Please try again.', 'error');
        } else {
            alert(err.message || 'Checkout failed. Please try again.');
        }
        // Re-enable buttons on error
        if (btnMonthly) btnMonthly.disabled = false;
        if (btnYearly) btnYearly.disabled = false;
        _checkoutInFlight = false;
    });
}

// --- Subscription Management ---

/**
 * Load subscription status for Settings tab.
 * Populates the subscription section if user has an active subscription.
 */
function loadSubscriptionInfo() {
    var section = document.getElementById('settingsSubscriptionSection');
    if (!section) return;

    AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/stripe/subscription-status')
        .then(function(response) { return response.json(); })
        .then(function(data) {
            section.style.display = '';
            var tierEl = document.getElementById('subscriptionTier');
            var detailsEl = document.getElementById('subscriptionDetails');
            var manageBtn = document.getElementById('subscriptionManageBtn');
            var upgradeBtn = document.getElementById('subscriptionUpgradeBtn');
            var tierLabel = data.tier.charAt(0).toUpperCase() + data.tier.slice(1);

            if (!data.subscription) {
                var isPaidTier = data.tier === 'pro' || data.tier === 'elite';
                if (tierEl) tierEl.textContent = tierLabel;
                if (detailsEl) detailsEl.textContent = isPaidTier ? 'Included with your account' : '';
                if (manageBtn) manageBtn.style.display = 'none';
                // Upgrade is offered to everyone without a paid tier
                if (upgradeBtn) upgradeBtn.style.display = isPaidTier ? 'none' : '';
                return;
            }

            if (tierEl) tierEl.textContent = tierLabel;

            if (detailsEl) {
                var sub = data.subscription;
                var parts = [];
                if (sub.billingCycle) parts.push(sub.billingCycle === 'yearly' ? '$29.99/year' : '$2.99/month');
                if (sub.status === 'trialing' && sub.trialEnd) {
                    parts.push('Trial ends ' + formatDate(sub.trialEnd));
                } else if (sub.cancelAt) {
                    parts.push('Cancels ' + formatDate(sub.cancelAt));
                } else if (sub.currentPeriodEnd) {
                    parts.push('Renews ' + formatDate(sub.currentPeriodEnd));
                }
                detailsEl.textContent = parts.join(' \u2022 ');
            }

            if (manageBtn) manageBtn.style.display = '';
            if (upgradeBtn) upgradeBtn.style.display = 'none';
        })
        .catch(function(err) {
            console.error('[Subscription] Failed to load status:', err);
        });
}

/**
 * Open Stripe Customer Portal for subscription management.
 */
function manageSubscription() {
    var btn = document.getElementById('subscriptionManageBtn');
    if (btn) btn.disabled = true;

    AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/stripe/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
    }).then(function(response) {
        return response.json();
    }).then(function(data) {
        if (data.url) {
            window.location.href = data.url;
        } else {
            throw new Error(data.error || 'Failed to open subscription management');
        }
    }).catch(function(err) {
        console.error('[Subscription] Portal error:', err.message);
        if (typeof showAppToast === 'function') {
            showAppToast(err.message || 'Failed to open subscription management', 'error');
        } else {
            alert(err.message || 'Failed to open subscription management');
        }
        if (btn) btn.disabled = false;
    });
}

// OVERLAY_CONFIG lives in map.min.js (loads after this bundle); guard in case it is absent.
function getOverlayConfigs() {
    return typeof OVERLAY_CONFIG !== 'undefined' ? OVERLAY_CONFIG : [];
}

// A2: Clear tier-dependent caches when membership changes (login or logout).
// Prevents showing cached pro-tier data (hourly forecast, tide details) to
// downgraded users, and ensures freshly-logged-in users get their tier's data.
function clearGatedCaches() {
    if (typeof tableState !== 'undefined') {
        tableState.recsCache.clear();
        tableState.detailCache.clear();
    }
    if (window.allConditionsData) {
        window.allConditionsData.forEach(function(item) {
            if (item.weatherData) item.weatherData._hourlyPeriods = null;
            item._forecastGated = undefined;
            item._forecastFreeHours = undefined;
            item._forecastTotalHours = undefined;
            item.tideData24h = null;
            item.sunriseSunsetData = null;
        });
    }
    // Lock/unlock Pro map overlays for the new tier (map.js is in the other bundle)
    if (typeof refreshLayersPanel === 'function') refreshLayersPanel();
}

// Global auth event handlers
async function handleAuthSuccess(user) {
    console.log('[Auth UI] Auth success, showing main app');
    await AUTH_UI.showMainApp();
    clearGatedCaches();

    // Landing page CTA: if user arrived via ?plan=pro, show upgrade modal after login
    if (typeof _pendingPlanUpgrade !== 'undefined' && _pendingPlanUpgrade) {
        _pendingPlanUpgrade = false;
        setTimeout(function() {
            if (typeof showUpgradeModal === 'function') showUpgradeModal();
        }, 500);
    }
}

function handleAuthError(message) {
    AUTH_UI.showAuthError(message);
}

function handleAuthLoading(isLoading) {
    AUTH_UI.setLoading(isLoading);
}

function handleLogout() {
    // If on auth-only tab, switch to public spots first (and drop its hash without a Back step)
    if (document.body.classList.contains('view-myspots') || document.body.classList.contains('view-logcatch')) {
        if (typeof switchTab === 'function') switchTab('publicSpots', { history: 'replace' });
    }

    AUTH_UI.hideUserMenu();
    AUTH_UI.showGreetingScreen();
    // The next sign-in on this device fetches its own preferences
    AUTH_UI.preferences = null;
    clearGatedCaches();
}

function handleSessionCompromised() {
    AUTH_UI.showGreetingScreen();
    AUTH_UI.showAuthError('Your session ended for security reasons. Please sign in again.');
    AUTH_UI.preferences = null;
    clearGatedCaches();
}

// Handle guest button click
function handleGuestClick() {
    AUTH_UI.showGuestConsentFlow();
}

// Export to global scope
window.AUTH_UI = AUTH_UI;

;
/* === js/tos-content.js === */
/**
 * Terms of Service Content
 * Extracted to separate file to keep main-server.js cleaner
 * Source: tos.html (content only, no HTML structure)
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/tos-content.js">
 * USED BY: main-server.js → TOS modal body, auth-ui.js → greeting consent screen
 * EXPORTS: TOS_CONTENT string as global constant
 */

const TOS_CONTENT = `
    <p class="effective-date">Effective Date: January 11, 2025</p>

    <p>These Terms of Service and Disclaimer ("Terms") govern your access to and use of Reel Talk (the "App"), a free application operated by Bao Huynh ("Company," "we," "us," or "our").</p>

    <p><strong>By accessing or using the App, you agree to these Terms. If you do not agree, do not use the App.</strong></p>

    <h2>1. Free App – Informational Use Only</h2>
    <p>The App is provided free of charge and offers fishing-related information, including but not limited to fishing locations, maps, tide data, weather data, and user-submitted content, for general informational purposes only.</p>
    <p>Information may be inaccurate, incomplete, outdated, or subject to change without notice. The Company does not guarantee accuracy, completeness, safety, legality, or suitability for any purpose.</p>
    <p>The App aggregates data from third-party sources including weather services, governmental agencies (NOAA), and mapping providers. We do not verify, endorse, or guarantee the accuracy of third-party data. Users assume all risks associated with reliance on such data.</p>
    <p>Gear, bait, and equipment recommendations are general suggestions only and may not be suitable, legal, or safe for all locations or conditions. Users must independently verify the appropriateness and legality of all equipment and techniques.</p>

    <h2>2. No Guarantee of Access or Legality</h2>
    <p>The App does not guarantee public access to any fishing location.</p>
    <p>Locations displayed are user-contributed or publicly available information. We do not inspect, verify safety, or confirm access rights for any location. Some locations may be on private property, require permits, have seasonal closures, or present serious safety hazards. The presence of a location in the App does not constitute a recommendation to visit it.</p>
    <p>You are solely responsible for:</p>
    <ul>
        <li>Determining whether a location is legally accessible</li>
        <li>Complying with posted signs, property boundaries, and access restrictions</li>
        <li>Avoiding trespass on private or restricted property</li>
        <li>Assessing safety hazards at any location</li>
    </ul>
    <p>Use of the App does not create any right of entry, access, or permission.</p>

    <h2>3. Regulations & Environmental Compliance</h2>
    <p>Fishing regulations, seasons, size limits, bag limits, and environmental protections vary by location and change frequently.</p>
    <p>You are solely responsible for:</p>
    <ul>
        <li>Verifying current fishing regulations</li>
        <li>Avoiding protected or restricted areas, including oyster reefs and conservation zones</li>
        <li>Complying with all applicable local, state, and federal laws</li>
    </ul>
    <p>The App does not provide legal, regulatory, or environmental advice.</p>

    <h2>4. Assumption of Risk & Weather Safety</h2>
    <p>Fishing, boating, and accessing waterways involve inherent risks, including but not limited to:</p>
    <ul>
        <li>Weather conditions</li>
        <li>Tides and water depth changes</li>
        <li>Vessel traffic</li>
        <li>Slippery or unstable terrain</li>
        <li>Wildlife and environmental hazards</li>
    </ul>
    <p><strong>You voluntarily assume all risks associated with using the App and any activities undertaken based on App information.</strong></p>
    <p><strong>Weather Safety Disclaimer:</strong> Weather and tide information may be outdated, inaccurate, or change rapidly. A favorable fishing conditions score does not indicate safe weather conditions. Users must independently assess weather safety before any outdoor activity. Never fish during severe weather, high winds, lightning, flooding, or other dangerous conditions.</p>

    <h2>5. Age Eligibility & Minor Use Eligibility</h2>
    <p>The App is intended for individuals 18 years of age or older. By using the App, you represent and warrant that:</p>
    <ul>
        <li>You are at least 18 years old, or</li>
        <li>You are a minor using the App with the consent and supervision of a parent or legal guardian.</li>
    </ul>

    <h3>Parental Responsibility</h3>
    <p>If you are a parent or legal guardian permitting a minor to use the App, you agree to:</p>
    <ul>
        <li>Supervise the minor's use of the App</li>
        <li>Assume full responsibility for the minor's actions</li>
        <li>Accept these Terms on behalf of the minor</li>
        <li>Be responsible for any legal, safety, or regulatory consequences arising from the minor's use of the App</li>
    </ul>

    <h3>No Targeting of Children</h3>
    <p>The App is not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If such information is discovered, we will delete it promptly.</p>

    <h3>Risk Acknowledgment for Minors</h3>
    <p>Fishing and boating involve inherent risks. Minors must be supervised at all times. The Company disclaims responsibility for injuries, accidents, citations, or violations involving minors.</p>

    <h2>6. No Warranties</h2>
    <p>The App is provided "as is" and "as available," without warranties of any kind, express or implied, including warranties of accuracy, reliability, safety, merchantability, or fitness for a particular purpose.</p>

    <h2>7. Limitation of Liability</h2>
    <p>To the maximum extent permitted by law, the Company shall not be liable for any damages arising out of or related to your use of the App, including but not limited to:</p>
    <ul>
        <li>Personal injury or death</li>
        <li>Property or vessel damage</li>
        <li>Trespass or access claims</li>
        <li>Fines, citations, or legal penalties</li>
        <li>Loss of data, profits, or fishing opportunities</li>
    </ul>
    <p><strong>Your sole remedy is to stop using the App.</strong></p>

    <h2>8. Indemnification</h2>
    <p>You agree to indemnify, defend, and hold harmless the Company, its members, officers, and agents from any claims, damages, losses, liabilities, or expenses (including attorneys' fees) arising from:</p>
    <ul>
        <li>Your use of the App</li>
        <li>Your violation of these Terms</li>
        <li>Your violation of any law or regulation</li>
        <li>Your submitted content or actions</li>
    </ul>

    <h2>9. Arbitration Agreement & Class Action Waiver</h2>
    <p><strong>Binding Arbitration:</strong> Any dispute, claim, or controversy arising out of or relating to these Terms or your use of the App shall be resolved by binding arbitration administered by the American Arbitration Association (AAA) under its Commercial Arbitration Rules. The arbitration shall take place in Harris County, Texas, or remotely via videoconference at the arbitrator's discretion.</p>
    <p><strong>Exception for Small Claims:</strong> Either party may bring an individual action in small claims court if the claim qualifies.</p>
    <p><strong>Class Action Waiver:</strong> You agree that all disputes must be brought only in your individual capacity and not as a plaintiff or class member in any class, collective, or representative action. The arbitrator may not consolidate claims or conduct any class proceedings.</p>
    <p><strong>30-Day Opt-Out Right:</strong> You may opt out of this arbitration agreement by sending written notice to winsoftware.texas@gmail.com within 30 days of first accepting these Terms. Your notice must include your name and a clear statement that you wish to opt out of the arbitration agreement. <strong>If you opt out of arbitration, your right to use the App will be immediately terminated and you must cease all use of the App.</strong></p>

    <h2>10. Privacy & Data Collection</h2>
    <p>The App collects limited data for operational purposes:</p>
    <ul>
        <li><strong>Analytics Data:</strong> We collect anonymous usage statistics including IP addresses, browser types, page views, and timestamps to monitor app performance and usage patterns.</li>
        <li><strong>Cookies:</strong> The App uses cookies for functionality and third-party advertising (Google AdSense).</li>
        <li><strong>Third-Party Services:</strong> We use OpenWeather API, NOAA data services, and Leaflet mapping services. These providers may collect data according to their own privacy policies.</li>
        <li><strong>No Sale of Data:</strong> We do not sell personal information to third parties.</li>
        <li><strong>Data Retention:</strong> Analytics data is retained for operational purposes and may be deleted at our discretion.</li>
    </ul>
    <p>By using the App, you consent to this data collection. If you are a California resident or subject to GDPR, you may have additional rights. Contact us at winsoftware.texas@gmail.com for data requests.</p>

    <h2>11. Governing Law & Venue</h2>
    <p>These Terms are governed by the laws of the State of Texas, without regard to conflict-of-law principles. Subject to the arbitration provisions above, any legal action must be brought in Harris County, Texas.</p>

    <h2>12. Severability</h2>
    <p>If any provision of these Terms is found to be unenforceable or invalid by a court of competent authority, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect. If the arbitration agreement is found unenforceable, the class action waiver shall remain enforceable to the maximum extent permitted by law.</p>

    <h2>13. Termination</h2>
    <p>We may suspend or terminate access to the App at any time, for any reason, without notice.</p>

    <h2>14. Changes to Terms</h2>
    <p>We may update these Terms from time to time. Continued use of the App after changes constitutes acceptance. You may be required to re-accept updated Terms.</p>

    <h2>15. Contact</h2>
    <p>Questions, concerns, or suggestions:<br>
    📝 <a href="https://forms.gle/fSTQryD48GvyH1cA7" target="_blank" rel="noopener noreferrer">Submit Feedback Form</a></p>
`;

;
/* === js/gear.js === */
/**
 * Dynamic Fishing Recommendations Engine
 * Generates condition-aware, actionable fishing advice
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/gear.js">
 * USED BY: ui-builders.js → buildDetailHTML() calls getGearRecommendationsForMap()
 * DEPENDS ON: cond.factors (Water Temp, Tide from scoring.js output)
 * EXPORTS: getGearRecommendationsForMap(loc, cond, weather, tideData) as global
 *
 * Phase 3: Client-side scoring functions (getBaitRecommendations, getRigRecommendations,
 * getActiveSpecies, etc.) removed. Bait/rig scoring now server-side via
 * GET /api/location/:key/recommendations. Remaining functions serve the map popup narrative.
 */

function getGearRecommendationsForMap(loc, cond, weather, tideData) {
    const temp = weather.main.temp;
    const wind = weather.wind.speed;
    const windDir = weather.wind.deg;
    const waterTempFactor = cond.factors.find(f => f.name === 'Water Temp');
    const tideFactor = cond.factors.find(f => f.name === 'Tide');
    const moonPhase = cond.moonPhase;
    const waterTemp = waterTempFactor && waterTempFactor.value !== 'N/A' ? parseFloat(waterTempFactor.value) : null;

    // Analyze conditions and build recommendations
    const analysis = analyzeFishingConditions(loc, cond, weather, waterTemp, wind, temp, moonPhase, tideFactor);

    // Pass cond and weather to access new API data (speciesScores, pressureTrend, etc.)
    return buildStreamlinedRecommendations(loc, analysis, cond.score, cond, weather);
}

/**
 * Analyze current conditions and determine best tactics.
 * Top species derived from server-side speciesScores (Phase 2).
 * Bait priority simplified — detailed bait/rig scoring now server-side.
 */
function analyzeFishingConditions(loc, cond, weather, waterTemp, wind, airTemp, moonPhase, tideFactor) {
    // Derive top species from server-side speciesScores
    let topSpecies = [];
    if (cond && cond.speciesScores) {
        topSpecies = getSortedSpecies(cond.speciesScores, {
            includeClosedSeason: false, maxCount: 2
        }).map(([, data]) => data.displayName);
    }
    if (topSpecies.length === 0) {
        topSpecies = (loc.targetSpecies || []).slice(0, 2);
    }

    // Simple bait priority from water temp (for map popup narrative only)
    let baitPriority;
    const isColdWater = waterTemp != null && waterTemp < 62;
    if (isColdWater || wind > 15) {
        baitPriority = [{ label: 'Cut Bait' }, { label: 'Soft Plastics' }, { label: 'Live Shrimp' }];
    } else if (waterTemp != null && waterTemp >= 70 && waterTemp <= 78) {
        baitPriority = [{ label: 'Live Shrimp' }, { label: 'Soft Plastics' }, { label: 'Cut Bait' }];
    } else {
        baitPriority = [{ label: 'Soft Plastics' }, { label: 'Live Shrimp' }, { label: 'Cut Bait' }];
    }

    return {
        topSpecies,
        whereToFish: getWhereToFish(loc.types[0], tideFactor, wind),
        whenToFish: getWhenToFish(tideFactor, moonPhase, cond),
        baitPriority
    };
}

/**
 * Suggest where to position based on conditions
 */
function getWhereToFish(locationType, tideFactor, wind) {
    const spots = [];

    // Tide value contains things like "High in 45 min" or "Low 23 min ago"
    const tideValue = (tideFactor && tideFactor.value) ? tideFactor.value : '';

    // Tide-based positioning: incoming = water rising towards high, outgoing = water falling towards low
    if (tideValue.includes('High')) {
        // Heading towards high tide or just passed it
        const timeMatch = tideValue.match(/(\d+)\s*min/);
        const minutes = timeMatch ? parseInt(timeMatch[1]) : 999;

        if (tideValue.includes('in') && minutes <= 120) {
            // Incoming tide - water rising
            spots.push('Grass flats and shallow areas');
            spots.push('Channel mouths and drop-offs');
        } else if (tideValue.includes('ago') && minutes <= 60) {
            // Just passed high - outgoing starting
            spots.push('Deeper channels and cuts');
            spots.push('Outflow side of structure');
        } else {
            spots.push('Deep holes and channels');
            spots.push('Around structure and pilings');
        }
    } else if (tideValue.includes('Low')) {
        // Heading towards low tide or just passed it
        const timeMatch = tideValue.match(/(\d+)\s*min/);
        const minutes = timeMatch ? parseInt(timeMatch[1]) : 999;

        if (tideValue.includes('in') && minutes <= 120) {
            // Outgoing tide - water falling
            spots.push('Deeper channels and cuts');
            spots.push('Outflow side of structure');
        } else if (tideValue.includes('ago') && minutes <= 60) {
            // Just passed low - incoming starting
            spots.push('Grass flats and shallow areas');
            spots.push('Channel mouths and drop-offs');
        } else {
            spots.push('Deep holes and channels');
            spots.push('Around structure and pilings');
        }
    } else {
        // No clear tide info - use defaults
        spots.push('Deep holes and channels');
        spots.push('Around structure and pilings');
    }

    // Location type specifics
    if (locationType === 'pier') {
        spots.push('Near pier pilings (structure)');
    } else if (locationType === 'jetty') {
        spots.push('Rock pockets and channel cuts');
    } else if (locationType === 'surf') {
        spots.push('First and second gut (troughs)');
    } else if (locationType === 'shore') {
        spots.push('Points and sandbars');
    } else if (locationType === 'bay' || locationType === 'wading') {
        spots.push('Oyster reefs and shell beds');
    } else if (locationType === 'kayak') {
        spots.push('Grass flats and marsh edges');
    } else if (locationType === 'channel') {
        spots.push('Channel ledges and drop-offs');
    }

    return spots.slice(0, 3);
}

/**
 * Calculate best fishing times - uses FBI forecast data when available
 * @param {Object} tideFactor - Tide factor from conditions
 * @param {Object} moonPhase - Moon phase data
 * @param {Object} cond - Full conditions object (for enhanced fallback logic)
 */
function getWhenToFish(tideFactor, moonPhase, cond) {
    // Try FBI-based recommendations first (uses window.fishingForecast)
    const fbiBased = getBestTimeSlots({ minScore: 45 });

    if (fbiBased && fbiBased.slots.length > 0) {
        return buildFBIBasedTiming(fbiBased, moonPhase);
    }

    // Fallback to enhanced tide-based logic
    return getWhenToFishEnhanced(tideFactor, moonPhase, cond);
}

/**
 * Get best fishing time slots based on forecast FBI scores
 * Uses window.fishingForecast data (populated from server-calculated FBI scores)
 * @param {Object} options - Configuration options
 * @param {number} options.minScore - Minimum FBI score to recommend (default: 45)
 * @returns {Object|null} Time slot recommendations or null if no forecast data
 */
function getBestTimeSlots(options = {}) {
    const { minScore = 45 } = options;
    const forecast = window.fishingForecast;

    // Return null if no forecast data (caller should fallback to tide-based logic)
    if (!forecast || !Array.isArray(forecast) || forecast.length === 0) {
        return null;
    }

    const now = new Date();
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    // Filter valid future slots with FBI scores above minimum threshold
    const validSlots = forecast
        .filter(slot => {
            const slotTime = new Date(slot.timestamp);
            return slot.fbiScore >= minScore && slotTime >= now;
        })
        .map(slot => ({
            timestamp: new Date(slot.timestamp),
            fbiScore: slot.fbiScore,
            data: slot
        }));

    if (validSlots.length === 0) return null;

    // Separate today's slots from future slots
    const todaySlots = validSlots.filter(s => s.timestamp <= todayEnd);
    const tomorrowSlots = validSlots.filter(s => s.timestamp > todayEnd);

    // Sort by FBI score descending (highest first)
    todaySlots.sort((a, b) => b.fbiScore - a.fbiScore);
    tomorrowSlots.sort((a, b) => b.fbiScore - a.fbiScore);

    // Check if tomorrow is significantly better (10+ points higher)
    const tomorrowBetter = tomorrowSlots.length > 0 &&
        todaySlots.length > 0 &&
        tomorrowSlots[0].fbiScore > todaySlots[0].fbiScore + 10;

    return {
        slots: todaySlots.slice(0, 3),  // Top 3 slots by FBI score
        tomorrowPeak: tomorrowBetter ? tomorrowSlots[0] : null,
        hasForecastData: true
    };
}

/**
 * Format time slot for display (e.g., "3 PM - 6 PM")
 */
function formatSlotTime(timestamp) {
    const hour = timestamp.getHours();
    const endHour = (hour + 3) % 24;
    const formatHour = (h) => {
        const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        const ampm = h >= 12 ? 'PM' : 'AM';
        return `${h12} ${ampm}`;
    };
    return `${formatHour(hour)} - ${formatHour(endHour)}`;
}

/**
 * Get descriptive label based on FBI score
 */
function getLabelForScore(score) {
    if (score >= 75) return 'Peak feeding time';
    if (score >= 60) return 'Good activity expected';
    return 'Fair conditions';
}

/**
 * Get label based on time of day (no emojis)
 */
function getIconForTime(hour) {
    if (hour >= 5 && hour < 9) return 'dawn';
    if (hour >= 9 && hour < 17) return 'day';
    if (hour >= 17 && hour < 20) return 'dusk';
    return 'night';
}

/**
 * Build timing object from FBI-based slots
 */
function buildFBIBasedTiming(fbiBased, moonPhase) {
    const timing = {
        slot1: { time: '', label: '', icon: '', fbiScore: null },
        slot2: { time: '', label: '', icon: '', fbiScore: null },
        slot3: { time: '', label: '', icon: '', fbiScore: null },
        showAlsoGood: false,
        dawn: '6:00 AM - 8:00 AM',
        dusk: '5:00 PM - 7:00 PM',
        reason: ''
    };

    const slots = fbiBased.slots;

    if (slots[0]) {
        const hour = slots[0].timestamp.getHours();
        timing.slot1 = {
            time: `${formatSlotTime(slots[0].timestamp)} (FBI ${slots[0].fbiScore})`,
            label: getLabelForScore(slots[0].fbiScore),
            icon: getIconForTime(hour),
            fbiScore: slots[0].fbiScore
        };
    }

    if (slots[1]) {
        const hour = slots[1].timestamp.getHours();
        timing.slot2 = {
            time: `${formatSlotTime(slots[1].timestamp)} (FBI ${slots[1].fbiScore})`,
            label: getLabelForScore(slots[1].fbiScore),
            icon: getIconForTime(hour),
            fbiScore: slots[1].fbiScore
        };
    }

    if (slots[2]) {
        const hour = slots[2].timestamp.getHours();
        timing.slot3 = {
            time: `${formatSlotTime(slots[2].timestamp)} (FBI ${slots[2].fbiScore})`,
            label: getLabelForScore(slots[2].fbiScore),
            icon: getIconForTime(hour),
            fbiScore: slots[2].fbiScore
        };
    } else if (fbiBased.tomorrowPeak) {
        const tomorrow = fbiBased.tomorrowPeak;
        const dayName = formatDate(tomorrow.timestamp, { weekday: 'short' });
        timing.slot3 = {
            time: `${dayName} looks better (FBI ${tomorrow.fbiScore})`,
            label: 'Consider tomorrow',
            icon: '',
            fbiScore: tomorrow.fbiScore
        };
    }

    // Score labels match getScoreLabel() ranges in config-frontend.js
    const topScore = slots[0]?.fbiScore || 50;
    if (topScore >= 75) {
        timing.reason = 'Conditions align for peak feeding activity';
    } else if (topScore >= 60) {
        timing.reason = 'Good conditions expected during these windows';
    } else {
        timing.reason = 'Best available windows given current conditions';
    }

    // Moon phase bonus
    if (moonPhase && moonPhase.score >= 8) {
        timing.reason += '. Strong moon phase boosts activity!';
    }

    return timing;
}

/**
 * Get sunrise/sunset times from API data or use seasonal defaults
 * @returns {Object} Sun times with formatted windows
 */
function getSunriseSunsetTimes() {
    const now = new Date();
    const month = now.getMonth();

    // Try to get real data from OpenWeather API
    if (typeof window !== 'undefined' && window.sunriseSunsetData) {
        const sunData = window.sunriseSunsetData;
        if (sunData.sunrise && sunData.sunset) {
            const sunrise = new Date(sunData.sunrise);
            const sunset = new Date(sunData.sunset);

            const sunriseHour = sunrise.getHours();
            const sunsetHour = sunset.getHours();

            return {
                sunriseHour,
                sunsetHour,
                dawnWindow: `${formatHour12(sunriseHour - 1)} - ${formatHour12(sunriseHour + 1)}`,
                duskWindow: `${formatHour12(sunsetHour - 1)} - ${formatHour12(sunsetHour + 1)}`
            };
        }
    }

    // Fallback: use shared TX Gulf Coast tables (precise decimal hours, rounded for display)
    const sunriseHour = Math.round(TX_SUNRISE_HOURS[month]);
    const sunsetHour = Math.round(TX_SUNSET_HOURS[month]);

    return {
        sunriseHour,
        sunsetHour,
        dawnWindow: `${formatHour12(sunriseHour - 1)} - ${formatHour12(sunriseHour + 1)}`,
        duskWindow: `${formatHour12(sunsetHour - 1)} - ${formatHour12(sunsetHour + 1)}`
    };
}

/**
 * Format hour in 12-hour format (e.g., "6 AM", "3 PM")
 */
function formatHour12(hour) {
    hour = ((hour % 24) + 24) % 24; // Normalize to 0-23
    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${h12} ${ampm}`;
}

/**
 * Enhanced fallback: Calculate best fishing times using all available condition data
 * Used when forecast FBI data is not available
 * @param {Object} tideFactor - Tide factor from conditions
 * @param {Object} moonPhase - Moon phase data
 * @param {Object} cond - Full conditions object with tideMovement, pressureTrend, etc.
 */
function getWhenToFishEnhanced(tideFactor, moonPhase, cond) {
    // Get real sunrise/sunset times if available
    const sunTimes = getSunriseSunsetTimes();

    const timing = {
        slot1: { time: '', label: '', icon: '' },
        slot2: { time: '', label: '', icon: '' },
        slot3: { time: '', label: '', icon: '' },
        showAlsoGood: false,
        dawn: sunTimes.dawnWindow,
        dusk: sunTimes.duskWindow,
        reason: ''
    };

    const now = new Date();
    const currentHour = now.getHours();

    // Check for pre-storm conditions (pressure falling) - fish NOW
    const isPreStorm = cond?.pressureTrend?.isPreStorm ||
                       (cond?.pressureTrend?.trend === 'falling' && cond?.pressureTrend?.changeRate < -0.2);

    // Get tide movement data if available
    const tideMovement = cond?.tideMovement;
    const hasStrongTide = tideMovement && (tideMovement.movement === 'strong' || tideMovement.movement === 'moderate');
    const tideStage = tideMovement?.stage || 'unknown';

    // Priority weights rank overlapping fishing windows. Higher priorities appear first
    // in the "When to Fish" recommendation: 100=pre-storm, 90=tide, 85=dawn(now), 75=dusk, 70=dawn(upcoming)
    const slots = [];

    // Priority 1: Pre-storm = fish NOW
    if (isPreStorm) {
        const endHour = Math.min(currentHour + 3, 23);
        slots.push({
            time: `NOW - ${formatHour12(endHour)}`,
            label: 'Pre-storm feeding frenzy',
            icon: '',
            priority: 100
        });
    }

    // Priority 2: Strong tide movement happening now
    if (hasStrongTide && !isPreStorm) {
        const stageLabel = tideStage === 'incoming' ? 'Incoming tide' :
                          tideStage === 'outgoing' ? 'Outgoing tide' : 'Active tide';
        slots.push({
            time: `NOW - ${formatHour12((currentHour + 2) % 24)}`,
            label: `${stageLabel} - fish are feeding`,
            icon: '',
            priority: 90
        });
    }

    // Priority 3: Dawn window (if not past)
    if (currentHour < sunTimes.sunriseHour + 2) {
        slots.push({
            time: sunTimes.dawnWindow,
            label: 'Sunrise feeding window',
            icon: '',
            priority: currentHour >= sunTimes.sunriseHour - 1 ? 85 : 70
        });
    }

    // Priority 4: Dusk window (if not past)
    if (currentHour < sunTimes.sunsetHour + 1) {
        slots.push({
            time: sunTimes.duskWindow,
            label: 'Sunset feeding window',
            icon: '',
            priority: 75
        });
    }

    // Priority 5: Parse tide timing from text as backup
    const tideValue = (tideFactor && tideFactor.value) ? tideFactor.value : '';
    const tideMatch = tideValue.match(/at\s+(\d+):(\d+)\s*([AP]M)/i);

    if (tideMatch && slots.length < 3) {
        let tideHour = parseInt(tideMatch[1]);
        const meridiem = tideMatch[3].toUpperCase();
        if (meridiem === 'PM' && tideHour !== 12) tideHour += 12;
        if (meridiem === 'AM' && tideHour === 12) tideHour = 0;

        // Only add if tide is in the future
        if (tideHour > currentHour || (tideHour < currentHour && tideHour + 24 - currentHour < 12)) {
            const beforeHour = (tideHour - 1 + 24) % 24;
            const afterHour = (tideHour + 1) % 24;

            slots.push({
                time: `${formatHour12(beforeHour)} - ${formatHour12(afterHour)}`,
                label: 'Around tide peak',
                icon: '',
                priority: 65
            });
        }
    }

    // Priority 6: Midday as last resort
    if (slots.length < 3 && currentHour < 14) {
        slots.push({
            time: '11 AM - 2 PM',
            label: 'Midday opportunity',
            icon: '',
            priority: 40
        });
    }

    // Sort by priority and take top 3
    slots.sort((a, b) => b.priority - a.priority);

    if (slots[0]) {
        timing.slot1 = { time: slots[0].time, label: slots[0].label, icon: slots[0].icon };
    }
    if (slots[1]) {
        timing.slot2 = { time: slots[1].time, label: slots[1].label, icon: slots[1].icon };
    }
    if (slots[2]) {
        timing.slot3 = { time: slots[2].time, label: slots[2].label, icon: slots[2].icon };
    }

    // Build reason based on conditions
    if (isPreStorm) {
        timing.reason = 'Barometer dropping — fish feed aggressively before fronts!';
    } else if (hasStrongTide) {
        timing.reason = 'Strong tide movement pushing baitfish';
    } else {
        timing.reason = 'Low light periods are peak feeding times';
    }

    // Moon phase bonus
    if (moonPhase && moonPhase.score >= 8) {
        timing.reason += '. Strong moon increases activity!';
    }

    return timing;
}

/**
 * Get rigging advice appropriate for the bait type (used by map popup narrative)
 */
function getRiggingAdvice(baitName, techniqueDetails) {
    const baitLower = baitName.toLowerCase();

    // Cut bait rigging (check BEFORE live bait to catch "cut shrimp", "cut mullet", etc.)
    if (baitLower.includes('cut')) {
        return 'chunk it on a Carolina rig with a 2-3oz weight, let it soak on the bottom';
    }
    if (baitLower.includes('crab')) {
        return 'quarter it and fish it on a bottom rig near structure';
    }

    // Live bait rigging
    if (baitLower.includes('shrimp') || baitLower.includes('live shrimp')) {
        return 'hook it through the horn under a popping cork, 2-3 feet down';
    }
    if (baitLower.includes('croaker') || baitLower.includes('piggy perch') || baitLower.includes('pinfish')) {
        return 'free-line it with a circle hook through the back, let it swim natural';
    }
    if (baitLower.includes('mullet') && baitLower.includes('live')) {
        return 'lip-hook it and free-line near structure';
    }
    // Mullet without "live" = assume cut
    if (baitLower.includes('mullet')) {
        return 'chunk it on a Carolina rig with a 2-3oz weight, let it soak on the bottom';
    }

    // Artificial lures
    if (baitLower.includes('plastic') || baitLower.includes('paddle') || baitLower.includes('gulp')) {
        return techniqueDetails ? techniqueDetails.toLowerCase() : 'on a 1/4oz jig head, work it slow along the bottom';
    }
    if (baitLower.includes('topwater') || baitLower.includes('plug')) {
        return 'walk it with a steady cadence, pause to let them catch up';
    }
    if (baitLower.includes('spoon')) {
        return 'cast it out and let it flutter down, then rip and drop';
    }

    // Default - don't add rigging if we're not sure
    return null;
}

/**
 * Get rig recommendation based on conditions decision tree (used by map popup narrative)
 */
function getRigRecommendation(location, conditions, weather) {
    // Extract water temp from factors
    const waterTempFactor = conditions?.factors?.find(f => f.name === 'Water Temp');
    const waterTemp = waterTempFactor?.value !== 'N/A' ? parseFloat(waterTempFactor?.value) : null;

    // Condition flags
    const isLowLight = conditions?.isLowLight ||
        (conditions?.minutesFromSunrise != null && conditions.minutesFromSunrise <= 45) ||
        (conditions?.minutesFromSunset != null && conditions.minutesFromSunset <= 45);
    const isWindy = weather?.wind?.speed >= 10;
    const isCloudy = conditions?.cloudCover?.percent >= 50;
    const isColdWater = waterTemp != null && waterTemp < 62;
    const tideMovement = conditions?.tideMovement?.movement || 'unknown';
    const tideStage = conditions?.tideMovement?.stage || 'unknown';
    const isMovingTide = tideMovement === 'strong' || tideMovement === 'moderate';
    const isSlackTide = tideMovement === 'slack';
    const isNearPilings = location?.types?.some(t => ['pier', 'jetty'].includes(t));
    const isOpenWater = location?.types?.some(t => ['shore', 'surf', 'bay', 'channel', 'wading'].includes(t));
    const isWarmingTrend = conditions?.pressureTrend?.trend === 'rising';

    // Decision tree

    // Slack tide -> Hi-Low Rig (fish stack up, cover multiple depths)
    if (isSlackTide) {
        return {
            rig: 'Hi-Low Rig',
            setup: 'double dropper loop with pyramid sinker',
            bait: 'cut shrimp or squid on each hook',
            why: 'fish stack up during slack water — cover multiple depths at once'
        };
    }

    // Moving tide + pilings + low light or cold -> Free Line
    if (isMovingTide && isNearPilings && (isLowLight || isColdWater)) {
        return {
            rig: 'Free Line',
            setup: 'no weight, circle hook through the back',
            bait: 'live shrimp or croaker',
            why: 'vertical along pilings lets the current carry your bait into the strike zone'
        };
    }

    // Moving tide + open water + cold or windy -> Carolina Rig
    if (isMovingTide && isOpenWater && (isColdWater || isWindy)) {
        const coldNote = isColdWater ? 'cold-water ' : '';
        return {
            rig: 'Carolina Rig',
            setup: '2-3oz egg sinker, 18" fluorocarbon leader, circle hook',
            bait: 'cut mullet or blue crab',
            why: `slow bottom presentation for ${coldNote}fish holding tight to structure`
        };
    }

    // Low light/cloudy/windy + warming trend -> Popping Cork
    if ((isLowLight || isCloudy || isWindy) && isWarmingTrend) {
        return {
            rig: 'Popping Cork',
            setup: '2-3 feet below cork, 1/4oz jig head or kahle hook',
            bait: 'live shrimp or paddle tail swim bait',
            why: 'rising temps and low visibility trigger aggressive shallow strikes'
        };
    }

    // Outgoing tide + open water -> Carolina Rig (bait funneling through cuts)
    if (tideStage === 'outgoing' && isOpenWater) {
        return {
            rig: 'Carolina Rig',
            setup: '2-3oz egg sinker, 18" leader, circle hook',
            bait: 'cut mullet',
            why: 'outgoing tide funnels bait through cuts — bottom rig intercepts them'
        };
    }

    // Default based on water temp
    if (isColdWater) {
        return {
            rig: 'Carolina Rig',
            setup: '2-3oz egg sinker, 18" leader, 4/0 circle hook',
            bait: 'cut mullet, let it soak',
            why: 'cold water means sluggish fish — slow bottom presentation is your best bet'
        };
    }

    // Default warm/prime conditions -> Popping Cork
    return {
        rig: 'Popping Cork',
        setup: '2-3 feet below cork, 1/4oz jig head',
        bait: 'live shrimp or paddle tail',
        why: 'versatile setup for actively feeding fish in prime conditions'
    };
}

/**
 * Build condition phrase from factor narrative + value
 * Narrative comes from factor modules (single source of truth),
 * enriched with measurement values for completeness
 */
function buildConditionPhrase(f) {
    if (!f.narrative) return null;
    const narrative = f.narrative.toLowerCase();
    const name = f.name.toLowerCase();

    if (name === 'water temp') return `${narrative} at <strong>${f.value}</strong>`;
    if (name === 'air temp') return `${narrative} at <strong>${f.value}</strong>`;
    if (name === 'wind') return `<strong>${f.value}</strong> — ${narrative}`;
    if (name === 'pressure') return narrative;
    if (name === 'tide') return narrative;
    if (name === 'cloud cover') return narrative;
    if (name === 'weather') return narrative;
    if (name === 'moon phase') return `<strong>${f.value}</strong> — ${narrative}`;
    if (name === 'time of day') return narrative;

    return narrative;
}

/**
 * Categorize factor as favorable/unfavorable
 * Uses category directly from factor modules (single source of truth)
 */
function categorizeFactorStatus(factor) {
    return factor.category || 'neutral';
}

/**
 * Build consolidated fishing report - conditions + tactics in one flowing narrative
 */
function buildStreamlinedRecommendations(loc, analysis, score, cond, weather) {
    const speciesText = analysis.topSpecies.join(' & ');
    const primaryBait = analysis.baitPriority[0]?.label || 'Live Shrimp';
    const backupBait = analysis.baitPriority[1]?.label || 'Soft Plastics';
    const spots = analysis.whereToFish;
    const slot1 = analysis.whenToFish.slot1;
    const slot2 = analysis.whenToFish.slot2;
    const primaryRigging = getRiggingAdvice(primaryBait, null);

    // Get condition-aware rig recommendation
    const rigRec = getRigRecommendation(loc, cond, weather);

    // Categorize all factors
    const favorable = [];
    const unfavorable = [];
    if (cond?.factors) {
        cond.factors.forEach(factor => {
            const category = categorizeFactorStatus(factor);
            if (category === 'favorable') favorable.push(factor);
            else if (category === 'unfavorable') unfavorable.push(factor);
        });
    }

    // Build condition phrases
    const favorablePhrases = favorable.map(buildConditionPhrase).filter(p => p);
    const unfavorablePhrases = unfavorable.map(buildConditionPhrase).filter(p => p);

    // Opening assessment based on score
    let opening = '';
    if (score >= 75) {
        opening = `<strong>This is the day you've been waiting for.</strong>`;
    } else if (score >= 60) {
        opening = `<strong>Solid conditions today.</strong>`;
    } else if (score >= 45) {
        opening = `<strong>It's fishable, but you'll need to work for it.</strong>`;
    } else if (score >= 30) {
        opening = `<strong>Gonna be a grind today, I won't lie.</strong>`;
    } else {
        opening = `<strong>Tough day to be on the water.</strong>`;
    }

    // Build conditions sentence (favorable "but" unfavorable)
    let conditionsSentence = '';
    if (favorablePhrases.length > 0 && unfavorablePhrases.length > 0) {
        const favText = favorablePhrases.join(', and ');
        const unfavText = unfavorablePhrases.join(', and ');
        conditionsSentence = `${favText.charAt(0).toUpperCase() + favText.slice(1)} but ${unfavText}.`;
    } else if (favorablePhrases.length > 0) {
        const favText = favorablePhrases.join(', and ');
        conditionsSentence = `${favText.charAt(0).toUpperCase() + favText.slice(1)}.`;
    } else if (unfavorablePhrases.length > 0) {
        const unfavText = unfavorablePhrases.join(', and ');
        conditionsSentence = `${unfavText.charAt(0).toUpperCase() + unfavText.slice(1)}.`;
    }

    // Tactical advice intro based on score
    let tacticalIntro = '';
    if (score >= 75) {
        tacticalIntro = `Get out there and make it count.`;
    } else if (score >= 60) {
        tacticalIntro = `Stay patient, work your spots methodically.`;
    } else if (score >= 45) {
        tacticalIntro = `Put in your time and stay adaptable.`;
    } else if (score >= 30) {
        tacticalIntro = `If you're patient, you might still hook into some <strong>${speciesText}</strong>.`;
    } else {
        tacticalIntro = `Consider waiting for better conditions, or stick to protected areas if you go.`;
    }

    // Build condition-aware backup bait phrase
    let backupPhrase = '';
    const backupLower = backupBait.toLowerCase();
    if (score < 45) {
        // Tough conditions - fish are sluggish, need slow/subtle presentation
        if (backupLower.includes('live')) {
            backupPhrase = `If that's not working, try <strong>${backupBait}</strong> — the natural movement might coax a bite.`;
        } else if (backupLower.includes('plastic') || backupLower.includes('gulp')) {
            backupPhrase = `If that's not working, try <strong>${backupBait}</strong> worked slow along the bottom.`;
        } else {
            backupPhrase = `If that's not working, go subtle with <strong>${backupBait}</strong> and a slow, patient retrieve.`;
        }
    } else if (score < 60) {
        // Fair conditions
        backupPhrase = `If they're not biting, try <strong>${backupBait}</strong> — sometimes a change is all they need.`;
    } else if (score < 75) {
        // Good conditions
        backupPhrase = `If that's not producing, switch to <strong>${backupBait}</strong>.`;
    } else {
        // Prime conditions - fish are active
        backupPhrase = `If they're not hitting, switch to <strong>${backupBait}</strong> and see if that wakes 'em up.`;
    }

    // Build tactics narrative with rig recommendation
    let tacticsNarrative = '';
    if (spots.length > 0) {
        tacticsNarrative = `Start by working <strong>${spots[0].toLowerCase()}</strong>`;
        if (spots.length > 1) {
            tacticsNarrative += `, and if that's dead, move over to <strong>${spots[1].toLowerCase()}</strong>`;
        }
        tacticsNarrative += `.`;
    }

    // Add rig recommendation
    if (rigRec) {
        tacticsNarrative += ` Set up a <strong>${rigRec.rig}</strong> (${rigRec.setup}) with <strong>${rigRec.bait}</strong> — ${rigRec.why}.`;
    } else {
        // Fallback to old bait-based rigging
        tacticsNarrative += ` Your go-to today is <strong>${primaryBait}</strong>`;
        if (primaryRigging) {
            tacticsNarrative += ` — ${primaryRigging}`;
        }
        tacticsNarrative += `.`;
    }
    tacticsNarrative += ` ${backupPhrase}`;

    // Timing narrative
    let timingNarrative = `Be on the water by <strong>${slot1.time}</strong> — that's today's prime window`;
    if (slot2?.time && !slot2.time.includes('looks better')) {
        timingNarrative += `. If that doesn't work, try <strong>${slot2.time}</strong>`;
    }
    timingNarrative += '.';

    return `
        <div class="dynamic-recommendations">
            <h3 style="color: #0C2B4E; margin: 0 0 14px 0; font-size: 15px; font-weight: 600;">
                Current Fishing Report
            </h3>

            <div style="background: #fff; padding: 16px 18px; border-radius: 8px; border: 1px solid #e0e6ed;">
                <p style="color: #2c3e50; font-size: 13px; margin: 0 0 12px 0; line-height: 1.75;">
                    ${opening}
                </p>
                <p style="color: #555; font-size: 12.5px; margin: 0 0 12px 0; line-height: 1.75;">
                    ${conditionsSentence}
                </p>
                <p style="color: #555; font-size: 12.5px; margin: 0 0 12px 0; line-height: 1.75;">
                    ${tacticalIntro} ${tacticsNarrative}
                </p>
                <p style="color: #555; font-size: 12.5px; margin: 0; line-height: 1.75;">
                    ${timingNarrative}
                </p>
            </div>
        </div>
    `;
}

;
/* === js/chart.js === */
/**
 * Tide Chart Drawing Utility
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/chart.js">
 * USED BY: ui-builders.js → buildForecastHTML() renders <canvas>,
 *          main-server.js calls drawForecastTideChart() after forecast table DOM insertion
 * EXPORTS: drawForecastTideChart, drawSidebarMiniChart as global functions
 */

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Set up a canvas for DPR-aware drawing.
 * @param {HTMLCanvasElement} canvas
 * @returns {{ ctx: CanvasRenderingContext2D, width: number, height: number }}
 */
function setupCanvas(canvas) {
    const dpr = window.devicePixelRatio || 1;
    const container = canvas.parentElement;
    let rect = container ? container.getBoundingClientRect() : {};
    let width = rect.width || canvas.offsetWidth || 400;
    let height = rect.height || canvas.offsetHeight || 100;
    if (width === 0) width = 400;
    if (height === 0) height = 100;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    return { ctx, width, height };
}

/**
 * Generate Catmull-Rom spline points for a data array.
 * @param {Array<{height: number}>} data
 * @param {function(number): number} scaleX - maps array index → canvas x
 * @param {function(number): number} scaleY - maps height value → canvas y
 * @param {number} tension
 * @param {number} step - interpolation step (0.1 = 10 pts/segment)
 * @returns {Array<{x: number, y: number}>}
 */
function getSplinePoints(data, scaleX, scaleY, tension = 0.4, step = 0.1) {
    const points = data.map((d, i) => ({ x: scaleX(i), y: scaleY(d.height) }));
    if (points.length < 2) return points;
    const out = [];
    for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i === 0 ? i : i - 1];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[i + 2 >= points.length ? i + 1 : i + 2];
        for (let t = 0; t <= 1; t += step) {
            const t2 = t * t, t3 = t2 * t;
            out.push({
                x: 0.5 * ((2*p1.x) + (-p0.x+p2.x)*t + (2*p0.x-5*p1.x+4*p2.x-p3.x)*t2 + (-p0.x+3*p1.x-3*p2.x+p3.x)*t3),
                y: 0.5 * ((2*p1.y) + (-p0.y+p2.y)*t + (2*p0.y-5*p1.y+4*p2.y-p3.y)*t2 + (-p0.y+3*p1.y-3*p2.y+p3.y)*t3),
            });
        }
    }
    out.push({ x: scaleX(data.length - 1), y: scaleY(data[data.length - 1].height) });
    return out;
}

/**
 * Find local high and low tide extrema in a tide data array.
 * Plateau-safe: a point that is >= one neighbor and > the other qualifies.
 * @param {Array<{time: string, height: number}>} tideData
 * @returns {Array<{index: number, type: 'H'|'L', height: number}>}
 */
function findTideExtrema(tideData) {
    if (!tideData || tideData.length < 3) return [];
    const out = [];
    for (let i = 1; i < tideData.length - 1; i++) {
        const prev = tideData[i - 1].height;
        const curr = tideData[i].height;
        const next = tideData[i + 1].height;
        if (curr >= prev && curr > next) {
            out.push({ index: i, type: 'H', height: curr });
        } else if (curr <= prev && curr < next) {
            out.push({ index: i, type: 'L', height: curr });
        }
    }
    return out;
}

// ---------------------------------------------------------------------------
// drawForecastTideChart — 48-hour tide chart for the forecast table
// ---------------------------------------------------------------------------

/**
 * Draw 48-hour tide forecast visualization for the forecast table
 * @param {HTMLCanvasElement} canvas
 * @param {Array} tideData - Array of {time, height} objects
 * @param {Array} allSlots - Array of slot objects with timestamp for alignment
 */
function drawForecastTideChart(canvas, tideData, allSlots) {
    if (!tideData || tideData.length === 0) {
        console.warn('[ForecastChart] No tide data available');
        return;
    }

    const container = canvas.parentElement;
    const dpr = window.devicePixelRatio || 1;
    let rect = container ? container.getBoundingClientRect() : {};
    let width = rect.width || canvas.offsetWidth || 800;
    let height = rect.height || canvas.offsetHeight || 280;
    if (width === 0) width = 800;
    if (height === 0) height = 280;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const padding = { top: 25, right: 10, bottom: 20, left: 10 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    ctx.clearRect(0, 0, width, height);

    // Dynamic Y-axis with 0.5ft padding, minimum 3ft range
    var heights = tideData.map(function(d) { return d.height; });
    var dataMin = Math.min.apply(null, heights);
    var dataMax = Math.max.apply(null, heights);
    var MIN_RANGE = 3;
    var PAD = 0.5;
    var minHeight, maxHeight;
    if ((dataMax - dataMin) + 2 * PAD >= MIN_RANGE) {
        minHeight = dataMin - PAD;
        maxHeight = dataMax + PAD;
    } else {
        var center = (dataMin + dataMax) / 2;
        minHeight = center - MIN_RANGE / 2;
        maxHeight = center + MIN_RANGE / 2;
    }
    const heightRange = maxHeight - minHeight;

    const scaleX = (index) => padding.left + (index / (tideData.length - 1)) * chartWidth;
    const scaleY = (h) => padding.top + chartHeight - ((h - minHeight) / heightRange) * chartHeight;

    // Day boundary vertical lines
    const days = {};
    allSlots.forEach((slot, idx) => {
        const dateKey = slot.timestamp.toDateString();
        if (!days[dateKey]) {
            days[dateKey] = idx;
        }
    });

    ctx.strokeStyle = '#ddd';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    Object.values(days).forEach((startIdx, i) => {
        if (i > 0) {
            const x = scaleX(startIdx);
            ctx.beginPath();
            ctx.moveTo(x, padding.top);
            ctx.lineTo(x, height - padding.bottom);
            ctx.stroke();
        }
    });
    ctx.setLineDash([]);

    const smoothPoints = getSplinePoints(tideData, scaleX, scaleY);

    // Gradient fill under the curve
    const gradient = ctx.createLinearGradient(0, padding.top, 0, height - padding.bottom);
    gradient.addColorStop(0, 'rgba(29, 84, 108, 0.35)');
    gradient.addColorStop(0.5, 'rgba(29, 84, 108, 0.15)');
    gradient.addColorStop(1, 'rgba(29, 84, 108, 0.02)');

    ctx.beginPath();
    ctx.moveTo(padding.left, height - padding.bottom);
    smoothPoints.forEach((p, i) => {
        if (i === 0) ctx.lineTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
    });
    ctx.lineTo(width - padding.right, height - padding.bottom);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    // Tide curve
    ctx.strokeStyle = '#1D546C';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    smoothPoints.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
    });
    ctx.stroke();

    // H/L markers (using shared findTideExtrema)
    const extrema = findTideExtrema(tideData);
    const markedPoints = extrema.map(e => ({
        ...e,
        color: e.type === 'H' ? '#3498db' : '#f39c12'
    }));

    // Also mark "good tide" points (1.5-2.0 ft range) that aren't already marked
    tideData.forEach((d, i) => {
        if (d.height >= 1.5 && d.height <= 2.0) {
            const alreadyMarked = markedPoints.some(p => Math.abs(p.index - i) <= 1);
            if (!alreadyMarked) {
                const nearbyGood = markedPoints.filter(p => p.type === 'G' && Math.abs(p.index - i) < 3);
                if (nearbyGood.length === 0) {
                    markedPoints.push({ index: i, type: 'G', height: d.height, color: '#2ecc71' });
                }
            }
        }
    });

    markedPoints.forEach(point => {
        const x = scaleX(point.index);
        const y = scaleY(point.height);

        ctx.fillStyle = point.color;
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = 'white';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.fillStyle = '#2c3e50';
        ctx.font = 'bold 9px Arial';
        ctx.textAlign = 'center';
        const labelY = point.type === 'L' ? y + 15 : y - 10;
        ctx.fillText(`${point.height.toFixed(1)}ft`, x, labelY);
    });
}

// ---------------------------------------------------------------------------
// drawTideChart — legacy standalone tide chart (used elsewhere)
// ---------------------------------------------------------------------------

function drawTideChart(canvas, tideData) {
    if (!tideData || tideData.length === 0) {
        console.warn('[Chart] No tide data available for chart');
        return;
    }

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const padding = { top: 30, right: 40, bottom: 40, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    ctx.clearRect(0, 0, width, height);

    const heights = tideData.map(d => d.height);
    const minHeight = Math.min(...heights);
    const maxHeight = Math.max(...heights);
    const heightRange = maxHeight - minHeight;

    const timestamps = tideData.map(d => new Date(d.time).getTime());
    const minTime = timestamps[0];
    const maxTime = timestamps[timestamps.length - 1];
    const timeRange = maxTime - minTime;

    const scaleX = (timeMs) => padding.left + ((timeMs - minTime) / timeRange) * chartWidth;
    const scaleY = (h) => padding.top + chartHeight - ((h - minHeight) / heightRange) * chartHeight;

    // Grid
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
        const y = padding.top + (chartHeight / 4) * i;
        ctx.beginPath();
        ctx.moveTo(padding.left, y);
        ctx.lineTo(width - padding.right, y);
        ctx.stroke();
    }

    // Gradient fill
    const gradient = ctx.createLinearGradient(0, padding.top, 0, height - padding.bottom);
    gradient.addColorStop(0, 'rgba(29, 84, 108, 0.3)');
    gradient.addColorStop(1, 'rgba(29, 84, 108, 0.05)');

    ctx.beginPath();
    ctx.moveTo(scaleX(timestamps[0]), height - padding.bottom);
    tideData.forEach((d, i) => ctx.lineTo(scaleX(timestamps[i]), scaleY(d.height)));
    ctx.lineTo(scaleX(timestamps[timestamps.length - 1]), height - padding.bottom);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    // Tide curve
    ctx.strokeStyle = '#1D546C';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    tideData.forEach((d, i) => {
        if (i === 0) ctx.moveTo(scaleX(timestamps[i]), scaleY(d.height));
        else ctx.lineTo(scaleX(timestamps[i]), scaleY(d.height));
    });
    ctx.stroke();

    // Current time indicator
    var nowMs = Date.now();
    if (nowMs >= minTime && nowMs <= maxTime) {
        var nowX = scaleX(nowMs);
        ctx.strokeStyle = '#e74c3c';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(nowX, padding.top);
        ctx.lineTo(nowX, height - padding.bottom);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = '#e74c3c';
        ctx.font = 'bold 11px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('NOW', nowX, padding.top - 10);
    }

    // High/low points
    tideData.forEach((d, i) => {
        if (d.isHighLow) {
            var x = scaleX(timestamps[i]);
            var y = scaleY(d.height);

            ctx.fillStyle = d.type === 'H' ? '#2ecc71' : '#3498db';
            ctx.beginPath();
            ctx.arc(x, y, 5, 0, Math.PI * 2);
            ctx.fill();

            ctx.fillStyle = '#2c3e50';
            ctx.font = 'bold 10px Arial';
            ctx.textAlign = 'center';
            var label = (d.type === 'H' ? 'High' : 'Low') + ' ' + d.height.toFixed(1) + 'ft';
            ctx.fillText(label, x, y - 12);
        }
    });

    // X-axis labels
    ctx.fillStyle = '#7f8c8d';
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    var labelCount = 6;
    var timeStep = timeRange / labelCount;
    for (var li = 0; li <= labelCount; li++) {
        var labelTimeMs = minTime + timeStep * li;
        var timeStr = formatDate(labelTimeMs, { hour: 'numeric', hour12: true });
        ctx.fillText(timeStr, scaleX(labelTimeMs), height - padding.bottom + 20);
    }

    // Y-axis labels
    ctx.textAlign = 'right';
    for (var i = 0; i <= 4; i++) {
        var heightValue = minHeight + (heightRange / 4) * (4 - i);
        var y = padding.top + (chartHeight / 4) * i;
        ctx.fillText(heightValue.toFixed(1) + ' ft', padding.left - 10, y + 4);
    }

    // Axes
    ctx.strokeStyle = '#2c3e50';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding.left, padding.top);
    ctx.lineTo(padding.left, height - padding.bottom);
    ctx.lineTo(width - padding.right, height - padding.bottom);
    ctx.stroke();
}

// ---------------------------------------------------------------------------
// Weather icon cache — pre-warmed at module load so icons are ready by the
// time a user opens the sidebar. _iconCache[file] is null (loading), false
// (failed), or an HTMLImageElement (ready).
// ---------------------------------------------------------------------------

const _iconCache = {};
function _preloadIcon(file) {
    if (file in _iconCache) return;
    _iconCache[file] = null;
    const img = new Image();
    img.onload  = () => { _iconCache[file] = img; };
    img.onerror = () => { _iconCache[file] = false; };
    img.src = 'img/' + file;
}
['clear-day.png', 'clear-night.png', 'partly-cloudy-day.png', 'partly-cloudy-night.png',
 'mostly-cloudy-day.png', 'cloudy.png', 'rain.png', 'light-rain.png',
 'fog.png', 'thunderstorm.png', 'snow.png', 'windy.png'
].forEach(_preloadIcon);

function _getWeatherIconFile(cloudCover, windSpeed, weatherText, hourOfDay) {
    const isNight = hourOfDay < 6 || hourOfDay >= 20;
    const w = (weatherText || '').toLowerCase();
    if (w.includes('thunder') || w.includes('storm')) return 'thunderstorm.png';
    if (w.includes('snow')    || w.includes('sleet')) return 'snow.png';
    if (w.includes('rain')    || w.includes('shower')) return 'rain.png';
    if (w.includes('drizzle'))                         return 'light-rain.png';
    if (w.includes('fog')     || w.includes('mist'))   return 'fog.png';
    if (windSpeed > 20)    return 'windy.png';
    if (cloudCover >= 80)  return 'cloudy.png';
    if (cloudCover >= 50)  return isNight ? 'partly-cloudy-night.png' : 'mostly-cloudy-day.png';
    if (cloudCover >= 20)  return isNight ? 'partly-cloudy-night.png' : 'partly-cloudy-day.png';
    return isNight ? 'clear-night.png' : 'clear-day.png';
}

// ---------------------------------------------------------------------------
// drawSidebarMiniChart — compact 24h tide + FBI chart for the map sidebar
// ---------------------------------------------------------------------------

/**
 * Draw the compact 24-hour tide + FBI overlay chart in the map sidebar hero card.
 * @param {HTMLCanvasElement} canvas
 * @param {Array<{time:string, height:number}>|null} tideData24h
 * @param {Array} localForecast - hourly FBI forecast slots with .timestamp (Date) and .fbiScore
 * @param {number|null} currentFBIScore - current real-time FBI 0–100, or null
 */
function drawSidebarMiniChart(canvas, tideData24h, localForecast, currentFBIScore) {
    // Use offsetWidth/offsetHeight: layout-stable dimensions unaffected by CSS transforms.
    // getBoundingClientRect() can return mid-animation values during the sidebar slide-in.
    const dpr = window.devicePixelRatio || 1;
    let width  = canvas.offsetWidth  || 400;
    let height = canvas.offsetHeight || 100;
    if (width  === 0) width  = 400;
    if (height === 0) height = 100;
    canvas.width  = width  * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    const pad = { top: 10, right: 4, bottom: 24, left: 22 };
    const cw = width - pad.left - pad.right;
    const ch = height - pad.top - pad.bottom;
    ctx.clearRect(0, 0, width, height);

    // Navy gradient background
    const bg = ctx.createLinearGradient(0, height * 0.15, 0, height);
    bg.addColorStop(0, 'rgba(10,23,45,0)');
    bg.addColorStop(1, 'rgba(10,23,45,1)');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, width, height);

    // Time axis + scale helpers (hoisted so all layers share them)
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const todayEnd   = new Date(); todayEnd.setHours(24, 0, 0, 0);
    const timeRange  = todayEnd - todayStart;
    const scaleTime  = (ms) => pad.left + ((ms - todayStart) / timeRange) * cw;
    const scaleFBI   = (score) => pad.top + ch - (score / 100) * ch;
    const tsOf       = (slot) => slot.timestamp instanceof Date ? slot.timestamp.getTime() : new Date(slot.timestamp).getTime();
    const nowX       = scaleTime(Date.now());

    // Pre-compute FBI dot/label position so tide labels can avoid it
    const todayForecast = (localForecast || []).filter(slot => {
        const t = tsOf(slot);
        return t >= todayStart && t < todayEnd && slot.fbiScore != null;
    });
    // Extend line to midnight: NWS last period starts at 11 PM, leaving a gap to the right edge.
    // Append a synthetic midnight point with the last known score (flat extrapolation).
    if (todayForecast.length > 0) {
        const lastTs = tsOf(todayForecast[todayForecast.length - 1]);
        if (todayEnd.getTime() - lastTs > 30 * 60000) {
            todayForecast.push({ timestamp: todayEnd, fbiScore: todayForecast[todayForecast.length - 1].fbiScore });
        }
    }
    const showFBIDot = todayForecast.length >= 2 && Number.isFinite(currentFBIScore);
    const fbiDotY    = showFBIDot ? scaleFBI(currentFBIScore) : null;
    const fbiLabelY  = showFBIDot ? fbiDotY - 8 : null; // label sits 8px above the dot

    // Vertical grid lines + time labels
    const gridHours = [6, 12, 18];
    const gridLabels = { 6: '6A', 12: '12P', 18: '6P' };
    ctx.strokeStyle = 'rgba(255,255,255,0.10)';
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    gridHours.forEach(h => {
        const x = scaleTime(todayStart.getTime() + h * 3600000);
        ctx.beginPath(); ctx.moveTo(x, pad.top); ctx.lineTo(x, height - pad.bottom); ctx.stroke();
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.font = '7px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(gridLabels[h], x, height - 2);
    });

    // Tide line + H/L markers
    const todayTide = (tideData24h || []).filter(d => {
        const t = new Date(d.time).getTime();
        return t >= todayStart && t < todayEnd;
    });

    if (todayTide.length >= 2) {
        const TPAD = 0.5, MIN_RANGE = 3.0;
        const tideHeights = todayTide.map(d => d.height);
        const dataMin = Math.min(...tideHeights), dataMax = Math.max(...tideHeights);
        let minH, maxH;
        if ((dataMax - dataMin) + 2 * TPAD >= MIN_RANGE) {
            minH = dataMin - TPAD; maxH = dataMax + TPAD;
        } else {
            const center = (dataMin + dataMax) / 2;
            minH = center - MIN_RANGE / 2; maxH = center + MIN_RANGE / 2;
        }
        const scaleH    = (h) => pad.top + ch - ((h - minH) / (maxH - minH)) * ch;
        const scaleTideX = (i) => scaleTime(new Date(todayTide[i].time).getTime());
        const spline    = getSplinePoints(todayTide, scaleTideX, scaleH, 0.4, 0.2);

        // Fill under tide curve
        const fillGrad = ctx.createLinearGradient(0, pad.top, 0, height - pad.bottom);
        fillGrad.addColorStop(0, 'rgba(255,255,255,0.12)');
        fillGrad.addColorStop(1, 'rgba(255,255,255,0.02)');
        ctx.beginPath();
        ctx.moveTo(pad.left, height - pad.bottom);
        spline.forEach(p => ctx.lineTo(p.x, p.y));
        ctx.lineTo(width - pad.right, height - pad.bottom);
        ctx.closePath();
        ctx.fillStyle = fillGrad; ctx.fill();

        // Tide curve
        ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 1.5; ctx.setLineDash([]);
        ctx.beginPath();
        spline.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
        ctx.stroke();

        // TIDE label at left edge, vertically aligned with the first tide point
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 7px Arial';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText('TIDE', pad.left - 3, scaleH(todayTide[0].height));
        ctx.textBaseline = 'alphabetic';

        // H/L markers — dots always draw; labels skip if they'd overlap the FBI score label
        const extrema = findTideExtrema(todayTide);
        const renderedXPositions = [];
        extrema.forEach(({ index, type, height: h }) => {
            const x = scaleTideX(index), y = scaleH(h);
            if (renderedXPositions.some(px => Math.abs(px - x) < 12)) return;
            renderedXPositions.push(x);
            ctx.fillStyle = '#FFFFFF';
            ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.fill();
            const labelY = type === 'H' ? y - 6 : y + 12;
            const collidesWithFBI  = fbiLabelY !== null
                && Math.abs(x - nowX) < 20
                && Math.abs(labelY - fbiLabelY) < 10;
            const collidesWithIcons = labelY >= (height - pad.bottom + 2);
            if (!collidesWithFBI && !collidesWithIcons) {
                ctx.fillStyle = '#FFFFFF';
                ctx.font = 'bold 7px Arial';
                ctx.textAlign = 'center';
                ctx.fillText(h.toFixed(1), x, labelY);
            }
        });
    }

    // NOW marker (vertical dashed line)
    if (nowX >= pad.left && nowX <= width - pad.right) {
        ctx.strokeStyle = 'rgba(255,80,80,0.9)';
        ctx.lineWidth = 0.8;
        ctx.setLineDash([3, 3]);
        ctx.beginPath(); ctx.moveTo(nowX, pad.top); ctx.lineTo(nowX, height - pad.bottom); ctx.stroke();
        ctx.setLineDash([]);
    }

    // FBI line
    if (todayForecast.length >= 2) {
        const scaleFBIX = (i) => scaleTime(tsOf(todayForecast[i]));
        const fbiSpline = getSplinePoints(
            todayForecast.map(s => ({ height: s.fbiScore })),
            scaleFBIX, scaleFBI, 0.4, 0.2
        );
        ctx.strokeStyle = '#FF7500'; ctx.lineWidth = 2; ctx.setLineDash([]);
        ctx.beginPath();
        fbiSpline.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
        ctx.stroke();

        // FBI label at left edge, vertically aligned with the first FBI point
        ctx.fillStyle = '#FF7500';
        ctx.font = 'bold 7px Arial';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText('FBI', pad.left - 3, scaleFBI(todayForecast[0].fbiScore));
        ctx.textBaseline = 'alphabetic';
    } else if (Number.isFinite(currentFBIScore)) {
        const y = scaleFBI(currentFBIScore);
        ctx.strokeStyle = '#FF7500'; ctx.lineWidth = 2; ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(width - pad.right, y); ctx.stroke();
        ctx.setLineDash([]);
    }

    // Bridge dot (color-coded) + score label above it
    if (showFBIDot) {
        // Interpolate forecast FBI at current moment for dot color
        const nowMs = Date.now();
        let forecastAtNow = todayForecast[0].fbiScore;
        for (let i = 0; i < todayForecast.length - 1; i++) {
            const t1 = tsOf(todayForecast[i]), t2 = tsOf(todayForecast[i + 1]);
            if (nowMs >= t1 && nowMs <= t2) {
                forecastAtNow = todayForecast[i].fbiScore + ((nowMs - t1) / (t2 - t1)) * (todayForecast[i + 1].fbiScore - todayForecast[i].fbiScore);
                break;
            }
            if (i === todayForecast.length - 2) forecastAtNow = todayForecast[todayForecast.length - 1].fbiScore;
        }

        // Dot: golden = beating forecast, red = below
        const dotColor = currentFBIScore >= forecastAtNow ? '#FFB549' : '#FF5050';
        ctx.fillStyle = dotColor;
        ctx.beginPath(); ctx.arc(nowX, fbiDotY, 3, 0, Math.PI * 2); ctx.fill();

        // Score label centered above the dot — same color as dot
        ctx.fillStyle = dotColor;
        ctx.font = '7px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(Math.round(currentFBIScore), nowX, fbiLabelY);
    }

    // Weather icon strip — between chart bottom and time labels
    // Icons: 12A · 3A · 6A · 9A · 12P · 3P · 6P · 9P · 12A(right)
    const ICON_SIZE  = 11;
    const iconY      = height - pad.bottom + 2;
    const iconHours  = [0, 3, 6, 9, 12, 15, 18, 21]; // right 12A added separately

    const closestSlot = (targetTs) => {
        let best = null, bestDiff = Infinity;
        (localForecast || []).forEach(slot => {
            const diff = Math.abs(tsOf(slot) - targetTs);
            if (diff < bestDiff) { bestDiff = diff; best = slot; }
        });
        return best;
    };

    ctx.filter = 'invert(1)';
    let midnightIconFile = null;
    iconHours.forEach(h => {
        const slot = closestSlot(todayStart.getTime() + h * 3600000);
        if (!slot) return;
        const file = _getWeatherIconFile(slot.cloudCover || 0, slot.wind?.speed || 0, slot.weather || '', h);
        if (h === 0) midnightIconFile = file;
        const img = _iconCache[file];
        if (!img) return;
        const x  = scaleTime(todayStart.getTime() + h * 3600000);
        const ix = Math.max(0, Math.min(width - ICON_SIZE, x - ICON_SIZE / 2));
        ctx.drawImage(img, ix, iconY, ICON_SIZE, ICON_SIZE);
    });
    // Right midnight — same icon as left midnight
    if (midnightIconFile && _iconCache[midnightIconFile]) {
        const x  = scaleTime(todayEnd.getTime());
        const ix = Math.max(0, Math.min(width - ICON_SIZE, x - ICON_SIZE / 2));
        ctx.drawImage(_iconCache[midnightIconFile], ix, iconY, ICON_SIZE, ICON_SIZE);
    }
    ctx.filter = 'none';
}

;
/* === js/ui-builders.js === */
/**
 * HTML Builder Functions (detailed views and popups)
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/ui-builders.js">
 * USED BY: main-server.js → expanded row click handler calls buildDetailHTML(), buildForecastHTML()
 *          main-server.js → map popup calls buildNarrativePrediction()
 *          gear.js → getGearRecommendationsForMap() references factor values from cond.factors
 * EXPORTS: buildDetailHTML(), buildForecastHTML(), buildNarrativePrediction(), capitalizeWords() as globals
 */

/**
 * Capitalize each word in a string
 */
function capitalizeWords(str) {
    return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
}

/**
 * Get color for species score bar (5-color gradient)
 * Red (0-20) → Orange (21-40) → Yellow (41-60) → Green (61-80) → Blue (81-100)
 */
function getScoreBarColor(score) {
    if (score <= 20) return '#ef4444';
    if (score <= 40) return '#f97316';
    if (score <= 60) return '#eab308';
    if (score <= 80) return '#22c55e';
    return '#3b82f6';
}

/**
 * Build skeleton HTML for bait/rig cards (shown while lazy-loading)
 */
function buildRecSkeleton() {
    return `<div class="rec-skeleton">
        <div class="rec-skeleton__line"></div>
        <div class="rec-skeleton__line rec-skeleton__line--medium"></div>
        <div class="rec-skeleton__line rec-skeleton__line--short"></div>
    </div>`;
}

/**
 * Build rig diagram as pill-node visual chain (parses ` ─ ` separator)
 */
function buildRigDiagramVisual(diagramStr) {
    if (!diagramStr) return '';
    const nodes = diagramStr.split(' \u2500 ').map(s => s.trim()).filter(Boolean);
    if (nodes.length === 0) return '';

    const pills = nodes.map((node, i) => {
        const isLast = i === nodes.length - 1;
        return `<span class="rig-diagram-visual__node">${node}</span>${isLast ? '' : '<span class="rig-diagram-visual__connector">\u2500</span>'}`;
    }).join('');

    return `<div class="rig-diagram-visual">${pills}</div>`;
}

/**
 * Build rig specs display (hook, leader, sinker, mainline)
 */
function buildRigSpecs(specs) {
    if (!specs) return '';
    const entries = Object.entries(specs)
        .filter(([, v]) => v)
        .map(([k, v]) => `<span class="rig-specs__item"><strong>${k}:</strong> ${v}</span>`)
        .join('');
    return entries ? `<div class="rig-specs">${entries}</div>` : '';
}

/**
 * Build a natural narrative prediction for the location
 */
function buildNarrativePrediction(loc, cond, weather) {
    const temp = weather.main.temp;
    const windSpeed = weather.wind.speed;
    const waterTempFactor = cond.factors.find(f => f.name === 'Water Temp');
    const tideFactor = cond.factors.find(f => f.name === 'Tide');
    const pressureFactor = cond.factors.find(f => f.name === 'Pressure');
    const moonPhase = cond.moonPhase;
    const waterTemp = waterTempFactor && waterTempFactor.value !== 'N/A' ? parseFloat(waterTempFactor.value) : null;

    // Analyze conditions using the gear.js function
    const analysis = analyzeFishingConditions(loc, cond, weather, waterTemp, windSpeed, temp, moonPhase, tideFactor);

    const score = cond.score;
    const speciesText = analysis.topSpecies.join(' & ');
    const statusWord = typeof getScoreLabel === 'function' ? getScoreLabel(score).toLowerCase() : 'fair';

    // Build the narrative
    const desc = loc.description || '';
    let narrative = desc
        ? `<strong>${loc.name}</strong> is ${desc.toLowerCase().startsWith('a') || desc.toLowerCase().startsWith('an') ? '' : 'a '}${desc.toLowerCase()}. `
        : `<strong>${loc.name}</strong>. `;

    // Condition verdict
    narrative += `The conditions are <strong>${statusWord}</strong> for <strong>${speciesText}</strong>. `;

    // Why (positive or negative factors)
    if (score >= 60) {
        // Good conditions - explain why it's good
        const positives = [];
        // Water temp: 62-82°F is the feeding zone
        if (waterTemp && waterTemp >= 62 && waterTemp <= 82) positives.push(`water temperature (${Math.round(waterTemp)}°F) is in the feeding zone`);
        // Moon phase
        if (moonPhase && moonPhase.score >= 8) positives.push(`${moonPhase.name} increases fish activity`);
        // Wind
        if (windSpeed <= 10) positives.push('calm winds make for comfortable fishing');
        // Tide movement
        if (tideFactor && tideFactor.value && (tideFactor.value.includes('in') && tideFactor.value.match(/(\d+)\s*min/) && parseInt(tideFactor.value.match(/(\d+)\s*min/)[1]) <= 90)) {
            positives.push('tide movement is driving baitfish');
        }
        // Pressure trend (falling = pre-storm feeding)
        if (pressureFactor && pressureFactor.status && (pressureFactor.status.toLowerCase().includes('falling') || pressureFactor.status.toLowerCase().includes('pre-storm'))) {
            positives.push('dropping pressure has fish feeding aggressively');
        }
        // Cloud cover (overcast keeps fish active) — from weather data directly
        const clouds = weather.clouds ? weather.clouds.all : 0;
        if (clouds >= 70) {
            positives.push('cloud cover keeps fish active and roaming');
        }

        if (positives.length > 0) {
            narrative += `Fish should be active because ${positives.slice(0, 2).join(' and ')}. `;
        }
    } else {
        // Tough conditions - explain challenges but encourage
        const challenges = [];
        // Water temp outside ideal
        if (waterTemp && waterTemp < 62) challenges.push(`water temperature (${Math.round(waterTemp)}°F) has fish sluggish`);
        if (waterTemp && waterTemp > 86) challenges.push(`water temperature (${Math.round(waterTemp)}°F) is stressing fish`);
        // High wind
        if (windSpeed > 15) challenges.push(`wind (${Math.round(windSpeed)} mph) may make casting difficult`);
        // Poor moon phase
        if (moonPhase && moonPhase.score < 5) challenges.push('moon phase is not ideal');
        // Midday lull — check time directly
        const nowHour = new Date().getHours();
        if (nowHour >= 11 && nowHour <= 15) {
            challenges.push('midday sun has fish hunkered down');
        }
        // Rising pressure (post-front sluggishness)
        if (pressureFactor && pressureFactor.status && pressureFactor.status.toLowerCase().includes('rising')) {
            challenges.push('rising pressure post-front has fish sluggish');
        }
        // Bright sun pushing fish deep — from weather data directly
        const cloudsChal = weather.clouds ? weather.clouds.all : 50;
        if (cloudsChal < 30 && nowHour >= 10 && nowHour <= 15) {
            challenges.push('bright sun is pushing fish into cover');
        }
        // Extreme air temp
        if (temp < 45 || temp > 95) challenges.push(`air temperature (${Math.round(temp)}°F) is extreme`);

        if (challenges.length > 0) {
            narrative += `Fish may be less active because ${challenges[0]}, but it's still worth trying. `;
        } else {
            narrative += `Conditions are challenging but fish can still be caught with patience. `;
        }
    }

    // Best fishing slots (FBI-based display includes score in time string)
    const slot1 = analysis.whenToFish.slot1;
    const slot2 = analysis.whenToFish.slot2;
    const slot3 = analysis.whenToFish.slot3;

    narrative += `<br><br><strong>Best times today:</strong> `;
    narrative += `Your prime slot is <strong>${slot1.time}</strong> (${slot1.label.toLowerCase()}). `;
    if (slot2?.time) {
        narrative += `Another good window is <strong>${slot2.time}</strong> (${slot2.label.toLowerCase()}). `;
    }
    if (slot3?.time) {
        narrative += `Also consider <strong>${slot3.time}</strong> (${slot3.label.toLowerCase()}).`;
    }

    return { narrative, analysis };
}

/**
 * Compute FBI trend direction from forecast data
 * Compares current score to average of next 12h forecast
 */
function getFBITrend(currentScore, forecastData) {
    if (!forecastData || forecastData.length < 4) return { arrow: '', cls: 'stable', label: 'Stable' };
    const next12h = forecastData.slice(0, 12);
    const validScores = next12h.filter(p => p.fbiScore != null);
    if (validScores.length === 0) return { arrow: '', cls: 'stable', label: 'Stable' };
    const avgFuture = validScores.reduce((sum, p) => sum + p.fbiScore, 0) / validScores.length;
    // ±3 delta threshold prevents noise: small FBI fluctuations show as "Stable"
    if (avgFuture > currentScore + 3) return { arrow: '\u25B2', cls: 'rising', label: 'Rising' };
    if (avgFuture < currentScore - 3) return { arrow: '\u25BC', cls: 'falling', label: 'Falling' };
    return { arrow: '\u25B8', cls: 'stable', label: 'Stable' };
}

/**
 * Build the Current Conditions ("now") card: FBI score + measures grid, plus its own
 * Refresh-on-Demand button (mirrors the forecast table's — same endpoint, same
 * cooldown/budget, since one ROD fetch refreshes both). Split out of buildDetailHTML()
 * so the ROD click handler in main-server.js can rebuild just this card in place.
 */
function buildFbiCardHTML(cond, loc, weather, locationKey) {
    const waterTemp = cond.factors.find(f => f.name === 'Water Temp');
    const pressure = cond.factors.find(f => f.name === 'Pressure');
    const tide = cond.factors.find(f => f.name === 'Tide');

    // Get wind display with direction image
    const windSpeed = weather.wind ? Math.round(weather.wind.speed) : 0;
    const hasGust = weather.wind && weather.wind.gust;
    const windGust = hasGust ? Math.round(weather.wind.gust) : windSpeed;
    const windDeg = weather.wind ? weather.wind.deg : 0;
    const windDir = typeof getWindDirection === 'function' ? getWindDirection(windDeg) : { name: 'N', img: '/img/wind-north.png' };
    const gustPrefix = hasGust ? '' : '<sup style="font-size: 0.6em;">*</sup>';
    const windDisplay = `${windSpeed} | ${gustPrefix}${windGust} mph`;

    // Cloud cover
    const cloudCover = weather.clouds ? weather.clouds.all + '%' : 'N/A';

    // Sunrise / Sunset
    let sunriseDisplay = 'N/A';
    let sunsetDisplay = 'N/A';
    if (weather.sys && weather.sys.sunrise && weather.sys.sunset) {
        const rise = new Date(weather.sys.sunrise * 1000);
        const set = new Date(weather.sys.sunset * 1000);
        const fmtTime = (d) => {
            const h = d.getHours() % 12 || 12;
            const m = d.getMinutes().toString().padStart(2, '0');
            const ap = d.getHours() >= 12 ? 'PM' : 'AM';
            return `${h}:${m} ${ap}`;
        };
        sunriseDisplay = fmtTime(rise);
        sunsetDisplay = fmtTime(set);
    }

    // Warning slot: every warning that has data, in the shared priority order, in one flex row.
    const cardWarnings = buildWarningPillRowHTML(cond, { modifier: 'card' });

    // FBI trend arrow from forecast data
    const trend = getFBITrend(cond.score, window.fishingForecast);
    const trendHtml = trend.arrow ? ` <span class="fbi-trend fbi-trend--${trend.cls}" title="${trend.label}">${trend.arrow}</span>` : '';
    const ratingLabel = typeof getScoreLabel === 'function' ? getScoreLabel(cond.score) : 'Fair';

    // Refresh-on-Demand button — always rendered (visible to free users too, paywall on
    // click, not on visibility), same rule as the forecast table's refresh button.
    const isProUser = typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.isPro();
    const refreshProBadgeHTML = !isProUser
        ? `<img class="rod-pro-badge" src="/img/pro-sticker.png" alt="Pro">`
        : '';
    const refreshBtnHTML = locationKey
        ? `<button type="button" class="fbi-card__refresh-btn" data-location-key="${escapeHtml(locationKey)}" title="Refresh current conditions with Premium Live Data (Pro)">
               <img src="/img/refresh-sparkle.png" alt="Refresh">
               ${refreshProBadgeHTML}
           </button>`
        : '';

    return `<div class="fbi-card">
            ${refreshBtnHTML}
            <div class="fbi-card__layout">
                <!-- Fish Bite Index Score -->
                <div class="fbi-card__score">
                    <div class="fbi-card__score-label">Fish Bite Index</div>
                    <div class="fbi-card__score-value fbi-tooltip" tabindex="0" data-tip-html data-tip="${(cond.explanation || 'FBI Score').replace(/"/g, '&quot;')}" style="color: ${typeof getScoreColor === 'function' ? getScoreColor(cond.score) : 'white'};">${cond.score}</div>
                    <div class="fbi-card__score-rating">${ratingLabel}${trendHtml}</div>
                </div>

                <!-- Measures Grid -->
                <div class="fbi-card__measures">
                    <div class="fbi-card__measure">
                        <div class="fbi-card__measure-label">Water Temp</div>
                        <div class="fbi-card__measure-value">${waterTemp.value}</div>
                    </div>
                    <div class="fbi-card__measure">
                        <div class="fbi-card__measure-label">Tide</div>
                        <div class="fbi-card__measure-value">${tide.value}</div>
                        ${tide.nextTide ? `<div class="fbi-card__measure-sub">${tide.nextTide}</div>` : ''}
                    </div>
                    <div class="fbi-card__measure">
                        <div class="fbi-card__measure-label">Wind | Gust</div>
                        <div class="fbi-card__measure-value">${windDisplay}</div>
                        <div class="fbi-card__measure-sub">${{N:'North',NE:'Northeast',E:'East',SE:'Southeast',S:'South',SW:'Southwest',W:'West',NW:'Northwest'}[windDir.name] || windDir.name}</div>
                    </div>
                    <div class="fbi-card__measure">
                        <div class="fbi-card__measure-label">Barometer</div>
                        <div class="fbi-card__measure-value">${pressure.value}</div>
                        ${pressure.trend ? `<div class="fbi-card__measure-sub">${pressure.trend}</div>` : ''}
                    </div>
                    <div class="fbi-card__measure">
                        <div class="fbi-card__measure-label">Weather | Cloud</div>
                        <div class="fbi-card__measure-value">${weather.weather?.[0]?.main || '-'} | ${cloudCover}</div>
                        ${cardWarnings || '<div class="fbi-card__measure-sub">Warning: -</div>'}
                    </div>
                    <div class="fbi-card__measure">
                        <div class="fbi-card__measure-label">Sunrise | Sunset</div>
                        <div class="fbi-card__measure-value">${sunriseDisplay} | ${sunsetDisplay}</div>
                    </div>
                </div>
            </div>
            ${buildBottomTypeHTML(cond, 'card')}
        </div>`;
}

function buildDetailHTML(cond, loc, weather, tideData24h, locationKey) {
    // Create Google Maps directions link
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lon}`;

    return `<div class="detail-wrapper">
        <div class="detail-nav-row">
            <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" class="fbi-card__nav-btn"><img src="./img/google-navigate.png" alt="Navigate"></a>
            <a role="button" class="quick-start-trip-btn fbi-card__nav-btn" data-location-name="${escapeHtml(loc.name)}" data-location-key="${locationKey || ''}" data-lat="${loc.lat || ''}" data-lon="${loc.lon || ''}" title="Start trip here"><img src="./img/start-trip.png" alt="Start Trip"></a>
        </div>
        ${buildFbiCardHTML(cond, loc, weather, locationKey)}

        ${buildSpeciesBaitRigCards(cond, locationKey)}

        <div class="forecast-lazy-target" data-location-key="${locationKey}">
            ${buildForecastTable(window.fishingForecast || [], null, loc.name, locationKey)}
        </div>
    </div>`;
}

/**
 * Build spot metadata section for the expanded row of a My Spot.
 * Shows coordinates, type, water type, notes, created date, and delete button.
 * @param {Object} item - Spot condition item with _spotMeta
 * @returns {string} HTML string
 */
function buildSpotMetadataHTML(item) {
    var meta = item._spotMeta || {};
    var loc = item.location || {};
    var lat = loc.lat != null ? Number(loc.lat).toFixed(4) : '--';
    var lon = loc.lon != null ? Number(loc.lon).toFixed(4) : '--';
    var typeLabel = meta.locationType ? escapeHtml(meta.locationType) : 'Unknown';
    var waterLabel = meta.waterType ? escapeHtml(meta.waterType) : 'Unknown';
    var dateStr = meta.createdAt ? formatDate(meta.createdAt) : '--';
    var notesHtml = meta.notes
        ? '<div class="myspot-metadata__row"><span class="myspot-metadata__label">Notes</span><span class="myspot-metadata__value">' + escapeHtml(meta.notes) + '</span></div>'
        : '';

    return '<div class="myspot-metadata">' +
        '<div class="myspot-metadata__header">Spot Info</div>' +
        '<div class="myspot-metadata__row"><span class="myspot-metadata__label">Coordinates</span><span class="myspot-metadata__value">' + lat + ', ' + lon + '</span></div>' +
        '<div class="myspot-metadata__row"><span class="myspot-metadata__label">Type</span><span class="myspot-metadata__value">' + typeLabel + '</span></div>' +
        '<div class="myspot-metadata__row"><span class="myspot-metadata__label">Water</span><span class="myspot-metadata__value">' + waterLabel + '</span></div>' +
        notesHtml +
        '<div class="myspot-metadata__row"><span class="myspot-metadata__label">Added</span><span class="myspot-metadata__value">' + dateStr + '</span></div>' +
        (meta.id ? '<div class="myspot-metadata__actions"><button class="myspot-delete-btn" data-spot-id="' + escapeHtml(String(meta.id)) + '">Delete Spot</button></div>' : '') +
    '</div>';
}

/**
 * Build error state content for a My Spot whose conditions failed to compute.
 * @param {Object} item - Spot error item with _error, _errorMessage, location
 * @returns {string} HTML string
 */
function buildSpotErrorHTML(item) {
    var loc = item.location || {};
    var name = escapeHtml(loc.name || 'Unknown Spot');
    var lat = loc.lat != null ? Number(loc.lat).toFixed(4) : '--';
    var lon = loc.lon != null ? Number(loc.lon).toFixed(4) : '--';
    var spotId = (item._spotMeta && item._spotMeta.id) || getSpotIdFromKey(item.key) || '';

    return '<div class="detail-wrapper myspot-error-content">' +
        '<div class="myspot-error-content__message">' +
            '<strong>' + name + '</strong>' +
            '<p>Conditions unavailable for this spot. The data sources may be temporarily down.</p>' +
            '<span class="myspot-metadata__value">' + lat + ', ' + lon + '</span>' +
        '</div>' +
        '<div class="myspot-metadata__actions"><button class="myspot-delete-btn" data-spot-id="' + escapeHtml(String(spotId)) + '">Delete Spot</button></div>' +
    '</div>';
}

/**
 * Build three-column card layout: Species Bite Likelihood, Bait to Use, Rig to Use
 * Species card renders immediately from SSE data; bait/rig show skeleton until lazy-load completes.
 * @param {Object} cond - Location conditions with .speciesScores
 * @param {string} locationKey - Location key for lazy-load targeting
 */
function buildSpeciesBaitRigCards(cond, locationKey) {
    // Build Species Bite Likelihood card with score bars + chips
    let speciesCard = '';
    if (cond && cond.speciesScores) {
        const sortedSpecies = getSortedSpecies(cond.speciesScores, SPECIES_CARD_SORT_OPTIONS);
        // Bottom notes are Pro detail; the bottom effect on the score itself applies to every tier
        const showBottomNote = typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.isPro();

        if (sortedSpecies.length > 0) {
            const RANK_BADGES = ['img/first-place.png', 'img/second-place.png', 'img/third-place.png'];
            const speciesItems = sortedSpecies.map(([key, data], idx) => {
                const rankHtml = idx < 3
                    ? `<div class="species-item__rank"><img src="${RANK_BADGES[idx]}" alt="${idx + 1}"></div>`
                    : `<div class="species-item__rank"></div>`;

                return `
                    <div class="species-item${idx < 3 ? ' species-item--top' : ''}" data-species-key="${key}" tabindex="0" role="button" aria-expanded="false">
                        ${rankHtml}
                        <span class="species-item__name">${data.displayName}</span>
                        <div class="species-item__detail">
                            ${data.explanation ? `<p class="species-item__explain">${data.explanation}</p>` : ''}
                            ${showBottomNote && data.bottomNote ? `<p class="species-item__bottom">${escapeHtml(data.bottomNote)}</p>` : ''}
                            ${data.regulationsNote ? `<p class="species-item__regs">${data.regulationsNote}</p>` : ''}
                        </div>
                    </div>
                `;
            }).join('');

            speciesCard = `
                <div class="info-card info-card-species">
                    <div class="info-card-title info-card-title-species">BITE LIKELIHOOD</div>
                    <div class="info-card-content">
                        <div class="rec-species-group">EXPAND FOR REGULATIONS</div>
                        ${speciesItems}
                    </div>
                </div>
            `;
        }
    }

    // Bait card — skeleton until lazy-load
    const baitCard = `
        <div class="info-card info-card-bait">
            <div class="info-card-title info-card-title-bait">BAIT TO USE</div>
            <div class="info-card-content" data-recs-target="bait" data-location-key="${locationKey || ''}">
                ${buildRecSkeleton()}
            </div>
        </div>
    `;

    // Rig card — skeleton until lazy-load
    const rigCard = `
        <div class="info-card info-card-rig">
            <div class="info-card-title info-card-title-rig">RIG TO USE</div>
            <div class="info-card-content" data-recs-target="rig" data-location-key="${locationKey || ''}">
                ${buildRecSkeleton()}
            </div>
        </div>
    `;

    return `
        <div class="species-bait-rig-cards">
            ${speciesCard}
            ${baitCard}
            ${rigCard}
        </div>
    `;
}

/**
 * Populate bait/rig cards after lazy-load completes.
 * Called by loadAndRenderRecommendations() in main-server.js.
 * @param {HTMLElement} expandRow - The expanded row container
 * @param {Object} recommendations - API response { species: [...] }
 * @param {boolean} isGated - Whether recommendations are gated (free user)
 */
function renderBaitRigCards(expandRow, recommendations, isGated) {
    if (!recommendations || !recommendations.species || recommendations.species.length === 0) return;

    var species = recommendations.species;
    // Sync with bite likelihood card — only show bait/rig for visible species
    var visibleSpeciesKeys = new Set(
        [].slice.call(expandRow.querySelectorAll('.species-item[data-species-key]'))
            .map(function(el) { return el.dataset.speciesKey; })
    );
    var topSpecies = visibleSpeciesKeys.size > 0
        ? species.filter(function(sp) { return visibleSpeciesKeys.has(sp.key); }).slice(0, 3)
        : species.slice(0, 3);

    // Build bait HTML — medal rank badges use displayRank (Decision #5)
    var RANK_BADGES = ['img/first-place.png', 'img/second-place.png', 'img/third-place.png'];
    var baitHtml = topSpecies.map(function(sp) {
        var baits = (sp.baits || []).slice(0, 3);
        if (baits.length === 0 && !(isGated && sp.gatedBaitCount > 0)) return '';

        var buildBaitItem = function(bait, i) {
            var rankIdx = (bait.displayRank || (i + 1)) - 1;
            return '<div class="rec-item" tabindex="0" role="button" aria-expanded="false">' +
                '<div class="rec-item__rank"><img src="' + (RANK_BADGES[rankIdx] || '') + '" alt="' + (rankIdx + 1) + '"></div>' +
                '<div class="rec-item__body">' +
                    '<div class="rec-item__header">' +
                        '<span class="rec-item__name">' + bait.name + '</span>' +
                    '</div>' +
                    (bait.whyItWorks ? '<div class="rec-item__why">' + bait.whyItWorks + '</div>' : '') +
                '</div>' +
            '</div>';
        };

        // Pro-gate inline row before visible items
        var placeholder = '';
        if (isGated && sp.gatedBaitCount > 0) {
            placeholder = buildProGateRow('bait');
        }

        return '<div class="rec-species-group">' + sp.displayName + '</div>' +
            placeholder +
            baits.map(function(b, i) { return buildBaitItem(b, i); }).join('');
    }).join('');

    // Build rig HTML — medal rank badges, click-to-expand specs
    // Compact view caps at 3 rigs; server handles gating (strips top 1 for free users)
    var rigHtml = topSpecies.map(function(sp) {
        var rigs = (sp.rigs || []).slice(0, 3);
        if (rigs.length === 0 && !(isGated && sp.gatedRigCount > 0)) return '';

        var buildRigItem = function(rig, i) {
            var rankIdx = (rig.displayRank || (i + 1)) - 1;
            return '<div class="rec-item" tabindex="0" role="button" aria-expanded="false">' +
                '<div class="rec-item__rank"><img src="' + (RANK_BADGES[rankIdx] || '') + '" alt="' + (rankIdx + 1) + '"></div>' +
                '<div class="rec-item__body">' +
                    '<div class="rec-item__header">' +
                        '<span class="rec-item__name">' + rig.name + '</span>' +
                    '</div>' +
                    (rig.whyItWorks ? '<div class="rec-item__why">' + rig.whyItWorks + '</div>' : '') +
                    (rig.specs ? buildRigSpecs(rig.specs) : '') +
                '</div>' +
            '</div>';
        };

        // Pro-gate inline row before visible items
        var placeholder = '';
        if (isGated && sp.gatedRigCount > 0) {
            placeholder = buildProGateRow('rig');
        }

        return '<div class="rec-species-group">' + sp.displayName + '</div>' +
            placeholder +
            rigs.map(function(r, i) { return buildRigItem(r, i); }).join('');
    }).join('');

    // Replace skeleton with actual content
    var baitTarget = expandRow.querySelector('[data-recs-target="bait"]');
    if (baitTarget) baitTarget.innerHTML = baitHtml || '<div style="font-size: 12px; color: #999;">No bait data available</div>';

    var rigTarget = expandRow.querySelector('[data-recs-target="rig"]');
    if (rigTarget) rigTarget.innerHTML = rigHtml || '<div style="font-size: 12px; color: #999;">No rig data available</div>';
}

/**
 * Renders a pro-gate inline row: [crown] Pro only ···· [Upgrade to Pro]
 * Used inside bait/rig cards. Relies on [data-upgrade] delegated click handler.
 * @param {string} [feature] - Read by the delegated handler's gate-hit beacon ('bait'/'rig')
 * @returns {string} HTML string
 */
function buildProGateRow(feature) {
    return '<div class="pro-gate-row" data-upgrade data-feature="' + (feature || 'unknown') + '">' +
        '<img class="pro-gate-row__icon" src="/img/first-place.png" alt="" width="18" height="18">' +
        '<span class="pro-gate-row__text">Pro feature</span>' +
        '<img class="pro-gate-row__icon" src="/img/pro-sticker.png" alt="" width="18" height="18">' +
        '<span class="pro-gate-row__spacer"></span>' +
        '<span class="pro-gate-row__cta">Upgrade</span>' +
    '</div>';
}

/**
 * Bottom type line: a stacked share bar in the Bed Type map colours (CONFIG.BED_TYPE_CLASSES)
 * plus the two largest classes in words. Pro only; free users and guests get the Pro gate row.
 * A spot with no bottom data (null: under 25% sample coverage) gets a muted "no survey data"
 * line for Pro, so the missing bar doesn't read as broken, and nothing for free and guests
 * (no point gating an empty line). undefined (a row cached before FBI 2.8.0) renders nothing.
 * @param {Object} cond - Location conditions with .bottomType
 * @param {'card'|'sidebar'} variant - Expanded FBI card (dark) or map sidebar (light)
 * @returns {string} HTML string
 */
function buildBottomTypeHTML(cond, variant) {
    const classes = typeof CONFIG !== 'undefined' && CONFIG.BED_TYPE_CLASSES;
    if (!cond || !classes || cond.bottomType === undefined) return '';
    const bottom = cond.bottomType;

    const label = '<span class="bottom-type__label">Bottom</span>';
    const isProUser = typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.isPro();
    if (bottom === null) {
        return isProUser
            ? `<div class="bottom-type bottom-type--${variant} bottom-type--none">${label}<span class="bottom-type__none">No survey data near this spot</span></div>`
            : '';
    }
    if (!bottom.shares) return '';
    if (!isProUser) {
        return `<div class="bottom-type bottom-type--${variant} bottom-type--locked">${label}${buildProGateRow('bottom_type')}</div>`;
    }

    // Shares are rounded server-side, so normalise widths by their sum
    const parts = Object.keys(classes)
        .map(key => ({ key, share: Number(bottom.shares[key]) || 0 }))
        .filter(p => p.share > 0);
    const total = parts.reduce((sum, p) => sum + p.share, 0);
    if (!total) return '';

    const words = parts.slice()
        .sort((a, b) => b.share - a.share)
        .map(p => `${classes[p.key].name} ${Math.round(p.share / total * 100)}%`);
    const segments = parts.map(p =>
        `<span class="bottom-type__seg" style="width: ${(p.share / total * 100).toFixed(2)}%; background: ${classes[p.key].color};"></span>`
    ).join('');

    return `<div class="bottom-type bottom-type--${variant}">
            ${label}
            <div class="bottom-type__bar" role="img" aria-label="${escapeHtml('Bottom type: ' + words.join(', '))}">${segments}</div>
            <span class="bottom-type__summary">${escapeHtml(words.slice(0, 2).join(' · '))}</span>
        </div>`;
}

/**
 * Build a 5-day forecast table with slider navigation
 * Shows 16 slots (48 hours) at a time, with slider to navigate through 40 total slots (5 days)
 * @param {Array} forecastData - Array of forecast data points
 * @param {Object|null} gatingInfo - { gated: true, freeHours, totalHours } for free users
 * @returns {string} HTML string for forecast table
 */
function buildForecastTable(forecastData, gatingInfo, locationName, locationKey) {
    if (!forecastData || forecastData.length === 0) {
        return `<div style="padding: 15px; background: #f8f9fa; border-radius: 6px; color: #7f8c8d; text-align: center;">
            No forecast data available
        </div>`;
    }

    // Generate unique ID for this table instance
    const tableId = `forecast-${Date.now()}`;

    const now = new Date();
    const currentHourStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), 0, 0, 0);

    const TOTAL_SLOTS = 168; // Max slots to display (7 days × 24h)

    // Column configuration
    const COLUMN_WIDTH = 55; // Fixed pixel width per time column
    const LABEL_COLUMN_WIDTH = 90; // Fixed label column width

    // Use actual forecast data points as slots (ensures FBI scores match narrative exactly)
    const allSlots = forecastData
        .filter(point => new Date(point.timestamp) >= currentHourStart)
        .slice(0, TOTAL_SLOTS)
        .map(point => {
            const slotTime = new Date(point.timestamp);
            const dayLabel = formatDate(slotTime, { weekday: 'short', month: 'short', day: 'numeric' });
            return {
                dayLabel: dayLabel,
                timestamp: slotTime,
                data: point,
                isHourly: point.isHourly !== false
            };
        });

    // Helper function to format sun time
    const formatSunTime = (hour, minute) => {
        const h = hour % 12 || 12;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const m = minute.toString().padStart(2, '0');
        return `${h}:${m} ${ampm}`;
    };

    // Get sunrise/sunset times - uses OpenWeather API data with accurate daily adjustment
    const getSunTimes = (date) => {
        // Try to get real data from OpenWeather API (stored in window.sunriseSunsetData)
        if (typeof window !== 'undefined' && window.sunriseSunsetData) {
            const sunData = window.sunriseSunsetData;
            if (sunData.sunrise && sunData.sunset) {
                const baseSunrise = new Date(sunData.sunrise);
                const baseSunset = new Date(sunData.sunset);

                // Calculate days from today to adjust times
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const targetDate = new Date(date);
                targetDate.setHours(0, 0, 0, 0);
                const daysDiff = Math.round((targetDate - today) / (1000 * 60 * 60 * 24));

                if (daysDiff === 0) {
                    // Today - use exact API data
                    return {
                        sunrise: { hour: baseSunrise.getHours(), minute: baseSunrise.getMinutes() },
                        sunset: { hour: baseSunset.getHours(), minute: baseSunset.getMinutes() }
                    };
                }

                // Calculate day of year for seasonal rate adjustment
                const dayOfYear = Math.floor((date - new Date(date.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));

                // Rate of change follows a sinusoidal pattern:
                // - Maximum (~2.5 min/day) near equinoxes (day 80 = Mar 21, day 266 = Sep 23)
                // - Minimum (~0.5 min/day) near solstices (day 172 = Jun 21, day 355 = Dec 21)
                const springEquinox = 80;

                // Determine direction: days getting longer (winter→summer) or shorter (summer→winter)
                const summerSolstice = 172;
                const winterSolstice = 355;
                const daysGettingLonger = (dayOfYear > winterSolstice || dayOfYear < summerSolstice);

                // Calculate cumulative adjustment for each day
                // (integrate rate over the days, but for simplicity use average rate at midpoint)
                const midpointDay = dayOfYear + daysDiff / 2;
                const avgRate = 0.5 + 2.0 * Math.abs(Math.sin(2 * Math.PI * (midpointDay - springEquinox) / 365));
                const totalAdjust = avgRate * Math.abs(daysDiff);

                // Apply direction: longer days = earlier sunrise, later sunset
                const direction = daysGettingLonger ? (daysDiff > 0 ? 1 : -1) : (daysDiff > 0 ? -1 : 1);
                const sunriseAdjust = -direction * totalAdjust; // Earlier sunrise = negative adjustment
                const sunsetAdjust = direction * totalAdjust;   // Later sunset = positive adjustment

                // Apply adjustments
                const sunriseMinutes = baseSunrise.getHours() * 60 + baseSunrise.getMinutes() + sunriseAdjust;
                const sunsetMinutes = baseSunset.getHours() * 60 + baseSunset.getMinutes() + sunsetAdjust;

                return {
                    sunrise: { hour: Math.floor(sunriseMinutes / 60), minute: Math.round(sunriseMinutes % 60) },
                    sunset: { hour: Math.floor(sunsetMinutes / 60), minute: Math.round(sunsetMinutes % 60) }
                };
            }
        }

        // Fallback: TX Gulf Coast monthly averages (decimal hours) when API data unavailable.
        // TODO: MULTI-STATE — these are hardcoded for ~29.5°N latitude.
        const month = date.getMonth();
        const dayOfMonth = date.getDate();
        // More granular daily values (interpolate within month using shared TX tables)
        const nextMonth = (month + 1) % 12;
        const progress = dayOfMonth / 30; // Approximate interpolation within month
        const sunriseHour = TX_SUNRISE_HOURS[month] + (TX_SUNRISE_HOURS[nextMonth] - TX_SUNRISE_HOURS[month]) * progress;
        const sunsetHour = TX_SUNSET_HOURS[month] + (TX_SUNSET_HOURS[nextMonth] - TX_SUNSET_HOURS[month]) * progress;
        return {
            sunrise: { hour: Math.floor(sunriseHour), minute: Math.round((sunriseHour % 1) * 60) },
            sunset: { hour: Math.floor(sunsetHour), minute: Math.round((sunsetHour % 1) * 60) }
        };
    };

    // Helper function to get weather icon based on conditions
    const getWeatherIcon = (slot) => {
        if (!slot.data) return '-';
        const cloud = slot.data.cloudCover || 0;
        const wind = slot.data.wind ? slot.data.wind.speed : 0;
        const hour = slot.timestamp.getHours();
        const weather = (slot.data.weather || '').toLowerCase();

        // Use real sunrise/sunset data to determine if it's night
        const sunTimes = getSunTimes(slot.timestamp);
        const isNight = hour < sunTimes.sunrise.hour || hour >= sunTimes.sunset.hour;

        // Determine weather icon
        let iconFile;
        if (weather.includes('thunder') || weather.includes('storm')) {
            iconFile = 'thunderstorm.png';
        } else if (weather.includes('snow') || weather.includes('sleet')) {
            iconFile = 'snow.png';
        } else if (weather.includes('rain') || weather.includes('shower')) {
            iconFile = 'rain.png';
        } else if (weather.includes('drizzle')) {
            iconFile = 'light-rain.png';
        } else if (weather.includes('fog') || weather.includes('mist')) {
            iconFile = 'fog.png';
        } else if (wind > 20) {
            iconFile = 'windy.png';
        } else if (cloud >= 80) {
            iconFile = 'cloudy.png';
        } else if (cloud >= 50) {
            iconFile = isNight ? 'partly-cloudy-night.png' : 'mostly-cloudy-day.png';
        } else if (cloud >= 20) {
            iconFile = isNight ? 'partly-cloudy-night.png' : 'partly-cloudy-day.png';
        } else {
            iconFile = isNight ? 'clear-night.png' : 'clear-day.png';
        }

        return `<img src="img/${iconFile}" alt="Weather" style="width: 24px; height: 24px; vertical-align: middle;">`;
    };

    // Metrics configuration
    const metrics = [
        { label: 'Time', rowClass: 'forecast-row-time', format: (slot) => {
            const hour = slot.timestamp.getHours();
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const hour12 = hour % 12 || 12;

            if (slot.isHourly) {
                // Hourly: show single hour
                return `${hour12}<br>${ampm}`;
            } else {
                // 3-hour: compact range — "6 - 9<br>AM" or "9 - 12<br>AM/PM"
                const endHour = (hour + 3) % 24;
                const endAmpm = endHour >= 12 ? 'PM' : 'AM';
                const endHour12 = endHour % 12 || 12;
                const periodLabel = ampm === endAmpm ? ampm : `${ampm}/${endAmpm}`;
                return `${hour12} - ${endHour12}<br>${periodLabel}`;
            }
        }},
        { label: 'FBI', format: (slot, numSlots) => {
            if (!slot.data) return '<div class="fbi-score-cell">-</div>';
            // Use server-calculated FBI score (algorithm protected server-side)
            const score = slot.data.fbiScore ?? 50;
            const explanation = slot.data.fbiExplanation || 'FBI Score';
            const color = typeof getScoreColor === 'function' ? getScoreColor(score) : '#888';
            // Escape double quotes for the data-tip attribute (server sends pre-formatted HTML)
            const tooltipHtml = explanation.replace(/"/g, '&quot;');
            return `<div class="fbi-score-cell">
                <span class="fbi-tooltip fbi-score-pill"
                      tabindex="0"
                      data-tip-placement="top"
                      data-tip-html
                      data-tip="${tooltipHtml}"
                      style="color: ${color}; border-color: ${color};">
                    ${score}
                </span>
            </div>`;
        }, isHighlight: true },
        { label: 'Sun', rowClass: 'forecast-row-compact', format: (slot) => {
            const hour = slot.timestamp.getHours();
            const sunTimes = getSunTimes(slot.timestamp);
            // For hourly slots, check exact hour; for 3-hour, check range
            const checkRange = slot.isHourly ? 1 : 3;
            const sunriseInSlot = sunTimes.sunrise.hour >= hour && sunTimes.sunrise.hour < hour + checkRange;
            const sunsetInSlot = sunTimes.sunset.hour >= hour && sunTimes.sunset.hour < hour + checkRange;
            if (sunriseInSlot) {
                const timeStr = formatSunTime(sunTimes.sunrise.hour, sunTimes.sunrise.minute);
                return `<div style="text-align: center;"><img src="img/sunrise.png" alt="Sunrise" style="width: 24px; height: 24px;"><br><span style="font-size: 9px;">${timeStr}</span></div>`;
            } else if (sunsetInSlot) {
                const timeStr = formatSunTime(sunTimes.sunset.hour, sunTimes.sunset.minute);
                return `<div style="text-align: center;"><img src="img/sunset.png" alt="Sunset" style="width: 24px; height: 24px;"><br><span style="font-size: 9px;">${timeStr}</span></div>`;
            }
            return '';
        }},
        { label: 'Weather', rowClass: 'forecast-row-compact', format: (slot) => getWeatherIcon(slot) },
        { label: 'Air (°F)', rowClass: 'forecast-row-compact', format: (slot) => slot.data?.airTemp ?? '-' },
        { label: 'Water (°F)', rowClass: 'forecast-row-compact', format: (slot) => {
            if (!slot.data || slot.data.waterTemp == null) return '-';
            return slot.data.waterTempEstimated ? `<sup style="font-size: 0.6em;">*</sup>${slot.data.waterTemp}` : slot.data.waterTemp;
        }},
        { label: 'Wind (mph)', rowClass: 'forecast-row-compact', format: (slot) => slot.data ? slot.data.wind.speed : '-' },
        { label: 'Gust (mph)', rowClass: 'forecast-row-compact', format: (slot) => {
            if (!slot.data) return '-';
            if (slot.data.wind.gust) return slot.data.wind.gust;
            return `<sup style="font-size: 0.6em;">*</sup>${slot.data.wind.speed}`;
        }},
        { label: 'Wind Dir.', rowClass: 'forecast-row-winddir', format: (slot) => {
            if (!slot.data) return '-';
            const dirToDeg = { 'N': 0, 'NNE': 22.5, 'NE': 45, 'ENE': 67.5, 'E': 90, 'ESE': 112.5, 'SE': 135, 'SSE': 157.5, 'S': 180, 'SSW': 202.5, 'SW': 225, 'WSW': 247.5, 'W': 270, 'WNW': 292.5, 'NW': 315, 'NNW': 337.5 };
            const deg = dirToDeg[slot.data.wind.direction] || 0;
            const windDir = typeof getWindDirection === 'function' ? getWindDirection(deg) : { name: 'N', img: '/img/wind-north.png' };
            return `<img src="${windDir.img}" alt="${windDir.name}" style="width: 18px; height: 18px; vertical-align: middle;" title="${windDir.name}">`;
        }},
        { label: 'Cloud (%)', rowClass: 'forecast-row-compact', format: (slot) => slot.data ? slot.data.cloudCover : '-' },
        { label: 'BP (mb)', rowClass: 'forecast-row-compact', format: (slot) => {
            if (!slot.data) return '-';
            return Math.round(slot.data.barometer);
        }}
    ];

    // Function to build table content for all slots
    // Gating boundary: slot index at which blur starts (null = no gating)
    const gateStartIdx = (gatingInfo && gatingInfo.gated) ? gatingInfo.freeHours : null;

    const buildTableContent = () => {
        const visibleSlots = allSlots;
        const actualCols = visibleSlots.length;
        const numSlots = actualCols;
        const colWidth = `${COLUMN_WIDTH}px`;

        // Group visible slots by day for header
        const slotsByDay = {};
        visibleSlots.forEach((slot, index) => {
            const dateKey = formatDate(slot.timestamp, { month: 'short', day: 'numeric' });
            if (!slotsByDay[dateKey]) {
                slotsByDay[dateKey] = { label: dateKey, count: 0, startIndex: index };
            }
            slotsByDay[dateKey].count++;
        });
        const days = Object.values(slotsByDay);

        let html = `
            <table class="forecast-table" style="table-layout: fixed;">
                <colgroup>
                    <col style="width: ${LABEL_COLUMN_WIDTH}px;">
                    ${Array(actualCols).fill(`<col style="width: ${colWidth};">`).join('')}
                </colgroup>
                <tbody>
        `;

        // Date header row (ungated — dates stay readable for context)
        html += `<tr><th class="forecast-table__date-label">Date</th>`;
        days.forEach((day) => {
            html += `<th colspan="${day.count}" class="forecast-table__day-header">${day.label}</th>`;
        });
        html += `</tr>`;

        // Metric rows
        metrics.forEach((metric) => {
            const trClass = metric.rowClass ? ` class="${metric.rowClass}"` : '';
            html += `<tr${trClass}><th class="forecast-table__metric-label">${metric.label}</th>`;
            visibleSlots.forEach((slot, idx) => {
                const prevSlot = idx > 0 ? visibleSlots[idx - 1] : null;
                const currentDay = slot.timestamp.toDateString();
                const prevDay = prevSlot ? prevSlot.timestamp.toDateString() : null;
                const dayStartCls = (idx === 0 || currentDay !== prevDay) ? ' forecast-table__cell--day-start' : '';
                const gatedCls = (gateStartIdx !== null && idx >= gateStartIdx) ? ' forecast-table__cell--gated' : '';
                html += `<td class="forecast-table__cell${dayStartCls}${gatedCls}">${metric.format(slot, numSlots)}</td>`;
            });
            html += `</tr>`;
        });

        // Tide row — gated portion blurred via CSS pseudo-element on the cell
        // Tide row — gated portion blurred via ::after using column ratio (responsive)
        const tideCanvasId = `${tableId}-tide-0`;
        const tideGatedAttr = (gateStartIdx !== null && gateStartIdx < actualCols)
            ? ` forecast-table__tide-cell--gated" style="--gate-pct: ${(gateStartIdx / actualCols * 100)}%`
            : '';
        html += `
            <tr>
                <th class="forecast-table__metric-label" style="vertical-align: middle;">Tide</th>
                <td colspan="${actualCols}" class="forecast-table__tide-cell${tideGatedAttr}">
                    <canvas id="${tideCanvasId}" style="width: 100%; height: 100%; display: block;"></canvas>
                </td>
            </tr>
        `;

        html += `</tbody></table>`;

        // Explain the asterisk the Water and Gust rows use. Without this the marker reads
        // as noise, and a water temp repeated across five days reads as a broken fallback
        // rather than the carried-forward observation it is.
        const hasEstimatedWater = visibleSlots.some(s => s.data && s.data.waterTempEstimated);
        const hasEstimatedGust = visibleSlots.some(s => s.data && s.data.wind && !s.data.wind.gust);
        if (hasEstimatedWater || hasEstimatedGust) {
            const notes = [];
            if (hasEstimatedWater) notes.push('water temp carried forward from the latest station reading');
            if (hasEstimatedGust) notes.push('gust estimated from sustained wind');
            html += `<div class="forecast-table__footnote"><sup>*</sup> ${notes.join('; ')}.</div>`;
        }

        return { html, tideCanvasId, visibleSlots, numSlots };
    };

    // Build table content with all slots
    const initialContent = buildTableContent();

    // Build pro-gate overlay for forecast (Phase 8)
    var gateOverlay = '';
    if (gatingInfo && gatingInfo.gated) {
        var lockedHours = gatingInfo.totalHours - gatingInfo.freeHours;
        gateOverlay = '<div class="pro-gate pro-gate--forecast" data-upgrade data-feature="forecast">' +
            '<div class="pro-gate__overlay">' +
                '<img class="pro-gate__icon" src="/img/hq-icon/crown.png" alt="" width="20" height="20">' +
                '<span class="pro-gate__text">+' + lockedHours + 'h forecast \u2014 Pro only</span>' +
                '<span class="pro-gate__cta">Upgrade to Pro</span>' +
            '</div>' +
        '</div>';
    }

    // Admin-only export button — captures the currently visible columns as a PNG (content creation tool)
    const exportBtnHTML = (typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.isAdmin())
        ? `<button type="button" class="forecast-table-export-btn" data-export-target="${tableId}-content" data-export-name="${escapeHtml(locationName || 'Location')}" title="Export visible forecast as image">
               <img src="/img/export-image.png" alt="Export">
           </button>`
        : '';

    // Refresh-on-Demand button — always rendered (visible to free users too, paywall on click, not on visibility)
    // Pro sticker only shown to non-pro users, as a corner callout that this is a paid feature
    const isProUser = typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.isPro();
    const refreshProBadgeHTML = !isProUser
        ? `<img class="rod-pro-badge" src="/img/pro-sticker.png" alt="Pro">`
        : '';
    const refreshBtnHTML = locationKey
        ? `<button type="button" class="forecast-table-refresh-btn" data-location-key="${escapeHtml(locationKey)}" title="Refresh with Premium Live Data (Pro)">
               <img src="/img/refresh-sparkle.png" alt="Refresh">
               ${refreshProBadgeHTML}
           </button>`
        : '';

    // Build main container — full table with horizontal scroll, no slider needed
    let tableHTML = `
        <div class="forecast-table-container" id="${tableId}-container">
            <div class="forecast-table-header">
                <span>📅 5 DAYS FORECAST</span>
                <div class="forecast-table-header-actions">
                    ${exportBtnHTML}
                    ${refreshBtnHTML}
                </div>
            </div>
            <div class="forecast-table-scroll" id="${tableId}-content" style="overflow-x: auto; -webkit-overflow-scrolling: touch;">
                ${initialContent.html}
            </div>
            ${gateOverlay}
        </div>
    `;

    // Setup tide chart and tooltips after DOM insertion
    const setupTideAndTooltips = (retries = 0) => {
        const contentDiv = document.getElementById(`${tableId}-content`);
        if (contentDiv) {
            requestAnimationFrame(() => {
                const canvas = document.getElementById(initialContent.tideCanvasId);
                if (canvas && typeof drawForecastTideChart === 'function') {
                    const tideData = initialContent.visibleSlots.map(slot => ({
                        time: slot.timestamp.toISOString(),
                        height: slot.data ? slot.data.tideHeight : 1.5
                    }));
                    drawForecastTideChart(canvas, tideData, initialContent.visibleSlots);
                }
                // Tooltips need no setup: tooltip.js listens on document for [data-tip]
            });
        } else if (retries < 20) {
            requestAnimationFrame(() => setupTideAndTooltips(retries + 1));
        }
    };

    requestAnimationFrame(() => setupTideAndTooltips(0));

    return tableHTML;
}

/**
 * The map sidebar's title block: the location name, its warning pill row, then the
 * Navigate / Start Trip / Close buttons. Shared by the scored sidebar and the errored-spot
 * sidebar so both open with the same title.
 *
 * The pills sit directly under the name because the title is the first thing read when the
 * sidebar opens, and a warning answers "should I go at all" before the chart and the report.
 * @param {Object} loc - { name, lat, lon }
 * @param {string} locationKey
 * @param {string} [pillRowHTML] - From buildWarningPillRowHTML(); '' when nothing is wrong
 * @returns {string}
 */
function buildSidebarTitleHTML(loc, locationKey, pillRowHTML) {
    // Google Maps navigation URL
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lon}`;

    return `<div class="sidebar-popup-title">
            <h3>${escapeHtml(loc.name)}</h3>${pillRowHTML || ''}
            <div class="sidebar-popup-title__actions">
                <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" class="navigate-btn-inline">
                    <img src="./img/google-navigate.png" alt="Navigate" />
                </a>
                <a role="button" class="quick-start-trip-btn navigate-btn-inline" data-location-name="${escapeHtml(loc.name)}" data-location-key="${locationKey || ''}" data-lat="${loc.lat || ''}" data-lon="${loc.lon || ''}" title="Start trip here">
                    <img src="./img/start-trip.png" alt="Start Trip" />
                </a>
                <a role="button" class="sidebar-title-close-btn navigate-btn-inline" title="Close">
                    <img src="./img/close-button.png" alt="Close" />
                </a>
            </div>
        </div>`;
}

function buildMapPopupHTML(cond, loc, weather, tideData24h, locationKey) {
    // Errored My Spot: the server returned no conditions and no weather. Show the title so the
    // pin still opens something, and say why the rest is missing.
    // The wrapper carries the key so a Premium Live Data refresh can find this sidebar
    // (repaintSidebarWarningPills()).
    // escapeHtml() leaves double quotes alone, so an attribute value needs them escaped too.
    const keyAttr = `data-location-key="${escapeHtml(locationKey || '').replace(/"/g, '&quot;')}"`;
    if (!cond) {
        return `<div class="sidebar-popup-wrapper" ${keyAttr}>
        ${buildSidebarTitleHTML(loc, locationKey, '')}
        <p class="sidebar-popup-unavailable">Conditions unavailable</p>
    </div>`;
    }

    const tide = cond.factors.find(f => f.name === 'Tide');
    const waterTemp = cond.factors.find(f => f.name === 'Water Temp');
    const pressure = cond.factors.find(f => f.name === 'Pressure');

    // Get wind data and format it with direction image
    const windSpeed = weather.wind ? Math.round(weather.wind.speed) : 0;
    const windDeg = weather.wind ? weather.wind.deg : 0;
    const windDir = typeof getWindDirection === 'function' ? getWindDirection(windDeg) : { name: 'N', img: '/img/wind-north.png' };
    const windImg = `<img src="${windDir.img}" alt="${windDir.name}" style="width: 20px; height: 20px; vertical-align: middle;" title="${windDir.name}">`;

    // Get consolidated fishing report (conditions + tactics)
    const fishingReportHTML = getGearRecommendationsForMap(loc, cond, weather, tideData24h);

    // Build background style for sidebar image card
    const hasBgImage = !!loc.pictureUrl1;
    const bgImageStyle = hasBgImage ? `background-image: url('${loc.pictureUrl1}');` : '';
    const cardClasses = hasBgImage ? 'sidebar-fbi-card has-bg-image' : 'sidebar-fbi-card';

    return `<div class="sidebar-popup-wrapper" ${keyAttr}>
        ${buildSidebarTitleHTML(loc, locationKey, buildWarningPillRowHTML(cond))}

        <div class="${cardClasses}" style="${bgImageStyle}">
            <div class="sidebar-fbi-card__gradient-overlay"></div>
            <canvas class="sidebar-mini-chart"></canvas>
        </div>

        ${buildBottomTypeHTML(cond, 'sidebar')}

        ${fishingReportHTML}
    </div>`;
}

// Export for Node.js (testing)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        buildSpeciesBaitRigCards,
        buildBottomTypeHTML,
        getScoreBarColor,
        buildRecSkeleton,
        buildFbiCardHTML,
        buildSidebarTitleHTML,
        buildMapPopupHTML
    };
}
;
/* === js/brag-board.js === */
/**
 * BRAG BOARD - Photo Overlay Feature
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/brag-board.js">
 * USED BY: main-server.js → brag board tab initialization
 * DEPENDS ON: window.allConditionsData (main-server.js), getScoreColor() (config-frontend.js)
 * EXPORTS: BRAG object with init(), render(), generateOverlay() as global
 *
 * Client-side canvas compositing to create branded fishing photos.
 * Uses Canvas 2D API to composite: photo + frame + data overlay.
 *
 * Dependencies: window.allConditionsData (from main-server.js)
 * Dependencies: getScoreColor() (from config-frontend.js)
 */

/* ==========================================================================
   CONSTANTS
   ========================================================================== */

const BRAG = {
    // Reference size — all measurements are designed at 1080px wide
    // and scaled proportionally to the actual image
    REF_WIDTH: 1080,

    // Colors (matching theme.scss)
    ORANGE_ACCENT: '#FF7500',
    GOLD_ACCENT: '#FFB549',
    WHITE: '#FFFFFF',
    WHITE_DIM: 'rgba(255, 255, 255, 0.7)',
    WHITE_MUTED: 'rgba(255, 255, 255, 0.5)',

    FONT: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
};

/* ==========================================================================
   STATE
   ========================================================================== */

const bragState = {
    initialized: false,
    photoLoaded: false,
    photoImage: null,
    selectedLocationKey: null,
    logoImage: null
};

// Load the logo on first Brag Board open, not at script load — most sessions never open it
function loadBragLogo() {
    const logo = new Image();
    logo.crossOrigin = 'anonymous';
    logo.onload = () => {
        bragState.logoImage = logo;
        // Re-render if canvas was already drawn without the logo
        if (bragState.photoLoaded && bragState.selectedLocationKey) {
            renderBragBoard();
        }
    };
    logo.onerror = (e) => { console.error('[BragBoard] Logo failed to load', e); };
    logo.src = 'img/reeltalk-hero-640.webp';
}

/* ==========================================================================
   INITIALIZATION
   ========================================================================== */

function isMobileDevice() {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        ('ontouchstart' in window && window.innerWidth <= 1024);
}

function initBragBoard() {
    if (bragState.initialized) return;
    bragState.initialized = true;

    loadBragLogo();
    populateBragLocations();

    const fileInput = document.getElementById('bragPhotoInput');
    const cameraInput = document.getElementById('bragCameraInput');
    const uploadZone = document.getElementById('bragUploadZone');
    const locationSelect = document.getElementById('bragLocationSelect');
    const downloadBtn = document.getElementById('bragDownloadBtn');
    const shareBtn = document.getElementById('bragShareBtn');
    const resetBtn = document.getElementById('bragResetBtn');

    // Action sheet elements
    const actionSheet = document.getElementById('bragActionSheet');
    const backdrop = document.getElementById('bragActionSheetBackdrop');
    const optCamera = document.getElementById('bragOptCamera');
    const optGallery = document.getElementById('bragOptGallery');
    const optCancel = document.getElementById('bragOptCancel');

    if (fileInput) fileInput.addEventListener('change', handlePhotoUpload);
    if (cameraInput) cameraInput.addEventListener('change', handlePhotoUpload);

    // On mobile: intercept upload zone click to show action sheet
    if (uploadZone && isMobileDevice()) {
        // Prevent the hidden file input from auto-triggering on zone click
        fileInput.classList.remove('brag-file-input');
        fileInput.classList.add('u-hidden');

        // The zone itself opens the sheet here, so make it a keyboard-reachable button
        uploadZone.setAttribute('tabindex', '0');
        uploadZone.setAttribute('role', 'button');
        registerKeyboardActivation('#bragUploadZone');

        const closeActionSheet = function () {
            if (actionSheet) actionSheet.classList.add('u-hidden');
        };

        uploadZone.addEventListener('click', function () {
            if (actionSheet) actionSheet.classList.remove('u-hidden');
        });

        if (optCamera) {
            optCamera.addEventListener('click', function () {
                closeActionSheet();
                cameraInput.click();
            });
        }
        if (optGallery) {
            optGallery.addEventListener('click', function () {
                closeActionSheet();
                fileInput.click();
            });
        }
        if (optCancel) optCancel.addEventListener('click', closeActionSheet);
        if (backdrop) backdrop.addEventListener('click', closeActionSheet);

        document.addEventListener('keydown', function (e) {
            if (e.key !== 'Escape' || !actionSheet || actionSheet.classList.contains('u-hidden')) return;
            closeActionSheet();
            uploadZone.focus();
        });
    }

    if (locationSelect) {
        locationSelect.addEventListener('change', function () {
            bragState.selectedLocationKey = this.value || null;
            tryRenderBragBoard();
        });
    }

    if (downloadBtn) downloadBtn.addEventListener('click', downloadBragImage);
    if (shareBtn) shareBtn.addEventListener('click', shareBragImage);
    if (resetBtn) resetBtn.addEventListener('click', resetBragBoard);

    // Hide Share button on desktop — Web Share API only works on mobile
    if (shareBtn && !isMobileDevice()) {
        shareBtn.classList.add('u-hidden');
    }
}

function populateBragLocations() {
    const select = document.getElementById('bragLocationSelect');
    if (!select || !window.allConditionsData) return;

    // Group by region
    const regions = {};
    window.allConditionsData.forEach(item => {
        const region = item.location.region || 'Other';
        if (!regions[region]) regions[region] = [];
        regions[region].push(item);
    });

    // Sort regions alphabetically, then locations within each region
    const sortedRegions = Object.keys(regions).sort();
    let html = '<option value="">Choose a fishing spot…</option>';

    sortedRegions.forEach(region => {
        html += `<optgroup label="${escapeHtml(region)}">`;
        regions[region]
            .sort((a, b) => a.location.name.localeCompare(b.location.name))
            .forEach(item => {
                const score = item.conditions ? item.conditions.score : '?';
                html += `<option value="${escapeHtml(item.key)}">${escapeHtml(item.location.name)} (FBI: ${score})</option>`;
            });
        html += '</optgroup>';
    });

    select.innerHTML = html;

    // Pre-select last expanded location if available
    if (window.lastExpandedLocationKey) {
        select.value = window.lastExpandedLocationKey;
        bragState.selectedLocationKey = window.lastExpandedLocationKey;
    }
}

/* ==========================================================================
   PHOTO HANDLING
   ========================================================================== */

function handlePhotoUpload(event) {
    const file = event.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = function (e) {
        const img = new Image();
        img.onload = function () {
            bragState.photoImage = img;
            bragState.photoLoaded = true;

            // Show preview thumbnail
            const preview = document.getElementById('bragPhotoPreview');
            const placeholder = document.getElementById('bragUploadPlaceholder');
            const zone = document.getElementById('bragUploadZone');

            if (preview) {
                preview.src = e.target.result;
                preview.classList.remove('u-hidden');
            }
            if (placeholder) placeholder.classList.add('u-hidden');
            if (zone) zone.classList.add('has-photo');

            // Dismiss action sheet if still open (mobile)
            const actionSheet = document.getElementById('bragActionSheet');
            if (actionSheet) actionSheet.classList.add('u-hidden');

            tryRenderBragBoard();
        };
        img.src = e.target.result;
    };
    reader.readAsDataURL(file);
}

/* ==========================================================================
   CANVAS RENDERING
   ========================================================================== */

function tryRenderBragBoard() {
    if (!bragState.photoImage || !bragState.selectedLocationKey) return;

    const container = document.getElementById('bragPreviewContainer');
    if (container) container.classList.remove('u-hidden');

    // Hide the input forms so preview fills available space
    const uploadFormGroup = document.getElementById('bragUploadZone')?.closest('.form-group');
    if (uploadFormGroup) uploadFormGroup.classList.add('u-hidden');
    const locationFormGroup = document.getElementById('bragLocationSelect')?.closest('.form-group');
    if (locationFormGroup) locationFormGroup.classList.add('u-hidden');

    renderBragBoard();
}

function renderBragBoard() {
    const canvas = document.getElementById('bragCanvas');
    if (!canvas) return;

    const image = bragState.photoImage;
    const locationData = getLocationData(bragState.selectedLocationKey);
    if (!locationData) return;

    // Match canvas to photo's native resolution
    canvas.width = image.width;
    canvas.height = image.height;

    const ctx = canvas.getContext('2d');
    const data = extractOverlayData(locationData);

    // Scale factor — all sizes designed at 1080px width
    const s = image.width / BRAG.REF_WIDTH;

    // Compositing pipeline: order matters — bottom overlay drawn last so text is never occluded
    ctx.drawImage(image, 0, 0);
    drawTopOverlay(ctx, canvas.width, canvas.height, s);
    drawBottomOverlay(ctx, canvas.width, canvas.height, s, data);
}

function drawTopOverlay(ctx, W, H, s) {
    const gradH = 100 * s;

    // Dark gradient fading down from top
    const grad = ctx.createLinearGradient(0, 0, 0, gradH);
    grad.addColorStop(0, 'rgba(0, 0, 0, 0.65)');
    grad.addColorStop(0.7, 'rgba(0, 0, 0, 0.25)');
    grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, gradH);

}

function drawBottomOverlay(ctx, W, H, s, data) {
    const gradH = 420 * s;
    const gradTop = H - gradH;

    // Dark gradient fading up from bottom
    const grad = ctx.createLinearGradient(0, gradTop, 0, H);
    grad.addColorStop(0, 'rgba(0, 0, 0, 0)');
    grad.addColorStop(0.25, 'rgba(0, 0, 0, 0.4)');
    grad.addColorStop(0.5, 'rgba(0, 0, 0, 0.7)');
    grad.addColorStop(1, 'rgba(0, 0, 0, 0.85)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, gradTop, W, gradH);

    const margin = 36 * s;
    const rightEdge = W - margin;

    // --- Location name ---
    let y = H - 340 * s;
    ctx.fillStyle = BRAG.WHITE;
    ctx.font = `bold ${66 * s}px ${BRAG.FONT}`;
    ctx.textAlign = 'left';
    ctx.fillText(data.locationName, margin, y);

    // --- Region, State subtitle ---
    y += 48 * s;
    ctx.fillStyle = BRAG.WHITE_DIM;
    ctx.font = `500 ${38 * s}px ${BRAG.FONT}`;
    const subtitle = [data.region, data.state].filter(Boolean).join(', ');
    ctx.fillText(subtitle, margin, y);

    // --- Date timestamp (left) + Logo & URL (right) ---
    y += 40 * s;
    ctx.font = `400 ${30 * s}px ${BRAG.FONT}`;
    ctx.fillStyle = BRAG.WHITE_MUTED;
    ctx.fillText(data.dateStr, margin, y);

    // Center of the 3rd column (Wind/Weather)
    const colWidth = (rightEdge - margin) / 3;
    const col3Center = margin + 2 * colWidth + colWidth / 2;

    // Logo centered above URL, aligned to 3rd column
    if (bragState.logoImage) {
        const logoH = 160 * s;
        const logoW = (bragState.logoImage.width / bragState.logoImage.height) * logoH;
        ctx.drawImage(bragState.logoImage, col3Center - logoW / 2, y - logoH + 5 * s, logoW, logoH);
    }

    // URL centered below logo
    ctx.textAlign = 'center';
    ctx.fillStyle = BRAG.WHITE;
    ctx.font = `600 ${24 * s}px ${BRAG.FONT}`;
    ctx.fillText('reeltalkfishing.com', col3Center, y + 20 * s);
    ctx.textAlign = 'left';

    // --- Conditions row 1: FBI, TIDE, WIND ---
    y += 80 * s;
    const fbiValue = `${data.fbiScore}`;
    drawConditionRow(ctx, y, margin, rightEdge, s, [
        { label: 'FISH BITE INDEX', value: fbiValue },
        { label: 'TIDE', value: data.tide },
        { label: 'WIND', value: data.windSpeed }
    ]);

    // --- Conditions row 2: WATER, AIR, WEATHER ---
    y += 80 * s;
    drawConditionRow(ctx, y, margin, rightEdge, s, [
        { label: 'WATER TEMPERATURE', value: data.waterTemp },
        { label: 'AIR TEMPERATURE', value: data.airTemp },
        { label: 'WEATHER', value: data.weather }
    ]);

}

function drawConditionRow(ctx, y, leftMargin, rightEdge, s, items) {
    const totalWidth = rightEdge - leftMargin;
    const colWidth = totalWidth / items.length;

    items.forEach((item, i) => {
        const centerX = leftMargin + i * colWidth + colWidth / 2;

        // Label (centered)
        ctx.textAlign = 'center';
        ctx.font = `600 ${28 * s}px ${BRAG.FONT}`;
        ctx.fillStyle = BRAG.ORANGE_ACCENT;
        ctx.fillText(item.label, centerX, y);

        // Value (centered)
        ctx.font = `bold ${42 * s}px ${BRAG.FONT}`;
        ctx.fillStyle = BRAG.WHITE;
        ctx.fillText(item.value, centerX, y + 42 * s);
    });

    ctx.textAlign = 'left';
}

/* ==========================================================================
   DATA EXTRACTION
   ========================================================================== */

function getLocationData(locationKey) {
    if (!window.allConditionsData) return null;
    return window.allConditionsData.find(c => c.key === locationKey) || null;
}

function extractOverlayData(locationData) {
    const loc = locationData.location;
    const cond = locationData.conditions || {};
    const weather = locationData.weatherData || {};
    const factors = cond.factors || [];

    const findFactor = (name) => {
        const f = factors.find(f => f.name === name);
        return f ? f.value : '--';
    };

    const score = cond.score || 0;
    const rating = typeof getScoreLabel === 'function' ? getScoreLabel(score) : 'Fair';

    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
    }) + ' ' + now.toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true
    });

    // Wind formatting
    const windSpeed = weather.wind
        ? `${Math.round(weather.wind.speed)} mph`
        : findFactor('Wind');

    // Air temp
    const airTemp = weather.main
        ? `${Math.round(weather.main.temp)}°F`
        : '--';

    // Weather description
    const weatherDesc = weather.weather && weather.weather[0]
        ? weather.weather[0].main
        : '--';

    // Moon phase
    const moonName = cond.moonPhase
        ? cond.moonPhase.name
        : '--';

    return {
        locationName: loc.name,
        region: loc.region || '',
        state: loc.state || 'Texas',
        fbiScore: score,
        fbiRating: rating,
        waterTemp: findFactor('Water Temp'),
        tide: findFactor('Tide'),
        windSpeed: windSpeed,
        airTemp: airTemp,
        weather: weatherDesc,
        moonName: moonName,
        dateStr: dateStr
    };
}

/* ==========================================================================
   DOWNLOAD & SHARE
   ========================================================================== */

function downloadBragImage() {
    const canvas = document.getElementById('bragCanvas');
    if (!canvas) return;

    canvas.toBlob(function (blob) {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        const locKey = bragState.selectedLocationKey || 'catch';
        const date = new Date().toISOString().slice(0, 10);
        a.download = `reeltalk-${locKey}-${date}.png`;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 'image/png');
}

async function shareBragImage() {
    const canvas = document.getElementById('bragCanvas');
    if (!canvas) return;

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    const file = new File([blob], 'reeltalk-catch.jpg', { type: 'image/jpeg' });

    if (!navigator.canShare || !navigator.canShare({ files: [file] })) {
        console.warn('[BragBoard] Web Share API file sharing not supported');
        return;
    }

    try {
        await navigator.share({
            title: 'My Catch - Reel Talk Fishing',
            text: 'Check out my catch! Get real-time fishing conditions at reeltalkfishing.com',
            files: [file]
        });
    } catch (err) {
        if (err.name !== 'AbortError') {
            console.error('[BragBoard] Share failed:', err);
        }
    }
}

function resetBragBoard() {
    bragState.photoImage = null;
    bragState.photoLoaded = false;
    bragState.selectedLocationKey = null;

    const fileInput = document.getElementById('bragPhotoInput');
    const preview = document.getElementById('bragPhotoPreview');
    const placeholder = document.getElementById('bragUploadPlaceholder');
    const zone = document.getElementById('bragUploadZone');
    const container = document.getElementById('bragPreviewContainer');
    const select = document.getElementById('bragLocationSelect');

    if (fileInput) fileInput.value = '';
    if (preview) {
        preview.src = '';
        preview.classList.add('u-hidden');
    }
    if (placeholder) placeholder.classList.remove('u-hidden');
    if (zone) zone.classList.remove('has-photo');
    // Restore input form groups
    const uploadFormGroup = zone?.closest('.form-group');
    if (uploadFormGroup) uploadFormGroup.classList.remove('u-hidden');
    const locationFormGroup = select?.closest('.form-group');
    if (locationFormGroup) locationFormGroup.classList.remove('u-hidden');
    if (container) container.classList.add('u-hidden');
    if (select) select.value = '';
}

;
/* === js/more-menu.js === */
/**
 * Mobile "More" Slide-Up Menu
 *
 * USED BY: main-server.js (DOMContentLoaded → MORE_MENU.init())
 * USES: window.switchTab (from main-server.js)
 * EXPORTS: MORE_MENU global (IIFE)
 */
var MORE_MENU = (function() {
    var _initialized = false;
    var _prevActiveNavItem = null;

    // Maps more-menu data-tab values to the body view class they produce
    var _tabToBodyClass = {
        bragBoard: 'view-brag',
        analytics: 'view-analytics'
    };

    function _getActiveMoreMenuTab() {
        var bodyClasses = document.body.classList;
        for (var tab in _tabToBodyClass) {
            if (bodyClasses.contains(_tabToBodyClass[tab])) return tab;
        }
        return null;
    }

    function init() {
        if (_initialized) return;
        _initialized = true;

        var moreBtn = document.getElementById('mobileMoreBtn');
        var menu = document.getElementById('moreMenu');
        var backdrop = document.getElementById('moreMenuBackdrop');

        if (!moreBtn || !menu) return;

        moreBtn.addEventListener('click', function() {
            menu.classList.contains('open') ? close() : open();
        });

        if (backdrop) {
            backdrop.addEventListener('click', function() {
                close();
            });
        }

        // Delegated click on menu items
        menu.addEventListener('click', function(e) {
            var item = e.target.closest('.more-menu-item');
            if (!item) return;
            var tab = item.dataset.tab;
            if (!tab) return;
            close();
            if (typeof window.switchTab === 'function') {
                window.switchTab(tab);
            }
        });

        // Escape closes the menu and returns focus to the More button
        document.addEventListener('keydown', function(e) {
            if (e.key !== 'Escape' || !menu.classList.contains('open')) return;
            close();
            moreBtn.focus();
        });

        // Close when any other bottom nav item is tapped
        var bottomNav = document.getElementById('bottomNav');
        if (bottomNav) {
            bottomNav.addEventListener('click', function(e) {
                var item = e.target.closest('.bottom-nav-item');
                if (item && item !== moreBtn && menu.classList.contains('open')) {
                    close();
                }
            });
        }
    }

    function open() {
        var menu = document.getElementById('moreMenu');
        var moreBtn = document.getElementById('mobileMoreBtn');
        if (!menu || !moreBtn) return;

        // Save which nav item was active before opening
        _prevActiveNavItem = document.querySelector('.bottom-nav-item.active');

        // Highlight More, unhighlight all others
        document.querySelectorAll('.bottom-nav-item').forEach(function(btn) {
            btn.classList.remove('active');
        });
        moreBtn.classList.add('active');
        moreBtn.setAttribute('aria-expanded', 'true');

        // Sync active state on menu items
        var activeTab = _getActiveMoreMenuTab();
        menu.querySelectorAll('.more-menu-item').forEach(function(item) {
            item.classList.toggle('active', item.dataset.tab === activeTab);
        });

        menu.classList.add('open');
    }

    function close() {
        var menu = document.getElementById('moreMenu');
        var moreBtn = document.getElementById('mobileMoreBtn');
        if (!menu) return;

        menu.classList.remove('open');

        // Remove More highlight
        if (moreBtn) {
            moreBtn.classList.remove('active');
            moreBtn.setAttribute('aria-expanded', 'false');
        }

        // Restore previously active nav item (switchTab will override if a tab was selected)
        if (_prevActiveNavItem) {
            _prevActiveNavItem.classList.add('active');
            _prevActiveNavItem = null;
        }
    }

    return {
        init: init,
        open: open,
        close: close
    };
})();

;
/* === js/utils/geolocation.js === */
/**
 * getAccurateLocation() replaces one-shot getCurrentPosition() with a short
 * watchPosition() loop: keep listening as fixes arrive, track the best (lowest)
 * accuracy seen, and resolve early once a "good enough" reading arrives —
 * otherwise resolve with the best sample once maxWaitMs is hit.
 *
 * @param {Object} [options]
 * @param {number} [options.desiredAccuracy=30] - meters; resolve early once a fix is at least this good
 * @param {number} [options.maxWaitMs=8000] - hard cap; resolve with best-so-far sample when hit
 * @param {number} [options.timeout=15000] - per-fix timeout passed to watchPosition
 * @returns {Promise<GeolocationPosition>}
 */
function getAccurateLocation(options) {
    options = options || {};
    var desiredAccuracy = options.desiredAccuracy != null ? options.desiredAccuracy : 30;
    var maxWaitMs = options.maxWaitMs != null ? options.maxWaitMs : 8000;
    var timeout = options.timeout != null ? options.timeout : 15000;

    return new Promise(function (resolve, reject) {
        var bestFix = null;
        var watchId = null;
        var maxWaitTimer = null;
        var lastError = null;
        var settled = false;

        function cleanup() {
            if (watchId !== null) {
                navigator.geolocation.clearWatch(watchId);
                watchId = null;
            }
            if (maxWaitTimer !== null) {
                clearTimeout(maxWaitTimer);
                maxWaitTimer = null;
            }
        }

        function settleResolve(position) {
            if (settled) return;
            settled = true;
            cleanup();
            resolve(position);
        }

        function settleReject(error) {
            if (settled) return;
            settled = true;
            cleanup();
            reject(error);
        }

        maxWaitTimer = setTimeout(function () {
            if (bestFix) {
                settleResolve(bestFix);
            } else if (lastError) {
                settleReject(lastError);
            } else {
                settleReject({ code: 3, message: 'Timeout expired' });
            }
        }, maxWaitMs);

        watchId = navigator.geolocation.watchPosition(
            function (position) {
                if (!bestFix || position.coords.accuracy < bestFix.coords.accuracy) {
                    bestFix = position;
                }
                if (position.coords.accuracy <= desiredAccuracy) {
                    settleResolve(bestFix);
                }
            },
            function (error) {
                if (bestFix) {
                    settleResolve(bestFix);
                } else {
                    lastError = error;
                    settleReject(error);
                }
            },
            { enableHighAccuracy: true, maximumAge: 0, timeout: timeout }
        );
    });
}

/**
 * Centralizes the PERMISSION_DENIED / POSITION_UNAVAILABLE / TIMEOUT -> user-facing
 * message mapping. Switches on numeric error.code (1/2/3 per the W3C spec's fixed
 * values), not symbolic property comparisons, so it works uniformly for real
 * GeolocationPositionError instances, the synthesized {code: 3} timeout fallback,
 * and hand-rolled test mocks.
 *
 * @param {{code: number}} error
 * @returns {string}
 */
function formatGeolocationError(error) {
    var code = error && error.code;
    switch (code) {
        case 1:
            return 'Location access denied. Please enable location permissions.';
        case 2:
            return 'Location information unavailable.';
        case 3:
            return 'Location request timed out. Please try again.';
        default:
            return 'Could not get your location';
    }
}

if (typeof module !== 'undefined' && module.exports) {
    module.exports = { getAccurateLocation: getAccurateLocation, formatGeolocationError: formatGeolocationError };
}

;
/* === js/spots.js === */
/**
 * Spots — My Spots conditions + CRUD (IIFE global)
 *
 * LOADED BY: app.html / app.ejs via bundle (dist/js/app.min.js)
 * USED BY: main-server.js → switchTab('mySpots') lazy-init, SSE complete → loadSpotConditions()
 * DEPENDS ON: auth-client.js (AUTH_CLIENT), api-client.js (API_CLIENT), ui-helpers.js (escapeHtml),
 *             shared-utils.js (validateSpotInput, formatLimitDisplay),
 *             main-server.js (buildLocationRowHTML, toggleRowExpansion, displayComparison, tableState)
 * EXPORTS: SPOTS object as global
 */
var SPOTS = (function() {
    var _initialized = false;
    var _spotConditionsCache = null;
    var _loadingPromise = null;
    var _tableHandlerAttached = false;
    var _publicTableHandlerAttached = false;
    var _limitCache = null;
    var _miniMap = null;
    var _miniMarker = null;
    var _miniMapInited = false;
    var _geolocating = false;
    var _inputSyncTimer = null;
    var _geocodeController = null;

    function _removeSpotItems(arr) {
        return arr.filter(function(c) { return !c._isMySpot; });
    }

    // --- Initialization ---

    function init() {
        if (_initialized) return;
        _initialized = true;

        // Click handler for My Spots table rows (expand/collapse)
        var mySpotsTable = document.getElementById('mySpotsTable');
        if (mySpotsTable && !_tableHandlerAttached) {
            mySpotsTable.addEventListener('click', function(e) {
                if (e.target.closest('.expanded-row')) return;
                if (e.target.closest('.myspot-delete-btn')) return;
                var row = e.target.closest('.location-row');
                if (!row) return;
                if (typeof hapticTap === 'function') hapticTap();
                var locationKey = row.getAttribute('data-location-key');
                var conditions = _spotConditionsCache || (typeof tableState !== 'undefined' ? tableState.allConditions : null);
                toggleRowExpansion(locationKey, conditions, 'ms-expand-' + locationKey);
            });
            _tableHandlerAttached = true;
        }

        // Click handler for public spots list in My Spots tab (expand/collapse rows)
        var publicTable = document.getElementById('mySpotsPublicTable');
        if (publicTable && !_publicTableHandlerAttached) {
            publicTable.addEventListener('click', function(e) {
                if (typeof handleTableClick === 'function') handleTableClick(e);
            });
            _publicTableHandlerAttached = true;
        }

        // Add/close button click — closes form if open, otherwise opens it (or handles limit)
        var addBtn = document.getElementById('mySpotsAddBtn');
        if (addBtn) {
            addBtn.addEventListener('click', function() {
                var section = document.getElementById('addSpotSection');
                if (section && !section.classList.contains('u-hidden')) {
                    hideAddForm();
                    return;
                }

                var atLimit = _limitCache && _limitCache.max !== -1 && _limitCache.count >= _limitCache.max;
                if (!atLimit) {
                    showAddForm();
                    return;
                }
                var tier = (typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.membershipTier) || 'free';
                if (tier === 'free') {
                    if (typeof showUpgradeModal === 'function') showUpgradeModal();
                } else {
                    if (typeof showAppToast === 'function') showAppToast('You\'ve reached your spot limit. Delete an existing spot to add a new one.', 'warning');
                }
            });
        }

        // Filter button click → close add form if open, then open filter popup
        var filterBtn = document.getElementById('mySpotsFilterBtn');
        if (filterBtn) {
            filterBtn.addEventListener('click', function(e) {
                var section = document.getElementById('addSpotSection');
                if (section && !section.classList.contains('u-hidden')) {
                    hideAddForm();
                }
                if (typeof toggleSetupPopup === 'function') toggleSetupPopup(e);
            });
        }

        // Save spot button
        var saveBtn = document.getElementById('saveSpotBtn');
        if (saveBtn) {
            saveBtn.addEventListener('click', function() { saveSpot(); });
        }

        // Close form button
        var closeBtn = document.getElementById('addSpotCloseBtn');
        if (closeBtn) {
            closeBtn.addEventListener('click', function() { hideAddForm(); });
        }

        // Geolocate button
        var geoBtn = document.getElementById('spotGeolocateBtn');
        if (geoBtn) {
            geoBtn.addEventListener('click', function() { _geolocate(); });
        }

        // Clear auto-filled flag when user manually types a name
        var spotNameEl = document.getElementById('spotName');
        if (spotNameEl) {
            spotNameEl.addEventListener('input', function() {
                spotNameEl.removeAttribute('data-auto-filled');
            });
        }

        // Bi-directional lat/lon → map sync
        _setupInputSync();

        // Load spot conditions if not cached
        if (!_spotConditionsCache) {
            loadSpotConditions();
        } else {
            renderMySpotsTable(_spotConditionsCache);
        }

        // Seed public list from already-loaded conditions data
        if (window.allConditionsData) {
            renderPublicList(window.allConditionsData);
        }

        // Keep public list in sync when conditions data arrives or refreshes
        window.addEventListener('conditionsUpdated', function(e) {
            if (e.detail && e.detail.data) renderPublicList(e.detail.data);
        });
    }

    // --- Core data loading ---

    function loadSpotConditions(forceRefresh) {
        // Return existing in-flight promise to coalesce concurrent callers
        if (_loadingPromise && !forceRefresh) return _loadingPromise;

        if (_spotConditionsCache && !forceRefresh) {
            mergeIntoMainList(_spotConditionsCache);
            renderMySpotsTable(_spotConditionsCache);
            loadLimitInfo();
            return;
        }

        _loadingPromise = AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/spots/conditions')
            .then(function(res) { return res.json(); })
            .then(function(data) {
                if (data.success) {
                    _spotConditionsCache = data.conditions || [];
                    mergeIntoMainList(_spotConditionsCache);
                    renderMySpotsTable(_spotConditionsCache);
                    loadLimitInfo();
                }
            })
            .catch(function(err) {
                console.error('[Spots] Conditions load error:', err.message);
            })
            .finally(function() {
                _loadingPromise = null;
            });
        return _loadingPromise;
    }

    // --- Merge spots into main list ---

    function mergeIntoMainList(spotConditions) {
        if (!window.allConditionsData) return;

        // 1. Filter out existing spot items
        window.allConditionsData = _removeSpotItems(window.allConditionsData);

        // 2. Push new spot conditions
        spotConditions.forEach(function(sc) {
            window.allConditionsData.push(sc);
        });

        // 3. Re-sort: error items to end, then by FBI score DESC
        window.allConditionsData.sort(function(a, b) {
            if (a._error && !b._error) return 1;
            if (!a._error && b._error) return -1;
            return ((b.conditions && b.conditions.score) || 0) - ((a.conditions && a.conditions.score) || 0);
        });

        // 4. Update tableState reference
        if (typeof tableState !== 'undefined') {
            tableState.allConditions = window.allConditionsData;
        }

        // 5. Re-render main table if it has been rendered
        var table = document.getElementById('comparisonTable');
        if (table && table.innerHTML && typeof displayComparison === 'function') {
            displayComparison(window.allConditionsData);
        }
    }

    // --- Render My Spots table ---

    function renderMySpotsTable(conditions) {
        var table = document.getElementById('mySpotsTable');
        if (!table) return;

        var atLimit = _limitCache && _limitCache.max !== -1 && _limitCache.count >= _limitCache.max;
        _updateAddBtn(atLimit);

        var html = '<colgroup><col style="width: auto;"></colgroup><tbody>';

        // Spot rows
        if (conditions && conditions.length > 0) {
            conditions.forEach(function(item) {
                var result = buildLocationRowHTML(item, 'ms-');
                html += result.locationRowHTML + result.expandRowHTML;
            });
        }

        html += '</tbody>';
        table.innerHTML = html;

        // Setup lazy backgrounds for spot rows (if function exists)
        if (typeof setupLazyBackgrounds === 'function') setupLazyBackgrounds();
    }

    function _updateFilterBtn() {
        var btn = document.getElementById('mySpotsFilterBtn');
        if (!btn) return;
        var regionEl = document.getElementById('popupRegionSelect');
        var stateEl = document.getElementById('popupStateSelect');
        var countEl = document.getElementById('popupCountSelect');
        var region = regionEl ? regionEl.value : 'all';
        var state = stateEl ? stateEl.value : 'all';
        var count = countEl ? countEl.value : 'all';
        var isActive = (region && region !== 'all') || (state && state !== 'all') || (count && count !== 'all');
        btn.classList.toggle('myspots-filter-btn--active', isActive);
    }

    function _updateAddBtn(atLimit) {
        var addBtn = document.getElementById('mySpotsAddBtn');
        if (!addBtn) return;
        if (atLimit) {
            addBtn.classList.add('myspots-add-btn--at-limit');
        } else {
            addBtn.classList.remove('myspots-add-btn--at-limit');
        }
    }

    // --- Form show/hide ---

    function showAddForm() {
        // Swap add button to close button
        var addBtn = document.getElementById('mySpotsAddBtn');
        if (addBtn) {
            var img = addBtn.querySelector('img');
            if (img) img.src = '/img/close-button.png';
            addBtn.setAttribute('aria-label', 'Close form');
        }

        // Hide private spots table and public list
        var table = document.getElementById('mySpotsTable');
        var wrapper = table && table.closest('.table-responsive');
        if (wrapper) wrapper.style.display = 'none';
        var listView = document.getElementById('mySpotsListView');
        if (listView) listView.style.display = 'none';

        var section = document.getElementById('addSpotSection');
        if (section) {
            section.classList.remove('u-hidden');
            section.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });

            // Initialize mini map on first open
            if (!_miniMapInited) {
                _initMiniMap();
            }

            // Leaflet needs invalidateSize after container becomes visible
            if (_miniMap) {
                setTimeout(function() { _miniMap.invalidateSize(); }, 100);
            }
        }
    }

    function hideAddForm() {
        var section = document.getElementById('addSpotSection');
        if (section) section.classList.add('u-hidden');

        // Restore add button icon
        var addBtn = document.getElementById('mySpotsAddBtn');
        if (addBtn) {
            var img = addBtn.querySelector('img');
            if (img) img.src = '/img/add-button.png';
            addBtn.setAttribute('aria-label', 'Add spot');
        }

        // Restore private spots table and public list
        var table = document.getElementById('mySpotsTable');
        var wrapper = table && table.closest('.table-responsive');
        if (wrapper) wrapper.style.display = '';
        var listView = document.getElementById('mySpotsListView');
        if (listView) listView.style.display = '';

        _resetMiniMap();
    }

    // --- Mini map ---

    function _initMiniMap() {
        if (_miniMapInited) return;
        if (typeof L === 'undefined') return;

        var mapEl = document.getElementById('spotMiniMap');
        if (!mapEl) return;

        _miniMap = L.map('spotMiniMap', {
            zoomControl: true,
            attributionControl: false,
            scrollWheelZoom: true,
            doubleClickZoom: false
        }).setView(CONFIG.MAP_DEFAULTS.center, CONFIG.MAP_DEFAULTS.zoom);

        L.tileLayer(CONFIG.MAP_DEFAULTS.tileLayer, {
            attribution: CONFIG.MAP_DEFAULTS.attribution,
            maxZoom: CONFIG.MAP_DEFAULTS.maxZoom
        }).addTo(_miniMap);

        _miniMap.on('click', function(e) {
            _dropPin(e.latlng.lat, e.latlng.lng);
        });

        _miniMapInited = true;
    }

    function _dropPin(lat, lon) {
        if (!_miniMap) return;

        var latRounded = parseFloat(lat.toFixed(6));
        var lonRounded = parseFloat(lon.toFixed(6));

        // Update lat/lon input fields
        var latEl = document.getElementById('spotLat');
        var lonEl = document.getElementById('spotLon');
        if (latEl) latEl.value = latRounded;
        if (lonEl) lonEl.value = lonRounded;

        // Create or move marker
        if (_miniMarker) {
            _miniMarker.setLatLng([latRounded, lonRounded]);
        } else {
            var pinIcon = L.icon({
                iconUrl: './img/top-pin-45.png',
                iconSize: [45, 45],
                iconAnchor: [22.5, 22.5]
            });

            _miniMarker = L.marker([latRounded, lonRounded], {
                icon: pinIcon,
                draggable: true
            }).addTo(_miniMap);

            _miniMarker.on('dragend', function() {
                var pos = _miniMarker.getLatLng();
                var dLat = parseFloat(pos.lat.toFixed(6));
                var dLon = parseFloat(pos.lng.toFixed(6));
                if (latEl) latEl.value = dLat;
                if (lonEl) lonEl.value = dLon;
                _reverseGeocode(dLat, dLon);
            });
        }

        _miniMap.panTo([latRounded, lonRounded]);
        _reverseGeocode(latRounded, lonRounded);
    }

    function _reverseGeocode(lat, lon) {
        if (_geocodeController) _geocodeController.abort();
        _geocodeController = new AbortController();

        var nameEl = document.getElementById('spotName');
        if (!nameEl) return;

        // Don't overwrite user-typed names
        if (nameEl.value && nameEl.getAttribute('data-auto-filled') !== 'true') return;

        var url = 'https://nominatim.openstreetmap.org/reverse?lat=' + lat + '&lon=' + lon + '&format=json&zoom=14';

        fetch(url, {
            signal: _geocodeController.signal,
            headers: { 'User-Agent': 'ReelTalkFishing/1.0' }
        })
        .then(function(res) { return res.json(); })
        .then(function(data) {
            if (!data || !data.address) return;
            var addr = data.address;
            var primary = addr.road || addr.beach || addr.water || addr.leisure || addr.tourism || '';
            var secondary = addr.suburb || addr.town || addr.city || addr.county || '';
            var name = primary && secondary ? primary + ', ' + secondary
                     : primary || secondary || '';
            if (!name) return;
            nameEl.value = name.substring(0, 100);
            nameEl.setAttribute('data-auto-filled', 'true');
        })
        .catch(function(err) {
            if (err.name === 'AbortError') return;
            // Silent fail — geocoding is a convenience
        });
    }

    function _geolocate() {
        if (_geolocating) return;
        if (!navigator.geolocation) {
            if (typeof showAppToast === 'function') {
                showAppToast('Geolocation is not supported by your browser', 'error');
            }
            return;
        }

        var btn = document.getElementById('spotGeolocateBtn');
        if (btn) btn.disabled = true;
        _geolocating = true;

        getAccurateLocation({ desiredAccuracy: 30, maxWaitMs: 8000 }).then(
            function(position) {
                _geolocating = false;
                if (btn) btn.disabled = false;
                _dropPin(position.coords.latitude, position.coords.longitude);
                if (_miniMap) _miniMap.setView([position.coords.latitude, position.coords.longitude], 15);
                if (position.coords.accuracy > 100 && typeof showAppToast === 'function') {
                    showAppToast('Location may be approximate (~' + Math.round(position.coords.accuracy) + 'm)', 'info');
                }
            },
            function(error) {
                _geolocating = false;
                if (btn) btn.disabled = false;
                if (typeof showAppToast === 'function') {
                    showAppToast(formatGeolocationError(error), 'error');
                }
            }
        );
    }

    function _setupInputSync() {
        var latEl = document.getElementById('spotLat');
        var lonEl = document.getElementById('spotLon');

        function handleInputChange() {
            if (_inputSyncTimer) clearTimeout(_inputSyncTimer);
            _inputSyncTimer = setTimeout(function() {
                var latVal = parseFloat(latEl ? latEl.value : '');
                var lonVal = parseFloat(lonEl ? lonEl.value : '');
                if (isNaN(latVal) || isNaN(lonVal)) return;
                if (latVal < -90 || latVal > 90) return;
                if (lonVal < -180 || lonVal > 180) return;
                _dropPin(latVal, lonVal);
                if (_miniMap) _miniMap.setView([latVal, lonVal], 13);
            }, 800);
        }

        if (latEl) latEl.addEventListener('input', handleInputChange);
        if (lonEl) lonEl.addEventListener('input', handleInputChange);
    }

    function _resetMiniMap() {
        if (_miniMarker && _miniMap) {
            _miniMap.removeLayer(_miniMarker);
            _miniMarker = null;
        }
        if (_miniMap) {
            _miniMap.setView(CONFIG.MAP_DEFAULTS.center, CONFIG.MAP_DEFAULTS.zoom);
        }
        if (_inputSyncTimer) {
            clearTimeout(_inputSyncTimer);
            _inputSyncTimer = null;
        }
        if (_geocodeController) {
            _geocodeController.abort();
            _geocodeController = null;
        }
    }

    // --- Save spot ---

    function saveSpot() {
        var nameEl = document.getElementById('spotName');
        var latEl = document.getElementById('spotLat');
        var lonEl = document.getElementById('spotLon');
        var typeEl = document.getElementById('spotType');
        var waterEl = document.getElementById('spotWaterType');
        var notesEl = document.getElementById('spotNotes');

        var data = {
            name: (nameEl && nameEl.value) || '',
            lat: (latEl && latEl.value) || '',
            lon: (lonEl && lonEl.value) || '',
            locationType: (typeEl && typeEl.value) || '',
            waterType: (waterEl && waterEl.value) || '',
            notes: (notesEl && notesEl.value) || ''
        };

        var validation = validateSpotInput(data);
        if (!validation.valid) {
            if (typeof showAppToast === 'function') {
                showAppToast(validation.errors[0], 'error');
            } else {
                alert(validation.errors.join('\n'));
            }
            return;
        }

        var saveBtn = document.getElementById('saveSpotBtn');
        if (saveBtn) saveBtn.disabled = true;

        AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/spots', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        })
        .then(function(res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.json();
        })
        .then(function(result) {
            if (saveBtn) saveBtn.disabled = false;
            if (result.success) {
                // Clear form fields
                if (nameEl) nameEl.value = '';
                if (latEl) latEl.value = '';
                if (lonEl) lonEl.value = '';
                if (typeEl) typeEl.value = '';
                if (waterEl) waterEl.value = '';
                if (notesEl) notesEl.value = '';

                hideAddForm();

                // Optimistically update limit count so CTA renders correctly immediately
                if (_limitCache) _limitCache.count++;

                // Invalidate cache and re-fetch conditions
                _spotConditionsCache = null;
                loadSpotConditions(true);

                // Invalidate catches dropdown (new spot available)
                if (typeof CATCHES !== 'undefined' && CATCHES.invalidateDropdown) {
                    CATCHES.invalidateDropdown();
                }

                if (typeof showAppToast === 'function') showAppToast('Spot saved!', 'success');
            } else {
                var msg = result.error || 'Failed to save spot';
                if (typeof showAppToast === 'function') {
                    showAppToast(msg, 'error');
                } else {
                    alert(msg);
                }
            }
        })
        .catch(function(err) {
            if (saveBtn) saveBtn.disabled = false;
            console.error('[Spots] Save error:', err.message);
            if (typeof showAppToast === 'function') {
                showAppToast('Failed to save spot', 'error');
            }
        });
    }

    // --- Delete sync (called from main-server.js delete handler) ---

    function handleSpotDeleted(key) {
        // Remove from client cache
        if (_spotConditionsCache) {
            _spotConditionsCache = _spotConditionsCache.filter(function(c) { return c.key !== key; });
        }

        // Optimistically update limit count so CTA renders correctly immediately
        if (_limitCache && _limitCache.count > 0) _limitCache.count--;

        // Remove from shared state (single source of truth for deletion)
        if (window.allConditionsData) {
            window.allConditionsData = window.allConditionsData.filter(function(c) { return c.key !== key; });
        }
        if (typeof tableState !== 'undefined' && tableState.allConditions) {
            tableState.allConditions = window.allConditionsData;
            tableState.detailCache.delete(key);
            tableState.recsCache.delete(key);
        }

        // Remove from My Spots table DOM
        var msLocRow = document.querySelector('#mySpotsTable .location-row[data-location-key="' + key + '"]');
        var msExpRow = document.getElementById('ms-expand-' + key);
        if (msLocRow) msLocRow.remove();
        if (msExpRow) msExpRow.remove();

        // Re-render CTA row with updated limit
        renderMySpotsTable(_spotConditionsCache || []);

        // Reload limit info
        loadLimitInfo();

        // Invalidate catches dropdown
        if (typeof CATCHES !== 'undefined' && CATCHES.invalidateDropdown) {
            CATCHES.invalidateDropdown();
        }
    }

    // --- Limit info ---

    function loadLimitInfo() {
        AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/spots/count')
            .then(function(res) { return res.json(); })
            .then(function(data) {
                if (data.success) {
                    _limitCache = { count: data.count, max: data.max, tier: data.tier };
                    var el = document.getElementById('spotLimitInfo');
                    if (el) {
                        el.textContent = formatLimitDisplay(data.count, data.max, data.tier);
                        el.className = 'spot-limit-counter';
                        if (data.max !== -1 && data.count >= data.max) {
                            el.classList.add('spot-limit-counter--exceeded');
                        }
                    }
                    // Dynamically update CTA row to reflect new limit state
                    _updateCTAState();
                }
            })
            .catch(function(err) {
                console.error('[Spots] Limit info error:', err.message);
            });
    }

    function _updateCTAState() {
        var atLimit = _limitCache && _limitCache.max !== -1 && _limitCache.count >= _limitCache.max;
        _updateAddBtn(atLimit);
    }

    // --- Lookups ---

    /**
     * Find a spot's cached condition item by its 'myspot_{id}' key.
     * Used by the Refresh-on-Demand click handler (main-server.js), which can't reach
     * _spotConditionsCache directly since it's private to this closure.
     */
    function findConditionByKey(key) {
        if (!_spotConditionsCache) return null;
        return _spotConditionsCache.find(function(c) { return c.key === key; }) || null;
    }

    // --- Backward compat for CATCHES dropdown ---

    function getSpots() {
        if (!_spotConditionsCache) return [];
        return _spotConditionsCache.map(function(c) {
            var meta = c._spotMeta || {};
            return {
                id: meta.id || '',
                name: (c.location && c.location.name) || '',
                lat: (c.location && c.location.lat) || null,
                lon: (c.location && c.location.lon) || null,
                key: c.key || (c.location && c.location.key) || null
            };
        });
    }

    // --- Public list render (Angler Map entries shown in My Spots tab) ---

    function renderPublicList(conditions) {
        var table = document.getElementById('mySpotsPublicTable');
        if (!table || !conditions) return;

        // Read current filter state from popup (defaults apply automatically on first render)
        var regionEl = document.getElementById('popupRegionSelect');
        var stateEl = document.getElementById('popupStateSelect');
        var countEl = document.getElementById('popupCountSelect');
        var region = regionEl ? regionEl.value : 'all';
        var state = stateEl ? stateEl.value : 'all';
        // Show count ("Top 3") caps the public rows that pass the region/state filters
        var limit = countEl && countEl.value !== 'all' ? parseInt(countEl.value, 10) : Infinity;
        var shown = 0;

        var html = '<colgroup><col style="width: auto;"></colgroup><tbody>';
        conditions.forEach(function(item) {
            if (shown >= limit) return;
            if (item._isMySpot) return;
            var loc = item.location || {};
            if (region && region !== 'all' && loc.region !== region) return;
            if (state && state !== 'all' && loc.state !== state) return;
            var result = buildLocationRowHTML(item, '');
            html += result.locationRowHTML + result.expandRowHTML;
            shown++;
        });
        html += '</tbody>';
        table.innerHTML = html;
        if (typeof setupLazyBackgrounds === 'function') setupLazyBackgrounds();
        _updateFilterBtn();
    }

    // --- Filtered render (called by map filter apply when My Spots tab is active) ---

    function renderFilteredTable(region, state) {
        // Filter private spots
        if (_spotConditionsCache) {
            var filteredPrivate = _spotConditionsCache.filter(function(item) {
                var loc = item.location || {};
                var regionMatch = !region || region === 'all' || loc.region === region;
                var stateMatch = !state || state === 'all' || loc.state === state;
                return regionMatch && stateMatch;
            });
            renderMySpotsTable(filteredPrivate);
        }

        // Public list reads current filter state from popup automatically
        if (window.allConditionsData) renderPublicList(window.allConditionsData);
    }

    // --- Auth cleanup ---

    function clear() {
        // Don't reset _initialized — event listeners persist across login/logout (DOM elements survive)
        _spotConditionsCache = null;
        _loadingPromise = null;
        _limitCache = null;

        // Remove spot items from allConditionsData
        if (window.allConditionsData) {
            window.allConditionsData = _removeSpotItems(window.allConditionsData);
            if (typeof tableState !== 'undefined') {
                tableState.allConditions = window.allConditionsData;
            }
        }

        // Clear CTA bar and My Spots table
        var ctaBar = document.getElementById('spotCtaBar');
        if (ctaBar) { ctaBar.innerHTML = ''; ctaBar.className = ''; }
        var table = document.getElementById('mySpotsTable');
        if (table) table.innerHTML = '';

        // Clear limit info
        var limitEl = document.getElementById('spotLimitInfo');
        if (limitEl) { limitEl.textContent = ''; limitEl.className = 'spot-limit-counter'; }

        // Hide add form + reset mini map
        hideAddForm();
        _resetMiniMap();

        // Re-render main table without spots
        var mainTable = document.getElementById('comparisonTable');
        if (mainTable && mainTable.innerHTML && typeof displayComparison === 'function' && window.allConditionsData) {
            displayComparison(window.allConditionsData);
        }
    }

    return {
        init: init,
        loadSpotConditions: loadSpotConditions,
        handleSpotDeleted: handleSpotDeleted,
        getSpots: getSpots,
        findConditionByKey: findConditionByKey,
        renderPublicList: renderPublicList,
        renderFilteredTable: renderFilteredTable,
        clear: clear,
        updateFilterBtn: _updateFilterBtn
    };
})();

;
/* === js/species-images.js === */
/**
 * Species watercolor image map — Journal catch card fallback photo.
 * USED BY: js/journal.js
 * EXPORTS: SPECIES_IMAGES global
 */
var SPECIES_IMAGES = (function () {
    // Maps app key → watercolor filename in /img/species/.
    // TO ADD A NEW SPECIES: add a line with the filename.
    var MAP = {
        redfish:             'Red drum in watercolor elegance.webp',
        speckled_trout:      'Speckled trout in soft watercolor tones.webp',
        flounder:            'Realistic flounder watercolor illustration.webp',
        black_drum:          'Black drum fish in watercolor style.webp',
        sheepshead:          'Sheepshead fish in watercolor detail.webp',
        spanish_mackerel:    'Spanish mackerel in watercolor detail.webp',
        pompano:             'Elegant pompano in soft watercolor.webp',
        whiting:             'Whiting fish in soft watercolor wash.webp',
        gafftopsail_catfish: 'Gafftopsail catfish in watercolor detail.webp',
        sand_trout:          'Sand trout in soft watercolor hues.webp',
        // tripletail:       'TODO — add filename when art is ready',
        // jack_crevalle:    'TODO — add filename when art is ready',
    };

    function getUrl(species) {
        var file = MAP[species];
        return file ? '/img/species/' + encodeURIComponent(file) : null;
    }

    return { MAP: MAP, getUrl: getUrl };
})();

;
/* === js/catches.js === */
/**
 * Catches — Log My Catch CRUD (IIFE global)
 *
 * LOADED BY: app.html / app.ejs via bundle (dist/js/app.min.js)
 * USED BY: main-server.js → switchTab('logCatch') lazy-init
 * DEPENDS ON: auth-client.js (AUTH_CLIENT), api-client.js (API_CLIENT),
 *             ui-helpers.js (escapeHtml), ui-builders.js (capitalizeWords),
 *             spots.js (SPOTS), ui-helpers.js (showDeleteConfirmModal)
 * EXPORTS: CATCHES object as global
 */
var CATCHES = (function() {
    // Read tracked species from server-rendered dropdown (R4 — single source of truth)
    var TRACKED_SPECIES = (function() {
        var opts = document.querySelectorAll('#catchSpecies option[value]');
        var list = [];
        opts.forEach(function(opt) {
            if (opt.value && opt.value !== 'other') list.push(opt.value);
        });
        return list.length > 0 ? list : [
            'redfish', 'speckled_trout', 'flounder', 'black_drum',
            'sheepshead', 'spanish_mackerel', 'jack_crevalle', 'pompano',
            'sand_trout', 'whiting', 'tripletail', 'gafftopsail_catfish'
        ];
    })();

    // Short initials for life list dots (display-only)
    var SPECIES_INITIALS = {
        redfish: 'RF', speckled_trout: 'ST', flounder: 'FL', black_drum: 'BD',
        sheepshead: 'SH', spanish_mackerel: 'SM', jack_crevalle: 'JC', pompano: 'PM',
        sand_trout: 'SN', whiting: 'WH', tripletail: 'TT', gafftopsail_catfish: 'GC'
    };

    var _initialized = false;
    var _catches = [];
    var _loading = false;
    var _atLimit = false;
    var _tableHandlerAttached = false;
    var _limitCache = null; // { count, max }
    var _personalBests = [];   // cached PB data from server
    var _pbLoaded = false;     // tracks if PB data has been fetched
    var _gridEl = null;        // cached grid element ref (R8)
    var _selectedFiles = [];  // File objects for photo upload
    var _previewUrls = [];    // Blob URLs for photo preview
    var MAX_PHOTOS = 1;

    // --- Pure functions (testable without DOM) ---

    function validateCatchInput(data) {
        var errors = [];
        if (!data.species || !data.species.trim()) {
            errors.push('Species is required');
        }
        if (data.quantity !== undefined && data.quantity !== null && data.quantity !== '') {
            var qty = parseInt(data.quantity, 10);
            if (isNaN(qty) || qty < 1) {
                errors.push('Quantity must be at least 1');
            }
        }
        if (data.catchDatetime) {
            var dt = new Date(data.catchDatetime);
            if (isNaN(dt.getTime())) {
                errors.push('Invalid date/time');
            } else if (dt > new Date()) {
                errors.push('Date/time cannot be in the future');
            }
        }
        if (data.weightLbs != null && data.weightLbs !== '') {
            var w = parseFloat(data.weightLbs);
            if (isNaN(w) || w <= 0) {
                errors.push('Weight must be greater than 0');
            }
        }
        if (data.lengthInches != null && data.lengthInches !== '') {
            var l = parseFloat(data.lengthInches);
            if (isNaN(l) || l <= 0) {
                errors.push('Length must be greater than 0');
            }
        }
        return { valid: errors.length === 0, errors: errors };
    }

    function findConditionsItem(locationKey) {
        if (!locationKey || !window.allConditionsData) return null;
        for (var i = 0; i < window.allConditionsData.length; i++) {
            if (window.allConditionsData[i].key === locationKey) {
                return window.allConditionsData[i];
            }
        }
        return null;
    }

    function findFactor(factors, name) {
        if (!factors) return null;
        for (var i = 0; i < factors.length; i++) {
            if (factors[i].name === name) return factors[i];
        }
        return null;
    }

    function degreesToCompass(deg) {
        var dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
        return dirs[Math.round(deg / 22.5) % 16] || '';
    }

    function fillConditionsFromLocation(selectEl) {
        var opt = selectEl.options[selectEl.selectedIndex];
        var key = (opt && opt.dataset) ? opt.dataset.key : null;
        var item = findConditionsItem(key);

        var fbiEl = document.getElementById('catchFBI');
        var waterTempEl = document.getElementById('catchWaterTemp');
        var tideEl = document.getElementById('catchTide');
        var windEl = document.getElementById('catchWind');
        var windDirEl = document.getElementById('catchWindDir');
        var barometerEl = document.getElementById('catchBarometer');
        var weatherEl = document.getElementById('catchWeather');

        if (!item || !item.conditions) {
            clearConditionFields();
            return;
        }

        var cond = item.conditions;
        var wd = item.weatherData;
        var ctx = cond.conditionContext;

        // FBI score from conditions.score
        if (fbiEl) fbiEl.value = cond.score != null ? Math.round(cond.score) + '%' : '';

        // Water Temp from conditionContext (numeric °F)
        if (waterTempEl) {
            waterTempEl.value = (ctx && ctx.waterTemp != null) ? Math.round(ctx.waterTemp) + '\u00b0F' : '';
        }

        // Tide from tideMovement (stage + movement)
        if (tideEl) {
            var tm = cond.tideMovement;
            if (tm && tm.stage) {
                var stage = tm.stage.charAt(0).toUpperCase() + tm.stage.slice(1);
                var mvmt = tm.movement ? tm.movement.charAt(0).toUpperCase() + tm.movement.slice(1) : '';
                tideEl.value = mvmt ? stage + ' | ' + mvmt : stage;
            } else {
                tideEl.value = '';
            }
        }

        // Wind from conditionContext (already mph) + weatherData.wind.deg for direction
        if (windEl) {
            windEl.value = (ctx && ctx.windSpeed != null) ? Math.round(ctx.windSpeed) + ' mph' : '';
        }
        if (windDirEl) {
            windDirEl.value = (wd && wd.wind && wd.wind.deg != null) ? degreesToCompass(wd.wind.deg) : '';
        }

        // Pressure from weatherData.main.pressure
        if (barometerEl) {
            barometerEl.value = (wd && wd.main && wd.main.pressure) ? wd.main.pressure + ' mb' : '';
        }

        // Weather from weatherData.weather[0].main
        if (weatherEl) {
            weatherEl.value = (wd && wd.weather && wd.weather[0]) ? wd.weather[0].main : '';
        }
    }

    function clearConditionFields() {
        var ids = ['catchFBI', 'catchWaterTemp', 'catchTide', 'catchWind', 'catchWindDir', 'catchBarometer', 'catchWeather'];
        ids.forEach(function(id) {
            var el = document.getElementById(id);
            if (el) el.value = '';
        });
    }

    function buildConditionsSnapshot(locationKey) {
        // Read from form fields (user may have edited auto-filled values)
        var fbiEl = document.getElementById('catchFBI');
        var waterTempEl = document.getElementById('catchWaterTemp');
        var tideEl = document.getElementById('catchTide');
        var windEl = document.getElementById('catchWind');
        var windDirEl = document.getElementById('catchWindDir');
        var barometerEl = document.getElementById('catchBarometer');
        var weatherEl = document.getElementById('catchWeather');

        var fbi = fbiEl ? parseFloat(fbiEl.value) : null;
        var waterTemp = waterTempEl ? parseFloat(waterTempEl.value) : null;
        var windSpeed = windEl ? parseFloat(windEl.value) : null;
        var pressure = barometerEl ? parseFloat(barometerEl.value) : null;
        var windDirection = windDirEl ? windDirEl.value.trim() : null;
        var tideDescription = tideEl ? tideEl.value.trim() : null;
        var weather = weatherEl ? weatherEl.value.trim() : null;

        // Also grab moonPhase from allConditionsData if available
        var item = findConditionsItem(locationKey);
        var moonRaw = (item && item.conditions) ? (item.conditions.moonPhase || null) : null;
        var moonPhase = moonRaw ? (moonRaw.name || String(moonRaw)) : null;

        // Only return snapshot if at least one field has data
        var hasData = !isNaN(fbi) || !isNaN(waterTemp) || !isNaN(windSpeed) ||
            !isNaN(pressure) || windDirection || tideDescription || weather || moonPhase;
        if (!hasData) return null;

        return {
            fbi: !isNaN(fbi) ? fbi : null,
            waterTemp: !isNaN(waterTemp) ? waterTemp : null,
            windSpeed: !isNaN(windSpeed) ? windSpeed : null,
            windDirection: windDirection || null,
            pressure: !isNaN(pressure) ? pressure : null,
            tideDescription: tideDescription || null,
            weather: weather || null,
            moonPhase: moonPhase
        };
    }

    function groupCatchesByDate(catches) {
        catches = catches || [];
        var groups = {};
        var order = [];

        catches.forEach(function(c) {
            var dateLabel;
            if (!c.catch_datetime || isNaN(new Date(c.catch_datetime).getTime())) {
                dateLabel = 'Unknown Date';
            } else {
                dateLabel = formatDate(c.catch_datetime);
            }

            if (!groups[dateLabel]) {
                groups[dateLabel] = [];
                order.push(dateLabel);
            }
            groups[dateLabel].push(c);
        });

        // Ensure "Unknown Date" is always last (A8)
        var unknownIdx = order.indexOf('Unknown Date');
        if (unknownIdx !== -1) {
            order.splice(unknownIdx, 1);
            order.push('Unknown Date');
        }

        return { groups: groups, order: order };
    }

    function formatSpeciesLabel(species) {
        if (!species) return 'Unknown';
        // capitalizeWords is a global from ui-builders.js (loads before catches.js in bundle)
        return capitalizeWords(species.replace(/_/g, ' '));
    }

    function formatConditionsSnippet(snap) {
        if (!snap) return '';
        var parts = [];
        if (snap.fbi != null) parts.push('FBI ' + snap.fbi + '%');
        if (snap.waterTemp != null) parts.push(snap.waterTemp + '\u00b0F water');
        if (snap.windSpeed != null) {
            var w = snap.windSpeed + ' mph';
            if (snap.windDirection) w += ' ' + snap.windDirection;
            parts.push(w);
        }
        if (snap.pressure != null) parts.push(snap.pressure + ' mb');
        if (snap.tideDescription) parts.push(snap.tideDescription);
        if (snap.weather) parts.push(snap.weather);
        if (snap.moonPhase) {
            var mp = (typeof snap.moonPhase === 'string') ? snap.moonPhase :
                     (snap.moonPhase.name || String(snap.moonPhase));
            parts.push(mp.replace(/_/g, ' '));
        }
        return parts.join(' \u2022 ');
    }

    // --- Photo upload functions ---

    /**
     * Dynamic loader for browser-image-compression (P1+P4).
     * Self-hosted at /js/vendor/, loaded on demand — free users never load the 28KB library.
     */
    var _compressionReady = null;
    function loadImageCompression() {
        if (_compressionReady) return _compressionReady;
        _compressionReady = new Promise(function(resolve, reject) {
            if (window.imageCompression) return resolve();
            var s = document.createElement('script');
            s.src = '/js/vendor/browser-image-compression.min.js';
            s.onload = resolve;
            s.onerror = function() {
                _compressionReady = null; // Allow retry
                reject(new Error('Failed to load image compression library'));
            };
            document.head.appendChild(s);
        });
        return _compressionReady;
    }

    /**
     * Compress a photo file client-side into full (1200px) + thumb (160px) WebP pair.
     * @returns {Promise<{ full: Blob, thumb: Blob }>}
     */
    function compressPhoto(file) {
        return loadImageCompression().then(function() {
            var fullOptions = {
                maxSizeMB: 1,
                maxWidthOrHeight: 1200,
                fileType: 'image/webp',
                useWebWorker: true,
                exifOrientation: true,
                preserveExif: false
            };
            var thumbOptions = {
                maxSizeMB: 0.2,
                maxWidthOrHeight: 600,
                fileType: 'image/webp',
                useWebWorker: true,
                preserveExif: false
            };
            return Promise.all([
                imageCompression(file, fullOptions),
                imageCompression(file, thumbOptions)
            ]).then(function(results) {
                return { full: results[0], thumb: results[1] };
            });
        });
    }

    /**
     * Derive thumbnail URL from full-size URL by convention (P2).
     * Full: catches/{userId}/{catchId}/{uuid}.webp → Thumb: ...{uuid}_thumb.webp
     */
    function getThumbUrl(fullUrl) {
        return fullUrl.replace('.webp', '_thumb.webp');
    }

    function getPbIds() {
        var ids = new Set();
        _personalBests.forEach(function (pb) { if (pb.id) ids.add(pb.id); });
        return ids;
    }

    // Returns a Promise<Set> — fetches PBs if not yet loaded (e.g. user visits journal first)
    function getPbIdsAsync() {
        if (_pbLoaded) return Promise.resolve(getPbIds());
        return AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/catches/personal-bests')
            .then(function (res) { return res.json(); })
            .then(function (data) {
                if (data.success) {
                    _personalBests = data.personalBests || [];
                    _pbLoaded = true;
                }
                return getPbIds();
            })
            .catch(function () { return new Set(); });
    }

    // Returns a Promise<Set> of trip IDs that contain at least one personal-best catch
    function getPbTripIdsAsync() {
        if (_pbLoaded) return Promise.resolve(getPbTripIds());
        return getPbIdsAsync().then(function () { return getPbTripIds(); });
    }

    function getPbTripIds() {
        var ids = new Set();
        _personalBests.forEach(function (pb) { if (pb.trip_id) ids.add(pb.trip_id); });
        return ids;
    }

    /**
     * Render the photo picker area based on user's membership tier.
     * Pro/Elite: gallery + camera buttons. Free: upgrade CTA button.
     */
    function renderPhotoPicker() {
        var target = document.getElementById('catchPhotoPickerTarget');
        if (!target) return;

        var tier = (typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.currentUser)
            ? (AUTH_CLIENT.currentUser.membershipTier || 'free')
            : 'free';

        if (tier === 'pro' || tier === 'elite') {
            target.innerHTML =
                '<div class="photo-picker-btns" id="photoPickerBtns">' +
                    '<button type="button" class="btn-muted photo-add-btn" id="photoAddBtn">+ Gallery</button>' +
                    '<button type="button" class="btn-muted photo-camera-btn" id="photoCameraBtn">Camera</button>' +
                '</div>' +
                '<div class="photo-preview-grid" id="photoPreviewGrid"></div>';

            var addBtn = document.getElementById('photoAddBtn');
            var cameraBtn = document.getElementById('photoCameraBtn');
            var fileInput = document.getElementById('catchPhotoInput');
            if (addBtn && fileInput) {
                addBtn.addEventListener('click', function() {
                    loadImageCompression().catch(function() {});
                    fileInput.click();
                });
            }
            if (cameraBtn) {
                cameraBtn.addEventListener('click', function() {
                    loadImageCompression().catch(function() {});
                    openCameraCapture();
                });
            }
        } else {
            target.innerHTML =
                '<button type="button" class="btn-muted photo-upgrade-btn" disabled>' +
                    'Upgrade to Pro to add catch photos' +
                '</button>';
            var fileInput = document.getElementById('catchPhotoInput');
            if (fileInput) fileInput.style.display = 'none';
        }
    }

    /**
     * Open a camera capture modal (getUserMedia) with a framing overlay.
     * Falls back to native capture input if camera access is denied or unavailable.
     */
    function openCameraCapture() {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            var fallback = document.getElementById('catchCameraInput');
            if (fallback) fallback.click();
            return;
        }

        var modal = document.createElement('div');
        modal.id = 'cameraCaptureModal';
        modal.className = 'camera-modal';
        modal.innerHTML =
            '<div class="camera-modal__viewport">' +
                '<video class="camera-modal__video" autoplay playsinline muted></video>' +
                '<div class="camera-modal__frame">' +
                    '<span class="camera-modal__hint">Frame your catch</span>' +
                '</div>' +
            '</div>' +
            '<div class="camera-modal__actions">' +
                '<button type="button" class="camera-modal__cancel">Cancel</button>' +
                '<button type="button" class="camera-modal__capture">Take Photo</button>' +
            '</div>';
        document.body.appendChild(modal);

        var video = modal.querySelector('.camera-modal__video');
        var stream = null;

        navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
            .then(function(s) {
                stream = s;
                video.srcObject = stream;
            })
            .catch(function() {
                closeModal();
                var fallback = document.getElementById('catchCameraInput');
                if (fallback) fallback.click();
            });

        function closeModal() {
            if (stream) stream.getTracks().forEach(function(t) { t.stop(); });
            if (modal.parentNode) document.body.removeChild(modal);
        }

        modal.querySelector('.camera-modal__cancel').addEventListener('click', closeModal);

        modal.querySelector('.camera-modal__capture').addEventListener('click', function() {
            if (!video.videoWidth) return;
            var frame = modal.querySelector('.camera-modal__frame');
            if (frame) frame.style.visibility = 'hidden';
            requestAnimationFrame(function() {
                var canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                canvas.getContext('2d').drawImage(video, 0, 0);
                canvas.toBlob(function(blob) {
                    if (!blob) return;
                    closeModal();
                    addPhotoFile(new File([blob], 'catch-photo.jpg', { type: 'image/jpeg' }));
                }, 'image/jpeg', 0.92);
            });
        });
    }

    /**
     * Add a single File to the photo queue (shared by gallery input and camera capture).
     */
    function addPhotoFile(file) {
        if (_selectedFiles.length >= MAX_PHOTOS) {
            if (typeof showAppToast === 'function') showAppToast('Maximum 1 photo per catch', 'error');
            return;
        }
        _selectedFiles.push(file);
        _previewUrls.push(URL.createObjectURL(file));
        renderPhotoPreview();
    }

    /**
     * Handle file input change: validate then delegate to addPhotoFile.
     */
    function handlePhotoSelect(e) {
        var files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        var file = files[0]; // max 1 photo per catch

        var allowed = ['image/jpeg', 'image/png', 'image/heic', 'image/webp'];
        if (allowed.indexOf(file.type) === -1) {
            if (typeof showAppToast === 'function') showAppToast('Only JPEG, PNG, HEIC, and WebP files allowed', 'error');
            e.target.value = '';
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            if (typeof showAppToast === 'function') showAppToast('Photo must be under 10MB', 'error');
            e.target.value = '';
            return;
        }

        addPhotoFile(file);
        e.target.value = '';
    }

    /**
     * Render photo preview in the form and hide picker buttons when at limit.
     */
    function renderPhotoPreview() {
        var grid = document.getElementById('photoPreviewGrid');
        if (!grid) return;

        var html = '';
        for (var i = 0; i < _previewUrls.length; i++) {
            html += '<div class="photo-preview-item">' +
                '<img src="' + _previewUrls[i] + '" alt="Preview">' +
                '<button type="button" class="photo-preview-remove" data-photo-idx="' + i + '">&times;</button>' +
            '</div>';
        }
        grid.innerHTML = html;

        var pickerBtns = document.getElementById('photoPickerBtns');
        if (pickerBtns) {
            pickerBtns.style.display = _selectedFiles.length >= MAX_PHOTOS ? 'none' : '';
        }
    }

    /**
     * Remove a photo from the selection by index.
     */
    function removeSelectedPhoto(idx) {
        if (idx < 0 || idx >= _selectedFiles.length) return;
        URL.revokeObjectURL(_previewUrls[idx]);
        _selectedFiles.splice(idx, 1);
        _previewUrls.splice(idx, 1);
        renderPhotoPreview();
    }

    /**
     * Clear all selected photos and revoke blob URLs.
     */
    function clearSelectedPhotos() {
        _previewUrls.forEach(function(url) { URL.revokeObjectURL(url); });
        _selectedFiles = [];
        _previewUrls = [];
        var grid = document.getElementById('photoPreviewGrid');
        if (grid) grid.innerHTML = '';
    }

    /**
     * Upload selected photos to R2 via presigned URLs after catch creation.
     * @param {string} catchId - The newly created catch's UUID
     * @returns {Promise<string[]>} Array of public URLs on success, empty on failure
     */
    function uploadPhotos(catchId) {
        if (_selectedFiles.length === 0) return Promise.resolve([]);

        // Step 1: Compress all photos into full + thumb pairs (P2)
        var compressPromises = _selectedFiles.map(function(f) { return compressPhoto(f); });

        return Promise.all(compressPromises)
            .then(function(pairs) {
                // Step 2: Request presigned URLs (count * 2 for full + thumb)
                return AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/catches/' + catchId + '/photos/presign', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ count: pairs.length * 2 })
                })
                .then(function(res) { return res.json(); })
                .then(function(presignData) {
                    if (!presignData.success || !presignData.uploads) {
                        throw new Error('[presign] ' + (presignData.error || 'Failed to get upload URLs'));
                    }

                    // Step 3: Upload full + thumb pairs to R2
                    // Presigned URLs come in order: [full0, thumb0, full1, thumb1, ...]
                    var uploadPromises = [];
                    for (var i = 0; i < pairs.length; i++) {
                        uploadPromises.push(
                            fetch(presignData.uploads[i * 2].uploadUrl, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'image/webp' },
                                body: pairs[i].full
                            }).then(function(r) {
                                if (!r.ok) throw new Error('[put-full] HTTP ' + r.status);
                                return r;
                            })
                        );
                        uploadPromises.push(
                            fetch(presignData.uploads[i * 2 + 1].uploadUrl, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'image/webp' },
                                body: pairs[i].thumb
                            }).then(function(r) {
                                if (!r.ok) throw new Error('[put-thumb] HTTP ' + r.status);
                                return r;
                            })
                        );
                    }

                    return Promise.all(uploadPromises).then(function() {
                        // Return only the full-size public URLs (odd indices are thumbs)
                        var fullUrls = [];
                        for (var j = 0; j < pairs.length; j++) {
                            fullUrls.push(presignData.uploads[j * 2].publicUrl);
                        }
                        return fullUrls;
                    });
                });
            })
            .then(function(publicUrls) {
                // Step 4: Confirm upload with server (full URLs only)
                return AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/catches/' + catchId + '/photos/confirm', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ urls: publicUrls })
                })
                .then(function(res) { return res.json(); })
                .then(function(confirmData) {
                    if (!confirmData.success) {
                        throw new Error('[confirm] ' + (confirmData.error || 'Failed to confirm upload'));
                    }
                    return publicUrls;
                });
            })
            .catch(function(err) {
                console.error('[Catches] Photo upload failed:', err.message);
                if (typeof showAppToast === 'function') {
                    showAppToast('Photo upload failed: ' + err.message, 'error');
                }
                return [];
            });
    }

    /**
     * Show a full-size photo lightbox overlay.
     */
    function showPhotoLightbox(url) {
        var existing = document.getElementById('photoLightbox');
        if (existing) existing.remove();

        var overlay = document.createElement('div');
        overlay.id = 'photoLightbox';
        overlay.className = 'photo-lightbox';
        overlay.innerHTML =
            '<div class="photo-lightbox__backdrop"></div>' +
            '<img src="' + escapeHtml(url) + '" class="photo-lightbox__img" alt="Catch photo">' +
            '<img src="img/close-button.png" class="photo-lightbox__close" alt="Close">';
        document.body.appendChild(overlay);

        overlay.addEventListener('click', function(e) {
            if (e.target === overlay || e.target.classList.contains('photo-lightbox__backdrop') || e.target.classList.contains('photo-lightbox__close')) {
                overlay.remove();
            }
        });
    }

    // --- HTML Builder functions ---

    function updateCTABar(atLimit) {
        var cta = document.getElementById('catchCTA');
        if (!cta) return;
        if (atLimit) {
            cta.innerHTML = '<div class="cta-content"><span>Daily catch limit reached.</span></div>';
            cta.classList.add('catch-cta-bar--disabled');
        } else {
            cta.innerHTML = '<div class="cta-content">+ <span>Click to log a catch</span></div>';
            cta.classList.remove('catch-cta-bar--disabled');
        }
    }

    function buildDateRowHTML(dateLabel, catches) {
        var totalQty = catches.reduce(function(sum, c) { return sum + (c.quantity || 1); }, 0);
        var speciesSet = {};
        catches.forEach(function(c) { if (c.species) speciesSet[c.species] = true; });
        var speciesList = Object.keys(speciesSet).map(formatSpeciesLabel).join(', ');
        var summary = totalQty + ' fish' + (speciesList ? ' \u00b7 ' + escapeHtml(speciesList) : '');
        var safeId = dateLabel.replace(/[^a-zA-Z0-9]/g, '-');

        return '<tr class="catch-date-row" data-expand-target="catch-expand-' + safeId + '">' +
            '<td><div class="catch-date-header">' +
                '<strong>' + escapeHtml(dateLabel) + '</strong>' +
                '<span class="catch-date-summary">' + escapeHtml(summary) + '</span>' +
            '</div></td>' +
        '</tr>';
    }

    function buildCatchExpandRowHTML(dateLabel, catches) {
        var safeId = dateLabel.replace(/[^a-zA-Z0-9]/g, '-');

        var entriesHtml = catches.map(function(c) {
            var species = escapeHtml(formatSpeciesLabel(c.species));
            var qty = c.quantity > 1 ? ' \u00d7 ' + c.quantity : '';

            // Row 1 details: length, weight, kept/released
            var r1Parts = [];
            if (c.length_inches) r1Parts.push(c.length_inches + ' in');
            if (c.weight_lbs) r1Parts.push(c.weight_lbs + ' lbs');
            if (c.kept_or_released) {
                r1Parts.push(c.kept_or_released.charAt(0).toUpperCase() + c.kept_or_released.slice(1));
            }
            var r1Detail = r1Parts.length > 0
                ? '<span class="catch-row-detail">' + r1Parts.join(' \u2022 ') + '</span>'
                : '';

            // Row 2: Location + conditions snippet
            var r2Parts = [];
            if (c.location_name) r2Parts.push(escapeHtml(c.location_name));
            var snap = c.conditions_snapshot;
            if (snap && typeof snap === 'object') {
                var snippet = formatConditionsSnippet(snap);
                if (snippet) r2Parts.push(escapeHtml(snippet));
            }
            var row2 = r2Parts.length > 0
                ? '<div class="catch-row2">' + r2Parts.join(' \u2022 ') + '</div>'
                : '';

            // Optional: notes on row 3
            var extras = [];
            if (c.notes) extras.push(escapeHtml(c.notes));
            var row3 = extras.length > 0
                ? '<div class="catch-row3">' + extras.join(' \u2022 ') + '</div>'
                : '';

            // Photo thumbnails (P2: use thumb URL for list, full URL for lightbox)
            var photoHtml = '';
            if (c.photo_urls && c.photo_urls.length > 0) {
                photoHtml = '<div class="catch-photos">';
                c.photo_urls.forEach(function(url) {
                    photoHtml += '<img src="' + escapeHtml(getThumbUrl(url)) + '" class="catch-photo-thumb" alt="Catch photo" loading="lazy" data-photo-url="' + escapeHtml(url) + '">';
                });
                photoHtml += '</div>';
            }

            return '<div class="catch-entry">' +
                '<div class="catch-info">' +
                    '<div class="catch-row1">' +
                        '<strong>' + species + qty + '</strong>' + r1Detail +
                    '</div>' +
                    row2 +
                    row3 +
                    photoHtml +
                '</div>' +
                '<button class="catch-delete-btn" data-catch-id="' + c.id + '" data-species-name="' + species + '" title="Delete catch"><img src="img/delete.png" alt="Delete" class="catch-delete-icon"></button>' +
            '</div>';
        }).join('');

        return '<tr class="expanded-row" id="catch-expand-' + safeId + '">' +
            '<td><div class="expanded-row-content">' + entriesHtml + '</div></td>' +
        '</tr>';
    }

    function renderCatchTable(catches) {
        var table = document.getElementById('catchesTable');
        if (!table) return;

        catches = catches || [];  // (A6)
        updateCTABar(_atLimit);

        if (catches.length === 0) {
            table.innerHTML = '';
            return;
        }

        var grouped = groupCatchesByDate(catches);
        var html = '';

        grouped.order.forEach(function(dateLabel) {
            html += buildDateRowHTML(dateLabel, grouped.groups[dateLabel]);
            html += buildCatchExpandRowHTML(dateLabel, grouped.groups[dateLabel]);
        });

        table.innerHTML = html;
    }

    // --- Personal Best builders ---

    function buildLifeListHTML(personalBests) {
        var caughtSpecies = {};
        personalBests.forEach(function(pb) {
            caughtSpecies[pb.species] = true;
        });
        var caughtCount = TRACKED_SPECIES.filter(function(sp) { return caughtSpecies[sp]; }).length;

        var dots = TRACKED_SPECIES.map(function(sp) {
            var initials = SPECIES_INITIALS[sp] || sp.charAt(0).toUpperCase();
            var caught = caughtSpecies[sp];
            var cls = caught ? 'pb-life-list__species--caught' : 'pb-life-list__species--uncaught';
            var title = formatSpeciesLabel(sp) + (caught ? ' (caught)' : '');
            return '<div class="pb-life-list__species ' + cls + '" title="' + escapeHtml(title) + '">' +
                initials + '</div>';
        }).join('');

        return '<div class="pb-life-list__progress">' +
            '<strong>' + caughtCount + '</strong>/' + TRACKED_SPECIES.length + ' species caught' +
        '</div>' +
        '<div class="pb-life-list__grid">' + dots + '</div>';
    }

    function buildPBCardHTML(pb) {
        var species = escapeHtml(formatSpeciesLabel(pb.species));

        // Measurements
        var stats = '';
        if (pb.weight_lbs) {
            stats += '<div class="pb-card__stat">' +
                '<span class="pb-card__stat-value">' + pb.weight_lbs + '</span>' +
                '<span class="pb-card__stat-label">lbs</span>' +
            '</div>';
        }
        if (pb.length_inches) {
            stats += '<div class="pb-card__stat">' +
                '<span class="pb-card__stat-value">' + pb.length_inches + '</span>' +
                '<span class="pb-card__stat-label">inches</span>' +
            '</div>';
        }

        // Meta: date + location
        var date = pb.catch_datetime
            ? formatDate(pb.catch_datetime)
            : '';
        var meta = '';
        if (date) meta += '<span>' + escapeHtml(date) + '</span>';
        if (pb.location_name) meta += '<span>' + escapeHtml(pb.location_name) + '</span>';

        // Conditions: FBI at time of catch (R7 — try-catch for corrupt snapshots)
        var condLine = '';
        if (pb.conditions_snapshot) {
            try {
                var snap = typeof pb.conditions_snapshot === 'string'
                    ? JSON.parse(pb.conditions_snapshot)
                    : pb.conditions_snapshot;
                var condParts = [];
                if (snap.fbi != null) condParts.push('FBI ' + snap.fbi + '%');
                if (snap.waterTemp != null) condParts.push(snap.waterTemp + '\u00b0F');
                if (snap.tideDescription) condParts.push(snap.tideDescription);
                if (condParts.length > 0) {
                    condLine = '<div class="pb-card__conditions">' +
                        escapeHtml(condParts.join(' \u2022 ')) + '</div>';
                }
            } catch (e) {
                console.warn('[Catches] Bad conditions_snapshot for PB:', pb.species, e.message);
            }
        }

        return '<div class="pb-card" data-species="' + escapeHtml(pb.species) + '">' +
            '<div class="pb-card__header">' +
                '<span class="pb-card__species">' + species + '</span>' +
            '</div>' +
            (stats ? '<div class="pb-card__measurements">' + stats + '</div>' : '') +
            '<div class="pb-card__meta">' + meta + '</div>' +
            condLine +
        '</div>';
    }

    function renderPBSection(personalBests) {
        var lifeListEl = document.getElementById('pbLifeList');
        var cardsEl = document.getElementById('pbCards');

        if (lifeListEl) {
            lifeListEl.innerHTML = buildLifeListHTML(personalBests);
        }

        if (cardsEl) {
            if (personalBests.length === 0) {
                cardsEl.innerHTML = '<div class="pb-empty">' +
                    '<div class="pb-empty__icon">\uD83C\uDFA3</div>' +
                    '<p>No personal bests yet.<br>Log a catch with weight or length to start tracking!</p>' +
                '</div>';
                return;
            }

            var html = personalBests.map(buildPBCardHTML).join('');
            cardsEl.innerHTML = html;
        }
    }

    function loadPersonalBests() {
        AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/catches/personal-bests')
            .then(function(res) { return res.json(); })
            .then(function(data) {
                if (data.success) {
                    _personalBests = data.personalBests || [];
                    _pbLoaded = true;
                    renderPBSection(_personalBests);
                }
            })
            .catch(function(err) {
                console.error('[Catches] PB load error:', err.message);
                if (typeof showAppToast === 'function') {
                    showAppToast('Failed to load personal bests', 'error');
                }
            });
    }

    // --- Show/Hide Form ---

    function showAddForm() {
        // Clear any leftover photo selection from a previous open/cancel cycle
        clearSelectedPhotos();
        // Rebuild dropdown each time to pick up latest spots + conditions data
        populateLocationDropdown();
        renderPhotoPicker();

        var endFormWrap = document.getElementById('journalEndFormWrap');
        if (endFormWrap) endFormWrap.style.display = 'none';

        var section = document.getElementById('logCatchSection');
        if (section) {
            section.style.display = '';
            section.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
        }
    }

    function hideAddForm() {
        var section = document.getElementById('logCatchSection');
        if (section) section.style.display = 'none';
    }

    // --- DOM / API functions ---

    function init() {
        if (_initialized) return;
        _initialized = true;

        // Cache grid element ref (R8)
        _gridEl = document.querySelector('.catch-log-grid');

        // --- Event delegation on stable parent (A3) ---
        var tabContainer = document.getElementById('tab-logCatch');
        if (tabContainer && !_tableHandlerAttached) {
            _tableHandlerAttached = true;

            // CTA bar click → showAddForm
            var ctaBar = document.getElementById('catchCTA');
            if (ctaBar) {
                ctaBar.addEventListener('click', function() {
                    if (!ctaBar.classList.contains('catch-cta-bar--disabled')) {
                        showAddForm();
                    }
                });
            }

            tabContainer.addEventListener('click', function(e) {
                // Photo thumbnail click → lightbox
                var photoThumb = e.target.closest('.catch-photo-thumb');
                if (photoThumb) {
                    e.stopPropagation();
                    var photoUrl = photoThumb.getAttribute('data-photo-url');
                    if (photoUrl) showPhotoLightbox(photoUrl);
                    return;
                }

                // Photo preview remove button
                var removeBtn = e.target.closest('.photo-preview-remove');
                if (removeBtn) {
                    e.stopPropagation();
                    var idx = parseInt(removeBtn.getAttribute('data-photo-idx'), 10);
                    removeSelectedPhoto(idx);
                    return;
                }

                // Date row click → expand/collapse
                var dateRow = e.target.closest('.catch-date-row');
                if (dateRow) {
                    var targetId = dateRow.getAttribute('data-expand-target');
                    var expandRow = document.getElementById(targetId);
                    if (expandRow) expandRow.classList.toggle('show');
                    return;
                }

                // Delete button → modal delete (with stopPropagation to prevent row collapse)
                var deleteBtn = e.target.closest('.catch-delete-btn');
                if (deleteBtn && deleteBtn.closest('#catchesTable')) {
                    e.stopPropagation();
                    var catchId = deleteBtn.getAttribute('data-catch-id');
                    var speciesName = deleteBtn.getAttribute('data-species-name') || 'this catch';
                    deleteCatch(catchId, speciesName);
                    return;
                }

                // Conditions toggle (stopPropagation to prevent row collapse)
                var condToggle = e.target.closest('.catch-conditions__toggle');
                if (condToggle) {
                    e.stopPropagation();
                    var body = condToggle.nextElementSibling;
                    if (body) body.classList.toggle('catch-conditions__body--open');
                    return;
                }
            });
        }

        // Cancel button on form
        var cancelBtn = document.getElementById('logCatchCancelBtn');
        if (cancelBtn) {
            cancelBtn.addEventListener('click', function() { hideAddForm(); });
        }

        // Log catch button
        var logBtn = document.getElementById('logCatchBtn');
        if (logBtn) {
            logBtn.addEventListener('click', function() { logCatch(); });
        }


        // Auto-fill conditions when location selected
        var locationSelect = document.getElementById('catchLocation');
        if (locationSelect) {
            locationSelect.addEventListener('change', function() {
                fillConditionsFromLocation(locationSelect);
            });
        }

        // Photo file input handlers
        var photoInput = document.getElementById('catchPhotoInput');
        if (photoInput) {
            photoInput.addEventListener('change', handlePhotoSelect);
        }
        var cameraInput = document.getElementById('catchCameraInput');
        if (cameraInput) {
            cameraInput.addEventListener('change', handlePhotoSelect);
        }

        // Log Catch button (in logbook active banner) — gate on active trip
        var logbookLogCatchBtn = document.getElementById('logbookLogCatchBtn');
        if (logbookLogCatchBtn) {
            logbookLogCatchBtn.addEventListener('click', function () {
                var tripId = (typeof LOGBOOK_STATE !== 'undefined') ? LOGBOOK_STATE.activeTripId : null;
                if (!tripId) {
                    if (typeof showAppToast === 'function') showAppToast('Start a trip first to log catches', 'error');
                    return;
                }
                showAddForm();
            });
        }

        populateLocationDropdown();
        renderPhotoPicker();
        loadCatches();
        loadLimitInfo();
    }

    function setDefaultDateTime() {
        var dateEl = document.getElementById('catchDate');
        var timeEl = document.getElementById('catchTime');
        var now = new Date();
        if (dateEl && !dateEl.value) {
            var yyyy = now.getFullYear();
            var mm = String(now.getMonth() + 1).padStart(2, '0');
            var dd = String(now.getDate()).padStart(2, '0');
            dateEl.value = yyyy + '-' + mm + '-' + dd;
        }
        if (timeEl && !timeEl.value) {
            timeEl.value = now.toTimeString().slice(0, 5);
        }
    }

    function populateLocationDropdown() {
        var select = document.getElementById('catchLocation');
        if (!select) return;

        // Always rebuild to pick up newly added spots — remove optgroups + options
        var firstOption = select.options[0]; // "Select location…"
        select.innerHTML = '';
        select.appendChild(firstOption);

        // Public locations from allConditionsData
        if (window.allConditionsData && window.allConditionsData.length > 0) {
            var publicGroup = document.createElement('optgroup');
            publicGroup.label = 'Public Spots';
            window.allConditionsData.forEach(function(item) {
                if (!item.location) return;
                var opt = document.createElement('option');
                opt.value = item.location.name;
                opt.textContent = item.location.name;
                opt.dataset.key = item.key;
                publicGroup.appendChild(opt);
            });
            select.appendChild(publicGroup);
        }

        // User's saved spots
        var savedSpots = (typeof SPOTS !== 'undefined') ? SPOTS.getSpots() : [];
        if (savedSpots.length > 0) {
            var myGroup = document.createElement('optgroup');
            myGroup.label = 'My Spots';
            savedSpots.forEach(function(spot) {
                var opt = document.createElement('option');
                opt.value = spot.name;
                opt.textContent = spot.name;
                opt.dataset.savedLocationId = spot.id;
                if (spot.key) opt.dataset.key = spot.key;
                if (spot.lat) opt.dataset.lat = spot.lat;
                if (spot.lon) opt.dataset.lon = spot.lon;
                myGroup.appendChild(opt);
            });
            select.appendChild(myGroup);
        }

    }

    function invalidateDropdown() {
        // kept for API compat — populateLocationDropdown always rebuilds now
    }

    function loadCatches() {
        if (_loading) return;
        _loading = true;

        AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/catches')
            .then(function(res) { return res.json(); })
            .then(function(data) {
                _loading = false;
                if (data.success) {
                    _catches = data.catches || [];
                    renderCatchTable(_catches);
                }
            })
            .catch(function(err) {
                _loading = false;
                console.error('[Catches] Load error:', err.message);
            });
    }

    function logCatch() {
        var speciesEl = document.getElementById('catchSpecies');
        var lengthEl = document.getElementById('catchLength');
        var weightEl = document.getElementById('catchWeight');
        var notesEl = document.getElementById('catchNotes');

        // Pull location + datetime from active trip
        var activeTrip = (typeof JOURNAL !== 'undefined') ? JOURNAL.getActiveTrip() : null;
        var locationName = activeTrip ? activeTrip.location_name : '';
        var locationKey = activeTrip ? activeTrip.location_key : null;
        // Fallback: read location key from the dropdown for standalone (non-trip) catches
        if (!locationKey) {
            var locSel = document.getElementById('catchLocation');
            var selOpt = locSel && locSel.options[locSel.selectedIndex];
            locationKey = (selOpt && selOpt.dataset.key) ? selOpt.dataset.key : null;
        }
        var catchDatetime = new Date().toISOString();

        var data = {
            species: speciesEl ? speciesEl.value : '',
            locationName: locationName,
            savedLocationId: null,
            lat: null,
            lon: null,
            locationKey: locationKey,
            catchDatetime: catchDatetime,
            quantity: '1',
            keptOrReleased: 'released',
            lengthInches: lengthEl && lengthEl.value ? lengthEl.value : null,
            weightLbs: weightEl && weightEl.value ? weightEl.value : null,
            notes: notesEl ? notesEl.value.trim() : '',
            tripId: (function() {
                var el = document.getElementById('catchTripId');
                return el && el.value ? el.value : null;
            })()
        };

        var validation = validateCatchInput(data);
        if (!validation.valid) {
            if (typeof showAppToast === 'function') {
                showAppToast(validation.errors[0], 'error');
            } else {
                alert(validation.errors.join('\n'));
            }
            return;
        }

        var logBtn = document.getElementById('logCatchBtn');
        if (logBtn) logBtn.disabled = true;

        AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/catches', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        })
        .then(function(res) { return res.json(); })
        .then(function(result) {
            if (logBtn) logBtn.disabled = false;
            if (result.success) {
                // Reset form
                if (speciesEl) speciesEl.value = '';
                if (lengthEl) lengthEl.value = '';
                if (weightEl) weightEl.value = '';
                if (notesEl) notesEl.value = '';

                // Optimistic update (A13)
                if (result.catch) {
                    _catches.unshift(result.catch);
                    renderCatchTable(_catches);
                } else {
                    loadCatches();  // fallback: full re-fetch if response shape unexpected
                }
                loadLimitInfo();
                hideAddForm();

                // Invalidate analytics so next visit re-fetches fresh data
                if (typeof ANALYTICS !== 'undefined') ANALYTICS.clear();

                // Upload photos if any were selected (Pro/Elite only)
                if (_selectedFiles.length > 0 && result.catch && result.catch.id) {
                    uploadPhotos(result.catch.id).then(function(urls) {
                        if (urls.length > 0) {
                            // Update in-memory catch with photo URLs
                            for (var i = 0; i < _catches.length; i++) {
                                if (_catches[i].id === result.catch.id) {
                                    _catches[i].photo_urls = (_catches[i].photo_urls || []).concat(urls);
                                    break;
                                }
                            }
                            renderCatchTable(_catches);
                        }
                        clearSelectedPhotos();
                    });
                } else {
                    clearSelectedPhotos();
                }

                // PB celebration toast + refresh (3H)
                if (result.catch && result.catch.is_personal_best) {
                    if (typeof showAppToast === 'function') {
                        showAppToast('New Personal Best! ' + formatSpeciesLabel(result.catch.species), 'success');
                    }
                    loadPersonalBests();
                } else if (typeof showAppToast === 'function') {
                    showAppToast('Catch logged!', 'success');
                }
            } else {
                var msg = result.error || 'Failed to log catch';
                if (typeof showAppToast === 'function') {
                    showAppToast(msg, 'error');
                } else {
                    alert(msg);
                }
            }
        })
        .catch(function(err) {
            if (logBtn) logBtn.disabled = false;
            console.error('[Catches] Log error:', err.message);
            if (typeof showAppToast === 'function') {
                showAppToast('Failed to log catch', 'error');
            }
        });
    }

    /**
     * Update a date row's summary after an entry is removed in-place.
     * Reads from the already-updated _catches array.
     */
    function updateDateRowSummary(expandRow) {
        if (!expandRow) return;
        var dateRow = document.querySelector('[data-expand-target="' + expandRow.id + '"]');
        if (!dateRow) return;

        var dateStrong = dateRow.querySelector('strong');
        if (!dateStrong) return;
        var dateLabelText = dateStrong.textContent;

        var grouped = groupCatchesByDate(_catches);
        var groupCatches = grouped.groups[dateLabelText];
        if (!groupCatches || groupCatches.length === 0) return;

        var totalQty = groupCatches.reduce(function(sum, c) { return sum + (c.quantity || 1); }, 0);
        var speciesSet = {};
        groupCatches.forEach(function(c) { if (c.species) speciesSet[c.species] = true; });
        var speciesList = Object.keys(speciesSet).map(formatSpeciesLabel).join(', ');
        var summary = totalQty + ' fish' + (speciesList ? ' \u00b7 ' + speciesList : '');

        var summaryEl = dateRow.querySelector('.catch-date-summary');
        if (summaryEl) summaryEl.textContent = summary;
    }

    function deleteCatch(id, speciesName) {
        var doDelete = function() {
            AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/catches/' + id, { method: 'DELETE' })
                .then(function(res) { return res.json(); })
                .then(function(result) {
                    if (result.success) {
                        // Remove from _catches array
                        _catches = _catches.filter(function(c) { return String(c.id) !== String(id); });

                        // In-place DOM update: remove entry without collapsing the expand row
                        var entryBtn = document.querySelector('.catch-delete-btn[data-catch-id="' + id + '"]');
                        var entryEl = entryBtn ? entryBtn.closest('.catch-entry') : null;
                        var expandContent = entryEl ? entryEl.closest('.expanded-row-content') : null;
                        var expandRow = expandContent ? expandContent.closest('.expanded-row') : null;

                        if (entryEl && expandContent) {
                            entryEl.remove();

                            // If date group is now empty, remove header + expand rows
                            var remaining = expandContent.querySelectorAll('.catch-entry');
                            if (remaining.length === 0) {
                                var dateRow = expandRow ? document.querySelector('[data-expand-target="' + expandRow.id + '"]') : null;
                                if (dateRow) dateRow.remove();
                                if (expandRow) expandRow.remove();
                            } else {
                                updateDateRowSummary(expandRow);
                            }
                        } else {
                            // Fallback: full re-render if DOM lookup fails
                            renderCatchTable(_catches);
                        }

                        loadLimitInfo();
                        updateCTABar(_atLimit);
                        loadPersonalBests();
                        if (typeof showAppToast === 'function') showAppToast('Catch deleted', 'success');
                    } else {
                        if (typeof showAppToast === 'function') {
                            showAppToast(result.error || 'Failed to delete catch', 'error');
                        }
                    }
                })
                .catch(function(err) {
                    console.error('[Catches] Delete error:', err.message);
                    if (typeof showAppToast === 'function') {
                        showAppToast('Failed to delete catch', 'error');
                    }
                });
        };

        // (A2) Guard with typeof check, fallback to confirm()
        if (typeof showDeleteConfirmModal === 'function') {
            showDeleteConfirmModal(speciesName, doDelete);
        } else if (confirm('Delete ' + speciesName + '?')) {
            doDelete();
        }
    }

    function loadLimitInfo() {
        AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/catches/daily-usage')
            .then(function(res) { return res.json(); })
            .then(function(data) {
                if (data.success) {
                    var max = data.max;
                    var count = data.count;
                    _limitCache = { count: count, max: max };

                    var el = document.getElementById('catchLimitInfo');
                    if (el) {
                        if (max === -1) {
                            el.textContent = count + ' logged today';
                        } else {
                            el.textContent = count + '/' + max + ' daily log used';
                        }
                        el.className = 'spot-limit-counter';
                        if (max !== -1 && count >= max) {
                            el.classList.add('spot-limit-counter--exceeded');
                            _atLimit = true;
                        } else {
                            if (max !== -1 && count >= max - 1) {
                                el.classList.add('spot-limit-counter--warning');
                            }
                            _atLimit = false;
                        }
                    }
                    updateCTABar(_atLimit);
                }
            })
            .catch(function(err) {
                console.error('[Catches] Limit info error:', err.message);
            });
    }

    function clear() {
        // Do NOT reset _initialized — event listeners on #tab-logCatch survive (A4)
        _catches = [];
        _loading = false;
        _limitCache = null;
        _atLimit = false;
        _personalBests = [];
        _pbLoaded = false;
        _gridEl = null;

        var table = document.getElementById('catchesTable');
        if (table) table.innerHTML = '';

        var lifeListEl = document.getElementById('pbLifeList');
        if (lifeListEl) lifeListEl.innerHTML = '';
        var cardsEl = document.getElementById('pbCards');
        if (cardsEl) cardsEl.innerHTML = '';

        hideAddForm();
        clearSelectedPhotos();

        var limitEl = document.getElementById('catchLimitInfo');
        if (limitEl) { limitEl.textContent = ''; limitEl.className = 'spot-limit-counter'; }
    }

    return {
        init: init,
        openForm: showAddForm,
        loadCatches: loadCatches,
        logCatch: logCatch,
        deleteCatch: deleteCatch,
        populateLocationDropdown: populateLocationDropdown,
        invalidateDropdown: invalidateDropdown,
        clear: clear,
        buildConditionsSnapshot: buildConditionsSnapshot,
        validateCatchInput: validateCatchInput,
        formatConditionsSnippet: formatConditionsSnippet,
        groupCatchesByDate: groupCatchesByDate,
        formatSpeciesLabel: formatSpeciesLabel,
        updateCTABar: updateCTABar,
        buildDateRowHTML: buildDateRowHTML,
        buildCatchExpandRowHTML: buildCatchExpandRowHTML,
        renderCatchTable: renderCatchTable,
        buildLifeListHTML: buildLifeListHTML,
        buildPBCardHTML: buildPBCardHTML,
        renderPBSection: renderPBSection,
        loadPersonalBests: loadPersonalBests,
        TRACKED_SPECIES: TRACKED_SPECIES,
        SPECIES_INITIALS: SPECIES_INITIALS,
        getThumbUrl: getThumbUrl,
        getPbIds: getPbIds,
        getPbIdsAsync: getPbIdsAsync,
        getPbTripIds: getPbTripIds,
        getPbTripIdsAsync: getPbTripIdsAsync,
        showPhotoLightbox: showPhotoLightbox
    };
})();

;
/* === js/journal.js === */
/**
 * Journal Tab — Trip log IIFE (admin-only)
 *
 * USED BY: main-server.js (switchTab lazy-init)
 * USES:    AUTH_CLIENT.fetchWithAuth, API_CLIENT.baseURL, allConditionsData, SPOTS
 * EXPORTS: window.JOURNAL { init, getActiveTrip, getActiveTripId }
 */
var JOURNAL = (function () {
    'use strict';

    var _initialized = false;
    var _activeTrip = null;
    var _trips = [];
    var _loading = false;
    var _nearestLocationCache = null; // session memo — cleared on page reload

    // --- Init ---

    function init() {
        if (_initialized) return;
        _initialized = true;
        attachEventListeners();
        loadActiveTrip();
        loadTrips();
    }

    function attachEventListeners() {
        var startBtn = document.getElementById('journalStartBtn');
        if (startBtn) startBtn.addEventListener('click', startTrip);

        var endBtn = document.getElementById('journalEndBtn');
        if (endBtn) endBtn.addEventListener('click', endTrip);

        var cancelEndBtn = document.getElementById('journalCancelEndBtn');
        if (cancelEndBtn) cancelEndBtn.addEventListener('click', function () {
            toggleEndForm(false);
        });

        // Logbook CTA (no active trip) → show start form
        var ctaEl = document.getElementById('logbookNoTripCTA');
        if (ctaEl) ctaEl.addEventListener('click', showStartTripForm);

        // Cancel start trip form
        var cancelStartBtn = document.getElementById('logbookCancelStartBtn');
        if (cancelStartBtn) cancelStartBtn.addEventListener('click', function () {
            var startForm = document.getElementById('logbookStartTripSection');
            if (startForm) startForm.style.display = 'none';
            var ctaEl = document.getElementById('logbookNoTripCTA');
            if (ctaEl) ctaEl.style.display = '';
        });

        // End trip button (in active banner)
        var endTripBtn = document.getElementById('logbookEndTripBtn');
        if (endTripBtn) endTripBtn.addEventListener('click', function () {
            toggleEndForm(true);
        });

        // Event delegation for trip history: expand/delete/retry
        var historyList = document.getElementById('journalHistoryList');
        if (historyList) {
            historyList.addEventListener('click', function (e) {
                var deleteBtn = e.target.closest('.journal-trip__delete');
                if (deleteBtn) {
                    var tripId = deleteBtn.dataset.tripId;
                    if (tripId) confirmDeleteTrip(tripId);
                    return;
                }
                var summaryEl = e.target.closest('.journal-trip__summary');
                if (summaryEl) {
                    var tripEl = summaryEl.closest('.journal-trip');
                    if (tripEl) toggleTripExpand(tripEl);
                    return;
                }
                var photoWrap = e.target.closest('.journal-catch-card__photo-wrap');
                if (photoWrap && photoWrap.dataset.photoUrl) {
                    CATCHES.showPhotoLightbox(photoWrap.dataset.photoUrl);
                    return;
                }
                var retryBtn = e.target.closest('.rec-error__retry-btn');
                if (retryBtn) {
                    var retryTripId = retryBtn.dataset.tripId;
                    var retryTripEl = retryBtn.closest('.journal-trip');
                    if (!retryTripId || !retryTripEl) return;
                    var detail = retryTripEl.querySelector('.journal-trip__detail');
                    if (!detail) return;
                    retryTripEl.dataset.catchesLoaded = '';
                    loadTripCatches(retryTripId, detail);
                }
            });
        }
    }

    // --- API Helpers ---

    function apiGet(path) {
        return AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/journal' + path)
            .then(function (res) { return res.json(); });
    }

    function apiPost(path, body) {
        return AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/journal' + path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        }).then(function (res) { return res.json(); });
    }

    function apiPut(path, body) {
        return AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/journal' + path, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        }).then(function (res) { return res.json(); });
    }

    function apiDelete(path) {
        return AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/journal' + path, {
            method: 'DELETE'
        }).then(function (res) { return res.json(); });
    }

    // --- Active Trip ---

    function loadActiveTrip() {
        apiGet('/active')
            .then(function (data) {
                if (data.success) {
                    _activeTrip = data.trip;
                    renderActiveTripBanner();
                    updateCatchFormTripLink();
                }
            })
            .catch(function (err) {
                console.error('[Journal] Failed to load active trip:', err.message);
            });
    }

    function renderActiveTripBanner() {
        var banner = document.getElementById('journalActiveBanner');
        var ctaEl = document.getElementById('logbookNoTripCTA');
        var startForm = document.getElementById('logbookStartTripSection');
        var endFormWrap = document.getElementById('journalEndFormWrap');

        if (!_activeTrip) {
            if (banner) banner.style.display = 'none';
            if (ctaEl) ctaEl.style.display = '';
            if (startForm) startForm.style.display = 'none';
            if (endFormWrap) endFormWrap.style.display = 'none';
            return;
        }

        if (banner) {
            banner.style.display = '';
            var nameEl = banner.querySelector('.journal-active__location');
            var timeEl = banner.querySelector('.journal-active__time');
            var catchesEl = banner.querySelector('.journal-active__catches');

            if (nameEl) nameEl.textContent = _activeTrip.location_name;
            if (timeEl) timeEl.textContent = formatElapsed(_activeTrip.start_time);
            if (catchesEl) {
                var count = _activeTrip.catch_count || 0;
                catchesEl.textContent = count + ' catch' + (count !== 1 ? 'es' : '');
            }

            // Update elapsed time every 30s
            if (banner._timerInterval) clearInterval(banner._timerInterval);
            banner._timerInterval = setInterval(function () {
                if (timeEl && _activeTrip) {
                    timeEl.textContent = formatElapsed(_activeTrip.start_time);
                }
            }, 30000);
        }

        if (ctaEl) ctaEl.style.display = 'none';
        if (startForm) startForm.style.display = 'none';
        if (endFormWrap) endFormWrap.style.display = 'none';

        // Sync shared state so CATCHES gate works after a page refresh
        if (typeof LOGBOOK_STATE !== 'undefined') {
            LOGBOOK_STATE.activeTripId = _activeTrip ? _activeTrip.id : null;
        }
    }

    function formatElapsed(startTimeStr) {
        var start = new Date(startTimeStr);
        var diff = Date.now() - start.getTime();
        if (diff < 0) return '0m';
        var hours = Math.floor(diff / 3600000);
        var mins = Math.floor((diff % 3600000) / 60000);
        return hours > 0 ? hours + 'h ' + mins + 'm' : mins + 'm';
    }

    // --- Start Trip Form ---

    function showStartTripForm() {
        var ctaEl = document.getElementById('logbookNoTripCTA');
        if (ctaEl) ctaEl.style.display = 'none';
        var startForm = document.getElementById('logbookStartTripSection');
        if (startForm) startForm.style.display = '';

        // Prefill current local datetime
        var now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        var el = document.getElementById('journalStartTime');
        if (el) el.value = now.toISOString().slice(0, 16);

        loadNearestLocation();
    }

    function loadNearestLocation() {
        if (!navigator.geolocation) return;

        if (_nearestLocationCache !== null) {
            applyNearestLocation(_nearestLocationCache);
            return;
        }

        getAccurateLocation({ maxWaitMs: 3000 }).then(function (pos) {
            var lat = pos.coords.latitude;
            var lon = pos.coords.longitude;
            AUTH_CLIENT.fetchWithAuth(
                API_CLIENT.baseURL + '/catches/nearest-location?lat=' + lat + '&lon=' + lon
            )
            .then(function (res) { return res.json(); })
            .then(function (data) {
                if (!data.success) return;
                _nearestLocationCache = data.location; // cache even if null
                applyNearestLocation(data.location);
            })
            .catch(function () {}); // silent fail — user can pick manually
        }, function () {}); // geolocation denied/timed out — silent fail
    }

    function applyNearestLocation(location) {
        if (!location) return;
        var select = document.getElementById('journalLocation');
        if (!select) return;

        // Match by dataset.key for public spots, by value (name) for private spots
        for (var i = 0; i < select.options.length; i++) {
            var opt = select.options[i];
            if (location.type === 'public') {
                if (opt.dataset.key === location.key) {
                    select.selectedIndex = i;
                    break;
                }
            } else {
                if (opt.value === location.name) {
                    select.selectedIndex = i;
                    break;
                }
            }
        }
    }

    // --- Start Trip ---

    function startTrip() {
        var locationEl = document.getElementById('journalLocation');
        var startTimeEl = document.getElementById('journalStartTime');

        if (!locationEl || !locationEl.value) {
            showToast('Select a location', 'error');
            return;
        }

        var selectedOption = locationEl.options[locationEl.selectedIndex];
        var locationKey = selectedOption.dataset.key || null;
        var lat = selectedOption.dataset.lat || null;
        var lon = selectedOption.dataset.lon || null;

        var body = {
            locationName: locationEl.value,
            locationKey: locationKey,
            lat: lat,
            lon: lon,
            startTime: startTimeEl && startTimeEl.value
                ? new Date(startTimeEl.value).toISOString()
                : new Date().toISOString()
        };

        var startBtn = document.getElementById('journalStartBtn');
        if (startBtn) startBtn.disabled = true;

        apiPost('', body)
            .then(function (data) {
                if (startBtn) startBtn.disabled = false;
                if (data.success) {
                    _activeTrip = data.trip;
                    if (typeof LOGBOOK_STATE !== 'undefined') LOGBOOK_STATE.activeTripId = data.trip.id;
                    renderActiveTripBanner();
                    updateCatchFormTripLink();
                    showToast('Trip started', 'success');
                    // Reset start form
                    if (locationEl) locationEl.value = '';
                    if (startTimeEl) startTimeEl.value = '';
                } else {
                    showToast(data.error || 'Failed to start trip', 'error');
                }
            })
            .catch(function (err) {
                if (startBtn) startBtn.disabled = false;
                showToast('Failed to start trip', 'error');
                console.error('[Journal] Start trip error:', err.message);
            });
    }

    // --- End Trip ---

    function toggleEndForm(show) {
        var endFormWrap = document.getElementById('journalEndFormWrap');
        if (endFormWrap) endFormWrap.style.display = show ? '' : 'none';
        if (show) {
            var catchSection = document.getElementById('logCatchSection');
            if (catchSection) catchSection.style.display = 'none';
        }
    }

    function endTrip() {
        if (!_activeTrip) return;

        var notesEl = document.getElementById('journalNotes');

        var body = {
            notes: notesEl ? notesEl.value.trim() || null : null
        };

        var endBtn = document.getElementById('journalEndBtn');
        if (endBtn) endBtn.disabled = true;

        apiPut('/' + _activeTrip.id + '/end', body)
            .then(function (data) {
                if (endBtn) endBtn.disabled = false;
                if (data.success) {
                    _activeTrip = null;
                    if (typeof LOGBOOK_STATE !== 'undefined') LOGBOOK_STATE.activeTripId = null;
                    renderActiveTripBanner();
                    updateCatchFormTripLink();
                    resetEndForm();
                    loadTrips();
                    showToast('Trip ended', 'success');
                } else {
                    showToast(data.error || 'Failed to end trip', 'error');
                }
            })
            .catch(function (err) {
                if (endBtn) endBtn.disabled = false;
                showToast('Failed to end trip', 'error');
                console.error('[Journal] End trip error:', err.message);
            });
    }

    function resetEndForm() {
        var ids = ['journalNotes'];
        ids.forEach(function (id) {
            var el = document.getElementById(id);
            if (el) el.value = '';
        });
        toggleEndForm(false);
    }

    // --- Trip History ---

    function loadTrips() {
        if (_loading) return;
        _loading = true;

        apiGet('?limit=50')
            .then(function (data) {
                _loading = false;
                if (data.success) {
                    _trips = data.trips || [];
                    renderTripHistory();
                }
            })
            .catch(function (err) {
                _loading = false;
                console.error('[Journal] Failed to load trips:', err.message);
            });
    }

    function renderTripHistory() {
        var container = document.getElementById('journalHistoryList');
        if (!container) return;

        if (_trips.length === 0) {
            container.innerHTML = '<p class="journal-empty">No trips logged yet. Start your first trip above.</p>';
            return;
        }

        // Group by month
        var groups = {};
        _trips.forEach(function (trip) {
            var d = new Date(trip.start_time);
            var key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
            var label = formatDate(d, { year: 'numeric', month: 'long' });
            if (!groups[key]) groups[key] = { label: label, trips: [] };
            groups[key].trips.push(trip);
        });

        var html = '';
        Object.keys(groups).sort().reverse().forEach(function (key) {
            var group = groups[key];
            html += '<div class="journal-month">';
            html += '<h4 class="journal-month__label">' + escapeHtml(group.label) + '</h4>';
            group.trips.forEach(function (trip) {
                html += buildTripCard(trip);
            });
            html += '</div>';
        });

        container.innerHTML = html;

        // Stamp PB markers on trips that contain a personal-best catch
        CATCHES.getPbTripIdsAsync().then(function (pbTripIds) {
            pbTripIds.forEach(function (tripId) {
                var tripEl = container.querySelector('[data-trip-id="' + tripId + '"]');
                if (!tripEl) return;
                if (!tripEl.querySelector('.journal-trip__pb-marker')) {
                    var marker = document.createElement('img');
                    marker.src = 'img/marker.png';
                    marker.className = 'journal-trip__pb-marker';
                    marker.alt = 'Personal best';
                    tripEl.insertBefore(marker, tripEl.firstChild);
                }
            });
        });
    }

    function buildTripCard(trip) {
        var startDate = new Date(trip.start_time);
        var dateStr = formatDate(startDate, { month: 'short', day: 'numeric' });
        var timeStr = formatDate(startDate, { hour: 'numeric', minute: '2-digit' });
        var duration = trip.end_time ? formatDuration(trip.start_time, trip.end_time) : 'In progress';
        var isActive = !trip.end_time;
        var fbi = trip.conditions_snapshot ? trip.conditions_snapshot.fbi : null;
        var catchCount = trip.catch_count || 0;

        var html = '<div class="journal-trip' + (isActive ? ' journal-trip--active' : '') + '" data-trip-id="' + trip.id + '">';

        // Summary row
        html += '<div class="journal-trip__summary">';
        html += '<div class="journal-trip__header">';
        html += '<span class="journal-trip__date">' + dateStr + '</span>';
        html += '<span class="journal-trip__location">' + escapeHtml(trip.location_name) + '</span>';
        html += '</div>';
        html += '<div class="journal-trip__meta">';
        html += '<span class="journal-trip__time">' + timeStr + '</span>';
        html += '<span class="journal-trip__duration">' + duration + '</span>';
        html += '<span class="journal-trip__catches">' + catchCount + ' catch' + (catchCount !== 1 ? 'es' : '') + '</span>';
        html += '</div>';
        html += '<button class="journal-trip__delete" data-trip-id="' + trip.id + '" title="Delete trip"><img src="img/delete.png" alt="Delete" class="journal-trip__delete-icon"></button>';
        html += '</div>';

        // Expandable detail section
        html += '<div class="journal-trip__detail" style="display:none;">';
        html += buildTripDetail(trip);
        html += '</div>';

        html += '</div>';
        return html;
    }

    function buildTripDetail(trip) {
        var html = '';

        // Timeline: Start pill -> connector -> catches (lazy-loaded) -> connector -> End pill
        html += '<div class="journal-timeline">';
        html += buildSnapshotDisplay(trip.conditions_snapshot || null, 'Start', trip.notes);
        html += '<div class="journal-timeline__connector"></div>';
        html += '<div class="journal-catches-target"></div>';
        if (trip.end_conditions_snapshot) {
            html += '<div class="journal-timeline__connector"></div>';
            html += buildSnapshotDisplay(trip.end_conditions_snapshot, 'End');
        }
        html += '</div>';

        return html;
    }

    function buildSnapshotDisplay(snapshot, label, notes) {
        if (!snapshot) {
            return '<div class="journal-timeline__pill journal-timeline__pill--empty">'
                + escapeHtml(label) + ' \u2014 no conditions recorded</div>';
        }
        var line = label + ' Conditions';
        if (snapshot.fbi != null) {
            line += ' &mdash; FBI <strong>' + Math.round(snapshot.fbi) + '</strong>';
            if (snapshot.fbiVersion && AUTH_CLIENT.currentUser && AUTH_CLIENT.currentUser.role === 'admin') {
                line += ' <span class="journal-snapshot__version">(v' + escapeHtml(snapshot.fbiVersion) + ')</span>';
            }
        }
        if (notes) {
            line += ' <span class="journal-snapshot__note">*Note: ' + escapeHtml(notes) + '</span>';
        }

        return '<div class="journal-timeline__pill">' + line + '</div>';
    }

    function getScoreColor(pct) {
        if (pct >= 80) return '#2196F3';
        if (pct >= 60) return '#4CAF50';
        if (pct >= 40) return '#FFC107';
        if (pct >= 20) return '#FF9800';
        return '#F44336';
    }

    // --- Trip Expand/Collapse ---

    function toggleTripExpand(tripEl) {
        var detail = tripEl.querySelector('.journal-trip__detail');
        if (!detail) return;

        var isOpen = detail.style.display !== 'none';
        detail.style.display = isOpen ? 'none' : '';

        // Lazy-load catches if expanding for the first time
        if (!isOpen && !tripEl.dataset.catchesLoaded) {
            var tripId = tripEl.dataset.tripId;
            loadTripCatches(tripId, detail);
        }
    }

    function loadTripCatches(tripId, detailEl) {
        var target = detailEl.querySelector('.journal-catches-target');
        if (!target) return;

        Promise.all([
            apiGet('/' + tripId),
            CATCHES.getPbIdsAsync()
        ])
            .then(function (results) {
                var data = results[0];
                var pbIds = results[1];
                if (!data.success || !data.trip) return;
                target.innerHTML = buildCatchEntries(data.trip.catches || [], pbIds);
                var tripEl = detailEl.closest('.journal-trip');
                if (tripEl) tripEl.dataset.catchesLoaded = 'true';
            })
            .catch(function (err) {
                console.error('[Journal] Failed to load catches:', err.message);
                target.innerHTML = '<div class="rec-error">'
                    + '<span>Failed to load catches.</span>'
                    + '<button class="rec-error__retry-btn" data-trip-id="' + escapeHtml(tripId) + '">Retry</button>'
                    + '</div>';
            });
    }

    function buildCatchEntries(catches, pbIds) {
        if (!catches || catches.length === 0) return '';
        pbIds = pbIds || new Set();
        var html = '';
        catches.forEach(function (c, i) {
            if (i > 0) html += '<div class="journal-timeline__connector"></div>';

            var time = formatDate(c.catch_datetime, { hour: 'numeric', minute: '2-digit' });
            var species = capitalize(c.species.replace(/_/g, ' '));
            var isPb = pbIds.has(c.id);

            var infoParts = [escapeHtml(species)];
            if (c.length_inches) infoParts.push(c.length_inches + '"');
            if (c.weight_lbs) infoParts.push(c.weight_lbs + ' lbs');
            if (c.quantity > 1) infoParts.push('x' + c.quantity);
            var snap = c.conditions_snapshot;
            if (snap && snap.fbi != null) infoParts.push('FBI ' + Math.round(snap.fbi));

            var notesHtml = c.notes
                ? ' <span class="journal-catch-card__note">*Note: ' + escapeHtml(c.notes) + '</span>'
                : '';

            var photoUrl = (c.photo_urls && c.photo_urls.length > 0) ? c.photo_urls[0] : null;
            var photoHtml;
            if (photoUrl) {
                photoHtml =
                    '<div class="journal-catch-card__photo-wrap" data-photo-url="' + escapeHtml(photoUrl) + '">' +
                        '<img src="' + escapeHtml(CATCHES.getThumbUrl(photoUrl)) + '" class="journal-catch-card__photo" alt="' + escapeHtml(species) + '" loading="lazy" onerror="this.onerror=null;this.src=\'' + escapeHtml(photoUrl) + '\'">' +
                    '</div>';
            } else if (c.species !== 'other') {
                var fallbackUrl = SPECIES_IMAGES.getUrl(c.species);
                photoHtml = fallbackUrl
                    ? '<img src="' + escapeHtml(fallbackUrl) + '" class="journal-catch-card__photo journal-catch-card__photo--fallback" alt="' + escapeHtml(species) + '" loading="lazy">'
                    : '';
            } else {
                photoHtml = '';
            }

            var markerHtml = isPb
                ? '<img src="img/best.png" class="journal-catch-card__pb-marker" alt="Personal best" title="Personal best for ' + escapeHtml(species) + '">'
                : '';

            html += '<div class="journal-catch">';
            html += '<div class="journal-catch__time">' + escapeHtml(time) + '</div>';
            html += '<div class="journal-catch-card' + (isPb ? ' journal-catch-card--pb' : '') + '">';
            html += markerHtml;
            html += '<div class="journal-catch-card__info">' + infoParts.join(' &bull; ') + notesHtml + '</div>';
            html += photoHtml;
            html += '</div>';
            html += '</div>';
        });
        return html;
    }

    // --- Delete Trip ---

    function confirmDeleteTrip(tripId) {
        var message = 'Delete this trip? All catches logged on this trip will be deleted too.';
        if (typeof showDeleteConfirmModal === 'function') {
            showDeleteConfirmModal('this trip', function () { deleteTrip(tripId); }, message);
        } else if (confirm(message)) {
            deleteTrip(tripId);
        }
    }

    function deleteTrip(tripId) {
        apiDelete('/' + tripId)
            .then(function (data) {
                if (data.success) {
                    if (_activeTrip && _activeTrip.id === tripId) {
                        _activeTrip = null;
                        if (typeof LOGBOOK_STATE !== 'undefined') LOGBOOK_STATE.activeTripId = null;
                    }
                    _trips = _trips.filter(function (t) { return t.id !== tripId; });
                    renderTripHistory();
                    // Trip catches are deleted server-side — refresh catch table + PBs + analytics
                    if (typeof CATCHES !== 'undefined') {
                        CATCHES.loadCatches();
                        CATCHES.loadPersonalBests();
                    }
                    if (typeof ANALYTICS !== 'undefined') ANALYTICS.clear();
                    showToast('Trip deleted', 'success');
                } else {
                    showToast(data.error || 'Failed to delete trip', 'error');
                }
            })
            .catch(function (err) {
                showToast('Failed to delete trip', 'error');
                console.error('[Journal] Delete error:', err.message);
            });
    }

    // --- Catch Form Integration ---

    function updateCatchFormTripLink() {
        var indicator = document.getElementById('catchTripIndicator');
        var tripIdField = document.getElementById('catchTripId');

        if (_activeTrip) {
            if (tripIdField) tripIdField.value = _activeTrip.id;
            // Auto-select location in catch form if it matches
            autoSelectCatchLocation(_activeTrip.location_name);
        } else {
            if (tripIdField) tripIdField.value = '';
        }
    }

    function autoSelectCatchLocation(locationName) {
        var locationEl = document.getElementById('catchLocation');
        if (!locationEl) return;
        for (var i = 0; i < locationEl.options.length; i++) {
            if (locationEl.options[i].value === locationName) {
                locationEl.selectedIndex = i;
                // Trigger change to fill conditions
                locationEl.dispatchEvent(new Event('change'));
                break;
            }
        }
    }

    // --- Location Dropdown ---

    function populateLocationDropdown() {
        var select = document.getElementById('journalLocation');
        if (!select) return;

        // Clear existing options except placeholder
        while (select.options.length > 1) select.remove(1);

        // Add public spots from allConditionsData
        if (typeof allConditionsData !== 'undefined' && allConditionsData) {
            allConditionsData.forEach(function (item) {
                var opt = document.createElement('option');
                opt.value = item.location.name;
                opt.textContent = item.location.name;
                opt.dataset.key = item.key;
                opt.dataset.lat = item.location.lat;
                opt.dataset.lon = item.location.lon;
                select.appendChild(opt);
            });
        }

        // Add saved spots
        if (typeof SPOTS !== 'undefined' && SPOTS.getSpots) {
            var spots = SPOTS.getSpots();
            if (spots && spots.length > 0) {
                var group = document.createElement('optgroup');
                group.label = 'My Spots';
                spots.forEach(function (spot) {
                    var opt = document.createElement('option');
                    opt.value = spot.name;
                    opt.textContent = spot.name;
                    opt.dataset.lat = spot.lat;
                    opt.dataset.lon = spot.lon;
                    group.appendChild(opt);
                });
                select.appendChild(group);
            }
        }
    }

    // --- Utilities ---

    function formatDuration(start, end) {
        var diff = new Date(end) - new Date(start);
        if (diff < 0) return '--';
        var hours = Math.floor(diff / 3600000);
        var mins = Math.floor((diff % 3600000) / 60000);
        return hours > 0 ? hours + 'h ' + mins + 'm' : mins + 'm';
    }

    function capitalize(str) {
        return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
    }

    function escapeHtml(str) {
        if (!str) return '';
        var div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    function showToast(msg, type) {
        if (typeof showAppToast === 'function') {
            showAppToast(msg, type);
        }
    }

    // --- Quick Start Trip (1-click from map/list) ---

    function quickStartTrip(locationName, locationKey, lat, lon) {
        var body = {
            locationName: locationName,
            locationKey: locationKey || null,
            lat: lat || null,
            lon: lon || null,
            startTime: new Date().toISOString()
        };

        apiPost('', body)
            .then(function (data) {
                if (data.success) {
                    _activeTrip = data.trip;
                    if (typeof LOGBOOK_STATE !== 'undefined') LOGBOOK_STATE.activeTripId = data.trip.id;
                    renderActiveTripBanner();
                    updateCatchFormTripLink();
                    showToast('Trip started', 'success');
                } else {
                    showToast(data.error || 'Failed to start trip', 'error');
                }
            })
            .catch(function (err) {
                showToast('Failed to start trip', 'error');
                console.error('[Journal] Quick start trip error:', err.message);
            });
    }

    // --- Public API ---

    return {
        init: function () {
            populateLocationDropdown();
            init();
        },
        getActiveTrip: function () { return _activeTrip; },
        getActiveTripId: function () { return _activeTrip ? _activeTrip.id : null; },
        quickStartTrip: quickStartTrip
    };
})();

;
/* === js/analytics.js === */
/**
 * Analytics — "My Stats" dashboard (IIFE global)
 *
 * LOADED BY: dist/js/app.min.js via bundle
 * USED BY:   main-server.js → switchTab('analytics') lazy-init
 * DEPENDS ON: auth-client.js (AUTH_CLIENT), api-client.js (API_CLIENT),
 *             ui-helpers.js (escapeHtml, formatDate, scrollBehavior), ui-builders.js (getScoreBarColor),
 *             tooltip.js (data-tip on the season tide chart)
 * EXPORTS: ANALYTICS object as global; module.exports for tests (pure helpers on the object)
 */
var ANALYTICS = (function () {

    // -------------------------------------------------------------------------
    // Constants
    // -------------------------------------------------------------------------

    var SPECIES_DISPLAY = {
        redfish: 'Redfish', speckled_trout: 'Speckled Trout', flounder: 'Flounder',
        black_drum: 'Black Drum', sheepshead: 'Sheepshead', spanish_mackerel: 'Spanish Mackerel',
        pompano: 'Pompano', sand_trout: 'Sand Trout', whiting: 'Whiting',
        tripletail: 'Tripletail', gafftopsail_catfish: 'Gafftopsail Catfish', jack_crevalle: 'Jack Crevalle'
    };

    // Maps badge key → filename in /img/badges/.
    var BADGE_IMAGE_MAP = {
        // Existing
        first_catch:      'First-catch.png',
        ten_catches:      'Ten-catches.png',
        fifty_catches:    'Fifty-catches.png',
        century_club:     'century-club.png',
        grand_slam:       'Grand-slam.png',
        life_lister:      'Life-lister.png',
        dawn_patrol:      'Dawn-patrol.png',
        full_moon:        'Full-moon.png',
        hot_streak_3:     'On-fire.png',
        hot_streak_7:     'Week-warrior.png',
        // Volume
        quarter_century:  'quarter-century.png',
        the_250:          'the-250.png',
        five_hundred:     'five-hundred.png',
        thousand_club:    'thousand-club.png',
        // Streaks
        hot_streak_14:    'two-weeks-strong.png',
        hot_streak_30:    'monthly-grind.png',
        // Species Variety
        triple_threat:    'triple-threat.png',
        species_hunter:   'species-hunter.png',
        inshore_slam:     'inshore-slam.png',
        // Lunar
        new_moon:         'new-moon.png',
        // Time of Day
        golden_hour:      'golden-hour.png',
        night_owl:        'night-owl.png',
        midday_heat:      'midday-heat.png',
        // Conditions
        perfect_day:      'perfect-day.png',
        storm_chaser:     'storm-chaser.png',
        deep_freeze:      'deep-freeze.png',
        hot_water:        'hot-water.png',
        // Seasonal
        winter_warrior:   'winter-warrior.png',
        spring_slam:      'spring-slam.png',
        summer_grind:     'summer-grind.png',
        fall_run:         'fall-run.png',
        // Species Dedication
        redfish_regular:  'bull-red.png',
        trout_hunter:     'trout-whisperer.png',
        flounder_pro:     'flat-rat.png',
        drum_devotee:     'drum-master.png',
        sheepshead_fan:   'convict-catcher.png',
        pompano_prize:    'pompano-gold.png',
        // Engagement
        photo_finish:     'photo-finish.png',
        note_taker:       'note-taker.png',
        location_scout:   'location-scout.png',
        catch_and_release:'catch-and-release.png',
        trophy_hunter:    'trophy-hunter.png',
        // Fun
        lucky_number:     'lucky-number.png',
        double_digit_day: 'double-digits.png'
    };

    var BADGE_DESCRIPTIONS = {
        // Existing
        first_catch:      'You made your first cast count — every angler starts somewhere.',
        ten_catches:      'Ten fish in the books. The habit is taking hold.',
        fifty_catches:    'Fifty catches logged. This is more than a hobby.',
        century_club:     'One hundred catches. You\'ve put in the time and it shows.',
        grand_slam:       'Five different species in a single day — a rare and memorable feat.',
        life_lister:      'Every tracked Gulf Coast species landed. The full list, complete.',
        dawn_patrol:      'On the water before the sun. You earned it the hard way.',
        full_moon:        'A catch made under a full moon. The tides were on your side.',
        hot_streak_3:     'Three consecutive days on the water without missing a beat.',
        hot_streak_7:     'Seven straight days fishing. Commitment like that gets results.',
        // Volume
        quarter_century:  'Twenty-five fish on the books. The habit is locked in.',
        the_250:          'Two hundred and fifty catches. You show up, every time.',
        five_hundred:     'Five hundred fish. This is a lifestyle.',
        thousand_club:    'A thousand catches. Few anglers ever get here.',
        // Streaks
        hot_streak_14:    'Fourteen straight days on the water. Sheer persistence.',
        hot_streak_30:    'A full month without missing a day. Legendary dedication.',
        // Species Variety
        triple_threat:    'Three different species in a single session — a well-rounded day.',
        species_hunter:   'Ten distinct species in the log. You go where the fish are.',
        inshore_slam:     'Redfish, trout, flounder, and drum in one day — the Texas Inshore Slam.',
        // Lunar
        new_moon:         'A catch made in the dark of a new moon. You know the water.',
        // Time of Day
        golden_hour:      'A catch at golden hour, when the light bends and fish feed.',
        night_owl:        'Fishing after midnight — when the dock lights draw them in.',
        midday_heat:      'A catch at high noon. Against the odds and worth it.',
        // Conditions
        perfect_day:      'Logged a catch when conditions were near-perfect. Timing matters.',
        storm_chaser:     'You fished through rain or a storm and still put one in the box.',
        deep_freeze:      'A catch in water under 52°F. Cold hands, warm results.',
        hot_water:        'A catch in water over 87°F. Heat didn\'t slow you down.',
        // Seasonal
        winter_warrior:   'A catch in the dead of winter. The fish don\'t take holidays either.',
        spring_slam:      'A spring catch — when the water warms and everything starts moving.',
        summer_grind:     'Fishing through the summer heat. Not for the faint of heart.',
        fall_run:         'An October catch during the fall run. The best time of year.',
        // Species Dedication
        redfish_regular:  'Ten redfish in the log. You\'ve learned their water.',
        trout_hunter:     'Ten speckled trout. You\'ve figured out where they hold.',
        flounder_pro:     'Ten flounder. Patient, precise, and flat-out effective.',
        drum_devotee:     'Ten black drum. You know the oyster bars they haunt.',
        sheepshead_fan:   'Ten sheepshead. Picking them off structure takes real skill.',
        pompano_prize:    'Five pompano. One of the most prized fish in the Gulf.',
        // Engagement
        photo_finish:     'You documented a catch with a photo — the memory is preserved.',
        note_taker:       'Five catches with notes attached. You pay attention to detail.',
        location_scout:   'Catches logged at five different spots. You explore.',
        catch_and_release:'Ten fish carefully returned. Conservation in action.',
        trophy_hunter:    'A double-digit catch by weight. That one is worth framing.',
        // Fun
        lucky_number:     'A catch on the 13th. Luck is just preparation meeting opportunity.',
        double_digit_day: 'Ten or more fish in a single outing. That\'s a full cooler day.'
    };

    // Maps species key → personal best badge in /img/badges/.
    var PB_BADGE_MAP = {
        redfish:             'PB-red.png',
        speckled_trout:      'PB-speckled-b.png',
        flounder:            'PB-flounder.png',
        black_drum:          'PB-black-drum.png',
        sheepshead:          'PB-sheepshead.png',
        spanish_mackerel:    'PB-spanish-mackerel.png',
        pompano:             'PB-pompano.png',
        sand_trout:          'PB-sandtrout.png',
        whiting:             'PB-whiting.png',
        gafftopsail_catfish: 'PB-gafftopsail-catfish.png',
        tripletail:          'PB-tripletail.png',
        jack_crevalle:       'PB-jack.png'
    };

    // Badge case order. A badge missing here is appended to the last group.
    var BADGE_GROUPS = [
        { name: 'Milestones', keys: ['first_catch', 'ten_catches', 'quarter_century', 'fifty_catches', 'century_club', 'the_250', 'five_hundred', 'thousand_club'] },
        { name: 'Streaks', keys: ['hot_streak_3', 'hot_streak_7', 'hot_streak_14', 'hot_streak_30'] },
        { name: 'Species', keys: ['triple_threat', 'grand_slam', 'inshore_slam', 'species_hunter', 'life_lister', 'redfish_regular', 'trout_hunter', 'flounder_pro', 'drum_devotee', 'sheepshead_fan', 'pompano_prize'] },
        { name: 'Time & moon', keys: ['dawn_patrol', 'golden_hour', 'midday_heat', 'night_owl', 'full_moon', 'new_moon'] },
        { name: 'Seasons & conditions', keys: ['spring_slam', 'summer_grind', 'fall_run', 'winter_warrior', 'perfect_day', 'storm_chaser', 'deep_freeze', 'hot_water'] },
        { name: 'Logbook habits', keys: ['photo_finish', 'note_taker', 'location_scout', 'catch_and_release', 'trophy_hunter', 'lucky_number', 'double_digit_day'] }
    ];

    // Groups shown on a phone before "Show all badges"
    var PHONE_BADGE_GROUPS = 2;

    var VOLUME_BADGES = ['first_catch', 'ten_catches', 'quarter_century', 'fifty_catches', 'century_club', 'the_250', 'five_hundred', 'thousand_club'];
    var STREAK_BADGES = ['hot_streak_3', 'hot_streak_7', 'hot_streak_14', 'hot_streak_30'];
    var DEDICATION_SPECIES = {
        redfish_regular: 'redfish', trout_hunter: 'speckled trout', flounder_pro: 'flounder',
        drum_devotee: 'black drum', sheepshead_fan: 'sheepshead', pompano_prize: 'pompano'
    };

    var SEEN_BADGES_KEY = 'rt.statsSeenBadges.';
    var NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

    // -------------------------------------------------------------------------
    // Private state
    // -------------------------------------------------------------------------

    var _initialized = false;
    var _data = null;
    var _tiles = [];
    var _chartObserver = null;
    var _eventsBound = false;

    // -------------------------------------------------------------------------
    // Pure helpers (exported for tests)
    // -------------------------------------------------------------------------

    /**
     * The 12 months ending with the current one, oldest first; months with no catches count 0.
     * @param {Array<{month: string, count: number}>} monthlyCounts - 'YYYY-MM' rows from the API
     * @param {Date} now
     * @returns {Array<{key: string, label: string, year: number, count: number}>}
     */
    function fillMonths(monthlyCounts, now) {
        var byMonth = {};
        (monthlyCounts || []).forEach(function (r) { byMonth[r.month] = r.count; });
        var months = [];
        for (var i = 11; i >= 0; i--) {
            var d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            var key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
            months.push({
                key: key,
                label: formatDate(d, { month: 'short' }),
                year: d.getFullYear(),
                count: byMonth[key] || 0
            });
        }
        return months;
    }

    /**
     * One tile per tracked species: caught species by catch count, then the ones not caught yet.
     * @returns {Array<{species: string, name: string, count: number, state: 'best'|'nosize'|'none', best: Object|null}>}
     */
    function trophyTiles(personalBests, speciesBreakdown) {
        var bestBySpecies = {};
        (personalBests || []).forEach(function (r) { bestBySpecies[r.species] = r; });
        var countBySpecies = {};
        (speciesBreakdown || []).forEach(function (r) { countBySpecies[r.species] = r.count; });

        var keys = Object.keys(SPECIES_DISPLAY);
        function tile(key) {
            var best = bestBySpecies[key] || null;
            var count = countBySpecies[key] || 0;
            var state = 'none';
            if (best) state = 'best';
            else if (count > 0) state = 'nosize';
            return { species: key, name: SPECIES_DISPLAY[key], count: count, state: state, best: best };
        }

        var tiles = keys.map(tile);
        var caught = tiles.filter(function (t) { return t.state !== 'none'; });
        caught.sort(function (a, b) {
            return (b.count - a.count) || (keys.indexOf(a.species) - keys.indexOf(b.species));
        });
        return caught.concat(tiles.filter(function (t) { return t.state === 'none'; }));
    }

    /**
     * The 3 locked badges closest to unlocking, among those with countable progress started.
     */
    function nextUpBadges(badges) {
        return (badges || [])
            .filter(function (b) { return !b.unlocked && b.progress && b.progress.have > 0; })
            .sort(function (a, b) {
                var ratio = (b.progress.have / b.progress.need) - (a.progress.have / a.progress.need);
                if (ratio) return ratio;
                return (a.progress.need - a.progress.have) - (b.progress.need - b.progress.have);
            })
            .slice(0, 3);
    }

    /** What's left to earn a badge, in plain words. */
    function progressHint(badge) {
        var p = badge.progress;
        var left = p.need - p.have;
        if (VOLUME_BADGES.indexOf(badge.key) !== -1) return left + ' more ' + (left === 1 ? 'catch' : 'catches');
        if (STREAK_BADGES.indexOf(badge.key) !== -1) return 'Fish ' + p.need + ' days in a row (best so far: ' + p.have + ')';
        if (badge.key === 'species_hunter' || badge.key === 'life_lister') return left + ' more species';
        if (badge.key === 'location_scout') return 'Catch at ' + left + ' more ' + (left === 1 ? 'spot' : 'spots');
        if (DEDICATION_SPECIES[badge.key]) return left + ' more ' + DEDICATION_SPECIES[badge.key];
        return p.have + ' of ' + p.need;
    }

    function isEmpty(data) {
        return !data || !(data.totalCatches > 0);
    }

    // -------------------------------------------------------------------------
    // Formatting
    // -------------------------------------------------------------------------

    function plural(n, one, many) {
        return NUMBER.format(n) + ' ' + (n === 1 ? one : many);
    }

    function measureParts(row) {
        var parts = [];
        if (row.length_inches != null) parts.push(NUMBER.format(parseFloat(row.length_inches)) + ' in');
        if (row.weight_lbs != null) parts.push(NUMBER.format(parseFloat(row.weight_lbs)) + ' lb');
        return parts;
    }

    function fbiPillHtml(score) {
        if (score == null) return '';
        return '<span class="stats-fbi" style="background:' + getScoreBarColor(score) + '">' + escapeHtml(String(score)) + '</span>';
    }

    function snapshotOf(row) {
        var s = row && row.conditions_snapshot;
        if (typeof s === 'string') {
            try { s = JSON.parse(s); } catch (e) { s = null; }
        }
        return s || {};
    }

    /**
     * Badge medal image, or a sized placeholder when the badge has no artwork.
     * @param {string} [extraClass] - prepended to the img class list (not applied to the placeholder)
     */
    function badgeImgHtml(badge, size, extraClass) {
        var file = BADGE_IMAGE_MAP[badge.key];
        if (!file) return '<span class="stats-badge__placeholder" style="width:' + size + 'px;height:' + size + 'px" aria-hidden="true"></span>';
        return '<img src="/img/badges/' + encodeURIComponent(file) + '" width="' + size + '" height="' + size + '" alt=""'
            + ' class="' + (extraClass ? extraClass + ' ' : '') + 'stats-badge__img' + (badge.unlocked ? '' : ' stats-silhouette') + '" loading="lazy">';
    }

    /**
     * Personal best image for a species.
     * @param {string} [extraClass] - prepended to the img class list
     */
    function pbImgHtml(species, size, silhouette, extraClass) {
        return '<img src="/img/badges/' + encodeURIComponent(PB_BADGE_MAP[species]) + '" width="' + size + '" height="' + size + '" alt=""'
            + ' class="' + (extraClass ? extraClass + ' ' : '') + 'stats-tile__img' + (silhouette ? ' stats-silhouette' : '') + '" loading="lazy">';
    }

    function sectionHead(title, aside, id) {
        return '<div class="stats-section__head">'
            + '<h3 class="stats-section__title"' + (id ? ' id="' + id + '"' : '') + '>' + title + '</h3>'
            + (aside ? '<span class="stats-section__aside">' + aside + '</span>' : '')
            + '</div>';
    }

    // -------------------------------------------------------------------------
    // Render: new badge banner
    // -------------------------------------------------------------------------

    function seenStorageKey() {
        var user = (typeof AUTH_CLIENT !== 'undefined') ? AUTH_CLIENT.currentUser : null;
        return user && user.id ? SEEN_BADGES_KEY + user.id : null;
    }

    // Array of seen keys, null when never stored, undefined when storage is unavailable
    function readSeenBadges() {
        var key = seenStorageKey();
        if (!key) return undefined;
        try {
            var raw = localStorage.getItem(key);
            if (raw === null) return null;
            var parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : null;
        } catch (e) {
            return undefined;
        }
    }

    function writeSeenBadges(keys) {
        var key = seenStorageKey();
        if (!key) return;
        try { localStorage.setItem(key, JSON.stringify(keys)); } catch (e) { /* storage unavailable */ }
    }

    function unlockedKeys(badges) {
        return badges.filter(function (b) { return b.unlocked; }).map(function (b) { return b.key; });
    }

    function renderBanner(groups, badges) {
        var seen = readSeenBadges();
        if (seen === undefined) return '';
        if (seen === null) {
            writeSeenBadges(unlockedKeys(badges));
            return '';
        }
        var fresh = [];
        groups.forEach(function (g) {
            g.badges.forEach(function (b) { if (b.unlocked && seen.indexOf(b.key) === -1) fresh.push(b); });
        });
        if (!fresh.length) return '';

        var first = fresh[0];
        var more = fresh.length > 1 ? ' <span class="stats-banner__more">and ' + (fresh.length - 1) + ' more</span>' : '';
        return '<section class="stats-banner" role="status" data-stats-banner>'
            + badgeImgHtml(first, 112, 'stats-banner__medal')
            + '<div class="stats-banner__text">'
            + '<p class="stats-banner__eyebrow">New badge since your last visit</p>'
            + '<h3 class="stats-banner__name">' + escapeHtml(first.name) + more + '</h3>'
            + '<p class="stats-banner__desc">' + escapeHtml(BADGE_DESCRIPTIONS[first.key] || '') + '</p>'
            + '</div>'
            + '<div class="stats-banner__actions">'
            + '<button type="button" class="stats-button stats-button--quiet" data-stats-action="see-badges">See all badges</button>'
            + '<button type="button" class="stats-button" data-stats-action="dismiss-banner">Got it</button>'
            + '</div>'
            + '</section>';
    }

    // -------------------------------------------------------------------------
    // Render: season tide
    // -------------------------------------------------------------------------

    function renderHero(data, tiles, months) {
        var caughtSpecies = tiles.filter(function (t) { return t.state !== 'none'; }).length;
        var best = (data.streaks && data.streaks.best) || 0;
        var days = data.daysFished || 0;
        var dayWord = days === 1 ? 'day' : 'days';
        var peak = months.reduce(function (m, x) { return x.count > m ? x.count : m; }, 0);

        var chart = peak > 0
            ? '<div class="stats-tide__chart" data-stats-tide></div>'
                + '<table class="stats-sr-only"><caption>Catches per month, last 12 months</caption>'
                + '<thead><tr><th scope="col">Month</th><th scope="col">Catches</th></tr></thead><tbody>'
                + months.map(function (m) { return '<tr><td>' + m.label + ' ' + m.year + '</td><td>' + m.count + '</td></tr>'; }).join('')
                + '</tbody></table>'
            : '<p class="stats-tide__empty">No catches in the last 12 months</p>';

        function fact(value, label) {
            return '<div class="stats-fact"><span class="stats-fact__value">' + value + '</span><span class="stats-fact__label">' + label + '</span></div>';
        }

        return '<section class="stats-hero" aria-labelledby="statsHeroTitle">'
            + '<div class="stats-hero__top">'
            + '<h3 class="stats-hero__title" id="statsHeroTitle">' + plural(data.totalCatches, 'catch', 'catches') + ' in the book</h3>'
            + '<div class="stats-hero__facts">'
            + fact(caughtSpecies + ' of ' + tiles.length, 'species caught')
            + fact(NUMBER.format(days), '<span class="stats-wide">' + dayWord + ' on the water</span><span class="stats-narrow">' + dayWord + ' fished</span>')
            + fact(plural(best, 'day', 'days'), 'best streak')
            + '</div>'
            + '</div>'
            + '<div class="stats-tide">'
            + '<div class="stats-tide__caption"><span>Last 12 months</span>'
            + (peak > 0 ? '<span class="stats-tide__now-key">' + plural(months[months.length - 1].count, 'catch', 'catches') + ' this month</span>' : '')
            + '</div>'
            + chart
            + '</div>'
            + '</section>';
    }

    // Smooth path through the points; control points are clamped so the curve never overshoots
    function smoothPath(pts) {
        var d = 'M ' + pts[0][0].toFixed(1) + ' ' + pts[0][1].toFixed(1);
        for (var i = 0; i < pts.length - 1; i++) {
            var p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
            var lo = Math.min(p1[1], p2[1]), hi = Math.max(p1[1], p2[1]);
            var c1x = p1[0] + (p2[0] - p0[0]) / 6, c2x = p2[0] - (p3[0] - p1[0]) / 6;
            var c1y = Math.min(hi, Math.max(lo, p1[1] + (p2[1] - p0[1]) / 6));
            var c2y = Math.min(hi, Math.max(lo, p2[1] - (p3[1] - p1[1]) / 6));
            d += ' C ' + c1x.toFixed(1) + ' ' + c1y.toFixed(1) + ', ' + c2x.toFixed(1) + ' ' + c2y.toFixed(1) + ', ' + p2[0].toFixed(1) + ' ' + p2[1].toFixed(1);
        }
        return d;
    }

    function tideSvg(months, width) {
        var compact = width < 520;
        var height = compact ? 190 : 230;
        var padL = compact ? 8 : 36, padR = compact ? 8 : 16, padT = 44, padB = 30;
        var innerW = width - padL - padR, innerH = height - padT - padB;
        var max = months.reduce(function (m, x) { return Math.max(m, x.count); }, 0);
        var top = max <= 10 ? 10 : Math.ceil(max / 10) * 10;
        var base = padT + innerH;
        var step = innerW / (months.length - 1);
        var pts = months.map(function (m, i) { return [padL + step * i, padT + innerH - innerH * m.count / top]; });
        var line = smoothPath(pts);
        var last = months.length - 1;
        var peakIdx = 0;
        months.forEach(function (m, i) { if (m.count > months[peakIdx].count) peakIdx = i; });

        var svg = '<svg class="stats-tide__svg" width="' + width + '" height="' + height + '" viewBox="0 0 ' + width + ' ' + height + '" aria-hidden="true">';
        [top / 2, top].forEach(function (v) {
            var y = padT + innerH - innerH * v / top;
            svg += '<line class="stats-tide__grid" x1="' + padL + '" x2="' + (width - padR) + '" y1="' + y + '" y2="' + y + '"></line>';
            if (!compact) svg += '<text class="stats-tide__axis" x="' + (padL - 10) + '" y="' + (y + 4) + '" text-anchor="end">' + NUMBER.format(v) + '</text>';
        });
        svg += '<line class="stats-tide__base" x1="' + padL + '" x2="' + (width - padR) + '" y1="' + base + '" y2="' + base + '"></line>';
        svg += '<path class="stats-tide__area" d="' + line + ' L ' + pts[last][0].toFixed(1) + ' ' + base + ' L ' + pts[0][0].toFixed(1) + ' ' + base + ' Z"></path>';
        svg += '<path class="stats-tide__line" d="' + line + '"></path>';

        months.forEach(function (m, i) {
            if (compact && i % 2 === 1) return;
            svg += '<text class="stats-tide__month" x="' + pts[i][0] + '" y="' + (height - 8) + '" text-anchor="middle">' + m.label + '</text>';
        });

        // Peak gets a direct label; the current month is a gold dot keyed in the caption above
        var anchor = 'middle';
        var peakX = pts[peakIdx][0];
        if (peakIdx >= last - 1) {
            anchor = 'end';
            peakX -= 10;
        } else if (peakIdx <= 1) {
            anchor = 'start';
            peakX += 10;
        }
        if (peakIdx !== last) svg += '<circle class="stats-tide__peak" cx="' + pts[peakIdx][0] + '" cy="' + pts[peakIdx][1] + '" r="5"></circle>';
        svg += '<text class="stats-tide__label" x="' + peakX + '" y="' + (pts[peakIdx][1] - 12) + '" text-anchor="' + anchor + '">High tide: ' + months[peakIdx].count + ' in ' + months[peakIdx].label + '</text>';
        svg += '<circle class="stats-tide__now" cx="' + pts[last][0] + '" cy="' + pts[last][1] + '" r="6"></circle>';

        // Hover targets for the shared tooltip
        months.forEach(function (m, i) {
            svg += '<rect class="stats-tide__hit" x="' + (pts[i][0] - step / 2) + '" y="' + padT + '" width="' + step + '" height="' + innerH + '"'
                + ' data-tip="' + m.label + ' ' + m.year + ': ' + plural(m.count, 'catch', 'catches') + '"></rect>';
        });
        return svg + '</svg>';
    }

    function mountTide(el, months) {
        var holder = el.querySelector('[data-stats-tide]');
        if (!holder) return;
        var drawnWidth = 0;
        var frame = 0;

        function draw() {
            frame = 0;
            var width = Math.floor(holder.clientWidth);
            if (!width || width === drawnWidth) return;
            drawnWidth = width;
            holder.innerHTML = tideSvg(months, width);
        }

        draw();
        if (typeof ResizeObserver === 'undefined') return;
        _chartObserver = new ResizeObserver(function () {
            if (!frame) frame = requestAnimationFrame(draw);
        });
        _chartObserver.observe(holder);
    }

    // -------------------------------------------------------------------------
    // Render: trophy wall
    // -------------------------------------------------------------------------

    function renderTrophyWall(tiles) {
        var bests = tiles.filter(function (t) { return t.state === 'best'; }).length;
        var html = tiles.map(function (t, i) {
            var name = '<span class="stats-tile__name">' + escapeHtml(t.name) + '</span>';
            if (t.state === 'none') {
                return '<div class="stats-tile stats-tile--none">' + pbImgHtml(t.species, 120, true) + name
                    + '<span class="stats-tile__note">Not caught yet</span></div>';
            }
            if (t.state === 'nosize') {
                return '<button type="button" class="stats-tile stats-tile--nosize" data-stats-trophy="' + i + '">'
                    + pbImgHtml(t.species, 120, true) + name
                    + '<span class="stats-tile__note">' + plural(t.count, 'catch', 'catches') + '</span>'
                    + '<span class="stats-tile__hint">Log a length to set a best</span></button>';
            }
            var b = t.best;
            var date = new Date(b.catch_datetime);
            return '<button type="button" class="stats-tile" data-stats-trophy="' + i + '">'
                + pbImgHtml(t.species, 120, false) + name
                + '<span class="stats-tile__measure">' + measureParts(b).map(function (p) { return '<span>' + p + '</span>'; }).join('') + '</span>'
                + (b.location_name ? '<span class="stats-tile__spot">' + escapeHtml(b.location_name) + '</span>' : '')
                + '<span class="stats-tile__date">'
                + '<span class="stats-wide">' + formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' }) + '</span>'
                + '<span class="stats-narrow">' + formatDate(date, { month: 'short', day: 'numeric' }) + '</span>'
                + '</span></button>';
        }).join('');

        return '<section class="stats-section" aria-labelledby="statsTrophyTitle">'
            + sectionHead('Trophy wall', plural(bests, 'personal best', 'personal bests'), 'statsTrophyTitle')
            + '<div class="stats-trophies">' + html + '</div>'
            + '</section>';
    }

    // -------------------------------------------------------------------------
    // Render: next up, spots, what works
    // -------------------------------------------------------------------------

    function renderNextUp(badges) {
        var next = nextUpBadges(badges);
        if (!next.length) return '';
        var rows = next.map(function (b) {
            var pct = Math.round(b.progress.have / b.progress.need * 100);
            return '<li class="stats-next__row">'
                + badgeImgHtml(b, 64)
                + '<div class="stats-next__body">'
                + '<div class="stats-next__line"><span class="stats-next__name">' + escapeHtml(b.name) + '</span>'
                + '<span class="stats-next__count">' + b.progress.have + ' of ' + b.progress.need + '</span></div>'
                + '<div class="stats-bar" role="progressbar" aria-label="' + escapeHtml(b.name) + '" aria-valuemin="0" aria-valuemax="' + b.progress.need + '" aria-valuenow="' + b.progress.have + '">'
                + '<div class="stats-bar__fill" style="width:' + pct + '%"></div></div>'
                + '<span class="stats-next__hint">' + escapeHtml(progressHint(b)) + '</span>'
                + '</div></li>';
        }).join('');
        return '<section class="stats-section" aria-labelledby="statsNextTitle">'
            + sectionHead('Next up', 'Closest badges', 'statsNextTitle')
            + '<ul class="stats-next">' + rows + '</ul>'
            + '</section>';
    }

    function renderSpots(topSpots) {
        var spots = (topSpots || []).slice(0, 5);
        if (!spots.length) return '';
        var rows = spots.map(function (s, i) {
            var species = s.top_species && SPECIES_DISPLAY[s.top_species];
            return '<li class="stats-spots__row">'
                + '<span class="stats-spots__rank">' + (i + 1) + '</span>'
                + '<div class="stats-spots__body"><span class="stats-spots__name">' + escapeHtml(s.location_name || 'Unnamed spot') + '</span>'
                + (species ? '<span class="stats-spots__species">Mostly ' + escapeHtml(species.toLowerCase()) + '</span>' : '')
                + '</div>'
                + '<span class="stats-spots__count">' + plural(s.total, 'catch', 'catches') + '</span>'
                + fbiPillHtml(s.avg_fbi)
                + '</li>';
        }).join('');
        return '<section class="stats-section" aria-labelledby="statsSpotsTitle">'
            + sectionHead('Where you catch', 'Avg FBI at catch', 'statsSpotsTitle')
            + '<ol class="stats-spots">' + rows + '</ol>'
            + '</section>';
    }

    function renderMethods(methods) {
        var rows = (methods || []).slice(0, 5);
        if (!rows.length) return '';
        var top = rows[0].count;
        var html = rows.map(function (m) {
            var how = [m.bait_used, m.fishing_method].filter(Boolean).join(', ');
            var species = SPECIES_DISPLAY[m.species] || m.species || 'Any species';
            return '<li class="stats-methods__row">'
                + '<div class="stats-methods__line"><span class="stats-methods__name">' + escapeHtml(species + ' on ' + how) + '</span>'
                + '<span class="stats-methods__count">' + m.count + '</span></div>'
                + '<div class="stats-bar stats-bar--thin" aria-hidden="true"><div class="stats-bar__fill" style="width:' + Math.round(m.count / top * 100) + '%"></div></div>'
                + '</li>';
        }).join('');
        return '<section class="stats-section" aria-labelledby="statsMethodsTitle">'
            + sectionHead('What works', 'By bait and method', 'statsMethodsTitle')
            + '<ol class="stats-methods">' + html + '</ol>'
            + '</section>';
    }

    // -------------------------------------------------------------------------
    // Render: badge case
    // -------------------------------------------------------------------------

    function badgeGroups(badges) {
        var byKey = {};
        badges.forEach(function (b) { byKey[b.key] = b; });
        var placed = {};
        var groups = BADGE_GROUPS.map(function (g) {
            var list = g.keys.filter(function (k) { return byKey[k]; }).map(function (k) { placed[k] = true; return byKey[k]; });
            return { name: g.name, badges: list };
        });
        var leftover = badges.filter(function (b) { return !placed[b.key]; });
        groups[groups.length - 1].badges = groups[groups.length - 1].badges.concat(leftover);
        return groups.filter(function (g) { return g.badges.length; });
    }

    function renderBadgeCase(groups, badges) {
        var earned = badges.filter(function (b) { return b.unlocked; }).length;
        function groupHtml(g) {
            var got = g.badges.filter(function (b) { return b.unlocked; }).length;
            return '<div class="stats-badge-group">'
                + '<p class="stats-badge-group__head"><span class="stats-badge-group__name">' + escapeHtml(g.name) + '</span>'
                + '<span class="stats-badge-group__count">' + got + ' of ' + g.badges.length + '</span></p>'
                + '<div class="stats-badge-grid">'
                + g.badges.map(function (b) {
                    return '<button type="button" class="stats-badge' + (b.unlocked ? '' : ' stats-badge--locked') + '" data-stats-badge="' + escapeHtml(b.key) + '">'
                        + badgeImgHtml(b, 72)
                        + '<span class="stats-badge__name">' + (b.unlocked ? '' : '<span class="stats-sr-only">Locked: </span>') + escapeHtml(b.name) + '</span>'
                        + '</button>';
                }).join('')
                + '</div></div>';
        }

        var shown = groups.slice(0, PHONE_BADGE_GROUPS).map(groupHtml).join('');
        var extra = groups.slice(PHONE_BADGE_GROUPS);
        var extraHtml = extra.length
            ? '<div class="stats-badges__extra" id="statsBadgesExtra">' + extra.map(groupHtml).join('') + '</div>'
                + '<button type="button" class="stats-button stats-button--quiet stats-badges__toggle" data-stats-action="toggle-badges" aria-expanded="false" aria-controls="statsBadgesExtra">Show all ' + badges.length + ' badges</button>'
            : '';

        return '<section class="stats-section stats-badges" id="statsBadges" aria-labelledby="statsBadgesTitle">'
            + sectionHead('Badges', earned + ' of ' + badges.length + ' earned', 'statsBadgesTitle')
            + shown + extraHtml
            + '</section>';
    }

    // -------------------------------------------------------------------------
    // Dialogs
    // -------------------------------------------------------------------------

    /** Native <dialog> like showDeleteConfirmModal(): Escape, close button, and a full backdrop click close it. */
    function openDialog(innerHtml, labelId) {
        var existing = document.getElementById('statsDialog');
        if (existing) existing.remove();

        var returnFocus = document.activeElement;
        var dialog = document.createElement('dialog');
        dialog.id = 'statsDialog';
        dialog.className = 'stats-dialog';
        dialog.setAttribute('aria-labelledby', labelId);
        dialog.innerHTML = '<div class="stats-dialog__body">'
            + '<button type="button" class="stats-dialog__close" aria-label="Close" data-stats-close>'
            + '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15"></path></svg>'
            + '</button>'
            + innerHtml
            + '</div>';
        document.body.appendChild(dialog);

        // A press must start and end on the backdrop; a text selection dragged out also clicks the dialog
        var pressedBackdrop = false;
        dialog.addEventListener('pointerdown', function (e) { pressedBackdrop = e.target === dialog; });
        dialog.addEventListener('click', function (e) {
            if (e.target === dialog) {
                if (pressedBackdrop) dialog.close();
                return;
            }
            if (e.target.closest('[data-stats-close]')) dialog.close();
        });
        dialog.addEventListener('close', function () {
            dialog.remove();
            if (returnFocus && typeof returnFocus.focus === 'function' && document.contains(returnFocus)) {
                returnFocus.focus();
            }
        });
        dialog.showModal();
    }

    function detailRow(label, valueHtml) {
        return '<div class="stats-dialog__row"><dt>' + label + '</dt><dd>' + valueHtml + '</dd></div>';
    }

    function openTrophyDialog(tile) {
        var name = escapeHtml(tile.name);
        if (tile.state === 'nosize') {
            openDialog(pbImgHtml(tile.species, 180, true, 'stats-dialog__img')
                + '<h2 class="stats-dialog__title" id="statsDialogTitle">' + name + '</h2>'
                + '<p class="stats-dialog__lead">' + plural(tile.count, 'catch', 'catches') + '</p>'
                + '<p class="stats-dialog__text">None has a length or weight yet. Add one when you log your next ' + escapeHtml(tile.name.toLowerCase()) + ' to set a personal best.</p>',
                'statsDialogTitle');
            return;
        }

        var b = tile.best;
        var snap = snapshotOf(b);
        var rows = '';
        if (b.location_name) rows += detailRow('Spot', escapeHtml(b.location_name));
        if (b.catch_datetime) rows += detailRow('Caught', formatDate(b.catch_datetime, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }));
        if (b.bait_used) rows += detailRow('Bait', escapeHtml(b.bait_used));
        if (b.fishing_method) rows += detailRow('Method', escapeHtml(b.fishing_method));
        if (snap.tideDescription) rows += detailRow('Tide', escapeHtml(String(snap.tideDescription).replace(' | ', ', ')));
        if (snap.fbi != null) rows += detailRow('FBI when caught', fbiPillHtml(snap.fbi));

        openDialog(pbImgHtml(tile.species, 180, false, 'stats-dialog__img')
            + '<h2 class="stats-dialog__title" id="statsDialogTitle">' + name + ' personal best</h2>'
            + '<p class="stats-dialog__lead">' + measureParts(b).join(', ') + '</p>'
            + (rows ? '<dl class="stats-dialog__rows">' + rows + '</dl>' : ''),
            'statsDialogTitle');
    }

    function openBadgeDialog(badge) {
        var status = badge.unlocked ? 'Earned' : 'Locked';
        var progress = !badge.unlocked && badge.progress
            ? '<p class="stats-dialog__text">' + badge.progress.have + ' of ' + badge.progress.need + '. ' + escapeHtml(progressHint(badge)) + '.</p>'
            : '';
        openDialog(badgeImgHtml(badge, 160, 'stats-dialog__img')
            + '<h2 class="stats-dialog__title" id="statsDialogTitle">' + escapeHtml(badge.name) + '</h2>'
            + '<p class="stats-dialog__lead' + (badge.unlocked ? ' stats-dialog__lead--earned' : '') + '">' + status + '</p>'
            + progress
            + '<p class="stats-dialog__text">' + escapeHtml(BADGE_DESCRIPTIONS[badge.key] || '') + '</p>',
            'statsDialogTitle');
    }

    // -------------------------------------------------------------------------
    // Render: empty state
    // -------------------------------------------------------------------------

    function renderEmpty() {
        return '<div class="analytics-empty">'
            + '<div class="analytics-empty__icon" aria-hidden="true">🎣</div>'
            + '<h3 class="analytics-empty__title">No catches yet</h3>'
            + '<p class="analytics-empty__text">Start logging catches to see your personal stats, badges, and trends.</p>'
            + '<button type="button" class="btn-primary analytics-empty__cta" data-stats-action="log-catch">Log Your First Catch</button>'
            + '</div>';
    }

    // -------------------------------------------------------------------------
    // Orchestrator
    // -------------------------------------------------------------------------

    function bindEvents(el) {
        if (_eventsBound) return;
        _eventsBound = true;
        el.addEventListener('click', function (e) {
            if (!_data) return;
            var trophy = e.target.closest('[data-stats-trophy]');
            if (trophy) {
                openTrophyDialog(_tiles[Number(trophy.getAttribute('data-stats-trophy'))]);
                return;
            }
            var badgeBtn = e.target.closest('[data-stats-badge]');
            if (badgeBtn) {
                var key = badgeBtn.getAttribute('data-stats-badge');
                var badge = (_data.badges || []).filter(function (b) { return b.key === key; })[0];
                if (badge) openBadgeDialog(badge);
                return;
            }
            var action = e.target.closest('[data-stats-action]');
            if (!action) return;
            var actionName = action.getAttribute('data-stats-action');
            switch (actionName) {
                case 'log-catch':
                    if (typeof switchTab === 'function') switchTab('logCatch');
                    break;
                case 'toggle-badges': {
                    var section = el.querySelector('.stats-badges');
                    var open = section.classList.toggle('stats-badges--expanded');
                    action.setAttribute('aria-expanded', String(open));
                    action.textContent = open ? 'Show fewer badges' : 'Show all ' + (_data.badges || []).length + ' badges';
                    break;
                }
                case 'dismiss-banner':
                case 'see-badges': {
                    writeSeenBadges(unlockedKeys(_data.badges || []));
                    var banner = el.querySelector('[data-stats-banner]');
                    if (banner) banner.remove();
                    if (actionName === 'see-badges') {
                        var target = document.getElementById('statsBadges');
                        if (target) target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
                    }
                    break;
                }
            }
        });
    }

    function disconnectChart() {
        if (_chartObserver) _chartObserver.disconnect();
        _chartObserver = null;
    }

    function render(data) {
        var el = document.getElementById('analyticsContent');
        if (!el) return;
        bindEvents(el);
        disconnectChart();

        if (isEmpty(data)) {
            el.innerHTML = renderEmpty();
            return;
        }

        var badges = data.badges || [];
        var groups = badgeGroups(badges);
        var months = fillMonths(data.monthlyCounts, new Date());
        _tiles = trophyTiles(data.personalBests, data.speciesBreakdown);

        var columns = renderNextUp(badges) + renderSpots(data.topSpots) + renderMethods(data.methods);

        el.innerHTML = '<div class="stats-dashboard">'
            + renderBanner(groups, badges)
            + renderHero(data, _tiles, months)
            + renderTrophyWall(_tiles)
            + (columns ? '<div class="stats-columns">' + columns + '</div>' : '')
            + renderBadgeCase(groups, badges)
            + '</div>';
        mountTide(el, months);
    }

    // -------------------------------------------------------------------------
    // Data loading
    // -------------------------------------------------------------------------

    function loadAnalytics() {
        var el = document.getElementById('analyticsContent');
        if (el) {
            el.innerHTML = '<div class="analytics-status">'
                + '<div class="analytics-status__spinner" role="status"></div>'
                + '<span>Loading stats…</span>'
                + '</div>';
        }

        AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/catches/analytics')
            .then(function (res) { return res.json(); })
            .then(function (json) {
                if (!json.success) throw new Error(json.error || 'Failed to load analytics');
                _data = json.analytics;
                render(_data);
            })
            .catch(function (err) {
                console.error('[Analytics] Load error:', err.message);
                if (el) {
                    el.innerHTML = '<div class="analytics-status">'
                        + '<p class="analytics-status__message">Failed to load stats. <button class="analytics-status__retry" id="analyticsRetryBtn">Retry</button></p>'
                        + '</div>';
                    var retryBtn = document.getElementById('analyticsRetryBtn');
                    if (retryBtn) {
                        retryBtn.addEventListener('click', function () {
                            _initialized = false;
                            init();
                        });
                    }
                }
            });
    }

    // -------------------------------------------------------------------------
    // Public API
    // -------------------------------------------------------------------------

    function init() {
        if (_initialized) return;
        _initialized = true;
        loadAnalytics();
    }

    function clear() {
        _initialized = false;
        _data = null;
        _tiles = [];
        disconnectChart();
        var el = document.getElementById('analyticsContent');
        if (el) el.innerHTML = '';
    }

    return {
        init: init,
        clear: clear,
        // Pure helpers, exposed for tests
        fillMonths: fillMonths,
        trophyTiles: trophyTiles,
        nextUpBadges: nextUpBadges,
        progressHint: progressHint,
        isEmpty: isEmpty
    };

})();

if (typeof module !== 'undefined' && module.exports) {
    module.exports = ANALYTICS;
}

;
/* === js/admin.js === */
/**
 * Admin Config Tab — frontend IIFE
 *
 * USED BY: main-server.js (switchTab lazy-init)
 * USES:    /api/admin/config endpoints
 * EXPORTS: window.ADMIN { init }
 */
var ADMIN = (function () {
    var _initialized = false;

    function init() {
        if (_initialized) return;
        _initialized = true;
        loadConfig();

        var saveBtn = document.getElementById('adminSaveConfigBtn');
        if (saveBtn) saveBtn.addEventListener('click', saveConfig);
    }

    function loadConfig() {
        AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/admin/config')
            .then(function (res) {
                if (!res.ok) throw new Error('HTTP ' + res.status);
                return res.json();
            })
            .then(function (data) {
                if (!data.success) return;
                var ttlInput = document.getElementById('adminSpotTtl');
                var radiusInput = document.getElementById('adminClusterRadius');
                if (ttlInput) ttlInput.value = data.config.spot_weather_ttl_hours;
                if (radiusInput) radiusInput.value = data.config.spot_cluster_radius_km;
            })
            .catch(function (err) {
                console.error('[Admin] Config load error:', err.message);
                showStatus('Failed to load config: ' + err.message, false);
            });
    }

    function saveConfig() {
        var ttlInput = document.getElementById('adminSpotTtl');
        var radiusInput = document.getElementById('adminClusterRadius');
        if (!ttlInput || !radiusInput) return;

        var updates = [
            { key: 'spot_weather_ttl_hours', value: Number(ttlInput.value) },
            { key: 'spot_cluster_radius_km', value: Number(radiusInput.value) }
        ];

        Promise.all(updates.map(function (update) {
            return AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/admin/config', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(update)
            }).then(function (res) {
                if (!res.ok) throw new Error('HTTP ' + res.status);
                return res.json();
            });
        }))
            .then(function (results) {
                var failed = results.filter(function (r) { return !r.success; });
                if (failed.length > 0) {
                    showStatus('Error: ' + failed[0].error, false);
                } else {
                    showStatus('Configuration saved', true);
                }
            })
            .catch(function (err) {
                console.error('[Admin] Config save error:', err.message);
                showStatus('Save failed: ' + err.message, false);
            });
    }

    function showStatus(message, success) {
        var el = document.getElementById('adminConfigStatus');
        if (!el) return;
        el.textContent = message;
        el.className = 'admin-config-status ' +
            (success ? 'admin-config-status--success' : 'admin-config-status--error');
        el.style.opacity = '1';
        setTimeout(function () { el.style.opacity = '0'; }, 3000);
    }

    // --- Forecast table image export (admin-only content creation tool) ---

    /**
     * Dynamic loader for html2canvas. Self-hosted at /js/vendor/, loaded on demand —
     * non-admin users never load the 190KB library since the export button doesn't render for them.
     */
    var _html2canvasReady = null;
    function loadHtml2Canvas() {
        if (_html2canvasReady) return _html2canvasReady;
        _html2canvasReady = new Promise(function (resolve, reject) {
            if (window.html2canvas) return resolve();
            var s = document.createElement('script');
            s.src = '/js/vendor/html2canvas.min.js';
            s.onload = resolve;
            s.onerror = function () {
                _html2canvasReady = null; // Allow retry
                reject(new Error('Failed to load export library'));
            };
            document.head.appendChild(s);
        });
        return _html2canvasReady;
    }

    function sanitizeFilenamePart(str) {
        var cleaned = String(str || '').trim().replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        return cleaned || 'Location';
    }

    /**
     * Capture the currently visible columns of a forecast table as a PNG and download it.
     * @param {HTMLElement} btn - the clicked .forecast-table-export-btn, carries data-export-target/-name
     */
    function exportForecastImage(btn) {
        var target = document.getElementById(btn.dataset.exportTarget);
        if (!target) return;

        var originalHTML = btn.innerHTML;
        btn.disabled = true;

        // html2canvas's x/y crop options operate in the cloned document's page
        // coordinates, not the target's own scroll space — with a wide, horizontally
        // scrolled table that crops into unrendered space and yields a blank image.
        // Render the full table (with the clone's overflow opened up so nothing gets
        // clipped) and crop client-side to the visible viewport instead.
        var scale = Math.max(window.devicePixelRatio || 1, 2);
        var cropX = target.scrollLeft;
        var cropY = target.scrollTop;
        var cropW = target.clientWidth;
        var cropH = target.clientHeight;

        loadHtml2Canvas()
            .then(function () {
                return window.html2canvas(target, {
                    width: target.scrollWidth,
                    height: target.scrollHeight,
                    backgroundColor: '#ffffff',
                    scale: scale,
                    onclone: function (clonedDoc, clonedEl) {
                        clonedEl.style.overflow = 'visible';
                    }
                });
            })
            .then(function (fullCanvas) {
                var out = document.createElement('canvas');
                out.width = cropW * scale;
                out.height = cropH * scale;
                out.getContext('2d').drawImage(
                    fullCanvas,
                    cropX * scale, cropY * scale, cropW * scale, cropH * scale,
                    0, 0, cropW * scale, cropH * scale
                );

                var dateStr = new Date().toISOString().slice(0, 10);
                var filename = sanitizeFilenamePart(btn.dataset.exportName) + '_ForecastTable_' + dateStr + '.png';
                var link = document.createElement('a');
                link.download = filename;
                link.href = out.toDataURL('image/png');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .catch(function (err) {
                console.error('[Admin] Forecast export failed:', err.message);
                if (typeof showAppToast === 'function') showAppToast('Export failed: ' + err.message, 'error');
            })
            .finally(function () {
                btn.disabled = false;
                btn.innerHTML = originalHTML;
            });
    }

    return { init: init, exportForecastImage: exportForecastImage };
})();

;
/* === js/devices.js === */
/**
 * Settings > Devices
 *
 * USED BY: main-server.js → switchTab('settings') calls DEVICES.load()
 * USES: AUTH_CLIENT.fetchWithAuth, API_CLIENT.baseURL,
 *       escapeHtml/timeAgo/showDeleteConfirmModal (ui-helpers.js), showAppToast (main-server.js)
 * EXPORTS: DEVICES global (IIFE); pure builders via module.exports for tests
 *
 * Lists the signed-in devices for the account ("1 of 2 devices") and lets the user sign out
 * any device other than this one. Revoking a session stops its refresh token right away, but
 * its current access token stays valid until it expires (up to an hour), so the copy says so.
 */

var DEVICES_REQUEST_TIMEOUT_MS = 8000;

// escapeHtml() covers text content but not quotes, so attribute values need these too
function escapeDeviceAttr(value) {
    return escapeHtml(String(value)).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function buildDevicesCountText(count, limit) {
    if (!limit) return count === 1 ? '1 device' : count + ' devices';
    return count + ' of ' + limit + (limit === 1 ? ' device' : ' devices');
}

function buildDeviceRowHTML(session, nowMs) {
    var label = session.deviceLabel || 'Unknown device';
    var lastActiveMs = Date.parse(session.lastActivityAt);
    var activeText = Number.isFinite(lastActiveMs)
        ? 'Active ' + timeAgo(Math.floor((nowMs - lastActiveMs) / 1000))
        : '';
    var meta = [activeText, session.ipAddress].filter(Boolean).join(' · ');

    return '<li class="settings-device" data-session-id="' + escapeDeviceAttr(session.id) + '">' +
        '<div class="settings-device__info">' +
            '<div class="settings-device__label">' + escapeHtml(label) +
                (session.isCurrent ? ' <span class="settings-role-badge">This device</span>' : '') +
            '</div>' +
            (meta ? '<div class="settings-device__meta">' + escapeHtml(meta) + '</div>' : '') +
        '</div>' +
        (session.isCurrent
            ? ''
            : '<button type="button" class="settings-device__signout" data-session-id="' + escapeDeviceAttr(session.id) +
                '" data-label="' + escapeDeviceAttr(label) + '">Sign out</button>') +
    '</li>';
}

var DEVICES = (function() {
    var _clickHandlerAttached = false;
    var _loadSeq = 0; // only the newest load may render

    function _elements() {
        return {
            section: document.getElementById('settingsDevicesSection'),
            list: document.getElementById('settingsDevicesList'),
            count: document.getElementById('settingsDevicesCount')
        };
    }

    // fetchWithAuth with an abort timeout (every network call gets one)
    function _request(path, options) {
        var controller = new AbortController();
        var timer = setTimeout(function() { controller.abort(); }, DEVICES_REQUEST_TIMEOUT_MS);
        var opts = Object.assign({}, options, { signal: controller.signal });
        return AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + path, opts)
            .then(function(response) { return response.json(); })
            .finally(function() { clearTimeout(timer); });
    }

    function _renderError() {
        var els = _elements();
        if (!els.list) return;
        els.count.textContent = '';
        els.list.innerHTML = '<li class="settings-device settings-device--error">' +
            '<span>Couldn\'t load devices.</span> ' +
            '<button type="button" class="settings-device__retry">Try again</button>' +
        '</li>';
    }

    function render(data) {
        var els = _elements();
        if (!els.section || !els.list) return;
        var sessions = data.sessions || [];
        var now = Date.now();
        els.count.textContent = buildDevicesCountText(sessions.length, data.deviceLimit);
        els.list.innerHTML = sessions.map(function(s) { return buildDeviceRowHTML(s, now); }).join('');
        els.section.hidden = false;
    }

    function load() {
        var els = _elements();
        if (!els.section || typeof AUTH_CLIENT === 'undefined' || !AUTH_CLIENT.isAuthenticated()) return;
        _attachClickHandler();

        var thisLoad = ++_loadSeq;
        _request('/auth/sessions')
            .then(function(data) {
                if (thisLoad !== _loadSeq) return; // a newer load started
                if (!data.success) throw new Error(data.error || 'Failed to load devices');
                render(data);
            })
            .catch(function(err) {
                if (thisLoad !== _loadSeq) return;
                console.error('[Devices] Load failed:', err.message);
                els.section.hidden = false;
                _renderError();
            });
    }

    function _signOut(sessionId, label) {
        _request('/auth/sessions/' + encodeURIComponent(sessionId), { method: 'DELETE' })
            .then(function(data) {
                if (!data.success) throw new Error(data.error || 'Failed to sign out device');
                showAppToast('Device signed out', 'success');
                load();
            })
            .catch(function(err) {
                console.error('[Devices] Sign out failed:', err.message);
                showAppToast('Couldn\'t sign out ' + label + '. Try again.', 'error');
            });
    }

    function _attachClickHandler() {
        if (_clickHandlerAttached) return;
        var list = _elements().list;
        if (!list) return;
        _clickHandlerAttached = true;
        list.addEventListener('click', function(e) {
            if (e.target.closest('.settings-device__retry')) {
                load();
                return;
            }
            var btn = e.target.closest('.settings-device__signout');
            if (!btn) return;
            var sessionId = btn.dataset.sessionId;
            var label = btn.dataset.label;
            showDeleteConfirmModal(
                label,
                function() { _signOut(sessionId, label); },
                'Sign out ' + label + '? That device will be signed out within the hour.',
                'Sign out'
            );
        });
    }

    return { load: load, render: render };
})();

if (typeof module !== 'undefined' && module.exports) {
    module.exports = { buildDeviceRowHTML: buildDeviceRowHTML, buildDevicesCountText: buildDevicesCountText };
}

;
/* === js/main-server.js === */
/**
 * MAIN APPLICATION LOGIC - Reel Talk Fishing App
 *
 * LOADED BY: app.html / app.ejs via <script src="/js/main-server.js">
 * DEPENDS ON: api-client.js, config-frontend.js, ui-builders.js, ui-helpers.js, chart.js,
 *             shared-utils.js, weather.js, gear.js, map.js, map-overlays.js, loading.js,
 *             auth-client.js, auth-ui.js, brag-board.js, tos-content.js
 * EXPORTS: window.allConditionsData (global state used by brag-board.js, gear.js)
 *
 * This is the primary entry point for the Reel Talk fishing app.
 * All data fetching and scoring logic happens on the backend server.
 *
 * CRITICAL FUNCTIONS (test after any changes):
 * - compareAllLocations() - Main data fetch from server
 * - renderTableByRegion() - Table rendering with filtering
 * - displayComparison() - UI coordination
 *
 * SECTIONS:
 * - Global State & Configuration
 * - Tab Navigation
 * - Data Timestamp & Refresh
 * - Data Fetching
 * - Table Rendering (CRITICAL)
 * - Consent & TOS Flow
 * - Initialization
 */

/* ==========================================================================
   GLOBAL STATE & CONFIGURATION
   ========================================================================== */

// Global storage for fishing conditions data
window.allConditionsData = null;

// Single choke point for all main data-load assignments.
// Dispatches 'conditionsUpdated' for future listeners; does not touch
// in-place item mutations (auth upgrade, spots.js) — those remain direct.
function setConditionsData(data) {
    window.allConditionsData = data;
    window.dispatchEvent(new CustomEvent('conditionsUpdated', { detail: { data: data } }));
}

// Cross-module logbook state — JOURNAL writes, CATCHES reads
var LOGBOOK_STATE = { activeTripId: null };

// Global variable to store update timestamp
let dataUpdatedAt = null;
let countdownInterval = null;

// SSR hydration flag
let isSSRHydrated = false;

// Prevents duplicate concurrent compareAllLocations() calls (e.g. showMainApp + manual call)
let conditionsLoadInFlight = null;

// Prefetch promise for fishing conditions (started during auth callback)
let conditionsPrefetchPromise = null;

// Light haptic tap for mobile feedback
function hapticTap() {
    if (navigator.vibrate) navigator.vibrate(10);
}

// Simple toast notification for app-level messages
function showAppToast(message, type, duration) {
    duration = duration || 4000;
    var toast = document.getElementById('app-toast');
    if (!toast) {
        toast = document.createElement('div');
        toast.id = 'app-toast';
        toast.className = 'app-toast';
        // Live region exists before its first message so screen readers announce updates
        toast.setAttribute('role', 'status');
        document.body.appendChild(toast);
    }
    // Errors interrupt; everything else waits for the reader to finish
    toast.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
    toast.textContent = message;
    toast.className = 'app-toast' + (type ? ' app-toast--' + type : '');
    toast.classList.add('show');
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(function() { toast.classList.remove('show'); }, duration);
}

// Checkout return: poll for pro activation after Stripe redirect (A4, A16)
var _checkoutPollTimeout = null;
function pollForProActivation() {
    var delays = [2000, 3000, 4000, 5000, 6000];
    var attempt = 0;

    function poll() {
        AUTH_CLIENT.fetchUserProfile().then(function() {
            if (AUTH_CLIENT.membershipTier === 'pro' || AUTH_CLIENT.membershipTier === 'elite') {
                // Success — clear gating caches and unlock Pro map overlays, show toast
                clearGatedCaches();
                // Update role badge
                if (typeof AUTH_UI !== 'undefined') AUTH_UI.showUserMenu();
                showAppToast('Welcome to Pro! Enjoy full access.', 'success', 5000);
                return;
            }
            attempt++;
            if (attempt < delays.length) {
                _checkoutPollTimeout = setTimeout(poll, delays[attempt]);
            } else {
                // Timeout — show fallback message
                showAppToast('Still activating \u2014 refresh the page in a moment.', 'info', 8000);
            }
        }).catch(function() {
            attempt++;
            if (attempt < delays.length) {
                _checkoutPollTimeout = setTimeout(poll, delays[attempt]);
            }
        });
    }

    _checkoutPollTimeout = setTimeout(poll, delays[0]);
    // A16: cleanup on navigation
    window.addEventListener('beforeunload', function() {
        clearTimeout(_checkoutPollTimeout);
    });
}

/**
 * Handle checkout URL params on page load.
 * Called after auth session is restored in DOMContentLoaded.
 */
function handleCheckoutReturn() {
    var params = new URLSearchParams(window.location.search);
    var checkoutStatus = params.get('checkout');
    if (!checkoutStatus) return;

    // Clean URL immediately
    window.history.replaceState({}, '', window.location.pathname + window.location.hash);

    if (checkoutStatus === 'success') {
        showAppToast('Activating your Pro subscription…', 'info', 10000);
        pollForProActivation();
    } else if (checkoutStatus === 'cancelled') {
        showAppToast('Checkout cancelled.', 'info');
    }
}

/**
 * Handle ?plan=pro URL param from landing page CTA.
 * If authenticated, shows upgrade modal immediately.
 * If not, stashes flag for handleAuthSuccess to pick up after login.
 */
var _pendingPlanUpgrade = false;
function handlePlanParam() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('plan') !== 'pro') return;

    // Clean URL immediately
    window.history.replaceState({}, '', window.location.pathname + window.location.hash);

    if (AUTH_CLIENT.isAuthenticated()) {
        // Already logged in — show upgrade modal after brief delay for UI to settle
        setTimeout(function() {
            if (typeof showUpgradeModal === 'function') showUpgradeModal();
        }, 500);
    } else {
        // Not logged in yet — stash flag for post-login
        _pendingPlanUpgrade = true;
    }
}

// Configuration constants (extracted from magic numbers throughout the file)
// Named APP_TIMING to avoid collision with CONFIG in config-frontend.js (both are global scope)
const APP_TIMING = {
    REC_FETCH_TIMEOUT_MS: 5000,      // AbortController timeout for recommendations fetch
    SSE_TIMEOUT_MS: 45000,            // SSE stream timeout before batch fallback
    MAX_FORECAST_PERIODS: 168,        // Weather forecast slice limit (7 days × 24h all-hourly)
    TIDE_CHART_DELAY_MS: 100,         // setTimeout delay for tide chart drawing
    BG_PRELOAD_MARGIN: '200px',       // IntersectionObserver rootMargin for lazy bg images
    AUTH_TIMEOUT_MS: 3000,            // Auth timeout before showing greeting screen
    COUNTDOWN_INTERVAL_MS: 30000,     // "Updated N min ago" refresh; smallest unit shown is minutes
};

// Table state: event delegation flags, expand tracking, caches
const tableState = {
    expandedKey: null,
    allConditions: null,
    handlers: {
        table: false,
        recsRetry: false,
        species: false,
        cardExpand: false,
        baitItem: false,
        rigItem: false,
        proUpgrade: false,
    },
    detailCache: new Map(),
    recsCache: new Map(),       // Per-session bait/rig recommendations keyed by locationKey
    renderedKeys: new Set(),    // Tracks which rows exist in DOM for incremental SSE append
};

function resetTableState() {
    tableState.expandedKey = null;
    tableState.detailCache.clear();
    tableState.renderedKeys.clear();
    // NOTE: Do NOT clear recsCache (Issue 16A) or handler flags (delegated handlers survive DOM rebuild)
}

let tabsInitialized = false;

// Lazy background images: IntersectionObserver loads images as rows scroll into view
let bgObserver = null;
function setupLazyBackgrounds(newRows) {
    const rows = newRows || document.querySelectorAll('.location-row[data-bg-url]');
    if (!rows.length) return;

    if (!('IntersectionObserver' in window)) {
        // Fallback: load all immediately
        rows.forEach(row => {
            const td = row.querySelector('td');
            if (td) td.style.backgroundImage = `url('${row.dataset.bgUrl}')`;
            row.removeAttribute('data-bg-url');
        });
        return;
    }

    if (!bgObserver) {
        bgObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const row = entry.target;
                    const td = row.querySelector('td');
                    if (td && row.dataset.bgUrl) {
                        td.style.backgroundImage = `url('${row.dataset.bgUrl}')`;
                        row.removeAttribute('data-bg-url');
                    }
                    bgObserver.unobserve(row);
                }
            });
        }, { rootMargin: APP_TIMING.BG_PRELOAD_MARGIN });
    }

    rows.forEach(row => bgObserver.observe(row));
}

/* ==========================================================================
   SERVICE WORKER REGISTRATION
   ========================================================================== */

if ('serviceWorker' in navigator) {
    // Auto-reload when a new SW takes control (ensures users get fresh assets)
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
            refreshing = true;
            console.log('[SW] New version active — reloading for fresh assets');
            window.location.reload();
        }
    });

    window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
            .then(reg => {
                console.log('[SW] Registered, scope:', reg.scope);
                // Check for waiting SW that installed while user was on the page
                if (reg.waiting) {
                    reg.waiting.postMessage({ type: 'SKIP_WAITING' });
                }
                // Listen for new SW installing in the background
                reg.addEventListener('updatefound', () => {
                    const newWorker = reg.installing;
                    if (!newWorker) return;
                    newWorker.addEventListener('statechange', () => {
                        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                            // New SW ready — tell it to activate immediately
                            newWorker.postMessage({ type: 'SKIP_WAITING' });
                        }
                    });
                });
            })
            .catch(err => console.error('[SW] Registration failed:', err));
    });
}

/* ==========================================================================
   TAB NAVIGATION
   ========================================================================== */

/**
 * Initialize tab navigation functionality
 */
function initializeTabs() {
    if (tabsInitialized) return;
    tabsInitialized = true;
    const bottomNavItems = document.querySelectorAll('.bottom-nav-item');
    const tabContents = document.querySelectorAll('.tab-content');

    /**
     * @param {string} targetTab
     * @param {{history?: 'push'|'replace'|'none'}} [options] - How to record the tab in the URL hash.
     *   'push' (default, user navigation) adds a Back step; 'replace' normalizes the URL without one;
     *   'none' is for popstate, where the URL is already right.
     */
    function switchTab(targetTab, options) {
        updateTabHash(targetTab, (options && options.history) || 'push');

        // Re-parent the shared desktop inline filter group into the target tab's header
        // (function defined in map.js). Only moves the node — listeners are wired once at
        // init in initInlineControls(), never here.
        if (typeof reparentInlineFilterGroup === 'function') {
            reparentInlineFilterGroup(targetTab);
        }

        // Clear all view classes, then set the one for the target tab
        // (view-table removed — List/Map toggle no longer exists in Angler Map tab)
        document.body.classList.remove('view-map', 'view-table', 'view-brag', 'view-settings', 'view-myspots', 'view-logcatch', 'view-admin', 'view-analytics');
        if (targetTab === 'publicSpots') {
            document.body.classList.add('view-map');
        } else if (targetTab === 'bragBoard') {
            document.body.classList.add('view-brag');
        } else if (targetTab === 'settings') {
            document.body.classList.add('view-settings');
        } else if (targetTab === 'mySpots') {
            document.body.classList.add('view-myspots');
        } else if (targetTab === 'logCatch') {
            document.body.classList.add('view-logcatch');
        } else if (targetTab === 'analytics') {
            document.body.classList.add('view-analytics');
        } else if (targetTab === 'admin') {
            document.body.classList.add('view-admin');
        }

        // Mark the target tab current (class + aria-current) in both nav systems
        tabContents.forEach(content => content.classList.remove('active'));
        setNavCurrent(bottomNavItems, targetTab);
        setNavCurrent(document.querySelectorAll('.v2-sidebar-item'), targetTab);

        var targetContent = document.getElementById('tab-' + targetTab);
        if (targetContent) {
            targetContent.classList.add('active');
        }

        // Resize map when switching to Public Spots tab
        // fishingMap is a top-level `let` in map.js: a global binding, not a window property
        if (targetTab === 'publicSpots' && typeof fishingMap !== 'undefined' && fishingMap) {
            requestAnimationFrame(function() { fishingMap.invalidateSize(); });
        }

        // Initialize Brag Board when switching to that tab
        if (targetTab === 'bragBoard' && typeof initBragBoard === 'function') {
            initBragBoard();
        }

        // Lazy-init Spots when switching to My Spots tab
        if (targetTab === 'mySpots' && typeof SPOTS !== 'undefined') {
            SPOTS.init();
            // Refresh public list on every visit (data may have updated since last visit)
            if (window.allConditionsData) SPOTS.renderPublicList(window.allConditionsData);
        }

        // Lazy-init Catches + Journal when switching to Log Catch tab
        if (targetTab === 'logCatch' && typeof CATCHES !== 'undefined') {
            CATCHES.init();
        }
        if (targetTab === 'logCatch' && typeof JOURNAL !== 'undefined') {
            JOURNAL.init();
        }

        // Lazy-init Admin when switching to Admin tab
        if (targetTab === 'admin' && typeof ADMIN !== 'undefined') {
            ADMIN.init();
        }

        // Refresh the signed-in devices list on every Settings visit
        if (targetTab === 'settings' && typeof DEVICES !== 'undefined') {
            DEVICES.load();
        }

        // Lazy-init Analytics when switching to My Stats tab
        if (targetTab === 'analytics' && typeof ANALYTICS !== 'undefined') {
            ANALYTICS.init();
        }

        // Scroll to top on tab switch (native feel)
        window.scrollTo({ top: 0, behavior: scrollBehavior() });
    }

    function updateTabHash(targetTab, mode) {
        if (mode === 'none') return;
        const hash = hashForTab(targetTab);
        if (window.location.hash === hash) return;
        const url = window.location.pathname + window.location.search + hash;
        if (mode === 'replace') {
            history.replaceState({ tab: targetTab }, '', url);
        } else {
            history.pushState({ tab: targetTab }, '', url);
        }
    }

    function isSignedIn() {
        return typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.isAuthenticated();
    }

    // Expose switchTab globally so sidebar IIFE and more-menu can call it
    window.switchTab = switchTab;

    // Back/Forward between tabs; 'replace' fixes a hash the current user may not open
    window.addEventListener('popstate', function() {
        switchTab(resolveTabFromHash(window.location.hash, isSignedIn()), { history: 'replace' });
    });

    // Open the tab named in the URL (deep link or reload) instead of always the map
    if (window.location.hash) {
        switchTab(resolveTabFromHash(window.location.hash, isSignedIn()), { history: 'replace' });
    }

    // Attach to bottom nav items (skip More button — no data-tab, handled by more-menu.js)
    bottomNavItems.forEach(button => {
        button.addEventListener('click', () => {
            var tab = button.getAttribute('data-tab');
            if (!tab) return;
            hapticTap();
            switchTab(tab);
        });
    });
}

/* ==========================================================================
   DATA TIMESTAMP & REFRESH
   ========================================================================== */

/**
 * Update countdown timer showing time since last update
 * Displays in banner overlay
 */
function updateCountdown() {
    const timestampElement = document.getElementById('dataTimestamp');
    if (!timestampElement || !dataUpdatedAt) return;

    const timeText = timeAgo(Math.floor((Date.now() - dataUpdatedAt) / 1000));

    timestampElement.textContent = timeText;

    setMobileUpdatedText(timeText, 'Refresh data, updated ' + timeText);
}

/**
 * Mobile "Updated" chip in the Angler Map header (the desktop footer shows the same time).
 * @param {string} text - Visible text, e.g. "5 min ago" or "Loading…"
 * @param {string} label - Accessible name for the refresh button
 */
function setMobileUpdatedText(text, label) {
    const textEl = document.getElementById('mobileDataTimestamp');
    if (textEl) textEl.textContent = text;
    const chip = document.getElementById('mobileUpdatedChip');
    if (chip) chip.setAttribute('aria-label', label);
}

/**
 * Refresh data by reloading all fishing conditions
 */
function refreshData() {
    compareAllLocations();
}

/* ==========================================================================
   DATA FETCHING
   ========================================================================== */

/**
 * Prefetch fishing conditions data in background (called during Google auth callback).
 * Starts a batch API call so data is ready by the time the app UI shows.
 * Does NOT touch the DOM — just fetches and buffers.
 */
function prefetchConditionsData() {
    if (conditionsPrefetchPromise || conditionsLoadInFlight || window.allConditionsData) return;
    console.log('[Main] Prefetching fishing conditions during auth...');
    conditionsPrefetchPromise = API_CLIENT.getFishingConditions().catch(err => {
        console.warn('[Main] Prefetch failed (will retry normally):', err.message);
        return null;
    });
}

/**
 * Fetch and display fishing conditions for all locations
 * Data is pre-cached on server (warmed on deploy, refreshed every 24h)
 * Users can manually refresh using the refresh button
 */
async function compareAllLocations() {
    // Prevent duplicate concurrent calls (showMainApp + explicit call race condition)
    if (conditionsLoadInFlight) {
        console.log('[Main] compareAllLocations already in progress, skipping duplicate call');
        return conditionsLoadInFlight;
    }

    // Set mutex BEFORE the IIFE starts executing (closes theoretical race window)
    let resolveLoad, rejectLoad;
    conditionsLoadInFlight = new Promise((r, j) => { resolveLoad = r; rejectLoad = j; });

    const loadPromise = (async () => {
        // Get DOM elements
        const tabContainer = document.getElementById('tabContainer');
        const errorDiv = document.getElementById('error');
        const timestampElement = document.getElementById('dataTimestamp');

        // Safety check for DOM elements
        if (!tabContainer || !errorDiv) {
            console.error('[Main] Critical DOM elements missing:', { tabContainer: !!tabContainer, errorDiv: !!errorDiv });
            return;
        }

        // Show main content immediately (data is cached on server)
        tabContainer.classList.remove('u-hidden');
        errorDiv.style.display = 'none';

        // Show loading state in timestamp
        if (timestampElement) {
            timestampElement.textContent = 'Loading…';
        }
        setMobileUpdatedText('Loading…', 'Refresh data, loading');

        // Initialize tabs while data loads
        initializeTabs();

        // Defer map init to next frame so the freshly-unhidden container has
        // layout dimensions — avoids Leaflet's "Cannot read 'x' of undefined"
        // (offset.js) when the map div has zero size.
        const txBounds = L.latLngBounds([26.0, -97.5], [30.0, -93.5]);
        requestAnimationFrame(function() {
            ensureMapInitialized(txBounds);
            if (fishingMap) {
                fishingMap.invalidateSize();
                fishingMap.fitBounds(txBounds, { padding: [50, 50], animate: false });
            }
        });

        try {
            // Use prefetched data if available (started during auth callback)
            if (conditionsPrefetchPromise) {
                const prefetched = await conditionsPrefetchPromise;
                conditionsPrefetchPromise = null;
                if (prefetched && prefetched.data && prefetched.data.length > 0) {
                    console.log('[Main] Using prefetched data:', prefetched.data.length, 'locations');
                    setConditionsData(prefetched.data);
                    dataUpdatedAt = new Date(prefetched.timestamp).getTime();
                    if (countdownInterval) clearInterval(countdownInterval);
                    countdownInterval = setInterval(updateCountdown, APP_TIMING.COUNTDOWN_INTERVAL_MS);
                    updateCountdown();
                    displayComparison(prefetched.data);
                    return;
                }
            }

            // Try SSE streaming first, fall back to batch
            if (typeof EventSource !== 'undefined') {
                await loadConditionsViaSSE();
            } else {
                await loadConditionsViaBatch();
            }
        } catch (error) {
            // Show user-friendly error message
            errorDiv.textContent = "Couldn't load fishing conditions. Check your connection, then refresh.";
            errorDiv.style.display = 'block';
            console.error('[Main] Error loading data:', error);
            if (timestampElement) {
                timestampElement.textContent = 'error';
            }
            setMobileUpdatedText('Tap to retry', 'Refresh data, last load failed');
        }
    })();

    const chip = document.getElementById('mobileUpdatedChip');
    if (chip) chip.setAttribute('aria-busy', 'true');
    loadPromise.then(resolveLoad, rejectLoad);
    loadPromise.finally(() => {
        conditionsLoadInFlight = null;
        if (chip) chip.removeAttribute('aria-busy');
    });
    return conditionsLoadInFlight;
}

/**
 * Load conditions via SSE streaming (progressive pin rendering)
 */
function loadConditionsViaSSE() {
    return new Promise((resolve, reject) => {
        // Reset incremental render state for fresh stream
        resetTableState();
        tableState.recsCache.clear();
        const table = document.getElementById('comparisonTable');
        if (table) table.innerHTML = '';

        const allConditions = [];
        const source = new EventSource(`${API_CLIENT.baseURL}/fishing-conditions/stream`);
        const timestampElement = document.getElementById('dataTimestamp');

        // Timeout: if no conditions received in 45s, fall back to batch
        let receivedData = false;
        const timeout = setTimeout(() => {
            if (!receivedData) {
                console.warn('[SSE] Timeout, falling back to batch');
                source.close();
                loadConditionsViaBatch().then(resolve).catch(reject);
            }
        }, APP_TIMING.SSE_TIMEOUT_MS);

        source.addEventListener('locations', (e) => {
            // Location metadata received — show skeleton pins on map
            const locationMeta = JSON.parse(e.data);
            console.log('[SSE] Received location metadata:', locationMeta.length, 'locations');
            showSkeletonPins(locationMeta);
        });

        source.addEventListener('conditions', (e) => {
            receivedData = true;
            const payload = JSON.parse(e.data);

            // Append new locations
            allConditions.push(...payload.locations);

            // Sort by score
            allConditions.sort((a, b) => b.conditions.score - a.conditions.score);

            // Progressive render: add new pins to map
            payload.locations.forEach(item => {
                progressiveAddMarker(item, allConditions);
            });

            // Progressive render: update table
            progressiveRenderTable(allConditions);

            // Update progress indicator
            if (timestampElement) {
                timestampElement.textContent = `Loading ${payload.progress} / ${payload.total}…`;
            }
            setMobileUpdatedText(`${payload.progress}/${payload.total}`, 'Refresh data, loading');
        });

        source.addEventListener('complete', (e) => {
            clearTimeout(timeout);
            source.close();

            const completion = JSON.parse(e.data);
            console.log('[SSE] Stream complete:', completion.total, 'locations', completion.cached ? '(cached)' : '(fresh)');

            // Store globally
            setConditionsData(allConditions);

            // Set timestamp
            dataUpdatedAt = new Date(completion.timestamp).getTime();
            if (countdownInterval) clearInterval(countdownInterval);
            countdownInterval = setInterval(updateCountdown, APP_TIMING.COUNTDOWN_INTERVAL_MS);
            updateCountdown();

            // Final render pass
            removeSkeletonPins();
            if (tableState.renderedKeys.size >= allConditions.length) {
                // All rows rendered progressively — skip table rebuild
                tableState.allConditions = allConditions;
                const tabContainer = document.getElementById('tabContainer');
                if (tabContainer) tabContainer.classList.remove('u-hidden');
                initializeTabs();
                const defaultState = 'Texas';
                showMapWithLocationsByRegion(allConditions, 'all', 'all', defaultState);
                populateRegionDropdown(allConditions);
                if (window.AUTH_UI) {
                    AUTH_UI.populateSettingsDropdowns();
                    AUTH_UI.applyDefaultFilters();
                    if (typeof showTopSpotCard === 'function') showTopSpotCard();
                }
                console.log('[SSE] Progressive render complete — skipped table rebuild');
            } else {
                displayComparison(allConditions);
            }

            // Load spot conditions after public conditions are ready
            if (typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.isAuthenticated() && typeof SPOTS !== 'undefined') {
                SPOTS.loadSpotConditions();
            }

            resolve();
        });

        source.addEventListener('error', () => {
            clearTimeout(timeout);
            source.close();
            console.warn('[SSE] Connection error, falling back to batch');

            if (allConditions.length > 0) {
                // Partial data received — use what we have
                setConditionsData(allConditions);
                removeSkeletonPins();
                displayComparison(allConditions);
                resolve();
            } else {
                loadConditionsViaBatch().then(resolve).catch(reject);
            }
        });
    });
}

/**
 * Load conditions via traditional batch API call (fallback)
 */
async function loadConditionsViaBatch() {
    console.log('[Main] Fetching fishing conditions (batch)...');
    const result = await API_CLIENT.getFishingConditions();
    console.log('[Main] Data received:', result?.data?.length, 'locations', result.cached ? '(cached)' : '(fresh)');

    // Store globally
    setConditionsData(result.data);

    // Record update time
    dataUpdatedAt = new Date(result.timestamp).getTime();
    if (countdownInterval) clearInterval(countdownInterval);
    countdownInterval = setInterval(updateCountdown, APP_TIMING.COUNTDOWN_INTERVAL_MS);
    updateCountdown();

    // Render everything
    displayComparison(result.data);

    // Load spot conditions after public conditions are ready
    if (typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.isAuthenticated() && typeof SPOTS !== 'undefined') {
        SPOTS.loadSpotConditions();
    }
}

/**
 * Delegated table click handler — shared by progressive, batch, and SSR paths.
 * Finds the clicked location row and toggles its expansion.
 */
function handleTableClick(e) {
    if (e.target.closest('.expanded-row')) return;
    const row = e.target.closest('.location-row');
    if (!row) return;
    hapticTap();
    const locationKey = row.getAttribute('data-location-key');
    console.log('[Table] Row clicked:', locationKey);
    window.lastExpandedLocationKey = locationKey;
    toggleRowExpansion(locationKey);
}

/**
 * Fire-and-forget beacon for named client-only UI interactions (gate-hit, card expand,
 * map-layer switch) that have no server round-trip today. Mirrors trackMaptilerSession()
 * in map.js. Never blocks or delays the UI action it's attached to.
 */
function sendInteractionBeacon(eventName, metadata) {
    const url = API_CLIENT.baseURL + '/interaction-event';
    const payload = JSON.stringify({ eventName, metadata: metadata || {} });
    if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    } else {
        fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(function () {});
    }
}

/**
 * Register a simple delegated click-to-toggle handler on document.
 * @param {string} flag - Key in tableState.handlers to guard against double-attach
 * @param {string} selector - CSS selector to match via closest()
 * @param {string} className - Class to toggle on the matched element
 * @param {string} [containerSelector] - Collapse siblings within this container on expand
 * @param {string} [interactionEventName] - If set, beacon this event when the item expands
 */
function registerToggleHandler(flag, selector, className, containerSelector, interactionEventName) {
    if (tableState.handlers[flag]) return;
    document.addEventListener('click', (e) => {
        const el = e.target.closest(selector);
        if (!el) return;
        e.stopPropagation();
        if (containerSelector) {
            const container = el.closest(containerSelector);
            if (container) {
                container.querySelectorAll('.' + className).forEach(other => {
                    if (other !== el) {
                        other.classList.remove(className);
                        other.setAttribute('aria-expanded', 'false');
                    }
                });
            }
        }
        const isNowExpanded = el.classList.toggle(className);
        el.setAttribute('aria-expanded', String(isNowExpanded));
        if (isNowExpanded && interactionEventName) {
            sendInteractionBeacon(interactionEventName, {});
        }
    });
    tableState.handlers[flag] = true;
}

/**
 * Register all document-level delegated handlers (once per page load).
 * Called from DOMContentLoaded. Safe to call multiple times — guards prevent double-attach.
 */
function registerDelegatedHandlers() {
    // Enter/Space opens location rows and species/bait/rig items, same as a click
    registerKeyboardActivation('.location-row, .species-item, .rec-item');

    // Accordion toggles — only one item open per card at a time
    registerToggleHandler('species', '.species-item', 'species-item--expanded', '.info-card-species');
    registerToggleHandler('baitItem', '.info-card-bait .rec-item', 'rec-item--expanded', '.info-card-bait', 'bait_card_expand');
    registerToggleHandler('rigItem', '.info-card-rig .rec-item', 'rec-item--expanded', '.info-card-rig', 'rig_card_expand');

    // Card expand toggle (custom logic — button text update)
    if (!tableState.handlers.cardExpand) {
        document.addEventListener('click', (e) => {
            const toggleBtn = e.target.closest('.card-expand-toggle');
            if (!toggleBtn) return;
            e.stopPropagation();
            const section = toggleBtn.previousElementSibling;
            if (section && section.classList.contains('card-expand-section')) {
                const isOpen = section.classList.toggle('card-expand-section--open');
                toggleBtn.textContent = isOpen
                    ? (toggleBtn.dataset.expandedText || 'Show Less')
                    : (toggleBtn.dataset.collapsedText || 'Show More');
            }
        });
        tableState.handlers.cardExpand = true;
    }

    // Retry handler for failed recommendation fetches (custom logic — skeleton reset + refetch)
    if (!tableState.handlers.recsRetry) {
        document.addEventListener('click', (e) => {
            const retryBtn = e.target.closest('.rec-error__retry-btn');
            if (retryBtn) {
                e.stopPropagation();
                const lk = retryBtn.dataset.locationKey;
                const er = document.getElementById(`expand-${lk}`);
                if (er) {
                    er.querySelectorAll('[data-recs-target]').forEach(el => {
                        el.innerHTML = buildRecSkeleton();
                    });
                    loadAndRenderRecommendations(lk, er);
                }
            }
        });
        tableState.handlers.recsRetry = true;
    }

    // Pro upgrade CTA — delegated handler on [data-upgrade] elements
    if (!tableState.handlers.proUpgrade) {
        document.addEventListener('click', function(e) {
            var upgradeEl = e.target.closest('[data-upgrade]');
            if (!upgradeEl) return;

            e.preventDefault();
            e.stopPropagation(); // Prevent row collapse or card toggle

            sendInteractionBeacon('gate_hit', { feature: upgradeEl.dataset.feature || 'unknown' });

            if (typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.isAuthenticated()) {
                showUpgradeModal();
            } else {
                showAppToast('Sign in to upgrade to Pro', 'info');
            }
        });
        tableState.handlers.proUpgrade = true;
    }

    // Admin forecast table export — delegated handler (lazy-loads html2canvas)
    if (!tableState.handlers.forecastExport) {
        document.addEventListener('click', function(e) {
            var btn = e.target.closest('.forecast-table-export-btn');
            if (!btn) return;
            e.stopPropagation();
            if (typeof ADMIN !== 'undefined' && ADMIN.exportForecastImage) {
                ADMIN.exportForecastImage(btn);
            }
        });
        tableState.handlers.forecastExport = true;
    }

    // Refresh-on-Demand button — delegated handler. Visible to all users; paywall on
    // click (not on visibility), mirroring the [data-upgrade] pattern above.
    // Two buttons share this handler (forecast table + fbi-card "now" card): both hit
    // the same endpoint and cooldown/budget, since one ROD fetch refreshes both sections,
    // so every successful refresh repaints both regardless of which button was clicked.
    if (!tableState.handlers.rodRefresh) {
        document.addEventListener('click', async function(e) {
            var btn = e.target.closest('.forecast-table-refresh-btn, .fbi-card__refresh-btn');
            if (!btn) return;
            e.stopPropagation();

            if (typeof AUTH_CLIENT === 'undefined' || !AUTH_CLIENT.isPro()) {
                if (typeof AUTH_CLIENT !== 'undefined' && AUTH_CLIENT.isAuthenticated()) {
                    showUpgradeModal();
                } else {
                    showAppToast('Sign in to use Premium Live Data', 'info');
                }
                return;
            }

            var locationKey = btn.dataset.locationKey;
            var spotId = getSpotIdFromKey(locationKey);
            var refreshUrl = spotId
                ? API_CLIENT.baseURL + '/spots/' + spotId + '/refresh'
                : API_CLIENT.baseURL + '/location/' + locationKey + '/refresh';

            btn.disabled = true;
            try {
                var res = await AUTH_CLIENT.fetchWithAuth(refreshUrl, { method: 'POST' });
                var data = await res.json();
                if (!data.success) {
                    showAppToast(data.error || 'Refresh failed', 'error');
                } else {
                    showAppToast('Refreshed conditions & forecast with premium live data', 'success');
                    // Update the in-memory item directly from the /refresh response and repaint —
                    // no second round-trip to /detail needed, the merge already happened server-side.
                    var item = (tableState.allConditions && tableState.allConditions.find(function(c) { return c.key === locationKey; }))
                        || (typeof SPOTS !== 'undefined' && SPOTS.findConditionByKey && SPOTS.findConditionByKey(locationKey));
                    if (item) {
                        if (!item.weatherData) item.weatherData = {};
                        item.weatherData._hourlyPeriods = data.hourlyPeriods;
                        if (data.weather) Object.assign(item.weatherData, data.weather);
                        if (data.conditions) item.conditions = data.conditions;
                        if (data.fbiTrend) item.fbiTrend = data.fbiTrend;
                        var expandRow = document.getElementById('expand-' + locationKey) || document.getElementById('ms-expand-' + locationKey);
                        renderFbiCard(item, locationKey, expandRow);
                        renderForecastAndTideChart(item, locationKey, expandRow);

                        // Repaint the score pill in the list row too. One key can be rendered in
                        // several tables (Angler list, My Spots public list, spots merged into the
                        // main list), so update every matching row. The row keeps its position;
                        // the next load re-sorts.
                        var tagHtml = buildFbiTagHTML(item);
                        var nameLineHtml = buildNameLineHTML(item);
                        document.querySelectorAll('tr.location-row[data-location-key="' + locationKey + '"]').forEach(function(row) {
                            var tagsLine = row.querySelector('.location-tags-line');
                            if (tagsLine) tagsLine.innerHTML = tagHtml;
                            // The warning pills live on the name line, not the tags line. A refresh
                            // can clear a bloom or surface a new one, so repaint them too or the row
                            // keeps the pre-refresh hazard while the expanded card shows the new one.
                            var nameLine = row.querySelector('.location-name-line');
                            if (nameLine) nameLine.innerHTML = nameLineHtml;
                        });
                        // A sidebar left open on the map may show the same location; repaint its
                        // pills too, or it keeps the pre-refresh hazard.
                        repaintSidebarWarningPills(document.getElementById('mapSidebarContent'), locationKey, item.conditions);
                    }
                    // Recs were scored against the old conditions — refetch on next expand.
                    tableState.recsCache.delete(locationKey);
                }
            } catch (err) {
                showAppToast('Refresh failed — try again', 'error');
            } finally {
                btn.disabled = false;
            }
        });
        tableState.handlers.rodRefresh = true;
    }

    // My Spot delete button — delegated handler
    if (!tableState.handlers.spotDelete) {
        document.addEventListener('click', function(e) {
            var btn = e.target.closest('.myspot-delete-btn');
            if (!btn) return;
            e.stopPropagation();
            var spotId = btn.dataset.spotId;
            if (!spotId) return;

            // Get spot name for confirmation message
            var row = btn.closest('.location-row');
            var spotName = row ? (row.querySelector('strong') || {}).textContent || 'this spot' : 'this spot';

            showDeleteConfirmModal(spotName, function() {
                AUTH_CLIENT.fetchWithAuth(API_CLIENT.baseURL + '/spots/' + spotId, { method: 'DELETE' })
                    .then(function(res) {
                        if (!res.ok) throw new Error('HTTP ' + res.status);
                        return res.json();
                    })
                    .then(function(result) {
                        if (result.success) {
                            var key = getSpotKeyFromId(spotId);
                            // Remove DOM rows from main table
                            var locRow = document.querySelector('#comparisonTable .location-row[data-location-key="' + key + '"]');
                            var expRow = document.getElementById('expand-' + key);
                            if (locRow) locRow.remove();
                            if (expRow) expRow.remove();
                            // Delegate all state cleanup to SPOTS (single source of truth)
                            if (typeof SPOTS !== 'undefined' && SPOTS.handleSpotDeleted) {
                                SPOTS.handleSpotDeleted(key);
                            }
                            if (typeof showAppToast === 'function') showAppToast('Spot deleted', 'success');
                        } else {
                            if (typeof showAppToast === 'function') showAppToast(result.error || 'Failed to delete spot', 'error');
                        }
                    })
                    .catch(function(err) {
                        console.error('[Spots] Delete error:', err.message);
                        if (typeof showAppToast === 'function') showAppToast('Failed to delete spot', 'error');
                    });
            });
        });
        tableState.handlers.spotDelete = true;
    }

    // Quick start trip button — delegated handler
    if (!tableState.handlers.quickStartTrip) {
        document.addEventListener('click', function(e) {
            var btn = e.target.closest('.quick-start-trip-btn');
            if (!btn) return;
            e.stopPropagation();
            if (typeof JOURNAL !== 'undefined' && typeof JOURNAL.quickStartTrip === 'function') {
                JOURNAL.quickStartTrip(
                    btn.dataset.locationName,
                    btn.dataset.locationKey || null,
                    btn.dataset.lat || null,
                    btn.dataset.lon || null
                );
            }
        });
        tableState.handlers.quickStartTrip = true;
    }
}

/**
 * Build the FBI score pill (score · label + trend arrow) for a list row.
 * Shared by buildLocationRowHTML() and the ROD click handler, which repaints the pill in place.
 * @param {Object} item - Location condition item
 * @returns {string} HTML for the contents of .location-tags-line
 */
function buildFbiTagHTML(item) {
    // Error spots show "Unavailable"
    if (item._isMySpot === true && item._error === true) {
        return `<span class="fbi-tag">-- · Unavailable</span>`;
    }

    const fbiScore = item.conditions?.score ?? '--';
    let trendHtml = '';
    // Server-computed fbiTrend whenever present, Stable included: SSR, SSE, REST and the ROD
    // response all send it. The client-side fallback renders Stable as ▸, which the server
    // trend never does, so gating on a non-empty arrow gave a Stable row a ▸ after a refresh.
    const trend = item.fbiTrend
        ? item.fbiTrend
        : (typeof getFBITrend === 'function' && typeof convertNWSPeriodsToForecast === 'function' && item.weatherData?._hourlyPeriods)
            ? (() => {
                const periods = Array.isArray(item.weatherData._hourlyPeriods) ? item.weatherData._hourlyPeriods.slice(0, APP_TIMING.MAX_FORECAST_PERIODS) : [];
                const forecast = convertNWSPeriodsToForecast(periods, item.tideData24h) || [];
                return getFBITrend(fbiScore, forecast);
            })()
            : { arrow: '', cls: 'stable', label: 'Stable' };
    if (trend.arrow) trendHtml = `<span class="fbi-trend fbi-trend--${trend.cls}" title="${trend.label}">${trend.arrow}</span>`;
    const numericScore = typeof fbiScore === 'number' ? fbiScore : 0;
    const scoreColor = typeof getScoreColor === 'function' ? getScoreColor(numericScore) : '';
    const scoreLabel = typeof getScoreLabel === 'function' ? getScoreLabel(numericScore) : '';
    const tagStyle = numericScore >= 70 ? ` style="background:${scoreColor}"` : '';
    return `<span class="fbi-tag"${tagStyle}>${fbiScore} · ${scoreLabel}${trendHtml}</span>`;
}

/**
 * Build the contents of .location-name-line: the name, the My Spot crown that marks
 * identity, then every warning pill that has data, in the shared priority order.
 *
 * Kept separate from buildLocationRowHTML() because the Premium Live Data refresh
 * repaints this line in place — the pills it carries are the only place a cleared or
 * newly-appearing hazard shows in the list row.
 * @param {Object} item - Location condition item
 * @returns {string} HTML for the contents of .location-name-line
 */
function buildNameLineHTML(item) {
    const crownHtml = item._isMySpot === true
        ? `<img src="./img/pro-sticker.png" class="myspot-crown" width="24" height="24" alt="My Spot">`
        : '';
    return `<strong>${escapeHtml(item.location.name)}</strong>` + crownHtml + buildWarningPillsHTML(item.conditions);
}

/**
 * Build HTML strings for a location row + its expand row shell.
 * Shared by progressiveRenderTable() and renderTableByRegion().
 * @param {Object} item - Location condition item { key, conditions, location, weatherData, tideData24h }
 * @returns {{ locationRowHTML: string, expandRowHTML: string }}
 */
function buildLocationRowHTML(item, expandIdPrefix) {
    var prefix = expandIdPrefix || '';
    const isMySpot = item._isMySpot === true;
    const isSpotError = isMySpot && item._error === true;
    const tagsHtml = buildFbiTagHTML(item);
    const nameLineHtml = buildNameLineHTML(item);

    // Row classes + background
    let rowClasses, bgDataAttr;
    if (isMySpot) {
        rowClasses = isSpotError ? 'location-row location-row--myspot location-row--myspot-error' : 'location-row location-row--myspot';
        bgDataAttr = '';
    } else {
        const hasBgImage = !!item.location.pictureUrl2;
        const safeBgUrl = hasBgImage ? item.location.pictureUrl2.replace(/['"()]/g, '') : '';
        bgDataAttr = hasBgImage ? ` data-bg-url="${safeBgUrl}"` : '';
        rowClasses = hasBgImage ? 'location-row has-bg-image' : 'location-row';
    }

    const spotId = isMySpot && item._spotMeta ? item._spotMeta.id : '';
    const deleteBtn = isMySpot ? `<button class="myspot-delete-btn" data-spot-id="${spotId}" aria-label="Delete spot" title="Delete spot"><img src="./img/delete.png" alt="Delete" width="18" height="18"></button>` : '';
    const locationRowHTML = `<tr class="${rowClasses}" data-location-key="${item.key}" tabindex="0" aria-expanded="false"${bgDataAttr}><td><div class="location-header-line"><div class="location-name-line">${nameLineHtml}</div><div class="location-tags-line">${tagsHtml}</div>${deleteBtn}</div></td></tr>`;
    const expandRowHTML = `<tr class="expanded-row" id="${prefix}expand-${item.key}"><td><div class="expanded-row-content" data-location-key="${item.key}"></div></td></tr>`;

    return { locationRowHTML, expandRowHTML };
}

/**
 * Progressive table rendering — re-renders table with data received so far
 */
function progressiveRenderTable(allConditionsSoFar) {
    const table = document.getElementById('comparisonTable');
    if (!table) return;

    tableState.allConditions = allConditionsSoFar;

    // First call: create table structure
    let tbody = table.querySelector('tbody');
    if (!tbody) {
        table.innerHTML = '<colgroup><col style="width: auto;"></colgroup><tbody></tbody>';
        tbody = table.querySelector('tbody');
    }

    // Build only NEW rows (items not yet in DOM)
    const newItems = allConditionsSoFar.filter(item => !tableState.renderedKeys.has(item.key));
    if (newItems.length === 0) return;

    let rowsHTML = '';
    newItems.forEach((item) => {
        const { locationRowHTML, expandRowHTML } = buildLocationRowHTML(item);
        rowsHTML += locationRowHTML + expandRowHTML;
        tableState.renderedKeys.add(item.key);
    });

    tbody.insertAdjacentHTML('beforeend', rowsHTML);

    if (!tableState.handlers.table) {
        table.addEventListener('click', handleTableClick);
        tableState.handlers.table = true;
    }

    // Pass only the newly-added rows to avoid re-scanning the full DOM on every SSE batch
    const newBgRows = newItems
        .map(item => tbody.querySelector(`.location-row[data-bg-url][data-location-key="${item.key}"]`))
        .filter(Boolean);
    if (newBgRows.length > 0) setupLazyBackgrounds(newBgRows);
}

/**
 * Display fishing conditions in comparison table and map
 *
 * Builds an interactive table showing all locations ranked by FBI score.
 * Each row is clickable to show detailed weather/tide information.
 * Also initializes the map with all locations filtered by region.
 *
 * @param {Array} allConditions - Fishing data for all locations
 */
function displayComparison(allConditions) {
    const tabContainer = document.getElementById('tabContainer');

    // Update countdown timer immediately
    updateCountdown();

    // Show tab container
    tabContainer.classList.remove('u-hidden');

    // Initialize tab navigation
    initializeTabs();

    // Initialize both table and map (default to 'all' regions, 'all' count, and 'Texas' state)
    const regionSelect = document.getElementById('regionSelect');
    const countSelect = document.getElementById('countSelect');
    const selectedRegion = regionSelect ? regionSelect.value : 'all';
    const selectedCount = countSelect ? countSelect.value : 'all';
    const defaultState = 'Texas';
    renderTableByRegion(allConditions, selectedRegion, selectedCount, defaultState);
    showMapWithLocationsByRegion(allConditions, selectedRegion, selectedCount, defaultState);

    // Populate region dropdown AFTER map init (popup <select> is created inside showMapWithLocationsByRegion)
    populateRegionDropdown(allConditions);

    // Populate settings dropdowns and apply saved default filters
    if (window.AUTH_UI) {
        AUTH_UI.populateSettingsDropdowns();
        AUTH_UI.applyDefaultFilters();
        if (typeof showTopSpotCard === 'function') showTopSpotCard();
    }
}

/* ==========================================================================
   TABLE RENDERING (CRITICAL)
   DO NOT MODIFY scoring logic without full regression testing
   ========================================================================== */

/**
 * Render comparison table filtered by region, state, and count
 * @param {Array} allConditions - All fishing data
 * @param {string} selectedRegion - Region to filter by ('all' for no filter)
 * @param {string} selectedCount - Number of spots to show ('all', '5', '10')
 * @param {string} selectedState - State to filter by ('all' for no filter)
 */
function renderTableByRegion(allConditions, selectedRegion = 'all', selectedCount = 'all', selectedState = 'all') {
    const table = document.getElementById('comparisonTable');
    if (!table) return;

    // Update module-level state for event delegation and lazy detail
    tableState.allConditions = allConditions;
    resetTableState();

    // Filter locations by selected state first
    let filteredConditions = selectedState === 'all'
        ? allConditions
        : allConditions.filter(item => item.location.state === selectedState);

    // Then filter by selected region (always include user spots)
    if (selectedRegion !== 'all') {
        filteredConditions = filteredConditions.filter(item => item._isMySpot || item.location.region === selectedRegion);
    }

    // Limit by count if specified
    if (selectedCount !== 'all') {
        const limit = parseInt(selectedCount, 10);
        filteredConditions = filteredConditions.slice(0, limit);
    }

    // Build table HTML - colgroup for fixed widths
    let html = '<colgroup><col style="width: auto;"></colgroup>';
    html += '<tbody>';

    // Build table rows for each location
    filteredConditions.forEach((item) => {
        const { locationRowHTML, expandRowHTML } = buildLocationRowHTML(item);
        html += locationRowHTML + expandRowHTML;
    });

    // Close table and render
    html += '</tbody>';
    table.innerHTML = html;

    // Attach delegated click handler ONCE (survives re-renders)
    if (!tableState.handlers.table) {
        table.addEventListener('click', handleTableClick);
        tableState.handlers.table = true;
    }

    setupLazyBackgrounds();
}

/**
 * Lazy-load bait/rig recommendations from server and populate cards.
 * Checks per-session cache first; fetches with 5s timeout on miss.
 * @param {string} locationKey - Location key for API endpoint
 * @param {HTMLElement} expandRow - The expanded row container DOM element
 */
async function loadAndRenderRecommendations(locationKey, expandRow) {
    // Check session cache first
    if (tableState.recsCache.has(locationKey)) {
        var cached = tableState.recsCache.get(locationKey);
        renderBaitRigCards(expandRow, cached, cached._gated);
        return;
    }

    var controller = new AbortController();
    var timeoutId = setTimeout(function() { controller.abort(); }, APP_TIMING.REC_FETCH_TIMEOUT_MS);

    try {
        var spotId = getSpotIdFromKey(locationKey);
        var recsUrl = spotId
            ? API_CLIENT.baseURL + '/spots/' + spotId + '/recommendations'
            : API_CLIENT.baseURL + '/location/' + locationKey + '/recommendations';
        var response = await AUTH_CLIENT.fetchWithAuth(
            recsUrl,
            { signal: controller.signal }
        );
        clearTimeout(timeoutId);

        if (!response.ok) throw new Error('HTTP ' + response.status);
        var data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed');

        // A7: Attach gated flag as property on the recommendations object
        if (data.recommendations) {
            data.recommendations._gated = data.gated || false;
        }

        tableState.recsCache.set(locationKey, data.recommendations);
        renderBaitRigCards(expandRow, data.recommendations, data.recommendations._gated);
    } catch (err) {
        clearTimeout(timeoutId);
        console.error('[Recs] Fetch failed:', err.message);
        // Show error state in bait/rig cards
        expandRow.querySelectorAll('[data-recs-target]').forEach(function(el) {
            el.innerHTML = '<div class="rec-error">' +
                'Couldn\'t load recommendations.' +
                '<button class="rec-error__retry-btn" data-location-key="' + locationKey + '">Retry</button>' +
            '</div>';
        });
    }
}

/**
 * Lazy-load detail data (hourly periods, tide data) for the forecast table and tide chart.
 * Enriches the in-memory item so re-expands don't re-fetch.
 */
async function loadAndRenderDetail(locationKey, item, expandRow) {
    var controller = new AbortController();
    var timeoutId = setTimeout(function() { controller.abort(); }, APP_TIMING.REC_FETCH_TIMEOUT_MS);

    try {
        var spotId = getSpotIdFromKey(locationKey);
        var detailUrl = spotId
            ? API_CLIENT.baseURL + '/spots/' + spotId + '/detail'
            : API_CLIENT.baseURL + '/location/' + locationKey + '/detail';
        var response = await AUTH_CLIENT.fetchWithAuth(
            detailUrl,
            { signal: controller.signal }
        );
        clearTimeout(timeoutId);

        if (!response.ok) throw new Error('HTTP ' + response.status);
        var data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed');

        // Enrich the in-memory item so future re-expands have full data
        if (!item.weatherData) item.weatherData = {};
        item.weatherData._hourlyPeriods = data.detail.hourlyPeriods;
        item.tideData24h = data.detail.tideData24h;
        if (data.detail.sunriseSunsetData) item.sunriseSunsetData = data.detail.sunriseSunsetData;

        // Store gating metadata for forecast table
        item._forecastGated = data.detail.forecastGated || false;
        item._forecastFreeHours = data.detail.freeHours;
        item._forecastTotalHours = data.detail.totalHours;

        // Only render if this row is still expanded
        if (expandRow.classList.contains('show')) {
            renderForecastAndTideChart(item, locationKey, expandRow);
        }
    } catch (err) {
        clearTimeout(timeoutId);
        console.warn('[Detail] Fetch failed, showing without forecast:', err.message);
    }
}

/**
 * Repaint the current-conditions (fbi-card) block in place from item.conditions/weatherData —
 * used after a Refresh-on-Demand call updates both in memory.
 */
function renderFbiCard(item, locationKey, targetExpandRow) {
    if (!item.conditions || typeof buildFbiCardHTML !== 'function') return;
    var expandRow = targetExpandRow || document.getElementById('expand-' + locationKey) || document.getElementById('ms-expand-' + locationKey);
    if (!expandRow) return;
    var fbiCard = expandRow.querySelector('.fbi-card');
    if (fbiCard) {
        fbiCard.outerHTML = buildFbiCardHTML(item.conditions, item.location, item.weatherData, locationKey);
    }
}

/**
 * Render forecast table and tide chart from item data (after detail data is available).
 */
function renderForecastAndTideChart(item, locationKey, targetExpandRow) {
    if (item.weatherData?._hourlyPeriods && typeof convertNWSPeriodsToForecast === 'function') {
        var periodsToUse = Array.isArray(item.weatherData._hourlyPeriods) ? item.weatherData._hourlyPeriods.slice(0, APP_TIMING.MAX_FORECAST_PERIODS) : [];
        window.fishingForecast = convertNWSPeriodsToForecast(periodsToUse, item.tideData24h) || [];
    }
    window.sunriseSunsetData = item.sunriseSunsetData || {};

    // Build gating info for forecast table (Phase 8)
    var gatingInfo = item._forecastGated
        ? { gated: true, freeHours: item._forecastFreeHours, totalHours: item._forecastTotalHours }
        : null;

    // Use the passed expand row, or fall back to DOM lookup
    var expandRow = targetExpandRow || document.getElementById('expand-' + locationKey) || document.getElementById('ms-expand-' + locationKey);
    if (expandRow) {
        var forecastTarget = expandRow.querySelector('.forecast-lazy-target[data-location-key="' + locationKey + '"]');
        if (forecastTarget && typeof buildForecastTable === 'function' && window.fishingForecast.length > 0) {
            forecastTarget.innerHTML = buildForecastTable(window.fishingForecast, gatingInfo, item.location.name, locationKey);
        }
    }

    // Draw tide chart
    if (item.tideData24h && typeof drawTideChart === 'function') {
        setTimeout(() => {
            const canvasId = `tideChart-${item.location.name.replace(/\s/g, '-')}`;
            const canvas = document.getElementById(canvasId);
            if (canvas) drawTideChart(canvas, item.tideData24h);
        }, APP_TIMING.TIDE_CHART_DELAY_MS);
    }
}

/**
 * Toggle row expansion with lazy detail loading, O(1) collapse, and tooltip lifecycle.
 * Unified handler for both SSR-hydrated and client-rendered tables.
 * @param {string} locationKey - Location key for the row
 * @param {Array} [allConditions] - Optional conditions data (SSR path passes this; client path uses tableState.allConditions)
 */
function toggleRowExpansion(locationKey, allConditions, expandRowId) {
    var eid = expandRowId || ('expand-' + locationKey);
    const expandRow = document.getElementById(eid);
    if (!expandRow) {
        console.warn('[Table] Expand row not found:', locationKey);
        return;
    }

    const table = expandRow.closest('table');
    const conditions = allConditions || tableState.allConditions;
    const locationRow = table?.querySelector(`.location-row[data-location-key="${locationKey}"]`);
    const isExpanding = !expandRow.classList.contains('show');

    // Collapse previous row by stored expand ID (cross-table safe)
    if (tableState._expandId && tableState._expandId !== eid) {
        const prevExpand = document.getElementById(tableState._expandId);
        if (prevExpand) {
            TOOLTIP.hideWithin(prevExpand);
            prevExpand.classList.remove('show');
            const prevRow = prevExpand.previousElementSibling;
            if (prevRow && prevRow.classList.contains('location-row')) prevRow.setAttribute('aria-expanded', 'false');
        }
    }

    // Toggle current row
    expandRow.classList.toggle('show');
    if (locationRow) locationRow.setAttribute('aria-expanded', String(isExpanding));
    tableState.expandedKey = expandRow.classList.contains('show') ? locationKey : null;
    tableState._expandId = expandRow.classList.contains('show') ? eid : null;

    // The tooltip lives in <body>, so it would outlive a collapsing row
    if (!isExpanding) {
        TOOLTIP.hideWithin(expandRow);
    }

    if (isExpanding) {
        try {
            const contentDiv = expandRow.querySelector('.expanded-row-content');
            const needsRender = !tableState.detailCache.has(locationKey) || !contentDiv.innerHTML.trim();
            if (contentDiv && needsRender) {
                if (!conditions) {
                    console.warn('[Table] No conditions data for expansion');
                } else {
                    const item = conditions.find(c => c.key === locationKey);
                    if (!item) {
                        console.warn('[Table] Location not found in conditions:', locationKey);
                    } else if (item._isMySpot && item._error) {
                        // Error spot — show error state with delete option
                        contentDiv.innerHTML = buildSpotErrorHTML(item);
                        tableState.detailCache.set(locationKey, true);
                    } else {
                        // Fire recommendations fetch (async, network-bound)
                        loadAndRenderRecommendations(locationKey, expandRow).catch(err => {
                            console.error('[Table] Recommendations load failed:', err);
                        });

                        // Render detail card immediately with available data
                        window.fishingForecast = [];
                        window.sunriseSunsetData = item.sunriseSunsetData || {};
                        contentDiv.innerHTML = buildDetailHTML(item.conditions, item.location, item.weatherData, item.tideData24h, locationKey);

                        tableState.detailCache.set(locationKey, true);

                        // Lazy-load forecast/tide detail if not already present
                        if (!item.weatherData?._hourlyPeriods) {
                            loadAndRenderDetail(locationKey, item, expandRow).catch(err => {
                                console.error('[Table] Detail load failed:', err);
                            });
                        } else {
                            // Full data available (SSR path) — render forecast immediately
                            renderForecastAndTideChart(item, locationKey, expandRow);
                        }
                    }
                }
            }
        } catch (e) {
            console.error('[Table] Error loading expanded row content:', e);
        }

        if (locationRow) {
            locationRow.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
        }

        // Draw tide chart on re-expand (first expand handled by renderForecastAndTideChart)
        if (tableState.detailCache.has(locationKey)) {
            const reExpandItem = conditions?.find(c => c.key === locationKey);
            if (reExpandItem?.tideData24h) {
                setTimeout(() => {
                    const canvasId = `tideChart-${reExpandItem.location.name.replace(/\s/g, '-')}`;
                    const canvas = document.getElementById(canvasId);
                    if (canvas) drawTideChart(canvas, reExpandItem.tideData24h);
                }, APP_TIMING.TIDE_CHART_DELAY_MS);
            }
        }

    }
}

/**
 * Populate every region/state <select> in the app — the mobile popup's and the desktop
 * inline filter group's — with regions/states derived from the current fishing data.
 * (populateRegionSelect/populateStateSelect are defined in map.js.)
 * @param {Array} allConditions - Fishing data for all locations
 */
function populateRegionDropdown(allConditions) {
    if (typeof populateRegionSelect === 'function') {
        populateRegionSelect(document.getElementById('popupRegionSelect'), allConditions);
        populateRegionSelect(document.getElementById('inlineRegionSelect'), allConditions);
    }
    if (typeof populateStateSelect === 'function') {
        populateStateSelect(document.getElementById('popupStateSelect'), allConditions);
        populateStateSelect(document.getElementById('inlineStateSelect'), allConditions);
    }
}

/* ==========================================================================
   CONSENT & TOS FLOW
   ========================================================================== */

/**
 * Handle Continue button click
 *
 * Called after user checks both consent boxes.
 * Hides the consent UI and starts loading fishing data.
 */
function handleContinue() {
    const tosCheckbox = document.getElementById('tosCheckbox');
    const pledgeCheckbox = document.getElementById('pledgeCheckbox');

    // Verify both checkboxes are checked (should always be true if button is enabled)
    if (tosCheckbox.checked && pledgeCheckbox.checked) {
        // Hide greeting screen and show main app
        const greetingScreen = document.getElementById('greetingScreen');
        if (greetingScreen) greetingScreen.style.display = 'none';

        // Load and display fishing data
        compareAllLocations();
    }
}


/**
 * TOS Inline Functions
 * TOS content is shown inline within the greeting card (step 3)
 */

/**
 * Open TOS inline view
 * Hides consent step, shows TOS content within same card
 */
function openTOSModal() {
    const consentStep = document.getElementById('consentStep');
    const tosStep = document.getElementById('tosStep');
    const tosBody = document.getElementById('tosInlineBody');
    const acceptButton = document.getElementById('tosAcceptButton');

    // Insert TOS content
    tosBody.innerHTML = TOS_CONTENT;

    // Reset Accept button to disabled
    acceptButton.disabled = true;

    // Toggle steps
    if (consentStep) consentStep.style.display = 'none';
    if (tosStep) tosStep.style.display = 'flex';

    // Add scroll listener to enable Accept button when scrolled to bottom
    tosBody.addEventListener('scroll', checkScrollPosition);

    // Check immediately in case content doesn't need scrolling
    setTimeout(() => checkScrollPosition.call(tosBody), 100);
}

/**
 * Check if user has scrolled to bottom of TOS content
 * Enables Accept button when scrolled to bottom
 */
function checkScrollPosition() {
    const tosBody = document.getElementById('tosInlineBody');
    const acceptButton = document.getElementById('tosAcceptButton');

    if (!tosBody || !acceptButton) return;

    // Check if scrolled to bottom (with 10px threshold)
    const scrolledToBottom = tosBody.scrollHeight - tosBody.scrollTop <= tosBody.clientHeight + 10;

    if (scrolledToBottom) {
        acceptButton.disabled = false;
    }
}

/**
 * Dismiss TOS modal and return to consent step
 * @param {boolean} accepted - Whether TOS was accepted (true) or cancelled (false)
 */
function closeTOSStep(accepted) {
    const tosCheckbox = document.getElementById('tosCheckbox');
    const tosStep = document.getElementById('tosStep');
    const consentStep = document.getElementById('consentStep');
    const tosBody = document.getElementById('tosInlineBody');
    if (tosCheckbox) tosCheckbox.checked = accepted;
    if (tosStep) tosStep.style.display = 'none';
    if (consentStep) consentStep.style.display = 'flex';
    if (tosBody) tosBody.removeEventListener('scroll', checkScrollPosition);
    updateContinueButton();
}
function closeTOSModal() { closeTOSStep(false); }
function acceptTOS() { closeTOSStep(true); }

/**
 * Update Continue button state based on checkboxes
 */
function updateContinueButton() {
    const tosCheckbox = document.getElementById('tosCheckbox');
    const pledgeCheckbox = document.getElementById('pledgeCheckbox');
    const continueButton = document.getElementById('continueButton');

    continueButton.disabled = !(tosCheckbox.checked && pledgeCheckbox.checked);
}

/* ==========================================================================
   INITIALIZATION
   ========================================================================== */

/**
 * Check for SSR data and hydrate if available
 * Returns true if SSR data was found and used
 */
function checkAndHydrateSSR() {
    // First check: Does the table already have SSR content?
    const table = document.getElementById('comparisonTable');
    const hasSSRRows = table && table.querySelectorAll('.location-row').length > 0;

    if (!hasSSRRows) {
        console.log('[SSR] No SSR table content found');
        return false;
    }

    // Second check: Try to parse embedded JSON data
    const ssrDataEl = document.getElementById('ssr-data');
    if (!ssrDataEl) {
        console.log('[SSR] Table has content but no JSON data - will fetch data for interactivity');
        // Initialize tabs immediately so UI is responsive
        initializeTabs();
        // Return false to trigger API call for full interactivity
        return false;
    }

    try {
        const ssrData = JSON.parse(ssrDataEl.textContent);
        if (!ssrData || !ssrData.locations || ssrData.locations.length === 0) {
            console.log('[SSR] SSR JSON empty, but table has content');
            isSSRHydrated = true;
            initializeTabs();
            return true;
        }

        console.log('[SSR] Hydrating with', ssrData.locations.length, 'locations');

        // Store data globally
        setConditionsData(ssrData.locations);
        isSSRHydrated = true;

        // Set update timestamp
        if (ssrData.timestamp) {
            dataUpdatedAt = new Date(ssrData.timestamp).getTime();
            if (countdownInterval) {
                clearInterval(countdownInterval);
            }
            countdownInterval = setInterval(updateCountdown, APP_TIMING.COUNTDOWN_INTERVAL_MS);
            updateCountdown();
        }

        // Hydrate the existing DOM (attach event listeners, init map)
        hydrateSSRContent(ssrData.locations);

        return true;
    } catch (error) {
        console.error('[SSR] Error parsing SSR data:', error);
        // JSON parsing failed - fetch data for interactivity
        initializeTabs();
        return false;
    }
}

/**
 * Hydrate server-rendered content with event listeners and interactive features
 * This does NOT rebuild the table HTML - it's already rendered by the server
 */
function hydrateSSRContent(allConditions) {
    const tabContainer = document.getElementById('tabContainer');
    const table = document.getElementById('comparisonTable');
    const errorDiv = document.getElementById('error');

    // Hide any error messages
    if (errorDiv) {
        errorDiv.style.display = 'none';
    }

    // Note: tabContainer visibility is controlled by auth flow
    // (showMainApp / showGreetingScreen in auth-ui.js)

    // Initialize tab navigation
    initializeTabs();

    // Map init deferred to compareAllLocations() → displayComparison() so it
    // opens at the correct zoom with staggered pin drops (no double tile render)

    // Attach delegated click handler to SSR-rendered table
    if (table && !tableState.handlers.table) {
        tableState.allConditions = allConditions;
        table.addEventListener('click', handleTableClick);
        tableState.handlers.table = true;
    }

    setupLazyBackgrounds();

    console.log('[SSR] Hydration complete with', allConditions ? allConditions.length : 0, 'locations');
}

// attachRowClickHandlers removed — unified into handleTableClick delegation

/**
 * Initialize page on load
 *
 * Sets up authentication, TOS modal behavior, and checkbox listeners.
 */
document.addEventListener('DOMContentLoaded', async function() {
    // Check for SSR data first - if available, hydrate instead of fetching
    const hasSSRData = checkAndHydrateSSR();

    // Register document-level delegated handlers (species, bait, rig toggles, etc.)
    registerDelegatedHandlers();

    // Tooltips for every [data-tip] element: FBI scores, section hints (tooltip.js)
    TOOLTIP.init();

    // Initialize mobile More menu
    if (typeof MORE_MENU !== 'undefined') {
        MORE_MENU.init();
    }

    // Initialize authentication
    // Show greeting screen after 3s max even if API is slow (Railway cold start)
    let greetingShown = false;
    const authTimeout = setTimeout(() => {
        if (document.body.classList.contains('auth-pending')) {
            console.warn('[Main] Auth timeout — showing greeting screen');
            document.body.classList.remove('auth-pending');
            AUTH_UI.showGreetingScreen();
            greetingShown = true;
        }
    }, APP_TIMING.AUTH_TIMEOUT_MS);

    if (typeof AUTH_CLIENT !== 'undefined') {
        // 1. Fetch config first (needed for Google client ID)
        await AUTH_CLIENT.fetchConfig();

        // 2. Start Google button loading immediately (runs in background
        //    while session restore happens — saves 1-3s on the button appearing)
        AUTH_CLIENT.initGoogleButton('googleButtonContainer');

        // 3. Try to restore existing session
        await AUTH_CLIENT.tryRestoreSession();

        clearTimeout(authTimeout);

        // Handle ?plan=pro from landing page CTA (before auth branch — stashes flag if needed)
        handlePlanParam();

        if (AUTH_CLIENT.isAuthenticated()) {
            // Remove auth-pending AFTER deciding to show main app
            // (prevents greeting screen flash from default CSS)
            document.body.classList.remove('auth-pending');
            await AUTH_UI.showMainApp(); // showMainApp() calls compareAllLocations() internally
            // Handle checkout return from Stripe redirect
            handleCheckoutReturn();
        } else {
            // Only show greeting if the auth timeout didn't already show it
            if (!greetingShown) {
                document.body.classList.remove('auth-pending');
                AUTH_UI.showGreetingScreen();
            } else {
                // Greeting appeared before the session check finished; explain a device-limit sign-out now
                AUTH_UI.showSignOutNotice();
            }

            // Check for auth_error from Google redirect mode (Firefox)
            const urlParams = new URLSearchParams(window.location.search);
            const authError = urlParams.get('auth_error');
            if (authError) {
                AUTH_UI.showAuthError('Sign-in failed. Please try again.');
                // Clean up URL
                window.history.replaceState({}, '', window.location.pathname + window.location.hash);
            }
        }
    } else {
        clearTimeout(authTimeout);
        // Fallback: Auth not available, show old consent flow
        document.body.classList.remove('auth-pending');
        console.warn('[Main] Auth client not available, using legacy flow');
        const consentSection = document.getElementById('consentSection');
        const continueButton = document.getElementById('continueButton');
        if (consentSection) consentSection.style.display = 'block';
        if (continueButton) continueButton.style.display = 'block';
    }

    // Setup TOS checkbox listeners (for guest flow)
    const tosCheckbox = document.getElementById('tosCheckbox');
    const pledgeCheckbox = document.getElementById('pledgeCheckbox');

    if (tosCheckbox) {
        // Intercept TOS checkbox clicks to show modal instead
        tosCheckbox.addEventListener('click', function(e) {
            e.preventDefault(); // Prevent default checkbox behavior
            openTOSModal();
        });
    }

    if (pledgeCheckbox) {
        // Listen for pledge checkbox changes
        pledgeCheckbox.addEventListener('change', updateContinueButton);
    }

    // Add Spot toggle button handler
    const addSpotToggleBtn = document.getElementById('addSpotToggleBtn');
    const addSpotSection = document.getElementById('addSpotSection');
    if (addSpotToggleBtn && addSpotSection) {
        addSpotToggleBtn.addEventListener('click', function() {
            addSpotSection.classList.toggle('u-hidden');
        });
    }

});

